import React, { useState, useEffect, useRef, useContext, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import s from './SiteSettings.module.sass';
import defaultSettings from '../../config/defaultSettings.json';
import useToast from '../../hooks/useToast';
import AppContext from '../../app-core/AppContext';
import AddLanguageModal from '../AddLanguageModal/AddLanguageModal';
import { getLanguageByCode, getAvailableLanguages } from '../../config/languages';
import { getFlatFlagsList, getFlagInfo, popularCountries } from '../../config/flags';
import FlagIcon from '../FlagIcon/FlagIcon';
import AdaptiveLogo from '../AdaptiveLogo/AdaptiveLogo';
import { getCachedConfigFlags, loadConfigFlags } from '../../utils/configFlagCache';
import { getCountryNameByFlag } from '../../utils/countryNames';
import { getApiUrl } from '../../config/api';
import { getCurrentUserRole, getAvailableTabs, canAccessSiteSettingsTab } from '../../utils/permissions';
import { googleFonts, loadFontByValue, loadGoogleFont, extractFontNameFromUrl } from '../../utils/fontLoader';
import { buildWalletsByCurrencyFromLegacy, CRYPTO_OPTIONS, WALLET_TYPES } from '../../utils/walletResolver';
import { notifyError } from '../../utils/notify';
import { confirmDialog } from '../../utils/confirmDialog';
import { plural, pluralConfigs } from '../../utils/plural';
import {
    SlidersHorizontal, Coins, Wallet as WalletGlyph, Landmark, Languages,
    LayoutTemplate, MessagesSquare, Download, Upload, RotateCcw, Save as SaveGlyph,
    Type, Image as ImageGlyph, Palette, LayoutDashboard, Bitcoin, UserRound,
    CircleUserRound, ShieldCheck, Star, Globe, FileText, Earth, CalendarClock,
    CalendarDays, Clock, BadgeCheck, SendHorizontal, Hash, CircleDollarSign,
    Flag, Wallpaper, Calculator, CreditCard, Zap, ChevronDown, Check, Trash2,
    ArrowUp, X as XGlyph, GripVertical, Trophy, PiggyBank, Percent, PlusCircle,
    ArrowLeftRight, Briefcase, PieChart, Crown, Gauge, Puzzle, Shuffle,
    Banknote, Gem, Flame, Eye, EyeOff, Plus, ImageUp,
    Menu, Mail, TriangleAlert, TrendingUp, Search, Minus,
    PencilLine, Folder, MessageSquareText, FoldVertical, FolderPlus,
    MessageSquarePlus, Braces, Pin, MoveHorizontal, ListChecks,
} from 'lucide-react';

// Импорт иконок криптовалют
import bitcoinIcon from '../../img/Bitcoin.svg.png';
import ethereumIcon from '../../img/Ethereum-ETH-icon.png';
import bnbIcon from '../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import usdcIcon from '../../img/usdclogo.png';
import solanaIcon from '../../img/solana.png';

// Импорт иконок для замены эмодзи
import siteSettingsIcon from '../../img/iconsas/site_settings.png';
import currencyRatesIcon from '../../img/iconsas/currency_rates.png';
import walletsIcon from '../../img/iconsas/wallets.png';
import nameUserChatIcon from '../../img/iconsas/name_user_and_chat_preset.png';
import supportChatIcon from '../../img/support-chat-normal.svg';
import timezoneIcon from '../../img/iconsas/timezone.png';
import znakovPosleZapyatoiIcon from '../../img/iconsas/znakov_posle_zapyatoi.png';
import currencySymbolIcon from '../../img/iconsas/currency_symbol.png';
import tarifsSettingsIcon from '../../img/iconsas/tarifs_settings.png';
import usersWinningsIcon from '../../img/iconsas/users_winnings.png';
import deposutSumIcon from '../../img/iconsas/deposut_sum.png';
import commissionSizeIcon from '../../img/iconsas/comission_size.png';
import additionalSumIcon from '../../img/iconsas/additional_sum.png';
import taxPercentIcon from '../../img/iconsas/tax_percent.png';
import cryptoConversionModalIcon from '../../img/iconsas/crypto_conversion_modal.png';
import bankConversionModalIcon from '../../img/iconsas/bank_conversion_modal.png';
import commissionModalIcon from '../../img/iconsas/comission_modal.png';
import verificationModalIcon from '../../img/iconsas/verification_modal.png';
import successModalIcon from '../../img/iconsas/success_modal.png';
import taxModalIcon from '../../img/iconsas/tax_modal.png';
import bankConfigurationIcon from '../../img/iconsas/bank_configuration.png';
import userInfoIcon from '../../img/iconsas/user_information.png';
import exportButtonIcon from '../../img/iconsas/export_button.png';
import importButtonIcon from '../../img/iconsas/import_button.png';
import resetSettingsButtonIcon from '../../img/iconsas/reset_settings_button.png';
import saveButtonIcon from '../../img/iconsas/save_button.png';
import quickActionsIcon from '../../img/iconsas/quickactions.png';
// Иконки для новых финансовых настроек
import transactionsIcon from '../../img/iconsas/transactions.png';
import customValuesIcon from '../../img/iconsas/custom_values.png';
// Тарифы
import tarif1Icon from '../../img/iconsas/1tarif.png';
import tarif2Icon from '../../img/iconsas/2tarif.png';
import tarif3Icon from '../../img/iconsas/3tarif.png';
import tarif4Icon from '../../img/iconsas/4tarif.png';
import viptarifIcon from '../../img/iconsas/viptarif.png';
// Новые иконки для заголовков  
import icon1 from '../../img/iconsas/1.png';
import icon2 from '../../img/iconsas/2.png';

// Иконки из меню для секций
import dashboardImg from '../../img/dashboard-normal.svg';
import trade from '../../img/trade-normal.svg';
import markets from '../../img/markets-normal.svg';
import wallet from '../../img/wallet-normal.svg';
import broker from '../../img/broker-program-normal.svg';
import mailbox from '../../img/mailbox-normal.svg';
import setting from '../../img/settings-normal.svg';
import supportChat from '../../img/support-chat-normal.svg';

// Поля строки брокера, доступные для одноразового редактирования в бинпале.
// Главный тумблер brokerEditableNames включает саму возможность, а под ним —
// дерево подтумблеров (brokerEditableFields.<key>), что именно можно править.
// По умолчанию (ключ не задан) — включено всё.
const BROKER_EDIT_FIELDS = [
    { key: 'name', label: 'Имя', hint: 'Двойной клик по имени получателя', Icon: UserRound },
    { key: 'bank', label: 'Банк', hint: 'Дропдаун из банков конфига', Icon: Landmark },
    { key: 'digits', label: 'Последние 4 цифры', hint: 'Последние цифры счёта получателя', Icon: CreditCard },
    { key: 'amount', label: 'Сумма', hint: 'Сумма перевода — в строке и карточке', Icon: Banknote },
    { key: 'time', label: 'Время', hint: 'Время транзакции в формате ЧЧ:ММ', Icon: Clock },
    { key: 'txId', label: 'Хэш (TX ID)', hint: 'Идентификатор транзакции', Icon: Hash },
];

// ── Nova: кастомный дропдаун вместо нативного <select>.
// Принимает те же props (value / onChange / children c <option> и <optgroup>),
// поэтому хендлеры вида (e) => e.target.value работают без изменений.
// optionIcon — необязательный рендер иконки опции: (value) => ReactNode
const NovaSelect = ({ value, onChange, children, style, disabled, optionIcon }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const rootRef = useRef(null);
    const btnRef = useRef(null);

    // Позиционируем поповер как fixed относительно кнопки — так он рисуется
    // поверх карточек и не обрезается их overflow. Раскрываем вверх, если
    // снизу мало места
    const place = () => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < 260 && r.top > spaceBelow;
        setPos({
            left: r.left,
            width: r.width,
            top: openUp ? undefined : r.bottom + 6,
            bottom: openUp ? (window.innerHeight - r.top + 6) : undefined,
            maxHeight: Math.min(300, (openUp ? r.top : spaceBelow) - 16),
        });
    };

    // Разбор детей с поддержкой <optgroup>: группа становится заголовком,
    // её опции — обычными пунктами
    const items = [];
    const walk = (nodes) => {
        React.Children.forEach(nodes, (ch) => {
            if (!ch || !ch.props) return;
            if (ch.type === 'optgroup') {
                items.push({ kind: 'group', label: ch.props.label });
                walk(ch.props.children);
            } else if (ch.type === 'option') {
                items.push({ kind: 'opt', value: ch.props.value, label: ch.props.children, disabled: !!ch.props.disabled });
            } else if (ch.props.children) {
                // Фрагменты/массивы из .map()
                walk(ch.props.children);
            }
        });
    };
    walk(children);
    const opts = items.filter((i) => i.kind === 'opt');
    const current = opts.find((o) => String(o.value) === String(value));

    const popRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        place();
        const onDoc = (e) => {
            if (rootRef.current && rootRef.current.contains(e.target)) return;
            if (popRef.current && popRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        // Скролл внутри самого поповера позицию не трогает — иначе список
        // «прыгает» обратно к выбранной опции при прокрутке
        const onReposition = (e) => {
            if (e && e.target && popRef.current && popRef.current.contains(e.target)) return;
            place();
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [open]);

    // При открытии — один раз подскроллить к выбранной опции.
    // Только при открытии: перепозиционирование не должно сбрасывать
    // свободную прокрутку списка
    const didAutoScroll = useRef(false);
    useEffect(() => {
        if (!open) { didAutoScroll.current = false; return; }
        if (didAutoScroll.current || !popRef.current) return;
        const pop = popRef.current;
        const sel = pop.querySelector('[data-selected="true"]');
        if (sel) {
            pop.scrollTop = sel.offsetTop - (pop.clientHeight - sel.offsetHeight) / 2;
        }
        didAutoScroll.current = true;
    }, [open, pos]);

    const novaRoot = typeof document !== 'undefined'
        ? document.querySelector('[data-nova-theme]') || document.body
        : null;

    return (
        <div className={s.novaSelect} ref={rootRef} style={style}>
            <button
                ref={btnRef}
                type="button"
                className={`${s.novaSelectBtn} ${open ? s.novaSelectOpen : ''}`}
                onClick={() => !disabled && setOpen((v) => !v)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={s.novaSelectValue}>
                    {optionIcon && <span className={s.novaSelectIcon}>{optionIcon(value)}</span>}
                    {current ? current.label : (value ?? '')}
                </span>
                <ChevronDown size={14} strokeWidth={2.2} className={s.novaSelectChevron} aria-hidden="true" />
            </button>
            {open && pos && novaRoot && createPortal(
                <div
                    className={s.novaSelectPop}
                    role="listbox"
                    ref={popRef}
                    style={{
                        position: 'fixed',
                        left: pos.left,
                        width: pos.width,
                        top: pos.top,
                        bottom: pos.bottom,
                        maxHeight: pos.maxHeight,
                    }}
                >
                    {items.map((item, idx) => {
                        if (item.kind === 'group') {
                            return (
                                <div key={`g-${idx}`} className={s.novaSelectGroup}>{item.label}</div>
                            );
                        }
                        const selected = String(item.value) === String(value);
                        // Дизейбл-опция (плейсхолдер) — показываем как подпись, не выбираем
                        if (item.disabled) {
                            return (
                                <div
                                    key={`${String(item.value)}-${idx}`}
                                    className={`${s.novaSelectOpt} ${s.novaSelectOptDisabled}`}
                                    aria-disabled="true"
                                    style={{ opacity: 0.55, cursor: 'default' }}
                                >
                                    <span className={s.novaSelectOptLabel}>{item.label}</span>
                                </div>
                            );
                        }
                        return (
                            <button
                                type="button"
                                key={`${String(item.value)}-${idx}`}
                                role="option"
                                aria-selected={selected}
                                data-selected={selected ? 'true' : 'false'}
                                className={`${s.novaSelectOpt} ${selected ? s.novaSelectOptSelected : ''}`}
                                onClick={() => {
                                    setOpen(false);
                                    if (onChange) onChange({ target: { value: item.value } });
                                }}
                            >
                                {optionIcon && <span className={s.novaSelectIcon}>{optionIcon(item.value)}</span>}
                                <span className={s.novaSelectOptLabel}>{item.label}</span>
                                {selected && <Check size={13} strokeWidth={2.6} aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>,
                novaRoot
            )}
        </div>
    );
};

// Обёртка: Nova получает кастомный дропдаун, классика — нативный select
// (optionIcon поддерживает только Nova — нативному select его не передаём)
const ThemedSelect = ({ nova, optionIcon, ...props }) => (
    nova ? <NovaSelect optionIcon={optionIcon} {...props} /> : <select {...props} />
);

// Плавное раскрытие/сворачивание (grid-rows 1fr↔0fr, как в MiniFigma).
// Контент монтируется только когда открыто (или во время анимации закрытия),
// поэтому тяжёлые секции не висят в DOM постоянно
const Collapsible = ({ open, children }) => {
    const [mounted, setMounted] = useState(open);
    const [expanded, setExpanded] = useState(open);
    useEffect(() => {
        if (open) {
            setMounted(true);
            // Двойной rAF: даём браузеру отрисовать свёрнутое состояние,
            // иначе монтирование и раскрытие попадают в один кадр — без анимации
            let r2;
            const r = requestAnimationFrame(() => {
                r2 = requestAnimationFrame(() => setExpanded(true));
            });
            return () => { cancelAnimationFrame(r); if (r2) cancelAnimationFrame(r2); };
        }
        setExpanded(false);
    }, [open]);
    if (!mounted) return null;
    return (
        <div
            className={`${s.collapsible} ${expanded ? '' : s.collapsibleClosed}`}
            onTransitionEnd={(e) => {
                if (e.propertyName === 'grid-template-rows' && !open) setMounted(false);
            }}
        >
            <div className={s.collapsibleInner}>{children}</div>
        </div>
    );
};

// Сегментированный переключатель с «перетекающим» бегунком:
// подложка плавно скользит к активной опции, подстраивая ширину и цвет.
// options: [{ value, label, color? }] — color задаёт цвет бегунка для опции.
const NovaSegmented = ({ options, value, onChange, className, ariaLabel }) => {
    const wrapRef = useRef(null);
    const [thumb, setThumb] = useState({ left: 0, width: 0, color: null, ready: false });
    const activeIndex = Math.max(0, options.findIndex((o) => o.value === value));

    const measure = () => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const btns = wrap.querySelectorAll('[data-seg-btn]');
        const btn = btns[activeIndex];
        if (!btn) return;
        setThumb({
            left: btn.offsetLeft,
            width: btn.offsetWidth,
            color: options[activeIndex]?.color || null,
            ready: true,
        });
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, options.length]);

    useEffect(() => {
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, options.length]);

    return (
        <div ref={wrapRef} className={`${s.novaSeg} ${className || ''}`} role="radiogroup" aria-label={ariaLabel}>
            <span
                className={s.novaSegThumb}
                aria-hidden="true"
                style={{
                    transform: `translateX(${thumb.left}px)`,
                    width: `${thumb.width}px`,
                    ...(thumb.color ? { background: thumb.color } : {}),
                    opacity: thumb.ready ? 1 : 0,
                }}
            />
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        data-seg-btn
                        className={`${s.novaSegBtn} ${active ? s.novaSegBtnActive : ''}`}
                        onClick={() => onChange(o.value)}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
};

const SiteSettingsPage = ({ settings, onSaveSettings, isNavbarCollapsed, currentConfigFile }) => {
    // Nova-скин: страница рендерится внутри [data-nova-theme], токены темы доступны
    const isNova = settings?.theme === 'nova';
    // Nova: глиф-иконка у лейбла поля (в классике остаются старые PNG,
    // которые в Nova скрыты через CSS)
    const fieldGlyph = (Icon) => (isNova
        ? <span className={s.fieldGlyph}><Icon size={14} strokeWidth={2.1} aria-hidden="true" /></span>
        : null);
    // Nova: конвертация emoji-флага из лейбла (🇲🇾 → 'my') в код для FlagIcon
    // и очистка лейбла от emoji — в кастомном дропдауне флаги рисуем сами
    const emojiFlagToCode = (label) => {
        const m = (label || '').match(/[\u{1F1E6}-\u{1F1FF}]{2}/u);
        if (!m) return null;
        return Array.from(m[0])
            .map((c) => String.fromCodePoint(c.codePointAt(0) - 0x1F1E6 + 97))
            .join('');
    };
    const stripEmojiFlag = (label) => (label || '').replace(/[\u{1F1E6}-\u{1F1FF}]{2}\s*/u, '').trim();
    // Флаг языка (ru/en/es…) нашей иконкой вместо эмодзи
    const langFlag = (langCode, className) => {
        const lang = getLanguageByCode(langCode);
        const country = emojiFlagToCode(lang?.flag);
        return <FlagIcon code={country || 'unknown'} className={className} alt="" />;
    };

    const [localSettings, setLocalSettings] = useState(settings || {});
    const [initialSettings, setInitialSettings] = useState(JSON.parse(JSON.stringify(settings || {}))); // Начальное состояние для отслеживания изменений
    const [activeTab, setActiveTab] = useState('general');
    const [userRole, setUserRole] = useState(null);
    const [availableTabs, setAvailableTabs] = useState([]);
    const [activeTariffTab, setActiveTariffTab] = useState('💰 1 ТАРИФ');
    const [reorderingBank, setReorderingBank] = useState(null);
    const [editingBankName, setEditingBankName] = useState({});
    const [availableNameFiles, setAvailableNameFiles] = useState([]);
    const [availableIrsBackgrounds, setAvailableIrsBackgrounds] = useState([]);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [walletCryptoToAdd, setWalletCryptoToAdd] = useState('');
    // Банковский режим: открыт ли дропдаун смены банка в шапке карточки
    const [bankPickerOpen, setBankPickerOpen] = useState(false);
    
    // Состояния для редактора пресетов чата
    const [isPresetEditorOpen, setIsPresetEditorOpen] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);
    const [activeTextarea, setActiveTextarea] = useState(null);
    const [modalDragStart, setModalDragStart] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [draggedPresetIndex, setDraggedPresetIndex] = useState(null);
    const [presetDragOverIndex, setPresetDragOverIndex] = useState(null);
    const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);

    // Состояние для Portal тултипа
    const [portalTooltip, setPortalTooltip] = useState(null);

    // Состояния для модала выбора конфигов при сохранении
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [selectedConfigs, setSelectedConfigs] = useState([]);
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
    // Флаги стран конфигов (общий кэш) + фильтр по странам в модале сохранения
    const [configFlags, setConfigFlags] = useState({});
    const [saveCountryFilter, setSaveCountryFilter] = useState([]);
    const [saveCountryOpen, setSaveCountryOpen] = useState(false);


    // Функция для загрузки списка доступных конфигов (локально)
    const loadAvailableConfigs = async () => {
        setIsLoadingConfigs(true);
        try {
            console.log('Загружаем список конфигов локально...');
            
            // Импортируем утилиту для работы с конфигами
            const { getLocalConfigsList } = await import('../../utils/configUtils');
            const configs = await getLocalConfigsList();
            
                setAvailableConfigs(configs);
                
                // Автоматически выбираем текущий конфиг, если он есть
                if (currentConfigFile) {
                    setSelectedConfigs([currentConfigFile]);
                }
                
                console.log('Загружено конфигов:', configs.length);
                return configs;
        } catch (error) {
            console.error('Ошибка при загрузке конфигов:', error);
            return [];
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // Функции для управления drag & drop при работе с полями ввода
    const handleInputFocus = (e) => {
        e.stopPropagation();
        setIsDraggingDisabled(true);
        // Отключаем draggable на родительском элементе
        const presetCard = e.target.closest('[draggable="true"]');
        if (presetCard) {
            presetCard.setAttribute('draggable', 'false');
        }
    };

    const handleInputBlur = (e) => {
        setIsDraggingDisabled(false);
        // Включаем обратно draggable на родительском элементе
        const presetCard = e.target.closest('[draggable="false"]');
        if (presetCard) {
            presetCard.setAttribute('draggable', 'true');
        }
    };

    const handleInputMouseDown = (e) => {
        e.stopPropagation();
        // Дополнительно отключаем draggable при нажатии мыши
        const presetCard = e.target.closest('[draggable]');
        if (presetCard) {
            presetCard.setAttribute('draggable', 'false');
        }
    };

    const handleInputMouseUp = (e) => {
        // Включаем обратно draggable при отпускании мыши, но с задержкой
        setTimeout(() => {
            if (!e.target.matches(':focus')) {
                const presetCard = e.target.closest('[draggable]');
                if (presetCard) {
                    presetCard.setAttribute('draggable', 'true');
                }
            }
        }, 100);
    };

    // Функция для получения русских названий действий
    const getDefaultActionName = (actionKey) => {
        const names = {
            commission: 'Комиссия',
            verification: 'Верификация', 
            success: 'Успех',
            tax: 'Налог',
            cryptoConversion: 'Конверт крипта',
            bankConversion: 'Конверт банк',
            code: 'Код',
            premium: 'Премиум доступ'
        };
        
        // Если это кастомный модал (начинается с custom_)
        if (actionKey.startsWith('custom_')) {
            const actionKeyWithoutPrefix = actionKey.replace('custom_', '');
            // Ищем в modalTemplates
            const customModal = localSettings.modalTemplates?.find(t => t.actionKey === actionKeyWithoutPrefix);
            if (customModal && customModal.name) {
                return customModal.name;
            }
        }
        
        return names[actionKey] || actionKey;
    };

    const walletTypeDescriptions = {
        main: 'Используется для генерации адреса в настройках приложения',
        commission: 'Кошелек для комиссионных платежей',
        verification: 'Кошелек для верификационных платежей',
        tax: 'Кошелек для налоговых платежей',
        cryptoConversion: 'Кошелек для конвертации криптовалют',
        bankConversion: 'Кошелек для банковской конвертации',
        premium: 'Кошелек для PREMIUM-взноса'
    };

    const getNormalizedWalletsByCurrency = (settings = localSettings) => (
        buildWalletsByCurrencyFromLegacy(settings, CRYPTO_OPTIONS)
    );

    const handleCurrencyWalletChange = (cryptoSymbol, walletKey, value) => {
        const cryptoMeta = CRYPTO_OPTIONS.find(option => option.symbol === cryptoSymbol) || {};

        setLocalSettings(prev => {
            const normalizedWallets = getNormalizedWalletsByCurrency(prev);

            return {
                ...prev,
                walletsByCurrency: {
                    ...normalizedWallets,
                    [cryptoSymbol]: {
                        ...(normalizedWallets[cryptoSymbol] || {}),
                        [walletKey]: {
                            ...(normalizedWallets[cryptoSymbol]?.[walletKey] || {}),
                            address: value,
                            qrValue: value,
                            network: normalizedWallets[cryptoSymbol]?.[walletKey]?.network || cryptoMeta.network || '',
                            chain: normalizedWallets[cryptoSymbol]?.[walletKey]?.chain || cryptoMeta.chain || ''
                        }
                    }
                }
            };
        });
    };

    const getConfiguredWalletCurrencies = (settings = localSettings) => {
        const walletsByCurrency = getNormalizedWalletsByCurrency(settings);
        const configured = Object.keys(walletsByCurrency || {});
        return configured.length > 0 ? configured : [settings.defaultCrypto || 'ETH'];
    };

    const handleAddWalletCurrency = () => {
        if (!walletCryptoToAdd) return;
        const cryptoMeta = CRYPTO_OPTIONS.find(option => option.symbol === walletCryptoToAdd);
        if (!cryptoMeta) return;

        setLocalSettings(prev => {
            const normalizedWallets = getNormalizedWalletsByCurrency(prev);
            if (normalizedWallets[walletCryptoToAdd]) {
                return prev;
            }

            const nextWallets = {
                ...normalizedWallets,
                [walletCryptoToAdd]: {}
            };

            WALLET_TYPES.forEach(({ key }) => {
                nextWallets[walletCryptoToAdd][key] = {
                    address: '',
                    qrValue: '',
                    network: cryptoMeta.network || '',
                    chain: cryptoMeta.chain || ''
                };
            });

            return {
                ...prev,
                walletsByCurrency: nextWallets
            };
        });
        setWalletCryptoToAdd('');
    };

    const handleRemoveWalletCurrency = (cryptoSymbol) => {
        setLocalSettings(prev => {
            const normalizedWallets = getNormalizedWalletsByCurrency(prev);
            const nextWallets = { ...normalizedWallets };
            delete nextWallets[cryptoSymbol];

            if (Object.keys(nextWallets).length === 0) {
                return {
                    ...prev,
                    walletsByCurrency: buildWalletsByCurrencyFromLegacy({
                        ...prev,
                        walletsByCurrency: {},
                        defaultCrypto: prev.defaultCrypto || 'ETH'
                    }, CRYPTO_OPTIONS)
                };
            }

            return {
                ...prev,
                walletsByCurrency: nextWallets
            };
        });
    };

    const normalizeWalletSettingsBeforeSave = (settings) => ({
        ...settings,
        walletsByCurrency: buildWalletsByCurrencyFromLegacy(settings, CRYPTO_OPTIONS)
    });

    // ── Банковский режим оплаты: ОДИН банк + счета по модулям ──
    // Банк всегда один — settings.paymentBank (переключается в шапке карточки).
    // Счета не зависят от банка: settings.bankAccounts[walletKey] = { number, fields }.
    // Старая модель settings.bankWallets[bankName][walletKey] мигрируется при правке.
    const getSelectedPaymentBank = (settings = localSettings) =>
        settings.paymentBank || settings.defaultPaymentBank || Object.keys(settings.bankWallets || {})[0] || '';

    const getLocalBankAccounts = (settings = localSettings) => {
        if (settings.bankAccounts && typeof settings.bankAccounts === 'object') return settings.bankAccounts;
        const bank = getSelectedPaymentBank(settings);
        const legacy = settings.bankWallets?.[bank];
        return (legacy && typeof legacy === 'object') ? legacy : {};
    };

    // Инициализируем bankAccounts (мигрируя со старой модели) для последующей правки
    const ensureBankAccounts = (prev) => {
        if (prev.bankAccounts && typeof prev.bankAccounts === 'object') {
            return JSON.parse(JSON.stringify(prev.bankAccounts));
        }
        const bank = getSelectedPaymentBank(prev);
        const legacy = prev.bankWallets?.[bank];
        return (legacy && typeof legacy === 'object') ? JSON.parse(JSON.stringify(legacy)) : {};
    };

    // Выбор (смена) единственного банка — меняется только логотип и название
    const handleSelectPaymentBank = (bankName) => {
        setLocalSettings(prev => ({ ...prev, paymentBank: bankName }));
        setBankPickerOpen(false);
    };

    const handleBankAccountChange = (walletKey, field, value) => {
        setLocalSettings(prev => {
            const bankAccounts = ensureBankAccounts(prev);
            bankAccounts[walletKey] = { ...(bankAccounts[walletKey] || {}), [field]: value };
            return { ...prev, bankAccounts };
        });
    };

    // Произвольные доп. поля реквизита (название + значение), сколько угодно
    const handleAddBankField = (walletKey) => {
        setLocalSettings(prev => {
            const bankAccounts = ensureBankAccounts(prev);
            const entry = { ...(bankAccounts[walletKey] || {}) };
            const fields = Array.isArray(entry.fields) ? [...entry.fields] : [];
            fields.push({ label: '', value: '' });
            entry.fields = fields;
            bankAccounts[walletKey] = entry;
            return { ...prev, bankAccounts };
        });
    };

    const handleBankFieldChange = (walletKey, index, prop, value) => {
        setLocalSettings(prev => {
            const bankAccounts = ensureBankAccounts(prev);
            const entry = { ...(bankAccounts[walletKey] || {}) };
            const fields = Array.isArray(entry.fields) ? entry.fields.map(f => ({ ...f })) : [];
            if (!fields[index]) fields[index] = { label: '', value: '' };
            fields[index][prop] = value;
            entry.fields = fields;
            bankAccounts[walletKey] = entry;
            return { ...prev, bankAccounts };
        });
    };

    const handleRemoveBankField = (walletKey, index) => {
        setLocalSettings(prev => {
            const bankAccounts = ensureBankAccounts(prev);
            const entry = { ...(bankAccounts[walletKey] || {}) };
            const fields = (Array.isArray(entry.fields) ? entry.fields : []).filter((_, i) => i !== index);
            entry.fields = fields;
            bankAccounts[walletKey] = entry;
            return { ...prev, bankAccounts };
        });
    };

    const [newlyAddedBank, setNewlyAddedBank] = useState(null);
    // Функция для получения списка файлов имен через API
    const getNameFilesList = async () => {
        try {
            const apiUrl = getApiUrl('api/files/names');
            console.log('[SiteSettings] Запрос списка файлов имен:', apiUrl);
            const response = await fetch(apiUrl);
            console.log('[SiteSettings] Ответ сервера:', response.status, response.statusText);
            
            if (response.ok) {
                const data = await response.json();
                console.log('[SiteSettings] Данные от сервера:', data);
                
                if (data.success && data.files && data.files.length > 0) {
                    console.log('[SiteSettings] Найдено файлов имен:', data.files.length, data.files);
                    return data.files;
                } else {
                    console.warn('[SiteSettings] Сервер вернул пустой список файлов или data.success = false');
                }
            } else {
                const errorText = await response.text();
                console.error('[SiteSettings] Ошибка ответа сервера:', response.status, errorText);
            }
            
            // Fallback: возвращаем дефолтный файл
            console.log('[SiteSettings] Используем fallback: names.txt');
                return [{ filename: 'names.txt', name: 'names.txt' }];
        } catch (error) {
            console.error('[SiteSettings] Ошибка загрузки файлов имен через API:', error);
            // Fallback: возвращаем дефолтный файл
            return [{ filename: 'names.txt', name: 'names.txt' }];
        }
    };

    // Функция для получения списка фоновых изображений IRS через API
    const getIrsBackgroundsList = async () => {
        try {
            const apiUrl = getApiUrl('api/files/irsbackgrounds');
            console.log('[SiteSettings] Запрос списка фоновых изображений IRS:', apiUrl);
            const response = await fetch(apiUrl);
            console.log('[SiteSettings] Ответ сервера:', response.status, response.statusText);
            
            if (response.ok) {
                const data = await response.json();
                console.log('[SiteSettings] Данные от сервера:', data);
                
                if (data.success && data.files && data.files.length > 0) {
                    console.log('[SiteSettings] Найдено фоновых изображений IRS:', data.files.length, data.files);
                    return data.files.map(file => ({
                        filename: file.filename,
                        name: file.name,
                        fullPath: file.filename
                    }));
                } else {
                    console.warn('[SiteSettings] Сервер вернул пустой список фоновых изображений или data.success = false');
                }
            } else {
                const errorText = await response.text();
                console.error('[SiteSettings] Ошибка ответа сервера:', response.status, errorText);
            }
            
            // Fallback: возвращаем пустой массив
            console.log('[SiteSettings] Используем fallback: пустой массив');
            return [];
        } catch (error) {
            console.error('[SiteSettings] Ошибка загрузки фоновых изображений IRS через API:', error);
            // Fallback: возвращаем пустой массив
            return [];
        }
    };

    const [collapsedTranslationSections, setCollapsedTranslationSections] = useState({
        header: true, // Скрыт по умолчанию
        tradingDashboard: true, // Скрыт по умолчанию
        navbar: true, // Скрыт по умолчанию
        dashboard: true, // Скрыт по умолчанию
        adminPanel: true, // Скрыт по умолчанию
        buttons: true, // Скрыт по умолчанию
        siteSettings: true, // Скрыт по умолчанию
        wallet: true, // Скрыт по умолчанию
        brokerProgram: true, // Скрыт по умолчанию
        settings: true, // Скрыт по умолчанию
        mailbox: true, // Скрыт по умолчанию
        commissionsModal: true, // Скрыт по умолчанию
        verificationModal: true, // Скрыт по умолчанию
        premiumModal: true, // Скрыт по умолчанию
        successModal: true, // Скрыт по умолчанию
        taxModal: true, // Скрыт по умолчанию
        codeModal: true, // Скрыт по умолчанию
        transactionErrorModal: true, // Скрыт по умолчанию
        bankTransferModal: true, // Скрыт по умолчанию
        chat: true // Скрыт по умолчанию
    });
    const [isAddLanguageModalOpen, setIsAddLanguageModalOpen] = useState(false);
    // Nova: фокус на одном языке ('' = показывать все) и живой поиск по ключам
    // Мультивыбор языков для фильтра переводов: пустой массив = все языки
    const [transFocusLangs, setTransFocusLangs] = useState([]);
    const toggleTransFocusLang = (langCode) => {
        setTransFocusLangs((prev) => (
            prev.includes(langCode) ? prev.filter((c) => c !== langCode) : [...prev, langCode]
        ));
    };
    const [transSearch, setTransSearch] = useState('');
    // Режим «только непереведённые»: показываем ключи без перевода на текущем
    // языке, раскрывая все секции; пустые секции прячутся через CSS (:has)
    const [showUntranslatedOnly, setShowUntranslatedOnly] = useState(false);
    const fileInputRef = useRef(null);

    // Набор всех ключей переводов, которые реально рендерит UI (собирается в
    // renderTranslationFields). Считаем по нему, а не по translations.ru —
    // иначе новые Nova-ключи, отсутствующие в конфиге, не учитываются
    const transKeysRef = useRef(new Set());
    const [untranslatedCount, setUntranslatedCount] = useState(0);

    // Пересчёт непереведённых для текущего языка. Запускается после рендера
    // вкладки переводов (когда ref уже наполнен) и при любом изменении значений
    useEffect(() => {
        if (activeTab !== 'translations') { setUntranslatedCount(0); return; }
        const trans = localSettings?.translations || {};
        const target = localSettings?.currentLanguage || 'ru';
        const tgt = trans[target];
        if (!tgt || typeof tgt !== 'object') { setUntranslatedCount(0); return; }
        let n = 0;
        transKeysRef.current.forEach((key) => {
            const val = key.split('.').reduce(
                (obj, k) => (obj && obj[k] != null ? obj[k] : ''), tgt
            ) || '';
            if (!String(val).trim()) n++;
        });
        setUntranslatedCount(n);
    }, [activeTab, localSettings?.translations, localSettings?.currentLanguage]);

    // Если непереведённых не осталось (или сменили язык), выходим из режима
    useEffect(() => {
        if (showUntranslatedOnly && untranslatedCount === 0) {
            setShowUntranslatedOnly(false);
        }
    }, [showUntranslatedOnly, untranslatedCount]);
    
    // Состояния для сворачивания/разворачивания MiniFigma
    const [collapsedSections, setCollapsedSections] = useState({});
    const [collapsedSubsections, setCollapsedSubsections] = useState({});
    
    // Состояния для редактора MiniFigma
    const [isSectionEditorOpen, setIsSectionEditorOpen] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [collapsedMessages, setCollapsedMessages] = useState({});
    const [collapsingMessages, setCollapsingMessages] = useState({});
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [draggedMessageIndex, setDraggedMessageIndex] = useState(null);
    const [messageDragOverIndex, setMessageDragOverIndex] = useState(null);
    
    // Состояния для drag & drop сообщений внутри групп
    const [draggedGroupMessageIndex, setDraggedGroupMessageIndex] = useState(null);
    const [groupMessageDragOverIndex, setGroupMessageDragOverIndex] = useState(null);
    const [draggedGroupIndex, setDraggedGroupIndex] = useState(null);
    
    // Состояния для модального окна выбора сообщений в группу
    const [isGroupSelectionModalOpen, setIsGroupSelectionModalOpen] = useState(false);
    const [selectedMessagesForGroup, setSelectedMessagesForGroup] = useState([]);
    const [newGroupTitle, setNewGroupTitle] = useState('🔁 Новая группа');
    const [newGroupColor, setNewGroupColor] = useState('#1e40af');
    const [newGroupAlwaysOpen, setNewGroupAlwaysOpen] = useState(false);
    
    // Состояния для переменных MiniFigma
    const [activeFigmaTextarea, setActiveFigmaTextarea] = useState(null);
    const [figmaPortalTooltip, setFigmaPortalTooltip] = useState(null);
    
    // Отладочный useEffect для отслеживания изменений состояния модального окна
    useEffect(() => {
        console.log('🔄 State changed - isSectionEditorOpen:', isSectionEditorOpen, 'editingSection:', editingSection);
    }, [isSectionEditorOpen, editingSection]);
    const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);
    const [sectionDragOverIndex, setSectionDragOverIndex] = useState(null);
    
    // ===== ФУНКЦИИ ДЛЯ УПРАВЛЕНИЯ РАЗДЕЛАМИ MINIFIGMA =====
    
    // Функция для открытия редактора раздела
    const openSectionEditor = (sectionId) => {
        console.log('openSectionEditor called with sectionId:', sectionId);
        console.log('localSettings.miniFigma?.sections:', localSettings.miniFigma?.sections);
        
        const section = (localSettings.miniFigma?.sections || []).find(s => s.id === sectionId);
        console.log('Found section:', section);
        
        if (section) {
            // Принудительно сбрасываем состояние перед установкой нового
            setIsSectionEditorOpen(false);
            setEditingSection(null);
            setCollapsedMessages({});
            setCollapsingMessages({});
            setCollapsedGroups({});
            
            // Используем setTimeout чтобы React успел обработать сброс состояния
            setTimeout(() => {
                setEditingSection({ ...section });
                
                // Устанавливаем все сообщения как свернутые по умолчанию
                const collapsedState = {};
                const collapsedGroupsState = {};
                
                (section.messages || []).forEach(message => {
                    if (message.type === 'group') {
                        // Группы по умолчанию свернуты
                        collapsedGroupsState[message.id] = true;
                        // Сообщения внутри групп тоже свернуты
                        (message.messages || []).forEach(groupMessage => {
                            collapsedState[`${message.id}_${groupMessage.id}`] = true;
                        });
                    } else {
                        // Обычные сообщения свернуты
                        collapsedState[message.id] = true;
                    }
                });
                
                setCollapsedMessages(collapsedState);
                setCollapsedGroups(collapsedGroupsState);
                
                setIsSectionEditorOpen(true);
                console.log('Modal should open now');
            }, 10);
        } else {
            console.log('Section not found!');
        }
    };

    // Функция для сохранения изменений раздела
    const saveSectionChanges = () => {
        if (!editingSection) return;
        
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const index = newSections.findIndex(s => s.id === editingSection.id);
        if (index !== -1) {
            newSections[index] = editingSection;
            
            // Обновляем локальные настройки и сохраняем
            const newSettings = {
                ...localSettings,
                miniFigma: {
                    ...localSettings.miniFigma,
                    sections: newSections
                }
            };
            setLocalSettings(newSettings);
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
        }
        
        setIsSectionEditorOpen(false);
        setEditingSection(null);
        // Убрано уведомление о сохранении - сохранение происходит только при нажатии кнопки "Сохранить"
    };

    // Функция для обновления названия раздела
    const updateSectionName = (sectionId, newName) => {
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const index = newSections.findIndex(s => s.id === sectionId);
        if (index !== -1) {
            newSections[index].name = newName;
            handleInputChange('miniFigma', 'sections', newSections);
        }
    };
    // Функция для включения/выключения раздела
    const toggleSection = (sectionId, enabled) => {
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const index = newSections.findIndex(s => s.id === sectionId);
        if (index !== -1) {
            newSections[index].enabled = enabled;
            handleInputChange('miniFigma', 'sections', newSections);
        }
    };

    // Функция для удаления раздела
    const deleteSection = async (sectionId) => {
        if (await confirmDialog({
            title: 'Удалить раздел?',
            message: 'Раздел пропадёт со страницы вместе со своим содержимым. Вернуть его будет нельзя.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Безвозвратно удалит раздел',
            cancelHint: 'Закроет окно, раздел останется на месте'
        })) {
            const newSections = (localSettings.miniFigma?.sections || []).filter(s => s.id !== sectionId);
            handleInputChange('miniFigma', 'sections', newSections);
            success('Раздел удален!', 2000);
        }
    };

    // Функции для drag & drop разделов
    const handleSectionDragStart = (e, index) => {
        setDraggedSectionIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleSectionDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedSectionIndex !== null && draggedSectionIndex !== index) {
            setSectionDragOverIndex(index);
        }
    };

    const handleSectionDragLeave = (e) => {
        // Проверяем, что мы действительно покинули элемент, а не перешли к дочернему
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setSectionDragOverIndex(null);
        }
    };

    const handleSectionDrop = (e, dropIndex) => {
        e.preventDefault();
        
        if (draggedSectionIndex === null || draggedSectionIndex === dropIndex) {
            return;
        }

        const sections = [...(localSettings.miniFigma?.sections || [])];
        const draggedSection = sections[draggedSectionIndex];
        
        // Удаляем элемент из старой позиции
        sections.splice(draggedSectionIndex, 1);
        
        // Вставляем в новую позицию
        const newIndex = draggedSectionIndex < dropIndex ? dropIndex - 1 : dropIndex;
        sections.splice(newIndex, 0, draggedSection);
        
        handleInputChange('miniFigma', 'sections', sections);
        
        // Сбрасываем состояния
        setDraggedSectionIndex(null);
        setSectionDragOverIndex(null);
    };

    const handleSectionDragEnd = () => {
        setDraggedSectionIndex(null);
        setSectionDragOverIndex(null);
    };

    // Функции для управления сообщениями в разделе
    const addMessageToSection = () => {
        if (!editingSection) return;
        
        const newMessage = {
            id: `message_${Date.now()}`,
            title: '',
            subtitle: '',
            text: ''
        };
        
        const updatedSection = {
            ...editingSection,
            messages: [...(editingSection.messages || []), newMessage]
        };
        
        setEditingSection(updatedSection);
        
        // Новые сообщения создаются раскрытыми
        setTimeout(() => {
            setCollapsedMessages(prev => ({
                ...prev,
                [newMessage.id]: false
            }));
        }, 50);
        
        // Прокручиваем к новому сообщению после его отрисовки
        setTimeout(() => {
            const modalScrollableContent = document.querySelector(`.${s.modalScrollableContent}`);
            if (modalScrollableContent) {
                // Ищем новое сообщение по data-message-id
                const newMessageElement = modalScrollableContent.querySelector(`[data-message-id="${newMessage.id}"]`);
                if (newMessageElement) {
                    // Прокручиваем к элементу с отступом сверху
                    const elementTop = newMessageElement.offsetTop;
                    const containerHeight = modalScrollableContent.clientHeight;
                    const scrollTop = Math.max(0, elementTop - 50); // 50px отступ сверху
                    
                    modalScrollableContent.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth'
                    });
                } else {
                    // Fallback: прокручиваем до конца с запасом
                    modalScrollableContent.scrollTo({
                        top: modalScrollableContent.scrollHeight + 200,
                        behavior: 'smooth'
                    });
                }
            }
        }, 200);
        
        // Автоматически сохраняем изменения
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
        if (sectionIndex !== -1) {
            newSections[sectionIndex] = updatedSection;
            const newSettings = {
                ...localSettings,
                miniFigma: {
                    ...localSettings.miniFigma,
                    sections: newSections
                }
            };
            setLocalSettings(newSettings);
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
        }
    };
    
    const addGroupToSection = () => {
        if (!editingSection) return;
        
        // Открываем модальное окно для выбора сообщений
        setSelectedMessagesForGroup([]);
        setNewGroupTitle('🔁 Новая группа');
        setNewGroupColor('#1e40af'); // Сбрасываем цвет на дефолтный
        setNewGroupAlwaysOpen(false);
        setIsGroupSelectionModalOpen(true);
    };

    // Функции для работы с модальным окном выбора сообщений
    const toggleMessageSelection = (messageId) => {
        setSelectedMessagesForGroup(prev => 
            prev.includes(messageId)
                ? prev.filter(id => id !== messageId)
                : [...prev, messageId]
        );
    };
    const createGroupFromSelectedMessages = () => {
        if (!editingSection || selectedMessagesForGroup.length === 0) return;
        
        const editingGroupIndex = window.editingGroupIndex;
        const isEditingExistingGroup = editingGroupIndex !== undefined;
        
        // Находим выбранные сообщения
        let availableMessages = (editingSection.messages || [])
            .filter(msg => msg.type !== 'group');
        
        // Если редактируем существующую группу, добавляем её сообщения к доступным
        if (isEditingExistingGroup) {
            const existingGroup = editingSection.messages[editingGroupIndex];
            if (existingGroup && existingGroup.type === 'group') {
                availableMessages = [...availableMessages, ...(existingGroup.messages || [])];
            }
        }
        
        const selectedMessages = availableMessages
            .filter(msg => selectedMessagesForGroup.includes(msg.id))
            .map(msg => ({ ...msg })); // Копируем сообщения
        
        // Создаем или обновляем группу
        const groupData = {
            id: isEditingExistingGroup ? editingSection.messages[editingGroupIndex].id : `group_${Date.now()}`,
            type: 'group',
            title: newGroupTitle,
            color: newGroupColor,
            alwaysOpen: newGroupAlwaysOpen,
            messages: selectedMessages
        };
        
        // Формируем новый список сообщений
        let newMessages = [];
        
        if (isEditingExistingGroup) {
            const existingGroup = editingSection.messages[editingGroupIndex];
            
            // Добавляем сообщения из старой группы, которые НЕ выбраны (они становятся отдельными)
            const unselectedMessages = (existingGroup.messages || [])
                .filter(msg => !selectedMessagesForGroup.includes(msg.id));
            
            // Создаем новый массив, заменяя группу на том же месте
            newMessages = (editingSection.messages || []).map((msg, index) => {
                if (index === editingGroupIndex) {
                    // Заменяем группу на обновленную версию
                    return groupData;
                }
                // Удаляем из основного списка только те сообщения, которые ВЫБРАНЫ для группы
                if (msg.type !== 'group' && selectedMessagesForGroup.includes(msg.id)) {
                    return null; // Помечаем для удаления
                }
                return msg;
            }).filter(msg => msg !== null); // Удаляем помеченные элементы
            
            // Добавляем неселектированные сообщения в конец
            newMessages = [...newMessages, ...unselectedMessages];
        } else {
            // При создании новой группы - удаляем только выбранные сообщения и добавляем группу в конец
            newMessages = (editingSection.messages || [])
                .filter(msg => !selectedMessagesForGroup.includes(msg.id));
            newMessages = [...newMessages, groupData];
        }
        
        const updatedSection = {
            ...editingSection,
            messages: newMessages
        };
        
        setEditingSection(updatedSection);
        
        // Автоматически сохраняем изменения
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
        if (sectionIndex !== -1) {
            newSections[sectionIndex] = updatedSection;
            const newSettings = {
                ...localSettings,
                miniFigma: {
                    ...localSettings.miniFigma,
                    sections: newSections
                }
            };
            setLocalSettings(newSettings);
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
        }
        
        // Закрываем модальное окно
        setIsGroupSelectionModalOpen(false);
        setSelectedMessagesForGroup([]);
        
        // Очищаем индекс редактируемой группы
        delete window.editingGroupIndex;
    };

    const closeGroupSelectionModal = () => {
        setIsGroupSelectionModalOpen(false);
        setSelectedMessagesForGroup([]);
        setNewGroupTitle('🔁 Новая группа');
        setNewGroupColor('#1e40af');
        setNewGroupAlwaysOpen(false);
        
        // Очищаем индекс редактируемой группы
        delete window.editingGroupIndex;
    };

    // Функция для переключения флага alwaysOpen у группы
    const toggleGroupAlwaysOpen = (groupIndex) => {
        if (!editingSection) return;
        
        const updatedMessages = [...(editingSection.messages || [])];
        const group = updatedMessages[groupIndex];
        
        if (group && group.type === 'group') {
            // Переключаем флаг alwaysOpen
            group.alwaysOpen = !group.alwaysOpen;
            
            const updatedSection = {
                ...editingSection,
                messages: updatedMessages
            };
            
            setEditingSection(updatedSection);
            
            // Автоматически сохраняем изменения
            const newSections = [...(localSettings.miniFigma?.sections || [])];
            const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
            if (sectionIndex !== -1) {
                newSections[sectionIndex] = updatedSection;
                const newSettings = {
                    ...localSettings,
                    miniFigma: {
                        ...localSettings.miniFigma,
                        sections: newSections
                    }
                };
                setLocalSettings(newSettings);
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            }
        }
    };

    // Функция для редактирования существующей группы
    const editExistingGroup = (groupIndex) => {
        if (!editingSection) return;
        
        const group = editingSection.messages[groupIndex];
        if (!group || group.type !== 'group') return;
        
        // Устанавливаем индекс редактируемой группы
        window.editingGroupIndex = groupIndex;
        
        // Заполняем форму данными группы
        setNewGroupTitle(group.title || '🔁 Группа');
        setNewGroupColor(group.color || '#1e40af');
        setNewGroupAlwaysOpen(group.alwaysOpen || false);
        
        // Выбираем все сообщения из группы
        const groupMessageIds = (group.messages || []).map(msg => msg.id);
        setSelectedMessagesForGroup(groupMessageIds);
        
        // Открываем модальное окно
        setIsGroupSelectionModalOpen(true);
    };
    // Функции для работы с переменными MiniFigma
    const getMiniFigmaVariableDescription = (variable) => {
        const currentTariff = Object.keys(localSettings.tariffBaseAmounts || {})[0] || '💰 1 ТАРИФ';
        const currency = localSettings.currencyFormat || 'RM';
        const baseAmount = localSettings.tariffBaseAmounts?.[currentTariff] || 7103.84;
        const additionalAmount = localSettings.tariffAdditionalAmounts?.[currentTariff] || 3240.56;
        const verifAmount = localSettings.tariffVerificationAmounts?.[currentTariff] || 492;
        const taxPercent = localSettings.tariffTaxPercents?.[currentTariff] || 4.4;
        const cryptoConvertAmount = localSettings.tariffConversionCommissions?.[currentTariff] || 459;
        const bankConvertAmount = localSettings.tariffBankFees?.[currentTariff] || 385;
        const commissionAmount = localSettings.tariffCommissions?.[currentTariff] || 549;
        const brokerFeeAmount = localSettings.tariffBrokerFees?.[currentTariff] || 250;
        const winningPercent = localSettings.tariffWinningPercentages?.[currentTariff] || 15;
        
        const formatAmount = (amount) => {
            if (isNaN(amount) || amount === null || amount === undefined) return '0';
            const num = parseFloat(amount);
            const parts = num.toFixed(2).split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1];
            
            if (localSettings.numberFormat === 'dot') {
                // Европейский формат: 1.234,56
                const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                return `${formattedInteger},${decimalPart}`;
            } else {
                // Американский формат: 1,234.56
                const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return `${formattedInteger}.${decimalPart}`;
            }
        };

        const descriptions = {
            '<USER_WIN>': `Выигрыш пользователя: ${formatAmount(baseAmount)} ${currency}`,
            '<DEPOSIT_AMOUNT>': `Депозитная сумма: ${formatAmount(baseAmount + additionalAmount)} ${currency}`,
            '<COMMISSION_SIZE>': `Размер комиссии: ${formatAmount(commissionAmount)} ${currency}`,
            '<VERIFICATION_AMOUNT>': `Сумма верификации: ${formatAmount(verifAmount)} ${currency}`,
            '<ADDITIONAL_AMOUNT>': `Дополнительная сумма: ${formatAmount(additionalAmount)} ${currency}`,
            '<TAX_PERCENT>': `Процент налога: ${taxPercent}%`,
            '<TAX_AMOUNT>': `Сумма налога: ${formatAmount((baseAmount + additionalAmount + commissionAmount) * (taxPercent / 100))} ${currency}`,
            '<CRYPTO_COMMISSION>': `Комиссия за конвертацию: ${formatAmount(cryptoConvertAmount)} ${currency}`,
            '<BANK_COMMISSION>': `Банковская комиссия: ${formatAmount(bankConvertAmount)} ${currency}`,
            '<COMMISSION_PERCENT>': `Процент комиссии: 0.0%`, // Будет обновляться из модала комиссии
            '<BROKER_FEE>': `Оплата работы брокера: ${formatAmount(brokerFeeAmount)} ${currency}`,
            '<WINNING_PERCENT>': `Процент от выигрыша: ${winningPercent}%`,
            '<WINNING_AMOUNT>': `${formatAmount(baseAmount * winningPercent / 100)} ${currency} (${winningPercent}%/${formatAmount(baseAmount)} ${currency})`,
            '<DEFAULT_CRYPTO>': `Криптовалюта по умолчанию: ${localSettings.defaultCrypto || 'ETH'}`
        };
        
        // Добавляем описания для переменных кастомных модалов
        if (localSettings.modalTemplates) {
            localSettings.modalTemplates.forEach(template => {
                if (template.financeFieldName && template.actionKey && template.enabled !== false) {
                    const actionKeyUpper = template.actionKey.toUpperCase();
                    const settingsKey = `tariff${template.actionKey.charAt(0).toUpperCase() + template.actionKey.slice(1)}Amounts`;
                    const rangesKey = `${settingsKey}Ranges`;
                    let customAmount = localSettings[settingsKey]?.[currentTariff];
                    if (customAmount === undefined || customAmount === null || customAmount === 0) {
                        const ranges = localSettings[rangesKey]?.[currentTariff];
                        if (ranges && ranges.min !== undefined && ranges.max !== undefined) {
                            customAmount = `${formatAmount(ranges.min)} - ${formatAmount(ranges.max)}`;
                        } else {
                            customAmount = '0';
                        }
                    } else {
                        customAmount = formatAmount(customAmount);
                    }
                    descriptions[`<${actionKeyUpper}_AMOUNT>`] = `${template.financeFieldName || template.name}: ${customAmount} ${currency}`;
                }
            });
        }

        return descriptions[variable] || variable;
    };

    const showFigmaPortalTooltip = (event, description) => {
        const rect = event.target.getBoundingClientRect();
        setFigmaPortalTooltip({
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
            description
        });
    };

    const hideFigmaPortalTooltip = () => {
        setFigmaPortalTooltip(null);
    };
    const insertFigmaVariableAtCursor = (variable) => {
        if (!activeFigmaTextarea) {
            // Попробуем найти любое поле textarea в модале MiniFigma, включая поля в группах
            const textareas = document.querySelectorAll(`
                [class*="miniFigmaModal"] textarea, 
                [class*="miniFigmaModal"] input[type="text"],
                [class*="groupMessages"] textarea,
                [class*="groupMessages"] input[type="text"]
            `);
            if (textareas.length > 0) {
                const lastTextarea = textareas[textareas.length - 1];
                setActiveFigmaTextarea(lastTextarea);
                setTimeout(() => insertFigmaVariableAtCursor(variable), 10);
                return;
            } else {
                return;
            }
        }
        
        const textarea = activeFigmaTextarea;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentValue = textarea.value || '';
        
        // Вставляем переменную в позицию курсора
        const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
        
        // Обновляем значение в состоянии
        const messageIndexData = textarea.dataset.messageIndex;
        const field = textarea.dataset.field;
        
        if (messageIndexData && field) {
            // Проверяем, это сообщение в группе или обычное сообщение
            if (messageIndexData.includes('_')) {
                // Это сообщение в группе (формат: "groupIndex_messageIndex")
                const [groupIndex, messageIndex] = messageIndexData.split('_').map(Number);
                if (!isNaN(groupIndex) && !isNaN(messageIndex)) {
                    updateGroupMessage(groupIndex, messageIndex, field, newValue);
                }
            } else {
                // Это обычное сообщение
                const messageIndex = parseInt(messageIndexData);
                if (!isNaN(messageIndex)) {
                    updateSectionMessage(messageIndex, field, newValue);
                }
            }
        }
        
        // Устанавливаем курсор после вставленной переменной
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 10);
    };

    // Функции для работы с сообщениями внутри групп
    const addMessageToGroup = (groupIndex) => {
        if (!editingSection) return;
        
        const newMessage = {
            id: `message_${Date.now()}`,
            title: '',
            subtitle: '',
            text: ''
        };
        
        const updatedMessages = [...(editingSection.messages || [])];
        const group = updatedMessages[groupIndex];
        
        if (group && group.type === 'group') {
            updatedMessages[groupIndex] = {
                ...group,
                messages: [...(group.messages || []), newMessage]
            };
            
            const updatedSection = {
                ...editingSection,
                messages: updatedMessages
            };
            
            setEditingSection(updatedSection);
            
            // Разворачиваем группу если она свернута и новое сообщение раскрыто
            const messageId = `${group.id}_${newMessage.id}`;
            setCollapsedGroups(prev => ({
                ...prev,
                [group.id]: false
            }));
            setCollapsedMessages(prev => ({
                ...prev,
                [messageId]: false
            }));
            
            // Прокручиваем к новому сообщению с минимальной задержкой
            setTimeout(() => {
                const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
                if (messageElement) {
                    messageElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                    });
                    // Фокусируемся на поле заголовка нового сообщения
                    const titleInput = messageElement.querySelector('input[placeholder*="Название"], input[placeholder*="заголовок"]');
                    if (titleInput) {
                        setTimeout(() => titleInput.focus(), 200);
                    }
                }
            }, 50);
            
            // Автоматически сохраняем изменения
            const newSections = [...(localSettings.miniFigma?.sections || [])];
            const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
            if (sectionIndex !== -1) {
                newSections[sectionIndex] = updatedSection;
                const newSettings = {
                    ...localSettings,
                    miniFigma: {
                        ...localSettings.miniFigma,
                        sections: newSections
                    }
                };
                setLocalSettings(newSettings);
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            }
        }
    };
    const removeMessageFromGroup = (groupIndex, messageIndex) => {
        if (!editingSection) return;
        
        const updatedMessages = [...(editingSection.messages || [])];
        const group = updatedMessages[groupIndex];
        
        if (group && group.type === 'group') {
            updatedMessages[groupIndex] = {
                ...group,
                messages: (group.messages || []).filter((_, index) => index !== messageIndex)
            };
            
            const updatedSection = {
                ...editingSection,
                messages: updatedMessages
            };
            
            setEditingSection(updatedSection);
            
            // Автоматически сохраняем изменения
            const newSections = [...(localSettings.miniFigma?.sections || [])];
            const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
            if (sectionIndex !== -1) {
                newSections[sectionIndex] = updatedSection;
                const newSettings = {
                    ...localSettings,
                    miniFigma: {
                        ...localSettings.miniFigma,
                        sections: newSections
                    }
                };
                setLocalSettings(newSettings);
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            }
        }
    };

    const updateGroupMessage = (groupIndex, messageIndex, field, value) => {
        if (!editingSection) return;
        
        const updatedMessages = [...(editingSection.messages || [])];
        const group = updatedMessages[groupIndex];
        
        if (group && group.type === 'group') {
            const updatedGroupMessages = (group.messages || []).map((message, index) =>
                index === messageIndex ? { ...message, [field]: value } : message
            );
            
            updatedMessages[groupIndex] = {
                ...group,
                messages: updatedGroupMessages
            };
            
            const updatedSection = {
                ...editingSection,
                messages: updatedMessages
            };
            
            setEditingSection(updatedSection);
            
            // Автоматически сохраняем изменения
            const newSections = [...(localSettings.miniFigma?.sections || [])];
            const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
            if (sectionIndex !== -1) {
                newSections[sectionIndex] = updatedSection;
                const newSettings = {
                    ...localSettings,
                    miniFigma: {
                        ...localSettings.miniFigma,
                        sections: newSections
                    }
                };
                setLocalSettings(newSettings);
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            }
        }
    };

    // Функции для drag & drop сообщений внутри групп
    const handleGroupMessageDragStart = (e, groupIndex, messageIndex) => {
        e.stopPropagation(); // Предотвращаем перетаскивание всей группы
        setDraggedGroupMessageIndex(messageIndex);
        setDraggedGroupIndex(groupIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleGroupMessageDragOver = (e, groupIndex, messageIndex) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Только если перетаскиваем сообщение в той же группе
        if (draggedGroupIndex === groupIndex && draggedGroupMessageIndex !== null) {
            setGroupMessageDragOverIndex(messageIndex);
        }
    };

    const handleGroupMessageDragLeave = (e) => {
        e.stopPropagation();
        // Проверяем, что мы действительно покинули элемент, а не перешли к дочернему
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setGroupMessageDragOverIndex(null);
        }
    };

    const handleGroupMessageDrop = (e, groupIndex, messageIndex) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (draggedGroupMessageIndex === null || draggedGroupIndex !== groupIndex) return;
        
        if (draggedGroupMessageIndex !== messageIndex) {
            const updatedMessages = [...(editingSection.messages || [])];
            const group = updatedMessages[groupIndex];
            
            if (group && group.type === 'group') {
                const groupMessages = [...(group.messages || [])];
                const draggedMessage = groupMessages[draggedGroupMessageIndex];
                
                // Используем более простую логику перестановки
                // Удаляем элемент из старой позиции
                groupMessages.splice(draggedGroupMessageIndex, 1);
                
                // Вставляем в новую позицию
                // Если перетаскиваем элемент вниз (на место элемента с большим индексом),
                // то после удаления исходного элемента индекс назначения уменьшается на 1
                let insertIndex = messageIndex;
                if (draggedGroupMessageIndex < messageIndex) {
                    insertIndex = messageIndex - 1;
                }
                
                groupMessages.splice(insertIndex, 0, draggedMessage);
                
                // Обновляем группу
                updatedMessages[groupIndex] = {
                    ...group,
                    messages: groupMessages
                };
                
                const updatedSection = {
                    ...editingSection,
                    messages: updatedMessages
                };
                
                setEditingSection(updatedSection);
                
                // Автоматически сохраняем изменения
                const newSections = [...(localSettings.miniFigma?.sections || [])];
                const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
                if (sectionIndex !== -1) {
                    newSections[sectionIndex] = updatedSection;
                    const newSettings = {
                        ...localSettings,
                        miniFigma: {
                            ...localSettings.miniFigma,
                            sections: newSections
                        }
                    };
                    setLocalSettings(newSettings);
                    // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                }
            }
        }
        
        setDraggedGroupMessageIndex(null);
        setGroupMessageDragOverIndex(null);
        setDraggedGroupIndex(null);
    };

    const handleGroupMessageDragEnd = (e) => {
        e.stopPropagation();
        setDraggedGroupMessageIndex(null);
        setGroupMessageDragOverIndex(null);
        setDraggedGroupIndex(null);
    };

    // Функции для drag & drop обычных сообщений и групп
    const handleMessageDragStart = (e, messageIndex) => {
        setDraggedMessageIndex(messageIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleMessageDragOver = (e, messageIndex) => {
        e.preventDefault();
        if (draggedMessageIndex !== null) {
            setMessageDragOverIndex(messageIndex);
        }
    };

    const handleMessageDragLeave = () => {
        setMessageDragOverIndex(null);
    };
    const handleMessageDrop = (e, messageIndex) => {
        e.preventDefault();
        
        if (draggedMessageIndex === null) return;
        
        if (draggedMessageIndex !== messageIndex) {
            const updatedMessages = [...(editingSection.messages || [])];
            const draggedMessage = updatedMessages[draggedMessageIndex];
            
            // Удаляем сообщение из старой позиции
            updatedMessages.splice(draggedMessageIndex, 1);
            
            // Вставляем в новую позицию
            const newIndex = draggedMessageIndex < messageIndex ? messageIndex - 1 : messageIndex;
            updatedMessages.splice(newIndex, 0, draggedMessage);
            
            const updatedSection = {
                ...editingSection,
                messages: updatedMessages
            };
            
            setEditingSection(updatedSection);
            
            // Автоматически сохраняем изменения
            const newSections = [...(localSettings.miniFigma?.sections || [])];
            const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
            if (sectionIndex !== -1) {
                newSections[sectionIndex] = updatedSection;
                const newSettings = {
                    ...localSettings,
                    miniFigma: {
                        ...localSettings.miniFigma,
                        sections: newSections
                    }
                };
                setLocalSettings(newSettings);
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            }
        }
        
        setDraggedMessageIndex(null);
        setMessageDragOverIndex(null);
    };

    const handleMessageDragEnd = () => {
        setDraggedMessageIndex(null);
        setMessageDragOverIndex(null);
    };

    const removeMessageFromSection = (messageIndex) => {
        if (!editingSection) return;
        
        setEditingSection(prev => ({
            ...prev,
            messages: (prev.messages || []).filter((_, index) => index !== messageIndex)
        }));
    };

    const updateSectionMessage = (messageIndex, field, value) => {
        if (!editingSection) return;
        
        const updatedSection = {
            ...editingSection,
            messages: (editingSection.messages || []).map((message, index) =>
                index === messageIndex ? { ...message, [field]: value } : message
            )
        };
        
        setEditingSection(updatedSection);
        
        // Автоматически сохраняем изменения
        const newSections = [...(localSettings.miniFigma?.sections || [])];
        const sectionIndex = newSections.findIndex(s => s.id === editingSection.id);
        if (sectionIndex !== -1) {
            newSections[sectionIndex] = updatedSection;
            const newSettings = {
                ...localSettings,
                miniFigma: {
                    ...localSettings.miniFigma,
                    sections: newSections
                }
            };
            setLocalSettings(newSettings);
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
        }
    };

    const toggleMessageCollapse = (messageId) => {
        const isCurrentlyCollapsed = collapsedMessages[messageId];
        
        if (!isCurrentlyCollapsed) {
            // Сворачиваем: сначала анимация сворачивания, потом скрытие
            setCollapsingMessages(prev => ({ ...prev, [messageId]: true }));
            
            setTimeout(() => {
                setCollapsedMessages(prev => ({ ...prev, [messageId]: true }));
                setCollapsingMessages(prev => ({ ...prev, [messageId]: false }));
            }, 300); // Длительность анимации collapseFields
        } else {
            // Разворачиваем: сразу показываем с анимацией появления
            setCollapsedMessages(prev => ({ ...prev, [messageId]: false }));
        }
    };

    const toggleGroupCollapse = (groupId) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    // ✅ Toast уведомления — через общий канал приложения (в Nova это Sonner).
    // Локальный useToast — только фолбэк: его тосты здесь не рендерятся
    const localToast = useToast();
    const appCtx = useContext(AppContext);
    const { success, error, warning, info } = appCtx?.toast || localToast;

    // ── Nova: живой предпросмотр кнопки отправки с дашборда.
    // Та же машина состояний, что в NovaWithdrawPanel: 2 сек «Обработка» →
    // результат по ВЫБРАННОМУ (несохранённому) поведению + открытие
    // НАСТОЯЩЕГО модала через контекст приложения. Модалы Nova рендерятся
    // глобально в App.js, поэтому открываются и поверх настроек
    const [sendPreviewState, setSendPreviewState] = useState('default');
    const sendPreviewTimers = useRef([]);
    useEffect(() => () => { sendPreviewTimers.current.forEach(clearTimeout); }, []);
    const SEND_BEHAVIOR_INFO = {
        default: { modal: null, result: 'success', open: null },
        // Маппинг сеттеров скопирован из NovaWithdrawPanel (исторически
        // showCommission открывает showVerifyModal и т.д.)
        showCommission: { modal: 'Комиссия', result: 'error', open: appCtx?.setshowVerifyModal },
        showVerification: { modal: 'Верификация', result: 'error', open: appCtx?.setSendingErrorModal },
        showSuccess: { modal: 'Успех', result: 'success', open: appCtx?.setShowModaWithdrawal },
        showTax: { modal: 'Налог', result: 'error', open: appCtx?.setshowNalogModal },
        showCryptoConversion: { modal: 'Крипто конвертация', result: 'error', open: appCtx?.setShowCryptoConversionModal },
        showBankConversion: { modal: 'Банк конвертация', result: 'error', open: appCtx?.setShowBankConversionModal },
        showPremium: { modal: 'Премиум доступ', result: 'error', open: appCtx?.setShowPremiumModal },
    };
    const runSendPreview = () => {
        if (sendPreviewState !== 'default') return;
        const behavior = localSettings.sendButtonBehavior || 'default';
        const info = SEND_BEHAVIOR_INFO[behavior] || SEND_BEHAVIOR_INFO.default;
        setSendPreviewState('loading');
        sendPreviewTimers.current.push(setTimeout(() => {
            setSendPreviewState(info.result);
            if (info.open) {
                // Открываем реальный модал — кнопка держит состояние,
                // пока он открыт (сброс в эффекте ниже)
                info.open(true);
            } else {
                // default: успех и возврат через 3 сек, как на дашборде
                sendPreviewTimers.current.push(setTimeout(() => setSendPreviewState('default'), 3000));
            }
        }, 2000));
    };
    // Сброс кнопки предпросмотра после закрытия модалов — как на дашборде
    const anyPreviewModalOpen = !!(appCtx && (
        appCtx.showVerifyModal || appCtx.sendingErrorModal || appCtx.showModaWithdrawal ||
        appCtx.showNalogModal || appCtx.showCryptoConversionModal ||
        appCtx.showBankConversionModal || appCtx.showPremiumModal
    ));
    useEffect(() => {
        const behavior = localSettings.sendButtonBehavior || 'default';
        if (behavior !== 'default' && sendPreviewState !== 'default' && sendPreviewState !== 'loading' && !anyPreviewModalOpen) {
            setSendPreviewState('default');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyPreviewModalOpen, sendPreviewState]);
    const sendPreviewHint = () => {
        const behavior = localSettings.sendButtonBehavior || 'default';
        const info = SEND_BEHAVIOR_INFO[behavior] || SEND_BEHAVIOR_INFO.default;
        if (sendPreviewState === 'loading') return 'Обработка ≈2 сек, как на дашборде…';
        if (sendPreviewState === 'success') return info.modal
            ? `Открыт модал «${info.modal}» — кнопка зелёная, пока он открыт`
            : 'Успех: через 3 сек кнопка вернётся в исходное состояние';
        if (sendPreviewState === 'error') return `Открыт модал «${info.modal}» — кнопка красная, пока он открыт`;
        return info.modal
            ? `Нажатие: обработка → модал «${info.modal}». Модал откроется по-настоящему`
            : 'Нажатие: обработка → успех → возврат в исходное';
    };

    // ── Nova: быстрые действия — порядок перетаскиванием. Приоритеты в
    // конфиге остаются (админка сортирует по ним), но выставляются
    // автоматически из позиции строки после броска
    const qaListRef = useRef(null);
    const [qaDragKey, setQaDragKey] = useState(null);
    const [qaOrder, setQaOrder] = useState([]);
    useEffect(() => {
        if (qaDragKey) return;
        setQaOrder(Object.entries(localSettings.quickActions || {})
            .sort(([, a], [, b]) => (a.priority || 999) - (b.priority || 999))
            .map(([k]) => k));
    }, [localSettings.quickActions, qaDragKey]);
    const startQaDrag = (e, key) => {
        e.preventDefault();
        setQaDragKey(key);
        const onMove = (ev) => {
            const list = qaListRef.current;
            if (!list) return;
            const others = Array.from(list.querySelectorAll('[data-qa-key]'))
                .filter((r) => r.dataset.qaKey !== key);
            let insert = others.length;
            for (let i = 0; i < others.length; i++) {
                const rect = others[i].getBoundingClientRect();
                if (ev.clientY < rect.top + rect.height / 2) { insert = i; break; }
            }
            setQaOrder((prev) => {
                const without = prev.filter((k) => k !== key);
                const next = [...without.slice(0, insert), key, ...without.slice(insert)];
                return next.join('|') === prev.join('|') ? prev : next;
            });
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            setQaDragKey(null);
            // Фиксируем позиции строк как приоритеты 1..N
            setQaOrder((curr) => {
                setLocalSettings((prev) => {
                    const qa = { ...(prev.quickActions || {}) };
                    curr.forEach((k, i) => { if (qa[k]) qa[k] = { ...qa[k], priority: i + 1 }; });
                    return { ...prev, quickActions: qa };
                });
                return curr;
            });
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Палитра сегментов диаграммы весов «Генерации сумм»
    const RANGE_COLORS = ['#7B7FF2', '#3ECF8E', '#E2B203', '#F06A6A', '#4DA3FF', '#B07BF2', '#FF9F6B'];

    // ── Nova: банки — порядок перетаскиванием. При броске приоритеты
    // нормализуются в 1..N — это убирает дубли и «прыжки» сортировки,
    // которые давал ручной ввод приоритета со свапом
    const bankListRef = useRef(null);
    const [bankDragName, setBankDragName] = useState(null);
    const [bankOrder, setBankOrder] = useState([]);
    useEffect(() => {
        if (bankDragName) return;
        setBankOrder(Object.keys(localSettings.banks || {})
            .sort((a, b) => {
                const pa = localSettings.banks[a]?.priority ?? 999;
                const pb = localSettings.banks[b]?.priority ?? 999;
                return pa - pb || a.localeCompare(b);
            }));
    }, [localSettings.banks, bankDragName]);
    // Механика как в чате: ряд следует за курсором (transform, без
    // ре-рендеров), соседи плавно расступаются, пунктирный «призрак»
    // показывает слот. Порядок и приоритеты коммитятся на отпускании
    const bankGhostRef = useRef(null);
    const startBankDrag = (e, name) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const list = bankListRef.current;
        if (!list) return;
        const rows = Array.from(list.querySelectorAll('[data-bank-name]'));
        const idx = rows.findIndex((r) => r.dataset.bankName === name);
        const el = rows[idx];
        if (!el) return;

        // Ближайший скролл-родитель — для автопрокрутки у краёв
        let scroller = list.parentElement;
        while (scroller && scroller !== document.body) {
            const st = getComputedStyle(scroller);
            if (/(auto|scroll)/.test(st.overflowY)) break;
            scroller = scroller.parentElement;
        }
        const startY = e.clientY;
        const startScroll = scroller ? scroller.scrollTop : 0;
        const step = el.offsetHeight;
        let active = false;
        let target = idx;
        let raf = 0;
        let lastY = startY;

        const applyShifts = () => {
            raf = 0;
            const dy = (lastY - startY) + ((scroller ? scroller.scrollTop : 0) - startScroll);
            el.style.transform = `translateY(${dy}px) scale(1.01)`;
            // Куда встанет ряд: сколько соседей выше его центра
            const center = el.offsetTop + el.offsetHeight / 2 + dy;
            let t = 0;
            rows.forEach((node, i) => {
                if (i === idx) return;
                if (node.offsetTop + node.offsetHeight / 2 < center) t++;
            });
            target = t;
            // Соседи между старой и новой позицией расступаются
            rows.forEach((node, i) => {
                if (i === idx) return;
                let shift = 0;
                if (idx < target && i > idx && i <= target) shift = -step;
                else if (target < idx && i >= target && i < idx) shift = step;
                node.style.transform = shift ? `translateY(${shift}px)` : '';
            });
            // Призрак — в освободившемся слоте
            const ghost = bankGhostRef.current;
            if (ghost) {
                let slotTop;
                if (target === idx) slotTop = el.offsetTop;
                else if (target > idx) slotTop = rows[target].offsetTop + rows[target].offsetHeight - step;
                else slotTop = rows[target].offsetTop;
                ghost.style.top = `${slotTop}px`;
                ghost.style.height = `${el.offsetHeight}px`;
            }
        };

        const onMove = (ev) => {
            lastY = ev.clientY;
            if (!active) {
                // Порог отделяет перетаскивание от случайного клика
                if (Math.abs(ev.clientY - startY) < 4) return;
                active = true;
                setBankDragName(name);
                rows.forEach((node, i) => {
                    if (i === idx) return;
                    node.style.transition = 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)';
                });
                if (bankGhostRef.current) bankGhostRef.current.style.display = 'block';
                document.body.style.cursor = 'grabbing';
            }
            if (scroller) {
                const rect = scroller.getBoundingClientRect();
                if (ev.clientY < rect.top + 32) scroller.scrollTop -= 9;
                else if (ev.clientY > rect.bottom - 32) scroller.scrollTop += 9;
            }
            if (!raf) raf = requestAnimationFrame(applyShifts);
            ev.preventDefault();
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (raf) cancelAnimationFrame(raf);
            document.body.style.cursor = '';
            if (bankGhostRef.current) bankGhostRef.current.style.display = 'none';
            rows.forEach((node) => {
                node.style.transform = '';
                node.style.transition = '';
            });
            setBankDragName(null);
            if (!active || target === idx) return;
            setBankOrder((prev) => {
                const next = [...prev];
                const [moved] = next.splice(idx, 1);
                next.splice(target, 0, moved);
                setLocalSettings((ls) => {
                    const banks = { ...(ls.banks || {}) };
                    next.forEach((n, i) => { if (banks[n]) banks[n] = { ...banks[n], priority: i + 1 }; });
                    return { ...ls, banks };
                });
                return next;
            });
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // ── Nova: редактор раздела Мини-фигмы — панель переменных (сложена по умолчанию)
    const [nfeVarsOpen, setNfeVarsOpen] = useState(false);
    // ── Nova: окно сохранения конфига — поиск по списку
    const [saveSearch, setSaveSearch] = useState('');
    // ── Nova: редактор пресета чата — панель переменных
    const [cpeVarsOpen, setCpeVarsOpen] = useState(false);
    const nfeListRef = useRef(null);
    const [nfeDragKey, setNfeDragKey] = useState(null);

    // Единая pointer-механика перетаскивания рядов редактора (как в банках):
    // ghost-слот показывает, куда встанет ряд; соседи расступаются.
    // Работает и для верхнего списка, и внутри тела группы.
    const startNfeRowDrag = (e, dragKey, onReorder) => {
        if (e.button !== 0) return;
        const grip = e.currentTarget;
        const el = grip.closest('[data-nfe-row]');
        const container = grip.closest('[data-nfe-list]');
        if (!el || !container) return;
        e.preventDefault();
        e.stopPropagation();
        const rows = Array.from(container.children).filter((c) => c.hasAttribute('data-nfe-row'));
        const idx = rows.indexOf(el);
        if (idx === -1) return;

        // Ближайший скроллящийся родитель — работает и в модалке, и на странице
        let scroller = container;
        while (scroller && scroller !== document.body) {
            const st = getComputedStyle(scroller);
            if (/(auto|scroll)/.test(st.overflowY)) break;
            scroller = scroller.parentElement;
        }
        const startY = e.clientY;
        const startScroll = scroller ? scroller.scrollTop : 0;
        // Точный шаг = расстояние между соседними рядами (учитывает зазор ИЛИ его отсутствие)
        const step = rows[1] ? Math.abs(rows[1].offsetTop - rows[0].offsetTop) : el.offsetHeight + 9;
        let active = false;
        let target = idx;
        let raf = 0;
        let lastY = startY;

        const prevPos = container.style.position;
        const ghost = document.createElement('div');
        ghost.className = s.nfeGhost;
        ghost.style.display = 'none';

        const applyShifts = () => {
            raf = 0;
            const dy = (lastY - startY) + ((scroller ? scroller.scrollTop : 0) - startScroll);
            el.style.transform = `translateY(${dy}px) scale(1.006)`;
            const center = el.offsetTop + el.offsetHeight / 2 + dy;
            let t = 0;
            rows.forEach((node, i) => {
                if (i === idx) return;
                if (node.offsetTop + node.offsetHeight / 2 < center) t++;
            });
            target = t;
            rows.forEach((node, i) => {
                if (i === idx) return;
                let shift = 0;
                if (idx < target && i > idx && i <= target) shift = -step;
                else if (target < idx && i >= target && i < idx) shift = step;
                node.style.transform = shift ? `translateY(${shift}px)` : '';
            });
            let slotTop;
            if (target === idx) slotTop = el.offsetTop;
            else if (target > idx) slotTop = rows[target].offsetTop + rows[target].offsetHeight - el.offsetHeight;
            else slotTop = rows[target].offsetTop;
            ghost.style.top = `${slotTop}px`;
            ghost.style.left = `${el.offsetLeft}px`;
            ghost.style.width = `${el.offsetWidth}px`;
            ghost.style.height = `${el.offsetHeight}px`;
        };

        const onMove = (ev) => {
            lastY = ev.clientY;
            if (!active) {
                if (Math.abs(ev.clientY - startY) < 4) return;
                active = true;
                setNfeDragKey(dragKey);
                container.style.position = prevPos || 'relative';
                container.appendChild(ghost);
                ghost.style.display = 'block';
                el.style.zIndex = '5';
                el.style.position = 'relative';
                rows.forEach((node, i) => {
                    if (i === idx) return;
                    node.style.transition = 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)';
                });
                document.body.style.cursor = 'grabbing';
            }
            if (scroller) {
                const rect = scroller.getBoundingClientRect();
                if (ev.clientY < rect.top + 34) scroller.scrollTop -= 10;
                else if (ev.clientY > rect.bottom - 34) scroller.scrollTop += 10;
            }
            if (!raf) raf = requestAnimationFrame(applyShifts);
            ev.preventDefault();
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (raf) cancelAnimationFrame(raf);
            document.body.style.cursor = '';
            if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
            container.style.position = prevPos;
            rows.forEach((node) => {
                node.style.transform = '';
                node.style.transition = '';
                node.style.zIndex = '';
                node.style.position = '';
            });
            setNfeDragKey(null);
            if (active && target !== idx) onReorder(idx, target);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Синхронизировать секцию в localSettings (без записи на диск).
    // Всегда работаем от свежего состояния через функциональный апдейт.
    const syncSectionToLocal = (updatedSection) => {
        setLocalSettings((ls) => {
            const sections = [...(ls.miniFigma?.sections || [])];
            const i = sections.findIndex((sec) => sec.id === updatedSection.id);
            if (i !== -1) sections[i] = updatedSection;
            return { ...ls, miniFigma: { ...ls.miniFigma, sections } };
        });
    };
    const reorderTopMessages = (from, to) => {
        if (!editingSection) return;
        const arr = [...(editingSection.messages || [])];
        if (from < 0 || from >= arr.length || to < 0 || to > arr.length) return;
        const [m] = arr.splice(from, 1);
        arr.splice(to, 0, m);
        const updated = { ...editingSection, messages: arr };
        setEditingSection(updated);
        syncSectionToLocal(updated);
    };
    const reorderGroupMessages = (groupIndex) => (from, to) => {
        if (!editingSection) return;
        const arr = [...(editingSection.messages || [])];
        const g = arr[groupIndex];
        if (!g || g.type !== 'group') return;
        const gm = [...(g.messages || [])];
        if (from < 0 || from >= gm.length || to < 0 || to > gm.length) return;
        const [m] = gm.splice(from, 1);
        gm.splice(to, 0, m);
        arr[groupIndex] = { ...g, messages: gm };
        const updated = { ...editingSection, messages: arr };
        setEditingSection(updated);
        syncSectionToLocal(updated);
    };
    // ── Пресеты чата: переупорядочивание списка пресетов и реплик внутри пресета
    const reorderChatPresets = (from, to) => {
        setLocalSettings((prev) => {
            const arr = [...(prev.chatPresets || [])];
            if (from < 0 || from >= arr.length || to < 0 || to > arr.length) return prev;
            const [m] = arr.splice(from, 1);
            arr.splice(to, 0, m);
            return { ...prev, chatPresets: arr };
        });
    };
    const reorderPresetMessages = (from, to) => {
        setEditingPreset((prev) => {
            if (!prev) return prev;
            const arr = [...(prev.messages || [])];
            if (from < 0 || from >= arr.length || to < 0 || to > arr.length) return prev;
            const [m] = arr.splice(from, 1);
            arr.splice(to, 0, m);
            return { ...prev, messages: arr };
        });
    };

    // ── Nova: разделы Мини-фигмы — перетаскивание как в банках/чате
    const figListRef = useRef(null);
    const figGhostRef = useRef(null);
    const [figDragId, setFigDragId] = useState(null);
    const startFigSectionDrag = (e, sectionId) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const list = figListRef.current;
        if (!list) return;
        const rows = Array.from(list.querySelectorAll('[data-fig-id]'));
        const idx = rows.findIndex((r) => r.dataset.figId === sectionId);
        const el = rows[idx];
        if (!el) return;

        let scroller = list.parentElement;
        while (scroller && scroller !== document.body) {
            const st = getComputedStyle(scroller);
            if (/(auto|scroll)/.test(st.overflowY)) break;
            scroller = scroller.parentElement;
        }
        const startY = e.clientY;
        const startScroll = scroller ? scroller.scrollTop : 0;
        const step = el.offsetHeight;
        let active = false;
        let target = idx;
        let raf = 0;
        let lastY = startY;

        const applyShifts = () => {
            raf = 0;
            const dy = (lastY - startY) + ((scroller ? scroller.scrollTop : 0) - startScroll);
            el.style.transform = `translateY(${dy}px) scale(1.01)`;
            const center = el.offsetTop + el.offsetHeight / 2 + dy;
            let t = 0;
            rows.forEach((node, i) => {
                if (i === idx) return;
                if (node.offsetTop + node.offsetHeight / 2 < center) t++;
            });
            target = t;
            rows.forEach((node, i) => {
                if (i === idx) return;
                let shift = 0;
                if (idx < target && i > idx && i <= target) shift = -step;
                else if (target < idx && i >= target && i < idx) shift = step;
                node.style.transform = shift ? `translateY(${shift}px)` : '';
            });
            const ghost = figGhostRef.current;
            if (ghost) {
                let slotTop;
                if (target === idx) slotTop = el.offsetTop;
                else if (target > idx) slotTop = rows[target].offsetTop + rows[target].offsetHeight - step;
                else slotTop = rows[target].offsetTop;
                ghost.style.top = `${slotTop}px`;
                ghost.style.height = `${el.offsetHeight}px`;
            }
        };

        const onMove = (ev) => {
            lastY = ev.clientY;
            if (!active) {
                if (Math.abs(ev.clientY - startY) < 4) return;
                active = true;
                setFigDragId(sectionId);
                rows.forEach((node, i) => {
                    if (i === idx) return;
                    node.style.transition = 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)';
                });
                if (figGhostRef.current) figGhostRef.current.style.display = 'block';
                document.body.style.cursor = 'grabbing';
            }
            if (scroller) {
                const rect = scroller.getBoundingClientRect();
                if (ev.clientY < rect.top + 32) scroller.scrollTop -= 9;
                else if (ev.clientY > rect.bottom - 32) scroller.scrollTop += 9;
            }
            if (!raf) raf = requestAnimationFrame(applyShifts);
            ev.preventDefault();
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (raf) cancelAnimationFrame(raf);
            document.body.style.cursor = '';
            if (figGhostRef.current) figGhostRef.current.style.display = 'none';
            rows.forEach((node) => {
                node.style.transform = '';
                node.style.transition = '';
            });
            setFigDragId(null);
            if (!active || target === idx) return;
            const sections = [...(localSettings.miniFigma?.sections || [])];
            const [moved] = sections.splice(idx, 1);
            sections.splice(target, 0, moved);
            handleInputChange('miniFigma', 'sections', sections);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Загрузка иконки банка на сервер (общая для обеих тем)
    const uploadBankIcon = async (bankName, file) => {
        if (!file) return;
        try {
            const asDataUrl = await new Promise((res, rej) => {
                const fr = new FileReader();
                fr.onload = () => res(fr.result);
                fr.onerror = rej;
                fr.readAsDataURL(file);
            });
            const resp = await fetch(`/api/files/bankicons/${file.name}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: asDataUrl }),
            });
            const j = await resp.json();
            if (j && j.success) {
                try {
                    const list = await (await fetch('/api/files/bankicons')).json();
                    if (Array.isArray(list.files)) setServerBankIcons(list.files);
                } catch (_) {}
                // сбрасываем кэш браузера для этого имени файла
                setIconVersions((prev) => ({ ...prev, [file.name]: Date.now() }));
                handleInputChange('banks', `${bankName}.iconFileName`, file.name);
            } else {
                notifyError('Не удалось загрузить иконку');
            }
        } catch (e) {
            if (e.message && e.message.includes('JSON')) {
                notifyError('Ошибка: сервер не поддерживает загрузку иконок. Используйте локальные иконки из папки src/img/bankicons/');
            } else {
                notifyError('Ошибка загрузки иконки: ' + e.message);
            }
        }
    };

    // Загружаем роль пользователя при монтировании компонента
    useEffect(() => {
        const loadUserRole = async () => {
            const role = await getCurrentUserRole();
            setUserRole(role);
            const tabs = getAvailableTabs(role);
            setAvailableTabs(tabs);
            // Если текущий таб недоступен, переключаемся на первый доступный
            if (tabs.length > 0 && !tabs.includes(activeTab)) {
                setActiveTab(tabs[0]);
            }
        };
        loadUserRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Компонент для табов
    const TabNavigation = () => {
        const tabs = [
            { id: 'general', label: 'Основные', icon: siteSettingsIcon, Glyph: SlidersHorizontal },
            { id: 'finance', label: 'Финансы', icon: currencyRatesIcon, Glyph: Coins },
            { id: 'wallets', label: 'Кошельки', icon: walletsIcon, Glyph: WalletGlyph },
            { id: 'banks', label: 'Банки', icon: bankConfigurationIcon, Glyph: Landmark },
            { id: 'translations', label: 'Переводы', icon: currencySymbolIcon, Glyph: Languages },
            { id: 'miniFigma', label: 'Мини-фигма', icon: quickActionsIcon, Glyph: LayoutTemplate },
            { id: 'chatPresets', label: 'Пресеты чата', icon: supportChatIcon, Glyph: MessagesSquare }
        ];

        return (
        <div className={s.sectionTabContainer}>
                {tabs.map(tab => {
                    if (!canAccessSiteSettingsTab(userRole, tab.id)) {
                        return null;
                    }
                    return (
            <button 
                            key={tab.id}
                            className={`${s.sectionTab} ${activeTab === tab.id ? s.active : ''}`}
                            onClick={() => setActiveTab(tab.id)}
            >
                            {isNova
                                ? <tab.Glyph size={15} strokeWidth={2} className={s.tabGlyph} aria-hidden="true" />
                                : <img src={tab.icon} alt={tab.label} className={s.tabIcon} />}
                            <span>{tab.label}</span>
            </button>
                    );
                })}
        </div>
    );
    };

    // Список тарифов для вкладок
    const tariffTabs = [
        { id: '💰 1 ТАРИФ', label: '1 ТАРИФ', icon: tarif1Icon },
        { id: '❗❗ 2 ТАРИФ', label: '2 ТАРИФ', icon: tarif2Icon },
        { id: '❗❗❗ 3 ТАРИФ', label: '3 ТАРИФ', icon: tarif3Icon },
        { id: '❗❗❗❗ 4 ТАРИФ', label: '4 ТАРИФ', icon: tarif4Icon },
        { id: '⚠️ VIP ТАРИФ', label: 'VIP ТАРИФ', icon: viptarifIcon }
    ];

    // Функция для получения иконки тарифа
    const getTariffIcon = (tariffId) => {
        const tariff = tariffTabs.find(t => t.id === tariffId);
        return tariff ? tariff.icon : tarif1Icon;
    };

    // Функция для получения иконки криптовалюты
    const getCryptoIcon = (crypto) => {
        const icons = {
            BTC: bitcoinIcon,
            ETH: ethereumIcon,
            BNB: bnbIcon,
            USDC: usdcIcon,
            SOL: solanaIcon
        };
        return icons[crypto] || bitcoinIcon;
    };
    // Полный список часовых поясов
    const timezones = [
        { value: 'Asia/Kuala_Lumpur', label: '🇲🇾 Malaysia (GMT+8)' },
        { value: 'Europe/London', label: '🇬🇧 London (GMT+0)' },
        { value: 'Europe/Paris', label: '🇫🇷 Paris (GMT+1)' },
        { value: 'Europe/Berlin', label: '🇩🇪 Berlin (GMT+1)' },
        { value: 'Europe/Rome', label: '🇮🇹 Rome (GMT+1)' },
        { value: 'Europe/Madrid', label: '🇪🇸 Madrid (GMT+1)' },
        { value: 'Europe/Amsterdam', label: '🇳🇱 Amsterdam (GMT+1)' },
        { value: 'Europe/Brussels', label: '🇧🇪 Brussels (GMT+1)' },
        { value: 'Europe/Vienna', label: '🇦🇹 Vienna (GMT+1)' },
        { value: 'Europe/Warsaw', label: '🇵🇱 Warsaw (GMT+1)' },
        { value: 'Europe/Prague', label: '🇨🇿 Prague (GMT+1)' },
        { value: 'Europe/Budapest', label: '🇭🇺 Budapest (GMT+1)' },
        { value: 'Europe/Zurich', label: '🇨🇭 Zurich (GMT+1)' },
        { value: 'Europe/Stockholm', label: '🇸🇪 Stockholm (GMT+1)' },
        { value: 'Europe/Oslo', label: '🇳🇴 Oslo (GMT+1)' },
        { value: 'Europe/Copenhagen', label: '🇩🇰 Copenhagen (GMT+1)' },
        { value: 'Europe/Helsinki', label: '🇫🇮 Helsinki (GMT+2)' },
        { value: 'Europe/Athens', label: '🇬🇷 Athens (GMT+2)' },
        { value: 'Europe/Bucharest', label: '🇷🇴 Bucharest (GMT+2)' },
        { value: 'Europe/Sofia', label: '🇧🇬 Sofia (GMT+2)' },
        { value: 'Europe/Kiev', label: '🇺🇦 Kiev (GMT+2)' },
        { value: 'Europe/Moscow', label: '🇷🇺 Moscow (GMT+3)' },
        { value: 'Europe/Istanbul', label: '🇹🇷 Istanbul (GMT+3)' },
        { value: 'Asia/Dubai', label: '🇦🇪 Dubai (GMT+4)' },
        { value: 'Asia/Karachi', label: '🇵🇰 Karachi (GMT+5)' },
        { value: 'Asia/Kolkata', label: '🇮🇳 Mumbai (GMT+5:30)' },
        { value: 'Asia/Dhaka', label: '🇧🇩 Dhaka (GMT+6)' },
        { value: 'Asia/Bangkok', label: '🇹🇭 Bangkok (GMT+7)' },
        { value: 'Asia/Ho_Chi_Minh', label: '🇻🇳 Ho Chi Minh (GMT+7)' },
        { value: 'Asia/Jakarta', label: '🇮🇩 Jakarta (GMT+7)' },
        { value: 'Asia/Singapore', label: '🇸🇬 Singapore (GMT+8)' },
        { value: 'Asia/Hong_Kong', label: '🇭🇰 Hong Kong (GMT+8)' },
        { value: 'Asia/Shanghai', label: '🇨🇳 Shanghai (GMT+8)' },
        { value: 'Asia/Taipei', label: '🇹🇼 Taipei (GMT+8)' },
        { value: 'Asia/Seoul', label: '🇰🇷 Seoul (GMT+9)' },
        { value: 'Asia/Tokyo', label: '🇯🇵 Tokyo (GMT+9)' },
        { value: 'Australia/Sydney', label: '🇦🇺 Sydney (GMT+10)' },
        { value: 'Australia/Melbourne', label: '🇦🇺 Melbourne (GMT+10)' },
        { value: 'Pacific/Auckland', label: '🇳🇿 Auckland (GMT+12)' },
        { value: 'America/New_York', label: '🇺🇸 New York (GMT-5)' },
        { value: 'America/Chicago', label: '🇺🇸 Chicago (GMT-6)' },
        { value: 'America/Denver', label: '🇺🇸 Denver (GMT-7)' },
        { value: 'America/Los_Angeles', label: '🇺🇸 Los Angeles (GMT-8)' },
        { value: 'America/Toronto', label: '🇨🇦 Toronto (GMT-5)' },
        { value: 'America/Vancouver', label: '🇨🇦 Vancouver (GMT-8)' },
        { value: 'America/Mexico_City', label: '🇲🇽 Mexico City (GMT-6)' },
        { value: 'America/Sao_Paulo', label: '🇧🇷 São Paulo (GMT-3)' },
        { value: 'America/Buenos_Aires', label: '🇦🇷 Buenos Aires (GMT-3)' },
        { value: 'America/Santiago', label: '🇨🇱 Santiago (GMT-3)' },
        { value: 'America/Lima', label: '🇵🇪 Lima (GMT-5)' },
        { value: 'America/Bogota', label: '🇨🇴 Bogotá (GMT-5)' },
        { value: 'America/Caracas', label: '🇻🇪 Caracas (GMT-4)' },
        { value: 'Africa/Cairo', label: '🇪🇬 Cairo (GMT+2)' },
        { value: 'Africa/Lagos', label: '🇳🇬 Lagos (GMT+1)' },
        { value: 'Africa/Johannesburg', label: '🇿🇦 Johannesburg (GMT+2)' },
        { value: 'Africa/Nairobi', label: '🇰🇪 Nairobi (GMT+3)' }
    ];

    // Синхронизируем локальные настройки с пропсами.
    // ВАЖНО: сбрасываем локальное состояние только если настройки реально
    // изменились по содержимому. Раньше сравнение шло по ссылке — родитель
    // пересоздавал объект settings, и несохранённые правки (например, выбор
    // файла имён) молча откатывались к сохранённым значениям
    const lastSyncedSettingsRef = useRef(null);
    useEffect(() => {
        if (settings) {
            const incoming = JSON.stringify(settings);
            if (incoming === lastSyncedSettingsRef.current) return;
            lastSyncedSettingsRef.current = incoming;
            // Инициализируем brokerAmountGeneration и selectedFlag если их нет
            const settingsWithDefaults = {
                ...settings,
                brokerAmountGeneration: settings.brokerAmountGeneration || defaultSettings.brokerAmountGeneration,
                selectedFlag: settings.selectedFlag || defaultSettings.selectedFlag
            };
            
            // Загружаем шрифт названия сайта, если он задан
            const siteTitleFont = settingsWithDefaults.siteTitleFont || defaultSettings.siteTitleFont;
            if (siteTitleFont) {
                loadFontByValue(siteTitleFont);
            }
            
            // Нормализуем namesFile: убираем путь, оставляем только имя файла
            if (settingsWithDefaults.namesFile) {
                let fileOnly = settingsWithDefaults.namesFile.split('/').pop();
                if (fileOnly && !fileOnly.endsWith('.txt')) {
                    fileOnly = fileOnly + '.txt';
                }
                if (fileOnly && fileOnly !== settingsWithDefaults.namesFile) {
                    settingsWithDefaults.namesFile = fileOnly;
                    console.log('[SiteSettings] Нормализован namesFile:', settings.namesFile, '->', fileOnly);
                }
            }
            
            // Исправляем переводы чата сразу при загрузке
            const correctedSettings = { ...settingsWithDefaults };
            
            // Убеждаемся, что структура переводов чата корректна
            if (!correctedSettings.translations) {
                correctedSettings.translations = {};
            }
            
            // Обновляем переводы только для существующих языков
            Object.keys(correctedSettings.translations || {}).forEach(lang => {
                if (!correctedSettings.translations[lang]) {
                    correctedSettings.translations[lang] = {};
                }
                if (!correctedSettings.translations[lang].chat) {
                    correctedSettings.translations[lang].chat = {};
                }
                if (!correctedSettings.translations[lang].chat.presets) {
                    correctedSettings.translations[lang].chat.presets = {};
                }
                
                // Обновляем сообщения пресетов правильными переводами
                ['verification', 'commission', 'tax', 'transfer'].forEach(presetType => {
                    if (!correctedSettings.translations[lang].chat.presets[presetType]) {
                        correctedSettings.translations[lang].chat.presets[presetType] = {};
                    }
                    
                    // Получаем количество сообщений для каждого пресета
                    const messageCount = presetType === 'verification' || presetType === 'commission' ? 6 : 9;
                    
                    for (let i = 1; i <= messageCount; i++) {
                        const messageKey = `message${i}`;
                        const defaultMessage = getDefaultChatMessage(presetType, i);
                        
                        if (defaultMessage && defaultMessage[lang]) {
                            correctedSettings.translations[lang].chat.presets[presetType][messageKey] = defaultMessage[lang];
                        }
                    }
                });
            });
            
            setLocalSettings(correctedSettings);
        }
    }, [settings]);
    
    // Применяем предпросмотр названия сайта и шрифта в реальном времени (без сохранения)
    useEffect(() => {
        // Пропускаем первый рендер, чтобы не применять изменения при инициализации
        if (!settings || Object.keys(localSettings).length === 0) return;
        
        const siteName = localSettings.siteName !== undefined ? localSettings.siteName : settings?.siteName;
        const siteTitleFont = localSettings.siteTitleFont !== undefined ? localSettings.siteTitleFont : settings?.siteTitleFont;
        const siteTitleFontWeight = localSettings.siteTitleFontWeight !== undefined ? localSettings.siteTitleFontWeight : settings?.siteTitleFontWeight;
        const siteTitleFontStyle = localSettings.siteTitleFontStyle !== undefined ? localSettings.siteTitleFontStyle : settings?.siteTitleFontStyle;
        const siteTitleLetterSpacing = localSettings.siteTitleLetterSpacing !== undefined ? localSettings.siteTitleLetterSpacing : settings?.siteTitleLetterSpacing;
        const siteTitleFontSize = localSettings.siteTitleFontSize !== undefined ? localSettings.siteTitleFontSize : settings?.siteTitleFontSize;
        const siteTitleLogoGap = localSettings.siteTitleLogoGap !== undefined ? localSettings.siteTitleLogoGap : settings?.siteTitleLogoGap;
            
        // Применяем предпросмотр напрямую к элементам через CSS переменные
        if (siteName !== undefined) {
            document.documentElement.style.setProperty('--site-name-preview', `"${siteName}"`);
            // Обновляем title страницы для предпросмотра
            document.title = siteName || '暗号交換';
        }
        
        if (siteTitleFont !== undefined) {
            document.documentElement.style.setProperty('--site-title-font-preview', siteTitleFont);
            // Загружаем шрифт если он изменился
            loadFontByValue(siteTitleFont);
        }
        
        if (siteTitleFontWeight !== undefined) {
            document.documentElement.style.setProperty('--site-title-font-weight-preview', siteTitleFontWeight);
        }
        
        if (siteTitleFontStyle !== undefined) {
            document.documentElement.style.setProperty('--site-title-font-style-preview', siteTitleFontStyle);
        }
        
        if (siteTitleLetterSpacing !== undefined) {
            document.documentElement.style.setProperty('--site-title-letter-spacing-preview', siteTitleLetterSpacing);
        }
        
        if (siteTitleFontSize !== undefined) {
            document.documentElement.style.setProperty('--site-title-font-size-preview', siteTitleFontSize);
        }
        
        // Применяем стили напрямую к элементам с классом brandText и fintechBrandText
        const brandTextElements = document.querySelectorAll('[class*="brandText"], [class*="fintechBrandText"]');
        brandTextElements.forEach((el) => {
            if (siteTitleFont !== undefined) {
                el.style.fontFamily = siteTitleFont;
            }
            if (siteTitleFontWeight !== undefined) {
                el.style.fontWeight = siteTitleFontWeight;
            }
            if (siteTitleFontStyle !== undefined) {
                el.style.fontStyle = siteTitleFontStyle;
            }
            // Применяем letterSpacing - если значение пустое или не указано, используем normal
            if (siteTitleLetterSpacing !== undefined && siteTitleLetterSpacing !== null && siteTitleLetterSpacing !== '') {
                // Если значение не содержит единиц измерения, добавляем px
                const spacingValue = siteTitleLetterSpacing.trim();
                if (spacingValue && !isNaN(parseFloat(spacingValue)) && !spacingValue.match(/px|em|rem|pt|%/)) {
                    el.style.letterSpacing = spacingValue + 'px';
                } else {
                    el.style.letterSpacing = spacingValue;
                }
            } else {
                el.style.letterSpacing = 'normal';
            }
            // Применяем fontSize
            if (siteTitleFontSize !== undefined && siteTitleFontSize !== null && siteTitleFontSize !== '') {
                const fontSizeValue = siteTitleFontSize.trim();
                if (fontSizeValue && !isNaN(parseFloat(fontSizeValue)) && !fontSizeValue.match(/px|em|rem|pt|%/)) {
                    el.style.fontSize = fontSizeValue + 'px';
                } else {
                    el.style.fontSize = fontSizeValue;
                }
            }
            // Обновляем текст названия сайта для предпросмотра
            if (siteName !== undefined && el.textContent !== siteName) {
                el.textContent = siteName || '暗号交換';
            }
            // Отступ между логотипом и названием — на родителе-контейнере бренда
            if (siteTitleLogoGap !== undefined && el.parentElement) {
                const gapVal = String(siteTitleLogoGap).trim();
                if (gapVal) {
                    el.parentElement.style.gap = (!isNaN(parseFloat(gapVal)) && !gapVal.match(/px|em|rem|pt|%/))
                        ? gapVal + 'px'
                        : gapVal;
                } else {
                    el.parentElement.style.gap = '';
                }
            }
        });
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localSettings.siteName, localSettings.siteTitleFont, localSettings.siteTitleFontWeight, localSettings.siteTitleFontStyle, localSettings.siteTitleLetterSpacing, localSettings.siteTitleFontSize, localSettings.siteTitleLogoGap]);

    // Загружаем список доступных файлов имен и фоновых изображений IRS при монтировании компонента
    useEffect(() => {
        const loadNameFiles = async () => {
            const nameFiles = await getNameFilesList();
        setAvailableNameFiles(nameFiles);
        };
        loadNameFiles();
        
        const loadIrsBackgrounds = async () => {
            const irsBackgrounds = await getIrsBackgroundsList();
        setAvailableIrsBackgrounds(irsBackgrounds);
        };
        loadIrsBackgrounds();
    }, []);

    // Функция для обновления списка файлов имен
    const refreshNameFilesList = async () => {
        const nameFiles = await getNameFilesList();
        setAvailableNameFiles(nameFiles);
    };

    // Функция для обновления списка фоновых изображений IRS
    const refreshIrsBackgroundsList = async () => {
        const irsBackgrounds = await getIrsBackgroundsList();
        setAvailableIrsBackgrounds(irsBackgrounds);
    };

    const handleSave = async () => {
        // Обновляем переводы чата с правильными значениями перед сохранением
        const updatedSettings = normalizeWalletSettingsBeforeSave({ ...localSettings });
        
        // Убеждаемся, что структура переводов чата корректна
        if (!updatedSettings.translations) {
            updatedSettings.translations = {};
        }
        
        // Обновляем переводы только для существующих языков
        Object.keys(updatedSettings.translations || {}).forEach(lang => {
            if (!updatedSettings.translations[lang]) {
                updatedSettings.translations[lang] = {};
            }
            if (!updatedSettings.translations[lang].chat) {
                updatedSettings.translations[lang].chat = {};
            }
            if (!updatedSettings.translations[lang].chat.presets) {
                updatedSettings.translations[lang].chat.presets = {};
            }
            
            // Обновляем сообщения пресетов правильными переводами
            ['verification', 'commission', 'tax', 'transfer'].forEach(presetType => {
                if (!updatedSettings.translations[lang].chat.presets[presetType]) {
                    updatedSettings.translations[lang].chat.presets[presetType] = {};
                }
                
                // Получаем количество сообщений для каждого пресета
                const messageCount = presetType === 'verification' || presetType === 'commission' ? 6 : 9;
                
                for (let i = 1; i <= messageCount; i++) {
                    const messageKey = `message${i}`;
                    const defaultMessage = getDefaultChatMessage(presetType, i);
                    
                    if (defaultMessage && defaultMessage[lang]) {
                        updatedSettings.translations[lang].chat.presets[presetType][messageKey] = defaultMessage[lang];
                    }
                }
            });
        });
        
        setLocalSettings(updatedSettings);
        
        // Если есть currentConfigFile, открываем модал для выбора конфигов
        if (currentConfigFile) {
            await loadAvailableConfigs();
            setIsSaveModalOpen(true);
        } else {
            // Если нет текущего конфига, сохраняем только в localStorage
            onSaveSettings(updatedSettings);
            // Обновляем initialSettings после сохранения
            setInitialSettings(JSON.parse(JSON.stringify(updatedSettings)));
            success('Настройки сохранены в localStorage! ✅', 3000);
        }
    };

    // Функция для обработки выбора конфигов в модале
    const handleConfigToggle = (configFilename) => {
        setSelectedConfigs(prev => {
            if (prev.includes(configFilename)) {
                return prev.filter(config => config !== configFilename);
            } else {
                return [...prev, configFilename];
            }
        });
    };
    // Обновляем initialSettings при изменении settings из пропсов
    useEffect(() => {
        if (settings) {
            const logMsg = `[SiteSettings] settings изменились\nquickActions: ${JSON.stringify(settings.quickActions || {}, null, 2)}`;
            console.log('🔍 [SiteSettings] settings изменились, обновляем localSettings и initialSettings');
            console.log('🔍 [SiteSettings] settings.quickActions:', JSON.stringify(settings.quickActions || {}, null, 2));
            // window.alert(logMsg); // Раскомментируйте для отладки
            setLocalSettings(settings);
            const cloned = JSON.parse(JSON.stringify(settings));
            console.log('🔍 [SiteSettings] cloned.quickActions:', JSON.stringify(cloned.quickActions || {}, null, 2));
            setInitialSettings(cloned);
        }
    }, [settings]);

    // Функция для нахождения только измененных полей между начальным и текущим состоянием
    const getChangedFields = (initial, current, path = '') => {
        const changes = {};
        
        // Специальная обработка для banks - сравниваем весь объект целиком
        // Это необходимо для корректного отслеживания удаленных банков
        if (path === '' && initial.banks && current.banks) {
            if (JSON.stringify(initial.banks) !== JSON.stringify(current.banks)) {
                changes.banks = current.banks;
            }
        }
        
        // Специальная обработка для quickActions - сравниваем весь объект целиком
        // Это необходимо для корректного сохранения всех полей (enabled, priority, emailEnabled)
        if (path === '' && initial.quickActions && current.quickActions) {
            // Нормализуем объекты перед сравнением (сортируем ключи)
            const normalizeQuickActions = (qa) => {
                const normalized = {};
                Object.keys(qa).sort().forEach(key => {
                    normalized[key] = qa[key];
                });
                return normalized;
            };
            const initialNormalized = normalizeQuickActions(initial.quickActions);
            const currentNormalized = normalizeQuickActions(current.quickActions);
            
            if (JSON.stringify(initialNormalized) !== JSON.stringify(currentNormalized)) {
                const logMsg = `[getChangedFields] quickActions изменены:\ninitial: ${JSON.stringify(initialNormalized, null, 2)}\ncurrent: ${JSON.stringify(currentNormalized, null, 2)}`;
                console.log('🔍 [getChangedFields] quickActions изменены:', {
                    initial: initialNormalized,
                    current: currentNormalized
                });
                // window.alert(logMsg); // Раскомментируйте для отладки
                changes.quickActions = current.quickActions;
            }
        }
        
        // Проверяем все ключи из текущего состояния
        for (const key in current) {
            // Пропускаем banks и quickActions на верхнем уровне - уже обработали выше
            if (path === '' && (key === 'banks' || key === 'quickActions')) {
                continue;
            }
            
            const currentPath = path ? `${path}.${key}` : key;
            const initialValue = initial[key];
            const currentValue = current[key];
            
            // Если ключа нет в начальном состоянии - это изменение
            if (!(key in initial)) {
                changes[key] = currentValue;
                continue;
            }
            
            // Для объектов рекурсивно проверяем изменения
            if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue) &&
                typeof initialValue === 'object' && initialValue !== null && !Array.isArray(initialValue)) {
                const nestedChanges = getChangedFields(initialValue, currentValue, currentPath);
                if (Object.keys(nestedChanges).length > 0) {
                    changes[key] = nestedChanges;
                }
            } 
            // Для массивов и примитивов сравниваем значения
            else if (JSON.stringify(initialValue) !== JSON.stringify(currentValue)) {
                changes[key] = currentValue;
            }
        }
        
        // Проверяем удаленные ключи (есть в initial, но нет в current)
        // Пропускаем banks и quickActions - уже обработали выше
        for (const key in initial) {
            if (path === '' && (key === 'banks' || key === 'quickActions')) {
                continue;
            }
            
            if (!(key in current)) {
                // Для объектов - помечаем как удаленное (пустой объект)
                if (typeof initial[key] === 'object' && initial[key] !== null && !Array.isArray(initial[key])) {
                    changes[key] = {};
                } else {
                    changes[key] = null;
                }
            }
        }
        
        return changes;
    };

    // Функция для применения только измененных полей к существующему конфигу
    const applyChangesToConfig = (existingConfig, changes) => {
        const result = { ...existingConfig };
        
        for (const key in changes) {
            const changeValue = changes[key];
            
            // Специальная обработка для banks - заменяем весь объект целиком
            if (key === 'banks' && typeof changeValue === 'object' && changeValue !== null && !Array.isArray(changeValue)) {
                result[key] = changeValue; // Полностью заменяем объект banks
                continue;
            }
            
            // Специальная обработка для quickActions - заменяем весь объект целиком
            // Это необходимо для корректного сохранения всех полей (enabled, priority, emailEnabled)
            if (key === 'quickActions' && typeof changeValue === 'object' && changeValue !== null && !Array.isArray(changeValue)) {
                result[key] = changeValue; // Полностью заменяем объект quickActions
                continue;
            }
            
            // Если значение - объект (но не массив), рекурсивно применяем изменения
            if (typeof changeValue === 'object' && changeValue !== null && !Array.isArray(changeValue)) {
                // Если это пустой объект {} - значит нужно удалить все ключи из существующего объекта
                if (Object.keys(changeValue).length === 0 && existingConfig[key] && typeof existingConfig[key] === 'object') {
                    result[key] = {};
                } else {
                result[key] = applyChangesToConfig(existingConfig[key] || {}, changeValue);
                }
            } else if (changeValue === null) {
                // Если значение null - удаляем ключ
                delete result[key];
            } else {
                // Для примитивов и массивов просто заменяем значение
                result[key] = changeValue;
            }
        }
        
        return result;
    };

    // Функция для глубокого мержа объектов (добавляет отсутствующие поля) - оставлена для обратной совместимости
    const deepMergeSettings = (existingConfig, newSettings) => {
        const merged = { ...existingConfig };
        
        // Рекурсивно мержим все поля из newSettings
        for (const key in newSettings) {
            if (newSettings[key] === null || newSettings[key] === undefined) {
                // Пропускаем null/undefined значения
                continue;
            }
            
            if (typeof newSettings[key] === 'object' && !Array.isArray(newSettings[key]) && newSettings[key] !== null) {
                // Для объектов рекурсивно мержим
                merged[key] = deepMergeSettings(existingConfig[key] || {}, newSettings[key]);
            } else {
                // Для примитивных значений и массивов используем новое значение
                merged[key] = newSettings[key];
            }
        }
        
        return merged;
    };

    const handleSaveToConfigs = async () => {
        if (selectedConfigs.length === 0) {
            error('Выберите хотя бы один конфиг для сохранения!', 3000);
            return;
        }

        let successCount = 0;
        let errorCount = 0;

        console.log('🔄 Сохраняем настройки в конфиги:', selectedConfigs);

        // Находим только измененные поля
        const normalizedLocalSettings = normalizeWalletSettingsBeforeSave(localSettings);
        const changedFields = getChangedFields(initialSettings, normalizedLocalSettings);
        console.log('📝 Измененные поля:', changedFields);
        
        if (Object.keys(changedFields).length === 0) {
            warning('Нет изменений для сохранения!', 3000);
            setIsSaveModalOpen(false);
            return;
        }

        // Импортируем утилиту для работы с конфигами
        const { saveLocalConfig, loadLocalConfig } = await import('../../utils/configUtils');

        for (const configFilename of selectedConfigs) {
            try {
                // Загружаем существующий конфиг, если он есть
                let existingConfig = {};
                try {
                    existingConfig = await loadLocalConfig(configFilename);
                    console.log(`📖 Загружен существующий конфиг ${configFilename}`);
                } catch (loadError) {
                    console.log(`📝 Конфиг ${configFilename} не существует или не может быть загружен, создаем новый`);
                    existingConfig = {};
                }
                
                // Применяем только измененные поля к существующему конфигу
                // Это сохранит все существующие данные и обновит только измененные поля
                const updatedConfig = applyChangesToConfig(existingConfig, changedFields);
                
                // ВАЖНО: Если изменен quickActions, убеждаемся что он полностью заменен
                if (changedFields.quickActions) {
                    updatedConfig.quickActions = changedFields.quickActions;
                    // Временное логирование через alert для отладки
                    const logMsg = `[SAVE] quickActions изменен:\n${JSON.stringify(updatedConfig.quickActions, null, 2)}`;
                    console.log('🔍 [SAVE] quickActions изменен, полностью заменяем:', JSON.stringify(updatedConfig.quickActions, null, 2));
                    // window.alert(logMsg); // Раскомментируйте для отладки
                }
                
                // Временное логирование для отладки
                const saveLog = `💾 Сохраняем конфиг: ${configFilename}\n` +
                    `existingConfig.quickActions: ${JSON.stringify(existingConfig.quickActions || {}, null, 2)}\n` +
                    `changedFields.quickActions: ${JSON.stringify(changedFields.quickActions || {}, null, 2)}\n` +
                    `updatedConfig.quickActions: ${JSON.stringify(updatedConfig.quickActions || {}, null, 2)}`;
                console.log(`💾 Сохраняем обновленный конфиг в ${configFilename} (только измененные поля)`);
                console.log('🔍 [SAVE] existingConfig.quickActions:', JSON.stringify(existingConfig.quickActions || {}, null, 2));
                console.log('🔍 [SAVE] changedFields.quickActions:', JSON.stringify(changedFields.quickActions || {}, null, 2));
                console.log('🔍 [SAVE] updatedConfig.quickActions:', JSON.stringify(updatedConfig.quickActions || {}, null, 2));
                // window.alert(saveLog); // Раскомментируйте для отладки
                
                const result = await saveLocalConfig(configFilename, updatedConfig);

                if (result.success) {
                    console.log(`✅ Конфиг ${configFilename} успешно сохранен`);
                    successCount++;
                } else {
                    console.error(`❌ Ошибка сохранения ${configFilename}:`, result.error);
                    errorCount++;
                }
            } catch (saveError) {
                console.error(`❌ Ошибка при сохранении ${configFilename}:`, saveError);
                errorCount++;
            }
        }

        // Применяем настройки через callback
        onSaveSettings(normalizedLocalSettings);
        
        // Обновляем initialSettings после успешного сохранения
        setLocalSettings(normalizedLocalSettings);
        setInitialSettings(JSON.parse(JSON.stringify(normalizedLocalSettings)));

        // Обновляем список конфигов после сохранения
        await loadAvailableConfigs();

        // Закрываем модал
        setIsSaveModalOpen(false);

        // Показываем результат
        const changedFieldsCount = Object.keys(changedFields).length;
        if (successCount > 0 && errorCount === 0) {
            success(`Изменения (${changedFieldsCount} ${plural(changedFieldsCount, 'поле', 'поля', 'полей')}) сохранены в ${pluralConfigs(successCount)}! ✅`, 4000);
        } else if (successCount > 0 && errorCount > 0) {
            warning(`Сохранено в ${pluralConfigs(successCount)}, ошибок: ${errorCount}`, 5000);
        } else {
            error(`Не удалось сохранить ни в один конфиг! Ошибок: ${errorCount}`, 5000);
        }
    };

    // Функция для закрытия модала
    const handleCloseSaveModal = () => {
        setIsSaveModalOpen(false);
        setSelectedConfigs([]);
        setSaveSearch('');
        setSaveCountryFilter([]);
        setSaveCountryOpen(false);
    };

    // При открытии модала сохранения подтягиваем флаги стран конфигов
    // (мгновенно из кэша, затем — актуализируем батчем)
    useEffect(() => {
        if (!isSaveModalOpen) return;
        setConfigFlags(getCachedConfigFlags());
        loadConfigFlags().then(setConfigFlags);
    }, [isSaveModalOpen]);

    const handleExport = async () => {
        // Если есть текущий конфиг, экспортируем его с именем файла
        const filename = currentConfigFile || `site_settings_${new Date().toISOString().split('T')[0]}.json`;
        const { downloadConfigFile } = await import('../../utils/configUtils');
        downloadConfigFile(filename, localSettings);
        success('Конфиг экспортирован!', 2000);
    };
    
    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                console.log('Начинаем импорт конфига:', file.name);
                const { importConfigFromFile, getLocalConfigsList, saveLocalConfig } = await import('../../utils/configUtils');
                const { filename, data } = await importConfigFromFile(file);
                
                console.log('Конфиг прочитан, имя файла:', filename);
                
                // Проверяем, существует ли уже конфиг с таким именем
                const existingConfigs = await getLocalConfigsList();
                const configExists = existingConfigs.some(c => c.filename === filename);

                if (configExists) {
                    // Конфиг уже существует - спрашиваем подтверждение
                    const confirmOverwrite = await confirmDialog({
                        title: 'Конфиг уже существует',
                        message: `Конфиг «${filename}» уже есть на сервере. Заменить его загружаемым файлом? Старая версия будет потеряна.`,
                        confirmText: 'Перезаписать',
                        cancelText: 'Отмена',
                        confirmHint: 'Заменит конфиг на сервере загружаемым файлом',
                        cancelHint: 'Отменит импорт, конфиг на сервере останется прежним'
                    });
                    
                    if (!confirmOverwrite) {
                        window.dispatchEvent(new CustomEvent('app:toast', { 
                            detail: { type: 'info', message: 'Импорт отменен' } 
                        }));
                        event.target.value = '';
                        return;
                        }
                }
                
                // Сохраняем конфиг на сервер с именем из файла
                console.log('Сохраняем конфиг на сервер:', filename);
                await saveLocalConfig(filename, data);
                
                // Обновляем список конфигов
                await loadAvailableConfigs();
                
                if (configExists) {
                    window.dispatchEvent(new CustomEvent('app:toast', { 
                        detail: { type: 'success', message: `Конфиг "${filename}" перезаписан на сервере! ✅` } 
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('app:toast', { 
                        detail: { type: 'success', message: `Конфиг "${filename}" импортирован на сервер! ✅` } 
                    }));
                }
                
                // НЕ применяем настройки автоматически и НЕ меняем открытый конфиг
                // Пользователь может загрузить импортированный конфиг вручную, если захочет
                
                console.log('Импорт завершен успешно');
            } catch (err) {
                console.error('Ошибка при импорте:', err);
                window.dispatchEvent(new CustomEvent('app:toast', { 
                    detail: { type: 'error', message: 'Ошибка при импорте файла: ' + err.message } 
                }));
            }
        }
        event.target.value = '';
    };

    const handleReset = async () => {
        if (await confirmDialog({
            title: 'Сбросить настройки?',
            message: 'Все настройки сайта вернутся к значениям по умолчанию. Текущие изменения будут потеряны.',
            confirmText: 'Сбросить',
            cancelText: 'Отмена',
            confirmHint: 'Вернёт все настройки к дефолтным значениям',
            cancelHint: 'Закроет окно, настройки не изменятся'
        })) {
            // Очищаем localStorage чтобы применились новые дефолты
            localStorage.removeItem('siteSettings');
            setLocalSettings(defaultSettings);
            warning('Настройки сброшены к дефолтным! Обновите страницу для применения новых настроек банков.', 5000);
        }
    };

    // ===== ФУНКЦИИ ДЛЯ УПРАВЛЕНИЯ ПРЕСЕТАМИ ЧАТА =====
    
    // Функция для добавления нового пресета
    const addNewPreset = () => {
        const newPreset = {
            id: Date.now().toString(),
            name: 'Новый пресет',
            enabled: true,
            icon: 'support-chat-normal.svg',
            messages: [
                {
                    author: 'Support',
                    text: 'Добро пожаловать! Меня зовут <SUPPORT NAME>, и я здесь, чтобы помочь вам. Как я могу помочь вам сегодня?'
                }
            ]
        };
        
        setLocalSettings(prev => ({
            ...prev,
            chatPresets: [...(prev.chatPresets || []), newPreset]
        }));
        
        success('Новый пресет добавлен!', 2000);
    };

    // Функция для обновления названия пресета
    const updatePresetName = (presetId, newName) => {
        setLocalSettings(prev => ({
            ...prev,
            chatPresets: (prev.chatPresets || []).map(preset =>
                preset.id === presetId ? { ...preset, name: newName } : preset
            )
        }));
    };
    // Функция для включения/выключения пресета
    const togglePreset = (presetId, enabled) => {
        setLocalSettings(prev => ({
            ...prev,
            chatPresets: (prev.chatPresets || []).map(preset =>
                preset.id === presetId ? { ...preset, enabled } : preset
            )
        }));
    };

    // Функция для удаления пресета
    const deletePreset = async (presetId) => {
        if (await confirmDialog({
            title: 'Удалить пресет?',
            message: 'Пресет чата будет удалён навсегда, восстановить его не получится.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Безвозвратно удалит пресет',
            cancelHint: 'Закроет окно, пресет останется'
        })) {
            setLocalSettings(prev => ({
                ...prev,
                chatPresets: (prev.chatPresets || []).filter(preset => preset.id !== presetId)
            }));
            success('Пресет удален!', 2000);
        }
    };

    // Функция для открытия редактора пресета
    const openPresetEditor = (presetId) => {
        const preset = (localSettings.chatPresets || []).find(p => p.id === presetId);
        if (preset) {
            setEditingPreset({ ...preset });
            setIsPresetEditorOpen(true);
        }
    };

    // Функция для сохранения изменений пресета
    const savePresetChanges = () => {
        if (!editingPreset) return;
        
        setLocalSettings(prev => ({
            ...prev,
            chatPresets: (prev.chatPresets || []).map(preset =>
                preset.id === editingPreset.id ? editingPreset : preset
            )
        }));
        
        setIsPresetEditorOpen(false);
        setEditingPreset(null);
        // Убрано уведомление о сохранении - сохранение происходит только при нажатии кнопки "Сохранить" в конфиги
    };

    // Функция для добавления нового сообщения в пресет
    const addMessageToPreset = () => {
        if (!editingPreset) return;
        
        const newMessage = {
            author: 'Support',
            text: 'Новое сообщение...'
        };
        
        setEditingPreset(prev => ({
            ...prev,
            messages: [...(prev.messages || []), newMessage]
        }));
    };

    // Функция для удаления сообщения из пресета
    const removeMessageFromPreset = (messageIndex) => {
        if (!editingPreset) return;
        
        setEditingPreset(prev => ({
            ...prev,
            messages: (prev.messages || []).filter((_, index) => index !== messageIndex)
        }));
    };

    // Функция для обновления сообщения в пресете
    const updatePresetMessage = (messageIndex, field, value) => {
        if (!editingPreset) return;
        
        setEditingPreset(prev => ({
            ...prev,
            messages: (prev.messages || []).map((message, index) =>
                index === messageIndex ? { ...message, [field]: value } : message
            )
        }));
    };

    // Функция для получения динамических описаний переменных
    const getVariableDescription = (variable) => {
        const currentTariff = Object.keys(localSettings.tariffBaseAmounts || {})[0] || '💰 1 ТАРИФ';
        const currency = localSettings.currencyFormat || 'RM';
        const baseAmount = localSettings.tariffBaseAmounts?.[currentTariff] || 7103.84;
        const additionalAmount = localSettings.tariffAdditionalAmounts?.[currentTariff] || 3240.56;
        const verifAmount = localSettings.tariffVerificationAmounts?.[currentTariff] || 492;
        const taxPercent = localSettings.tariffTaxPercents?.[currentTariff] || 4.4;
        const cryptoConvertAmount = localSettings.tariffConversionCommissions?.[currentTariff] || 459;
        const bankConvertAmount = localSettings.tariffBankFees?.[currentTariff] || 385;
        const commissionAmount = localSettings.tariffCommissions?.[currentTariff] || 549;
        const brokerFeeAmount = localSettings.tariffBrokerFees?.[currentTariff] || 250;
        const winningPercent = localSettings.tariffWinningPercentages?.[currentTariff] || 15;
        const userName = localSettings.defaultUserName || 'Brandon Lucas Lee';
        
        const formatAmount = (amount) => {
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(amount);
        };

        const descriptions = {
            '<SUPPORT NAME>': {
                title: 'Имя агента поддержки',
                desc: 'Генерируется автоматически из файла имен при каждом новом чате',
                example: 'Пример: "Ahmad Rahman", "Sarah Lim"'
            },
            '<USER NAME>': {
                title: 'Имя пользователя',
                desc: 'Берется из админки',
                example: `Текущее: "${userName}"`
            },
            '<MONEY>': {
                title: 'Базовая сумма тарифа',
                desc: 'Только основная сумма депозита без дополнительных начислений',
                example: `Текущая: ${formatAmount(baseAmount)} ${currency}`
            },
            '<MONEY AND ADDITIONAL AMOUNT>': {
                title: 'Базовая + дополнительная сумма',
                desc: 'Основная сумма плюс "прибыль от торговли софта"',
                example: `${formatAmount(baseAmount)} ${currency} + ${formatAmount(additionalAmount)} ${currency} = ${formatAmount(baseAmount + additionalAmount)} ${currency}`
            },
            '<MONEY+ADD AMOUNT+VERIF MONEYBACK>': {
                title: 'Полная сумма с возвратом верификации',
                desc: 'Базовая + дополнительная + сумма верификации которая вернется',
                example: `${formatAmount(baseAmount + additionalAmount + verifAmount)} ${currency}`
            },
            '<BANK>': {
                title: 'Название банка',
                desc: 'Отображает текущий выбранный банк из настроек',
                example: 'Пример: "Maybank", "CIMB Bank", "Public Bank"'
            },
            '<VERIF SUM>': {
                title: 'Сумма верификации',
                desc: 'Сумма которую нужно заплатить для верификации аккаунта',
                example: `Текущая: ${formatAmount(verifAmount)} ${currency}`
            },
            '<YESTERDAY DATE>': {
                title: 'Вчерашняя дата',
                desc: 'Автоматически рассчитывается как текущая дата минус 1 день',
                example: `Пример: "${new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString()}"`
            },
            '<ACCOUNT NUMBER>': {
                title: 'Номер счета/IBAN/BIC/',
                desc: 'Отображает текущий счет пользователя',
                example: ''
            },
            '<TAX_PERCENT>': {
                title: 'Процент налога',
                desc: 'Процентная ставка налога для текущего тарифа',
                example: `Текущий: ${taxPercent}%`
            },
            '<TAX_AMOUNT>': {
                title: 'Сумма налога',
                desc: 'Рассчитанная сумма налога ((Выигрыш + Доп.сумма + Комиссия) × Процент налога)',
                example: `Пример: ${formatAmount((baseAmount + additionalAmount + commissionAmount) * (taxPercent / 100))} ${currency}`
            },
            '<TODAY DATE>': {
                title: 'Сегодняшняя дата',
                desc: 'Автоматически подставляется текущая дата',
                example: `Сегодня: "${new Date().toLocaleDateString()}"`
            },
            '<USD AMOUNT>': {
                title: 'Сумма в долларах США',
                desc: 'Конвертированная сумма по текущему курсу',
                example: 'Пример: "$2,450.00", "$8,750.00"'
            },
            '<CRYPTO_CONVERT_AMOUNT>': {
                title: 'Комиссия за конвертацию криптовалюты',
                desc: 'Берется из настроек "Комиссия за конвертацию"',
                example: `Текущая: ${formatAmount(cryptoConvertAmount)} ${currency}`
            },
            '<BANK_CONVERT_AMOUNT>': {
                title: 'Банковская комиссия',
                desc: 'Берется из настроек "Банковская комиссия"',
                example: `Текущая: ${formatAmount(bankConvertAmount)} ${currency}`
            }
        };

        const info = descriptions[variable];
        if (!info) return variable;

        return `${info.title}\n\n${info.desc}\n\n${info.example}`;
    };
    // Функции для управления Portal тултипом
    const showPortalTooltip = (event, content) => {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        
        setPortalTooltip({
            content: content,
            x: rect.left + rect.width / 2,
            y: rect.top - 12, // Увеличиваем отступ для стрелочки
            visible: true
        });
    };

    const hidePortalTooltip = () => {
        setPortalTooltip(null);
    };

    // Функция для вставки переменной в активное поле
    const insertVariableAtCursor = (variable) => {
        console.log('Вставляем переменную:', variable);
        console.log('Активное поле:', activeTextarea);
        
        if (!activeTextarea) {
            console.log('Нет активного поля! Попробуем найти последнее сфокусированное поле...');
            // Попробуем найти любое поле textarea в модале (классика или Nova-редактор)
            const textareas = document.querySelectorAll('[class*="presetEditorModal"] textarea, [class*="cpeList"] textarea');
            if (textareas.length > 0) {
                const lastTextarea = textareas[textareas.length - 1];
                console.log('Используем последнее поле:', lastTextarea);
                setActiveTextarea(lastTextarea);
                // Повторяем вызов с найденным полем
                setTimeout(() => insertVariableAtCursor(variable), 10);
                return;
            } else {
                console.log('Не найдено ни одного поля textarea');
                return;
            }
        }
        
        const textarea = activeTextarea;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentValue = textarea.value || '';
        
        console.log('Текущее значение:', currentValue);
        console.log('Позиция курсора:', start, '-', end);
        
        // Вставляем переменную в позицию курсора
        const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
        console.log('Новое значение:', newValue);
        
        // Обновляем значение в состоянии
        const messageIndex = parseInt(textarea.dataset.messageIndex);
        console.log('Индекс сообщения:', messageIndex);
        
        if (!isNaN(messageIndex)) {
            updatePresetMessage(messageIndex, 'text', newValue);
            
            // Устанавливаем курсор после вставленной переменной
            setTimeout(() => {
                const newCursorPos = start + variable.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                textarea.focus();
                console.log('Курсор установлен на позицию:', newCursorPos);
            }, 100);
        } else {
            console.error('Неверный индекс сообщения:', messageIndex);
        }
    };

    // Функции для drag & drop пресетов
    const handlePresetDragStart = (e, index) => {
        setDraggedPresetIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handlePresetDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setPresetDragOverIndex(index);
    };

    const handlePresetDragLeave = () => {
        setPresetDragOverIndex(null);
    };

    const handlePresetDrop = (e, dropIndex) => {
        e.preventDefault();
        
        if (draggedPresetIndex === null || draggedPresetIndex === dropIndex) {
            setDraggedPresetIndex(null);
            setPresetDragOverIndex(null);
            return;
        }

        // Перемещаем пресет
        const presets = [...(localSettings.chatPresets || [])];
        const draggedPreset = presets[draggedPresetIndex];
        
        // Удаляем из старой позиции
        presets.splice(draggedPresetIndex, 1);
        
        // Вставляем в новую позицию
        const newIndex = draggedPresetIndex < dropIndex ? dropIndex - 1 : dropIndex;
        presets.splice(newIndex, 0, draggedPreset);

        // Обновляем состояние
        setLocalSettings(prev => ({
            ...prev,
            chatPresets: presets
        }));

        setDraggedPresetIndex(null);
        setPresetDragOverIndex(null);
    };

    const handlePresetDragEnd = () => {
        setDraggedPresetIndex(null);
        setPresetDragOverIndex(null);
    };

    // Функции для обработки drag'а в модальном окне
    const handleModalMouseDown = (e) => {
        setModalDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleModalClick = (e) => {
        if (!modalDragStart) {
            setIsPresetEditorOpen(false);
            return;
        }

        // Проверяем, был ли это drag или обычный клик
        const dragDistance = Math.sqrt(
            Math.pow(e.clientX - modalDragStart.x, 2) + 
            Math.pow(e.clientY - modalDragStart.y, 2)
        );

        // Если расстояние меньше 5 пикселей, считаем это кликом
        if (dragDistance < 5) {
            setIsPresetEditorOpen(false);
        }

        setModalDragStart(null);
    };


    // Специальная функция для обработки изменения приоритета банка
    const handleBankPriorityChange = (bankName, newPriority) => {
        // Проверяем валидность приоритета
        if (!newPriority || newPriority < 1 || isNaN(newPriority)) {
            return;
        }
        
        setLocalSettings(prev => {
            const banks = { ...prev.banks };
            const currentPriority = banks[bankName]?.priority;
            
            // Если приоритет не изменился, ничего не делаем
            if (currentPriority === newPriority) {
                return prev;
            }
            
            // Ищем банк с таким приоритетом
            const conflictingBank = Object.keys(banks).find(name => 
                name !== bankName && banks[name]?.priority === newPriority
            );
            
            // Если есть конфликт приоритетов - меняем местами
            if (conflictingBank) {
                banks[conflictingBank] = {
                    ...banks[conflictingBank],
                    priority: currentPriority
                };
            }
            
            // Обновляем приоритет текущего банка
            banks[bankName] = {
                ...(banks[bankName] || {}),
                priority: newPriority
            };
            
            const newSettings = {
                ...prev,
                banks
            };
            
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            return newSettings;
        });
        
        // Моментально обновляем отображение без задержек
        setForceUpdate(prev => prev + 1);
    };
    // Функция для добавления нового банка
    const handleAddBank = () => {
        // Создаем временное имя для нового банка
        let tempBankName = 'Новый банк';
        let counter = 1;
        
        // Убеждаемся что имя уникально
        while (localSettings.banks && localSettings.banks[tempBankName]) {
            tempBankName = `Новый банк ${counter}`;
            counter++;
        }
        
        setLocalSettings(prev => {
            // Сдвигаем приоритеты всех существующих банков на +1
            const updatedBanks = { ...prev.banks };
            Object.keys(updatedBanks).forEach(bankName => {
                updatedBanks[bankName] = {
                    ...updatedBanks[bankName],
                    priority: (updatedBanks[bankName]?.priority || 0) + 1
                };
            });
            
            // Добавляем новый банк с приоритетом 1 (в начало)
            updatedBanks[tempBankName] = {
                enabled: true,  // ✅ Новые банки включены по умолчанию
                priority: 1,
                iconFileName: 'default-bank.png'
            };
            
            const newSettings = {
                ...prev,
                banks: updatedBanks
            };
            
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            return newSettings;
        });
        
        // Отмечаем этот банк как новый
        setNewlyAddedBank(tempBankName);
        
        // Автоматически начинаем редактирование названия и фокусируемся на поле
        setTimeout(() => {
            handleStartEditingBankName(tempBankName);
            // Фокусируемся на input поле (первый элемент - новый банк)
            const bankCards = document.querySelectorAll(`[class*="bankNameInput"]`);
            const firstInput = bankCards[0];
            if (firstInput) {
                firstInput.focus();
                firstInput.select(); // Выделяем весь текст для удобства редактирования
            }
        }, 200);
        
        // Убираем подсветку через 3 секунды
        setTimeout(() => {
            setNewlyAddedBank(null);
        }, 3000);
        
        // Принудительно обновляем для корректного отображения
        setForceUpdate(prev => prev + 1);
    };

    // Функция для переключения сворачивания секций переводов
    const toggleTranslationSection = (sectionName) => {
        setCollapsedTranslationSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };

    // Функция для добавления нового языка
    const handleAddLanguage = (language) => {
        const languageCode = language.code;
        
        setLocalSettings(prev => {
            // Создаем новую структуру переводов с пустыми значениями для нового языка
            const newTranslations = { ...prev.translations };
            
            // Инициализируем новый язык пустой структурой, копируя ключи из русского языка
            if (!newTranslations[languageCode]) {
                newTranslations[languageCode] = {};
                
                // Копируем структуру из русского языка, но с пустыми значениями
                const copyStructureWithEmptyValues = (obj) => {
                    const result = {};
                    for (const key in obj) {
                        if (typeof obj[key] === 'object' && obj[key] !== null) {
                            result[key] = copyStructureWithEmptyValues(obj[key]);
                        } else {
                            result[key] = ''; // Пустое значение для перевода
                        }
                    }
                    return result;
                };
                
                if (newTranslations.ru) {
                    newTranslations[languageCode] = copyStructureWithEmptyValues(newTranslations.ru);
                }
            }
            
            const newSettings = {
                ...prev,
                translations: newTranslations
            };
            
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            return newSettings;
        });
        
        // Убрано уведомление о сохранении - сохранение происходит только при нажатии кнопки "Сохранить"
    };

    // Функция для удаления языка
    const handleDeleteLanguage = async (languageCode) => {
        const availableLanguages = Object.keys(localSettings.translations || {});
        
        // Проверяем что это не единственный язык и не русский (базовый)
        if (availableLanguages.length <= 1) {
            error('Нельзя удалить единственный язык!', 3000);
            return;
        }
        
        if (languageCode === 'ru') {
            error('Нельзя удалить русский язык - он базовый!', 3000);
            return;
        }
        
        const language = getLanguageByCode(languageCode);
        const languageName = language ? `${language.name} (${language.nativeName})` : languageCode.toUpperCase();
        
        if (await confirmDialog({
            title: 'Удалить язык?',
            message: `Все переводы на «${languageName}» будут безвозвратно удалены. Если этот язык сейчас активен, сайт переключится на русский.`,
            confirmText: 'Удалить язык',
            cancelText: 'Оставить',
            confirmHint: 'Удалит язык и все его переводы',
            cancelHint: 'Закроет окно, переводы останутся'
        })) {
            setLocalSettings(prev => {
                // Удаляем язык из переводов
                const newTranslations = { ...prev.translations };
                delete newTranslations[languageCode];
                
                // Если удаляется текущий активный язык, переключаемся на русский
                const newCurrentLanguage = prev.currentLanguage === languageCode ? 'ru' : prev.currentLanguage;
                
                const newSettings = {
                    ...prev,
                    currentLanguage: newCurrentLanguage,
                    translations: newTranslations
                };
                
                console.log('🗑️ Удаляем язык:', languageCode);
                console.log('🔄 Новые переводы:', Object.keys(newTranslations));
                console.log('🌐 Новый текущий язык:', newCurrentLanguage);
                
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
            
            // Принудительно обновляем состояние для гарантированного перерендера
            setTimeout(() => {
                setLocalSettings(prev => ({ ...prev }));
            }, 100);
            
            // Убрано уведомление о сохранении - сохранение происходит только при нажатии кнопки "Сохранить" в конфиги
        }
    };
    // Функция для рендеринга полей переводов для всех языков
    // Nova: иконка секции переводов по её ключу
    const TRANS_SECTION_GLYPHS = {
        header: LayoutDashboard,
        tradingDashboard: TrendingUp,
        navbar: Menu,
        dashboard: Gauge,
        adminPanel: SlidersHorizontal,
        buttons: Zap,
        siteSettings: SlidersHorizontal,
        wallet: WalletGlyph,
        brokerProgram: Briefcase,
        settings: UserRound,
        mailbox: Mail,
        commissionsModal: CircleDollarSign,
        verificationModal: ShieldCheck,
        premiumModal: Crown,
        successModal: BadgeCheck,
        taxModal: Percent,
        codeModal: Hash,
        transactionErrorModal: TriangleAlert,
        bankTransferModal: Landmark,
        chat: MessagesSquare,
    };
    const transSectionGlyph = (name) => {
        const G = TRANS_SECTION_GLYPHS[name] || FileText;
        return <span className={s.transSectionGlyph}><G size={14} strokeWidth={2} aria-hidden="true" /></span>;
    };

    // Человекочитаемый заголовок ключа: последний сегмент, camelCase → слова
    const humanizeTransKey = (key) => {
        const last = String(key).split('.').pop() || key;
        const spaced = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    const renderTranslationFields = (translationKey, title = '', description = '', placeholders = {}) => {
        // Регистрируем ключ в общем наборе (для подсчёта непереведённых).
        // Делаем это до любых ранних return, чтобы набор был полным
        if (translationKey && transKeysRef.current && !transKeysRef.current.has(translationKey)) {
            transKeysRef.current.add(translationKey);
        }
        // Если title является объектом, значит это placeholders (старый формат вызова)
        if (typeof title === 'object' && title !== null) {
            placeholders = title;
            title = '';
            description = description || '';
        }
        // Если description является объектом, значит это placeholders
        if (typeof description === 'object' && description !== null) {
            placeholders = description;
            description = '';
        }
        // Фильтруем только существующие языки с непустыми переводами
        const availableLanguages = Object.keys(localSettings.translations || {}).filter(langCode => 
            localSettings.translations[langCode] && 
            typeof localSettings.translations[langCode] === 'object'
        );

        const getValue = (langCode) => translationKey.split('.').reduce(
            (obj, key) => (obj && obj[key] ? obj[key] : ''),
            localSettings.translations?.[langCode]
        ) || '';

        // ── Nova-вариант: карточка ключа с фокусом на языке и живым поиском
        if (isNova) {
            const q = transSearch.trim().toLowerCase();
            if (q) {
                const inKey = translationKey.toLowerCase().includes(q);
                const inTitle = String(title || '').toLowerCase().includes(q);
                const inVals = availableLanguages.some((lc) => getValue(lc).toLowerCase().includes(q));
                if (!inKey && !inTitle && !inVals) return null;
            }
            // Режим «только непереведённые»: прячем ключ, если на текущем языке
            // перевод уже есть (непустая строка). Плейсхолдер переводом не считаем
            if (showUntranslatedOnly) {
                const targetLang = localSettings.currentLanguage || 'ru';
                if (getValue(targetLang).trim()) return null;
            }
            let shownLangs = transFocusLangs.length
                ? availableLanguages.filter((lc) => transFocusLangs.includes(lc))
                : availableLanguages;
            // В режиме «непереведённые» гарантируем видимость поля текущего языка,
            // даже если фокус выставлен на другие языки — иначе нечего заполнять
            if (showUntranslatedOnly) {
                const targetLang = localSettings.currentLanguage || 'ru';
                if (availableLanguages.includes(targetLang) && !shownLangs.includes(targetLang)) {
                    shownLangs = [targetLang, ...shownLangs];
                }
            }
            const autoGrow = (el) => {
                if (!el) return;
                el.style.height = 'auto';
                el.style.height = Math.max(40, el.scrollHeight) + 'px';
            };
            return (
                <div className={s.transCard}>
                    <div className={s.transCardHead}>
                        <span className={s.transCardTitle}>{title || humanizeTransKey(translationKey)}</span>
                        <span className={s.transCardKey}>{translationKey}</span>
                    </div>
                    {description && <div className={s.transCardDesc}>{description}</div>}
                    <div className={s.transLangGrid}>
                        {shownLangs.map((langCode) => {
                            const language = getLanguageByCode(langCode);
                            const placeholder = placeholders[langCode] || '';
                            const displayValue = getValue(langCode) || placeholder;
                            return (
                                <div key={langCode} className={s.transLangItem}>
                                    <span className={s.transLangTag}>
                                        {langFlag(langCode, s.transLangFlag)}
                                        {langCode.toUpperCase()}
                                    </span>
                                    <textarea
                                        className={s.transTextarea}
                                        value={displayValue}
                                        placeholder={placeholder}
                                        rows={1}
                                        spellCheck="false"
                                        ref={autoGrow}
                                        onChange={(e) => {
                                            handleInputChange('translations', `${langCode}.${translationKey}`, e.target.value);
                                            autoGrow(e.target);
                                        }}
                                        onInput={(e) => autoGrow(e.target)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return (
            <div className={s.field}>
                <div className={s.fieldInfo}>
                    <div className={s.fieldLabel}>
                        <strong>{translationKey}</strong>
                    </div>
                    <div className={s.fieldDescription}>
                        {description}
                    </div>
                </div>
                <div className={s.fieldInput}>
                    <div className={s.translationInputs}>
                        {availableLanguages.map(langCode => {
                            const language = getLanguageByCode(langCode);
                            const langLabel = language ? `${language.flag} ${langCode.toUpperCase()}` : `🌍 ${langCode.toUpperCase()}`;
                            const placeholder = placeholders[langCode] || '';
                            
                            // Получаем значение из текущей структуры переводов
                            const currentValue = translationKey.split('.').reduce((obj, key) => {
                                return obj && obj[key] ? obj[key] : '';
                            }, localSettings.translations?.[langCode]) || '';
                            
                            // Если значение пустое и есть плейсхолдер, используем плейсхолдер как значение
                            const displayValue = currentValue || placeholder;
                            
                            return (
                                <div key={langCode} className={s.translationRow}>
                                    <span className={s.langLabel}>{langLabel}:</span>
                                    <textarea
                                        className={s.input}
                                        value={displayValue}
                                        onChange={(e) => {
                                            handleInputChange('translations', `${langCode}.${translationKey}`, e.target.value);
                                            // Обновляем высоту при изменении
                                            setTimeout(() => {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = Math.max(38, e.target.scrollHeight) + 'px';
                                            }, 0);
                                        }}
                                        placeholder={placeholder}
                                        rows={1}
                                        style={{
                                            minHeight: '38px',
                                            resize: 'vertical',
                                            overflow: 'hidden',
                                            height: 'auto'
                                        }}
                                        ref={(textarea) => {
                                            if (textarea) {
                                                // Устанавливаем правильную высоту при монтировании
                                                setTimeout(() => {
                                                    textarea.style.height = 'auto';
                                                    textarea.style.height = Math.max(38, textarea.scrollHeight) + 'px';
                                                }, 0);
                                            }
                                        }}
                                        onInput={(e) => {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.max(38, e.target.scrollHeight) + 'px';
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };
    // Функция для получения дефолтных сообщений чата
    const getDefaultChatMessage = (presetType, messageNum) => {
        const defaultMessages = {
            verification: {
                1: { ru: 'Здравствуйте! Меня зовут <SUPPORT NAME>, и я здесь, чтобы помочь вам. Чем могу помочь сегодня?', en: 'Hello! My name is <SUPPORT NAME>, and I\'m here to assist you. How may I help you today?', ms: 'Hello! Nama saya <SUPPORT NAME>, dan saya di sini untuk membantu anda. Bagaimana saya boleh membantu anda hari ini?' },
                2: { ru: 'Здравствуйте. Мне нужно узнать о моем клиенте <CLIENT NAME>. Мы пытаемся обработать перевод <MONEY> на их счет <CBU/CVU> в <BANK>. Запрошен верификационный платеж. Не могли бы вы пояснить обоснование этого требования?', en: 'Hello. I need to inquire about my client <CLIENT NAME>. We are attempting to process a transfer of <MONEY> to their account <CBU/CVU> at <BANK>. A verification payment has been requested. Could you please clarify the rationale behind this requirement?', ms: 'Hello. Saya perlu bertanya tentang klien saya <CLIENT NAME>. Kami cuba memproses pemindahan <MONEY> ke akaun mereka <CBU/CVU> di <BANK>. Pembayaran pengesahan telah diminta. Bolehkah anda menjelaskan rasional di sebalik keperluan ini?' },
                3: { ru: 'Приношу извинения за неудобства. Позвольте мне посмотреть на это для вас и проверить детали вашего запроса.', en: 'I apologize for any inconvenience. Let me take a look at this for you and verify the details of your inquiry.', ms: 'Saya memohon maaf atas sebarang kesulitan. Biarkan saya melihat ini untuk anda dan mengesahkan butiran pertanyaan anda.' },
                4: { ru: 'Я рассмотрел запрос на вывод, поданный под именем <CLIENT NAME> на сумму <MONEY>. Требование верификационного платежа было введено как защитная мера для защиты активов вашего клиента. После успешной верификации и подтверждения того, что информация получателя соответствует данным отправителя, мы продолжим перевод средств на указанный адрес. Эта процедура является неотъемлемой частью нашей комплексной системы безопасности, разработанной для предотвращения несанкционированных транзакций и мошеннической деятельности посредниками, которые могут попытаться перенаправить средства клиентов на несанкционированные счета. Мы ценим ваше понимание и сотрудничество с этими необходимыми протоколами безопасности.\\n\\nОбратите внимание, что по завершении процесса верификации сумма <VERIF SUM> будет автоматически возмещена вместе с выводом ваших средств.', en: 'I have reviewed the withdrawal request submitted under the name of <CLIENT NAME> for the amount of <MONEY>. The verification payment requirement has been implemented as a protective measure to safeguard your client\'s assets. Upon successful verification and confirmation that the recipient\'s information corresponds with the sender\'s details, we shall proceed with the transfer of funds to the designated address. This procedure constitutes an integral component of our comprehensive security framework, designed to prevent unauthorized transactions and fraudulent activities by intermediaries who may attempt to divert client funds to unauthorized accounts. We appreciate your understanding and cooperation with these necessary security protocols.\\n\\nPlease note that upon completion of the verification process, the amount of <VERIF SUM> will be automatically reimbursed in conjunction with the withdrawal of your funds.', ms: 'Saya telah menyemak permintaan pengeluaran yang dikemukakan atas nama <CLIENT NAME> untuk jumlah <MONEY>. Keperluan pembayaran pengesahan telah dilaksanakan sebagai langkah perlindungan untuk melindungi aset klien anda. Setelah pengesahan berjaya dan pengesahan bahawa maklumat penerima sesuai dengan butiran pengirim, kami akan meneruskan pemindahan dana ke alamat yang ditetapkan. Prosedur ini merupakan komponen penting dalam rangka kerja keselamatan komprehensif kami, yang direka untuk mencegah transaksi tanpa kebenaran dan aktiviti penipuan oleh perantara yang mungkin cuba mengalihkan dana klien ke akaun tanpa kebenaran. Kami menghargai pemahaman dan kerjasama anda dengan protokol keselamatan yang diperlukan ini.\\n\\nSila ambil perhatian bahawa setelah selesai proses pengesahan, jumlah <VERIF SUM> akan dikembalikan secara automatik bersama dengan pengeluaran dana anda.' },
                5: { ru: 'Спасибо за подробное объяснение. Я передам эту информацию своему клиенту.', en: 'Thank you for the detailed explanation. I\'ll pass this information along to my client.', ms: 'Terima kasih atas penjelasan terperinci. Saya akan menyampaikan maklumat ini kepada klien saya.' },
                6: { ru: 'Пожалуйста! Если вам понадобится дополнительная помощь, не стесняйтесь обращаться в любое время. Хорошего дня!', en: 'You\'re welcome! If you need any further assistance, feel free to reach out anytime. Have a great day!', ms: 'Sama-sama! Jika anda memerlukan bantuan lanjut, jangan ragu untuk menghubungi kami pada bila-bila masa. Semoga hari anda menyenangkan!' }
            },
            commission: {
                1: { ru: 'Привет! Я <SUPPORT NAME> из нашего отдела обслуживания клиентов. Чем могу помочь сегодня?', en: 'Hi there! I\'m <SUPPORT NAME> from our customer service department. What can I help you with today?', ms: 'Hai! Saya <SUPPORT NAME> dari jabatan perkhidmatan pelanggan kami. Apa yang boleh saya bantu hari ini?' },
                2: { ru: 'Привет. Мой клиент <CLIENT NAME> недавно завершил процесс верификационного платежа. Однако мы все еще не можем продолжить вывод прибыли. Не могли бы вы объяснить, что препятствует этой транзакции?', en: 'Hi. My client <CLIENT NAME> has recently completed the verification payment process. However, we are still unable to proceed with the withdrawal of earnings. Could you please explain what\'s preventing this transaction?', ms: 'Hai. Klien saya <CLIENT NAME> baru-baru ini telah menyelesaikan proses pembayaran pengesahan. Walau bagaimanapun, kami masih tidak dapat meneruskan pengeluaran pendapatan. Bolehkah anda menjelaskan apa yang menghalang transaksi ini?' },
                3: { ru: 'Прошу прощения за неудобства. Мы внедрили новую политику вывода с <YESTERDAY DATE>. Все выводы теперь имеют стандартную комиссию за операции между японскими и американскими биржами. Полная информация была отправлена через ваши каналы уведомлений.', en: 'I apologize for the inconvenience. We\'ve implemented a new withdrawal policy as of <YESTERDAY DATE>. All withdrawals now have a standard processing fee for operations between Japanese and US exchanges. The full details were sent through your notification channels.', ms: 'Saya memohon maaf atas kesulitan. Kami telah melaksanakan dasar pengeluaran baru mulai <YESTERDAY DATE>. Semua pengeluaran kini mempunyai yuran pemprosesan standard untuk operasi antara pertukaran Jepun dan AS. Butiran penuh telah dihantar melalui saluran pemberitahuan anda.' },
                4: { ru: 'Я ценю разъяснение. Однако я хотел бы предложить заранее уведомлять брокеров о таких изменениях политики. Это ставит меня в трудное положение с моими клиентами, когда я не информирован заранее.', en: 'I appreciate the clarification. However, I\'d like to suggest giving brokers advance notice about policy changes like this. It puts me in a difficult position with my clients when I\'m not informed beforehand.', ms: 'Saya menghargai penjelasan. Walau bagaimanapun, saya ingin mencadangkan untuk memberikan notis awal kepada broker tentang perubahan dasar seperti ini. Ia meletakkan saya dalam kedudukan yang sukar dengan klien saya apabila saya tidak dimaklumkan terlebih dahulu.' },
                5: { ru: 'Я понимаю ваши опасения и приношу извинения за неудобства. После оплаты комиссии <WITHDRAWAL AMOUNT> будет переведена на счет <ACCOUNT NUMBER> в <BANK> под именем <CLIENT NAME>. Спасибо за понимание.', en: 'I understand your concerns and apologize for the inconvenience. Once the commission fee is paid, <WITHDRAWAL AMOUNT> will be transferred to account <ACCOUNT NUMBER> at <BANK> under <CLIENT NAME>\'s name. Thank you for your understanding.', ms: 'Saya memahami kebimbangan anda dan memohon maaf atas kesulitan. Setelah yuran komisen dibayar, <WITHDRAWAL AMOUNT> akan dipindahkan ke akaun <ACCOUNT NUMBER> di <BANK> atas nama <CLIENT NAME>. Terima kasih atas pemahaman anda.' },
                6: { ru: 'Есть ли что-то еще, с чем я могу помочь вам сегодня?', en: 'Is there anything else I can help you with today?', ms: 'Adakah ada perkara lain yang boleh saya bantu anda hari ini?' }
            },
            tax: {
                1: { ru: 'Здравствуйте! Я <SUPPORT NAME>, старший специалист по соответствию. Как я могу помочь вам сегодня?', en: 'Hello! I\'m <SUPPORT NAME>, a senior compliance specialist. How can I assist you today?', ms: 'Hello! Saya <SUPPORT NAME>, pakar pematuhan kanan. Bagaimana saya boleh membantu anda hari ini?' },
                2: { ru: 'Здравствуйте. Мне нужно разъяснение относительно налогового начисления IRS, наложенного на моего клиента. Клиент, о котором идет речь, это <CLIENT NAME>, и мы пытаемся обработать перевод <WITHDRAWAL AMOUNT> на их счет <CBU/CVU> в <BANK>. Не могли бы вы предоставить подробное объяснение нормативной базы для этого налогового требования?', en: 'Hello. I need clarification regarding the IRS tax assessment imposed on my client. The client in question is <CLIENT NAME>, and we are attempting to process a transfer of <WITHDRAWAL AMOUNT> to their account <CBU/CVU> at <BANK>. Could you please provide a detailed explanation of the regulatory basis for this tax requirement?', ms: 'Hello. Saya memerlukan penjelasan mengenai taksiran cukai IRS yang dikenakan ke atas klien saya. Klien yang dimaksudkan ialah <CLIENT NAME>, dan kami cuba memproses pemindahan <WITHDRAWAL AMOUNT> ke akaun mereka <CBU/CVU> di <BANK>. Bolehkah anda memberikan penjelasan terperinci tentang asas peraturan untuk keperluan cukai ini?' },
                3: { ru: 'Конечно. Позвольте мне найти счет <CLIENT NAME> и просмотреть историю транзакций.', en: 'Absolutely. Let me pull up <CLIENT NAME>\'s account and review the transaction history.', ms: 'Sudah tentu. Biarkan saya mencari akaun <CLIENT NAME> dan menyemak sejarah transaksi.' },
                4: { ru: 'После рассмотрения я могу подтвердить, что запрос на перевод <WITHDRAWAL AMOUNT> в настоящее время приостановлен до выполнения обязательных налоговых обязательств.\n\nОбратите внимание, что с начала этого финансового года наша платформа обязана строго соблюдать усиленные международные стандарты налогообложения, особенно для трансграничных финансовых транзакций.\n\nВ этом конкретном случае ваш клиент, будучи резидентом Аргентины, получил прибыль через нашу платформу, работающую в Японии. Учитывая, что части этих транзакций обрабатываются через регулятивные рамки Соединенных Штатов—особенно при участии цифровых активов USDT и BNB—транзакция подпадает под юрисдикцию протоколов налогообложения Службы внутренних доходов (IRS).\n\nХотя вы служите авторизованным брокером и способствовали этим транзакциям, юридическая ответственность за налоговые обязательства лежит на конечном бенефициаре, а именно <CLIENT NAME>. Этот принцип установлен международными правилами финансового соответствия, которые предписывают, что налоговые обязанности принадлежат лицу, получающему денежную выгоду.\n\nПожалуйста, поймите, что без выполнения этих налоговых обязательств транзакция остается объектом автоматической блокировки, наложенной международными регулятивными органами. Это не дискреционная мера нашей платформы, а обязательный компонент глобальной рамки борьбы с отмыванием денег (AML), который нельзя обойти.\n\nДля вашего клиента <CLIENT NAME> применимая налоговая ставка составляет <TAX_PERCENT> от общей суммы вывода <WITHDRAWAL AMOUNT>, что приводит к требуемому налоговому платежу <TAX_AMOUNT>.\n\nПосле подтверждения завершения налогового платежа средства будут освобождены, и перевод будет обработан в течение одного часа.', en: 'Upon review, I can confirm that the transfer request for <WITHDRAWAL AMOUNT> is currently suspended pending completion of mandatory tax obligations.\n\nPlease be advised that effective from the beginning of this fiscal year, our platform is required to maintain strict compliance with enhanced international taxation standards, particularly for cross-border financial transactions.\n\nIn this specific case, your client, being a resident of Argentina, has generated profits through our Japanese-operated platform. Given that portions of these transactions are processed through United States regulatory frameworks—particularly when involving USDT and BNB digital assets—the transaction falls under the jurisdiction of the Internal Revenue Service (IRS) taxation protocols.\n\nWhile you serve as the authorized broker and have facilitated these transactions, the legal liability for tax obligations rests with the ultimate beneficiary, namely <CLIENT NAME>. This principle is established under international financial compliance regulations, which mandate that tax responsibilities belong to the individual who receives the monetary benefit.\n\nPlease understand that without fulfillment of these tax obligations, the transaction remains subject to an automatic hold imposed by international regulatory authorities. This is not a discretionary measure by our platform, but rather a mandatory component of the global anti-money laundering (AML) framework that cannot be circumvented.\n\nFor your client <CLIENT NAME>, the applicable tax rate is <TAX_PERCENT> of the total withdrawal amount of <WITHDRAWAL AMOUNT>, resulting in a required tax payment of <TAX_AMOUNT>.\n\nUpon verification of tax payment completion, the funds will be released and the transfer will be processed within one hour.', ms: 'Setelah semakan, saya boleh mengesahkan bahawa permintaan pemindahan untuk <WITHDRAWAL AMOUNT> kini digantung sementara menunggu penyelesaian kewajipan cukai mandatori.\n\nSila ambil maklum bahawa berkuat kuasa dari awal tahun fiskal ini, platform kami dikehendaki mengekalkan pematuhan ketat dengan standard cukai antarabangsa yang dipertingkatkan, terutamanya untuk transaksi kewangan rentas sempadan.\n\nDalam kes khusus ini, klien anda, sebagai pemastautin Argentina, telah menjana keuntungan melalui platform kami yang dikendalikan Jepun. Memandangkan bahagian transaksi ini diproses melalui rangka kerja peraturan Amerika Syarikat—terutamanya apabila melibatkan aset digital USDT dan BNB—transaksi tersebut berada di bawah bidang kuasa protokol cukai Perkhidmatan Hasil Dalam Negeri (IRS).\n\nWalaupun anda berkhidmat sebagai broker yang dibenarkan dan telah memudahkan transaksi ini, liabiliti undang-undang untuk kewajipan cukai terletak pada benefisiari muktamad, iaitu <CLIENT NAME>. Prinsip ini ditetapkan di bawah peraturan pematuhan kewangan antarabangsa, yang mewajibkan tanggungjawab cukai kepunyaan individu yang menerima faedah monetari.\n\nSila faham bahawa tanpa pemenuhan kewajipan cukai ini, transaksi kekal tertakluk kepada penahanan automatik yang dikenakan oleh pihak berkuasa kawal selia antarabangsa. Ini bukan langkah budi bicara platform kami, tetapi komponen mandatori rangka kerja anti-pengubahan wang haram (AML) global yang tidak boleh dielakkan.\n\nUntuk klien anda <CLIENT NAME>, kadar cukai yang berkenaan ialah <TAX_PERCENT> daripada jumlah pengeluaran <WITHDRAWAL AMOUNT>, mengakibatkan pembayaran cukai yang diperlukan <TAX_AMOUNT>.\n\nSetelah pengesahan penyelesaian pembayaran cukai, dana akan dibebaskan dan pemindahan akan diproses dalam masa satu jam.' },
                5: { ru: 'Я должен спросить - почему этот налог не требовался раньше? Это второй мой клиент, которому пришлось платить это. Что изменилось в правилах?', en: 'I have to ask - why wasn\'t this tax required before? This is the second client of mine who\'s had to pay this. What changed with the regulations?', ms: 'Saya terpaksa bertanya - mengapa cukai ini tidak diperlukan sebelum ini? Ini adalah klien kedua saya yang terpaksa membayar ini. Apa yang berubah dengan peraturan?' },
                6: { ru: 'Ранее эти налоговые требования не применялись автоматически. Однако с января этого года было установлено комплексное многостороннее соглашение между Японией, Соединенными Штатами и несколькими латиноамериканскими странами, включая Аргентину, как часть координированной инициативы по предотвращению уклонения от налогов с криптовалют.\n\nВ рамках текущей регулятивной рамки, когда любая транзакция включает движение средств через эти юрисдикции через международные платформы, запускается автоматический механизм соответствия, который удерживает транзакцию до завершения налогового платежа. Это применяется конкретно к случаям, где прибыль превышает предопределенные пороги или включает трансграничные транзакции между этими участвующими странами.\n\nХотя предыдущие протоколы позволяли рассмотрение случай за случаем по усмотрению, текущие правила требуют строгого соответствия без исключений. Наша платформа была обязана внедрить комплексные системные модификации для обеспечения полного соблюдения этих усиленных регулятивных стандартов.', en: 'Previously, these taxation requirements were not automatically enforced. However, effective January of this year, a comprehensive multilateral agreement has been established between Japan, the United States, and several Latin American countries, including Argentina, as part of a coordinated initiative to prevent cryptocurrency tax evasion.\n\nUnder the current regulatory framework, when any transaction involves funds flowing through these jurisdictions via international platforms, an automatic compliance mechanism is triggered that withholds the transaction pending tax payment completion. This applies specifically to cases where profits exceed predetermined thresholds or involve cross-border transactions between these participating countries.\n\nWhile previous protocols allowed for case-by-case discretionary review, the current regulations mandate strict compliance without exception. Our platform has been required to implement comprehensive system modifications to ensure full adherence to these enhanced regulatory standards.', ms: 'Sebelum ini, keperluan cukai ini tidak dikuatkuasakan secara automatik. Walau bagaimanapun, berkuat kuasa Januari tahun ini, perjanjian pelbagai hala yang komprehensif telah diwujudkan antara Jepun, Amerika Syarikat, dan beberapa negara Amerika Latin, termasuk Argentina, sebagai sebahagian daripada inisiatif yang diselaraskan untuk mencegah pengelakan cukai mata wang kripto.\n\nDi bawah rangka kerja kawal selia semasa, apabila mana-mana transaksi melibatkan dana yang mengalir melalui bidang kuasa ini melalui platform antarabangsa, mekanisme pematuhan automatik dicetuskan yang menahan transaksi sementara menunggu penyelesaian pembayaran cukai. Ini terpakai khusus kepada kes di mana keuntungan melebihi ambang yang telah ditetapkan atau melibatkan transaksi rentas sempadan antara negara-negara yang mengambil bahagian ini.\n\nWalaupun protokol sebelumnya membenarkan semakan budi bicara kes demi kes, peraturan semasa mewajibkan pematuhan ketat tanpa pengecualian. Platform kami telah dikehendaki melaksanakan pengubahsuaian sistem yang komprehensif untuk memastikan pematuhan penuh kepada standard kawal selia yang dipertingkatkan ini.' },
                7: { ru: 'Могу ли я помочь вам с чем-то еще относительно этого?', en: 'Can I help you with anything else regarding this?', ms: 'Bolehkah saya membantu anda dengan apa-apa lagi mengenai perkara ini?' },
                8: { ru: 'Спасибо за подробное объяснение. Я обращусь, если у меня будут другие вопросы об этом.', en: 'Thanks for the thorough explanation. I\'ll reach out if I have any other questions about this.', ms: 'Terima kasih atas penjelasan yang menyeluruh. Saya akan menghubungi jika saya mempunyai soalan lain tentang perkara ini.' },
                9: { ru: 'Спасибо за обращение и спасибо за ваше постоянное партнерство. Хорошего продуктивного дня!', en: 'Thank you for reaching out, and thanks for your continued partnership. Have a productive day!', ms: 'Terima kasih kerana menghubungi, dan terima kasih atas kerjasama berterusan anda. Semoga hari anda produktif!' }
            },
            transfer: {
                1: { ru: 'Здравствуйте. Мой клиент <CLIENT NAME> недавно завершил процесс верификационного платежа и успешно подтвердил свои учетные данные. Однако при попытке обработать вывод перевод был заблокирован вашей системой. Не могли бы вы разъяснить конкретные причины этой приостановки транзакции?', en: 'Hello. My client <CLIENT NAME> has recently completed the verification payment process and successfully confirmed their account credentials. However, when attempting to process the withdrawal, the transfer has been blocked by your system. Could you please clarify the specific reasons for this transaction suspension?', ms: 'Hello. Klien saya <CLIENT NAME> baru-baru ini telah menyelesaikan proses pembayaran pengesahan dan berjaya mengesahkan kelayakan akaun mereka. Walau bagaimanapun, apabila cuba memproses pengeluaran, pemindahan telah disekat oleh sistem anda. Bolehkah anda menjelaskan sebab-sebab khusus untuk penggantungan transaksi ini?' },
                2: { ru: 'Спасибо за обращение в нашу команду соответствия. Я понимаю, что это нужно решить быстро. Дайте мне минуту, чтобы проверить счет вашего клиента и историю транзакций.', en: 'Thanks for contacting our compliance team. I understand this needs to be resolved quickly. Give me a moment to check your client\'s account and transaction history.', ms: 'Terima kasih kerana menghubungi pasukan pematuhan kami. Saya faham ini perlu diselesaikan dengan cepat. Beri saya seketika untuk memeriksa akaun klien anda dan sejarah transaksi.' },
                3: { ru: 'Спасибо за терпение. После всестороннего рассмотрения я могу подтвердить, что запрос на вывод составляет <WITHDRAWAL AMOUNT> в <BANK> от <TODAY DATE>. Перевод был временно приостановлен из-за дополнительных процедур верификации, инициированных в соответствии с требованиями Центрального банка Аргентинской Республики (BCRA) согласно Закону № 25.246 относительно предотвращения отмывания денег (AML). Согласно этим регулятивным положениям, финансовые учреждения и платежные системы обязаны проводить селективную верификацию международных транзакций, особенно тех, которые превышают определенные денежные пороги или демонстрируют нестандартные профили транзакций. В случае вашего клиента процесс верификации был автоматически запущен, что привело к временной приостановке.', en: 'Thank you for your patience. Upon comprehensive review, I can confirm that the withdrawal request is for <WITHDRAWAL AMOUNT> to <BANK> dated <TODAY DATE>. The transfer has been temporarily suspended due to additional verification procedures initiated in accordance with the requirements of the Central Bank of the Argentine Republic (BCRA) under Law No. 25.246 regarding anti-money laundering (AML) prevention. Under these regulatory provisions, financial institutions and payment systems are obligated to conduct selective verification of international transactions, particularly those exceeding certain monetary thresholds or exhibiting non-standard transaction profiles. In your client\'s case, the verification process was automatically triggered, resulting in the temporary suspension.', ms: 'Terima kasih atas kesabaran anda. Setelah semakan menyeluruh, saya boleh mengesahkan bahawa permintaan pengeluaran adalah untuk <WITHDRAWAL AMOUNT> ke <BANK> bertarikh <TODAY DATE>. Pemindahan telah digantung sementara disebabkan prosedur pengesahan tambahan yang dimulakan selaras dengan keperluan Bank Pusat Republik Argentina (BCRA) di bawah Undang-undang No. 25.246 mengenai pencegahan pengubahan wang haram (AML). Di bawah peruntukan kawal selia ini, institusi kewangan dan sistem pembayaran diwajibkan menjalankan pengesahan selektif transaksi antarabangsa, terutamanya yang melebihi ambang monetari tertentu atau mempamerkan profil transaksi bukan standard. Dalam kes klien anda, proses pengesahan telah dicetuskan secara automatik, mengakibatkan penggantungan sementara.' },
                4: { ru: 'Я знаю, что другие клиенты успешно выводили в тот же банк. Почему транзакция моего клиента обрабатывается по-другому и подвергается дополнительному рассмотрению?', en: 'I know other clients have successfully withdrawn to the same bank. Why is my client\'s transaction being treated differently and subjected to additional review?', ms: 'Saya tahu klien lain berjaya mengeluarkan ke bank yang sama. Mengapa transaksi klien saya diperlakukan secara berbeza dan tertakluk kepada semakan tambahan?' },
                5: { ru: 'Я полностью понимаю ваше беспокойство, и это действительно обоснованный вопрос. Вы правы, что другие транзакции в <BANK> были обработаны успешно, поскольку процедуры верификации AML применяются не повсеместно, а на селективной основе. Критерии отбора зависят от различных факторов, включая сумму транзакции, частоту транзакций и другие параметры, определяемые банковской системой или алгоритмами BCRA. В конкретном случае вашего клиента транзакция была автоматически выбрана для усиленной верификации, что, к сожалению, привело к этой задержке. Будьте уверены, что это не связано с какой-либо ошибкой со стороны вашего клиента или операций нашей платформы; это стандартное процедурное требование, разработанное для обеспечения безопасности и соответствия регулятивным стандартам BCRA.', en: 'I completely understand your concern, and this is indeed a valid inquiry. You are correct that other transactions to <BANK> have been processed successfully, as AML verification procedures are not applied universally but rather on a selective basis. The selection criteria depend on various factors including transaction amount, frequency of transactions, and other parameters determined by the banking system or BCRA algorithms. In your client\'s specific case, the transaction was automatically selected for enhanced verification, which unfortunately resulted in this delay. Please be assured that this is not related to any error on your client\'s part or our platform\'s operations; it is a standard procedural requirement designed to ensure security and compliance with BCRA regulatory standards.', ms: 'Saya benar-benar memahami kebimbangan anda, dan ini sememangnya pertanyaan yang sah. Anda betul bahawa transaksi lain ke <BANK> telah diproses dengan berjaya, kerana prosedur pengesahan AML tidak digunakan secara universal tetapi secara selektif. Kriteria pemilihan bergantung pada pelbagai faktor termasuk jumlah transaksi, kekerapan transaksi, dan parameter lain yang ditentukan oleh sistem perbankan atau algoritma BCRA. Dalam kes khusus klien anda, transaksi telah dipilih secara automatik untuk pengesahan yang dipertingkatkan, yang malangnya mengakibatkan kelewatan ini. Sila yakin bahawa ini tidak berkaitan dengan sebarang kesilapan di pihak klien anda atau operasi platform kami; ini adalah keperluan prosedur standard yang direka untuk memastikan keselamatan dan pematuhan dengan standard kawal selia BCRA.' },
                6: { ru: 'Учитывая эту ситуацию, какие альтернативы вы можете предложить для решения этого?', en: 'Given this situation, what alternatives can you offer to resolve this?', ms: 'Memandangkan situasi ini, apakah alternatif yang boleh anda tawarkan untuk menyelesaikan perkara ini?' },
                7: { ru: 'Наша основная цель - обеспечить, чтобы ваш клиент получил свои средства как можно быстрее, и у нас есть несколько альтернативных решений. Наиболее эффективный вариант - обработать вывод в долларах США через систему карт UnionPay. Эти транзакции обрабатываются через международные платежные сети, что обходит местные требования верификации AML и обеспечивает завершение транзакции в течение 30 минут без дополнительных комиссий или процедур верификации. Для продолжения с этим вариантом вашему клиенту потребуется предоставить свой Loading ID карты UnionPay.', en: 'Our primary objective is to ensure your client receives their funds as expeditiously as possible, and we have several alternative solutions available. The most efficient option is to process the withdrawal in United States dollars through a UnionPay card system. These transactions are processed through international payment networks, which circumvents local AML verification requirements and enables transaction completion within 30 minutes without additional fees or verification procedures. To proceed with this option, your client would need to provide their UnionPay card Loading ID.', ms: 'Objektif utama kami adalah memastikan klien anda menerima dana mereka secepat mungkin, dan kami mempunyai beberapa penyelesaian alternatif yang tersedia. Pilihan yang paling berkesan adalah memproses pengeluaran dalam dolar Amerika Syarikat melalui sistem kad UnionPay. Transaksi ini diproses melalui rangkaian pembayaran antarabangsa, yang mengelakkan keperluan pengesahan AML tempatan dan membolehkan penyelesaian transaksi dalam masa 30 minit tanpa yuran tambahan atau prosedur pengesahan. Untuk meneruskan dengan pilihan ini, klien anda perlu memberikan Loading ID kad UnionPay mereka.' },
                8: { ru: 'Какова была бы точная сумма в долларах США, которую получит мой клиент с этим вариантом?', en: 'What would be the exact USD amount my client receives with this option?', ms: 'Berapakah jumlah USD tepat yang akan diterima oleh klien saya dengan pilihan ini?' },
                9: { ru: 'Вывод в долларах США составит <USD AMOUNT> долларов. Если вы можете предоставить детали вывода, я могу обработать этот запрос прямо сейчас.', en: 'The USD withdrawal would be <USD AMOUNT> dollars. If you can provide the withdrawal details, I can process this request right away.', ms: 'Pengeluaran USD akan menjadi <USD AMOUNT> dolar. Jika anda boleh memberikan butiran pengeluaran, saya boleh memproses permintaan ini segera.' }
            }
        };
        
        const message = defaultMessages[presetType]?.[messageNum];
        if (message) {
            return message;
        }
        
        // Fallback - если сообщение не найдено, возвращаем пустые значения
        return { ru: '', en: '', ms: '' };
    };

    // Функция для удаления банка
    const handleDeleteBank = async (bankName) => {
        if (await confirmDialog({
            title: 'Удалить банк?',
            message: `Банк «${bankName}» пропадёт из списка выбора банков на сайте.`,
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Уберёт банк из списка',
            cancelHint: 'Закроет окно, банк останется в списке'
        })) {
            setLocalSettings(prev => {
                const newBanks = { ...prev.banks };
                const deletedBankPriority = newBanks[bankName]?.priority || 999;
                
                // Удаляем банк
                delete newBanks[bankName];
                
                // Пересчитываем приоритеты всех банков с приоритетом больше удаленного
                Object.keys(newBanks).forEach(otherBankName => {
                    const otherBankPriority = newBanks[otherBankName]?.priority || 999;
                    if (otherBankPriority > deletedBankPriority) {
                        newBanks[otherBankName] = {
                            ...newBanks[otherBankName],
                            priority: otherBankPriority - 1
                        };
                    }
                });
                
                const newSettings = {
                    ...prev,
                    banks: newBanks
                };
                
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
            
            // Убираем подсветку если удаляется новый банк
            if (newlyAddedBank === bankName) {
                setNewlyAddedBank(null);
            }
        }
    };

    // Функция для начала редактирования названия банка
    const handleStartEditingBankName = (bankName) => {
        setEditingBankName(prev => ({
            ...prev,
            [bankName]: bankName
        }));
    };

    // Функция для обновления временного названия банка
    const handleUpdateEditingBankName = (bankName, newValue) => {
        setEditingBankName(prev => ({
            ...prev,
            [bankName]: newValue
        }));
    };
    // Функция для завершения редактирования названия банка
    const handleFinishEditingBankName = (oldName) => {
        const newName = editingBankName[oldName];
        
        if (!newName || newName.trim() === '' || newName.trim() === oldName) {
            // Сбрасываем временное название
            setEditingBankName(prev => {
                const newEditing = { ...prev };
                delete newEditing[oldName];
                return newEditing;
            });
            return;
        }
        
        const trimmedName = newName.trim();
        
        // Проверяем, что новое название не занято
        if (localSettings.banks && localSettings.banks[trimmedName] && trimmedName !== oldName) {
            error('Банк с таким названием уже существует!', 3000);
            // Сбрасываем к старому названию
            setEditingBankName(prev => {
                const newEditing = { ...prev };
                delete newEditing[oldName];
                return newEditing;
            });
            return;
        }
        
        // Обновляем название банка
        setLocalSettings(prev => {
            const newBanks = { ...prev.banks };
            const bankData = newBanks[oldName];
            
            delete newBanks[oldName];
            newBanks[trimmedName] = bankData;
            
            const newSettings = {
                ...prev,
                banks: newBanks
            };
            
            // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
            return newSettings;
        });
        
        // Очищаем состояние редактирования
        setEditingBankName(prev => {
            const newEditing = { ...prev };
            delete newEditing[oldName];
            return newEditing;
        });
        
        // Убираем подсветку нового банка при переименовании
        if (newlyAddedBank === oldName) {
            setNewlyAddedBank(trimmedName);
            // Убираем подсветку через 1 секунду после переименования
            setTimeout(() => {
                setNewlyAddedBank(null);
            }, 1000);
        }
        
        // Принудительно обновляем компонент для корректного отображения
        setForceUpdate(prev => prev + 1);
    };

    // Функция для парсинга чисел с поддержкой запятой и обработкой пустых значений
    const parseNumber = (value, isInt = false) => {
        if (value === '' || value === null || value === undefined) {
            return isInt ? 0 : 0;
        }
        // Заменяем запятую на точку для правильного парсинга
        const normalizedValue = String(value).replace(',', '.');
        const parsed = isInt ? parseInt(normalizedValue, 10) : parseFloat(normalizedValue);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Вспомогательная функция для рендеринга поля с диапазонами
    const renderFieldWithRanges = (fieldKey, rangesKey, icon, label, description, step = '0.01', isInt = false, suffix = '') => {
        const fixedValue = localSettings[fieldKey]?.[activeTariffTab] ?? '';
        const rangeValue = localSettings[rangesKey]?.[activeTariffTab] || {};
        const isRandomized = localSettings.randomizeRanges;

        return (
            <div className={s.fieldInput} style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Фиксированная сумма</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="number"
                            step={step}
                            className={s.numberInput}
                            value={fixedValue}
                            onChange={(e) => handleInputChange(fieldKey, activeTariffTab, parseNumber(e.target.value, isInt))}
                            disabled={isRandomized}
                            style={{ opacity: isRandomized ? 0.5 : 1, width: '100%' }}
                        />
                        {suffix && <span className={s.inputSuffix} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>{suffix}</span>}
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>От</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="number"
                            step={step}
                            className={s.numberInput}
                            value={rangeValue.min ?? ''}
                            onChange={(e) => {
                                const ranges = localSettings[rangesKey] || {};
                                const tariffRanges = ranges[activeTariffTab] || {};
                                handleInputChange(rangesKey, activeTariffTab, { ...tariffRanges, min: parseNumber(e.target.value, isInt) });
                            }}
                            disabled={!isRandomized}
                            style={{ opacity: !isRandomized ? 0.5 : 1, width: '100%' }}
                        />
                        {suffix && <span className={s.inputSuffix} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>{suffix}</span>}
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>До</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="number"
                            step={step}
                            className={s.numberInput}
                            value={rangeValue.max ?? ''}
                            onChange={(e) => {
                                const ranges = localSettings[rangesKey] || {};
                                const tariffRanges = ranges[activeTariffTab] || {};
                                handleInputChange(rangesKey, activeTariffTab, { ...tariffRanges, max: parseNumber(e.target.value, isInt) });
                            }}
                            disabled={!isRandomized}
                            style={{ opacity: !isRandomized ? 0.5 : 1, width: '100%' }}
                        />
                        {suffix && <span className={s.inputSuffix} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>{suffix}</span>}
                    </div>
                </div>
            </div>
        );
    };

    // ── Nova: строка настройки тарифа. Показывает только актуальные поля:
    // при рандомизации — «от…до», иначе — одно фиксированное значение
    const renderNovaTariffRow = (Glyph, label, description, fieldKey, rangesKey, opts = {}) => {
        const { step = '0.01', isInt = false, suffix = '', emoji = null } = opts;
        const isRnd = !!localSettings.randomizeRanges;
        const fixedValue = localSettings[fieldKey]?.[activeTariffTab] ?? '';
        const rangeValue = localSettings[rangesKey]?.[activeTariffTab] || {};
        const setRangePart = (patch) => {
            const tariffRanges = (localSettings[rangesKey] || {})[activeTariffTab] || {};
            handleInputChange(rangesKey, activeTariffTab, { ...tariffRanges, ...patch });
        };
        return (
            <div className={s.tariffRow} key={fieldKey}>
                <span className={s.tariffRowGlyph}>
                    {emoji ? <span className={s.tariffRowEmoji}>{emoji}</span> : <Glyph size={14} strokeWidth={1.9} aria-hidden="true" />}
                </span>
                <div className={s.tariffRowInfo}>
                    <span className={s.tariffRowName}>{label}</span>
                    <span className={s.tariffRowDesc}>{description}</span>
                </div>
                {isRnd ? (
                    <div className={s.tariffRangePair}>
                        <label className={s.tariffInputBox}>
                            <span className={s.tariffInputTag}>от</span>
                            <input
                                type="number"
                                step={step}
                                value={rangeValue.min ?? ''}
                                onChange={(e) => setRangePart({ min: parseNumber(e.target.value, isInt) })}
                            />
                            {suffix && <span className={s.tariffSuffix}>{suffix}</span>}
                        </label>
                        <label className={s.tariffInputBox}>
                            <span className={s.tariffInputTag}>до</span>
                            <input
                                type="number"
                                step={step}
                                value={rangeValue.max ?? ''}
                                onChange={(e) => setRangePart({ max: parseNumber(e.target.value, isInt) })}
                            />
                            {suffix && <span className={s.tariffSuffix}>{suffix}</span>}
                        </label>
                    </div>
                ) : (
                    <label className={s.tariffInputBox}>
                        <input
                            type="number"
                            step={step}
                            value={fixedValue}
                            onChange={(e) => handleInputChange(fieldKey, activeTariffTab, parseNumber(e.target.value, isInt))}
                        />
                        {suffix && <span className={s.tariffSuffix}>{suffix}</span>}
                    </label>
                )}
            </div>
        );
    };

    const handleInputChange = (section, key, value) => {
        if (section === '' || !section) {
            // Для полей верхнего уровня
            setLocalSettings(prev => {
                let newSettings = { ...prev };
                
                // Обрабатываем вложенные ключи (например, quickActions.commission.enabled)
                if (key.includes('.')) {
                    const keys = key.split('.');
                    let current = newSettings;
                    
                    // Создаем вложенную структуру до предпоследнего ключа
                    for (let i = 0; i < keys.length - 1; i++) {
                        if (!current[keys[i]]) {
                            current[keys[i]] = {};
                        }
                        current = current[keys[i]];
                    }
                    
                    // Устанавливаем значение в последний ключ
                    current[keys[keys.length - 1]] = value;
                } else {
                    // Простой ключ
                    newSettings[key] = value;
                }
                
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
        } else if (section === 'modalWallets') {
            // Для modalWallets (трёхуровневая вложенность)
            setLocalSettings(prev => ({
                ...prev,
                modalWallets: {
                    ...prev.modalWallets,
                    [key]: value
                }
            }));
        } else if (section === 'banks') {
            // Для банков (вложенная структура bankName.property)
            const [bankName, property] = key.split('.');
            
            // Специальная обработка для приоритета
            if (property === 'priority') {
                handleBankPriorityChange(bankName, parseInt(value));
                return;
            }
            
            setLocalSettings(prev => {
                const newSettings = {
                    ...prev,
                    banks: {
                        ...prev.banks,
                        [bankName]: {
                            ...(prev.banks && prev.banks[bankName] ? prev.banks[bankName] : {}),
                            [property]: value
                        }
                    }
                };
                
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
        } else if (section === 'translations') {
            // Для переводов (сложная структура translations.lang.section.key или translations.lang.section.subsection.key)
            const keys = key.split('.');
            setLocalSettings(prev => {
                let newTranslations = { ...prev.translations };
                
                // Создаем структуру для любой глубины вложенности
                if (keys.length >= 2) {
                    const [lang, ...translationPath] = keys;
                    
                    // Инициализируем язык если его нет
                    if (!newTranslations[lang]) newTranslations[lang] = {};
                    
                    // Создаем вложенную структуру до предпоследнего ключа
                    let current = newTranslations[lang];
                    for (let i = 0; i < translationPath.length - 1; i++) {
                        if (!current[translationPath[i]]) {
                            current[translationPath[i]] = {};
                        }
                        current = current[translationPath[i]];
                    }
                    
                    // Устанавливаем значение в последний ключ
                    const lastKey = translationPath[translationPath.length - 1];
                    current[lastKey] = value;
                }
                
                const newSettings = {
                    ...prev,
                    translations: newTranslations
                };
                
                // НЕ сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
        } else if (section === 'miniFigma') {
            // Для MiniFigma
            setLocalSettings(prev => {
                const newSettings = {
                    ...prev,
                    miniFigma: {
                        ...prev.miniFigma,
                        [key]: value
                    }
                };
                
                // Не сохраняем автоматически - только при нажатии кнопки "Сохранить"
                return newSettings;
            });
        } else if (section.endsWith('Ranges')) {
            // Для диапазонов тарифов (например, tariffBaseAmountsRanges)
            setLocalSettings(prev => {
                const newSettings = {
                    ...prev,
                    [section]: {
                        ...(prev[section] || {}),
                        [key]: value // value уже объект { min, max }
                    }
                };
                return newSettings;
            });
        } else {
            // Для обычных вложенных полей
            setLocalSettings(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [key]: value
                }
            }));
        }
    };
    // Динамическое получение списка иконок банков из папки bankicons
    const [serverBankIcons, setServerBankIcons] = useState([]);
    // Версии иконок для сброса кэша браузера при перезаписи файла тем же именем.
    // Ключ — имя файла, значение — метка времени последней загрузки
    const [iconVersions, setIconVersions] = useState({});
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/files/bankicons');
                // Проверяем что ответ действительно JSON
                const contentType = r.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    // Если не JSON - значит endpoint не существует, используем локальные иконки
                    return;
                }
                const j = await r.json();
                if (j && Array.isArray(j.files)) {
                    setServerBankIcons(j.files);
                }
            } catch (e) {
                // Игнорируем ошибки - используем локальные иконки из bundledBankIcons
                console.log('Используем локальные иконки банков');
            }
        })();
    }, []);
    // Локальные дефолтные иконки из сборки (fallback на случай отсутствия require.context)
    const bundledBankIcons = [
        'alliance.png','astropay.png','bbva.png','belo.png','bna.png','brubank.png','ciudad.png','credicoop.png','cuentadni.png','default-bank.png','fiwind.png','galicia.png','gxbank.png','lemon.png','macro.png','mastercard.png','mercadopago.png','naranja.png','otrobanco.png','pampa.png','patagonia.png','personalpay.png','prex.png','provincia.png','santander.png','supervielle.png','uala.png','visa.png',
        'AmBank.png','Bank_Rakyat_Malaysia.png','BigPay.png','Boost.png','CIMB_Group.png','FavePay.png','GoPayz.png','GrabPay.png','HSBC_Malaysia.png','HongLeongBank.png','ICBC.png','Instapay_eWallet.png','MAE_by_Maybank2u.png','Maybank.png','OCBC_Bank_Malaysia.png','POSB.png','Public_Bank_Berhad.png','RHB_Bank.png','Revolut.png','ShopeePay.png','Touch_n_Go_eWallet.png','UOB_Malaysia.png','Wise_TransferWise.png','YouTrip.png'
    ];
    const mergedBankIcons = Array.from(new Set([...(serverBankIcons||[]), ...bundledBankIcons]));

    // Функция для получения URL иконки банка (совместима с динамической системой)
    const getBankIconUrl = (iconFileName) => {
        if (!iconFileName) return '/bankicons/default-bank.png';
        if (typeof iconFileName === 'string' && (iconFileName.startsWith('http://') || iconFileName.startsWith('https://') || iconFileName.startsWith('/bankicons/'))) {
            return iconFileName;
        }
        // приоритет: серверная папка без пересборки
        const ver = iconVersions[iconFileName];
        // добавляем ?v=<timestamp> только после перезагрузки файла — чтобы
        // сбросить кэш браузера при обновлении иконки с тем же именем
        return ver ? `/bankicons/${iconFileName}?v=${ver}` : `/bankicons/${iconFileName}`;
    };

    // Фиксированный список банков (как в App.js const networks)
    const banksList = [
        'Mercado Pago', 'Naranja X', 'BNA+', 'Lemon Cash', 'Fiwind',
        'UALA', 'Brubank', 'Cuenta DNI', 'Santander', 'Banco Galicia', 
        'BBVA', 'Personal Pay', 'Prex', 'Banco Provincia', 'Banco Macro',
        'Banco Ciudad', 'Banco Pampa', 'Astro Pay', 'Banco Patagonia',
        'Supervielle', 'Credicoop', 'Belo', 'ICBC', 'VISA', 'MasterCard', 'Otro Banco'
    ];

    // Проверяем, что настройки загружены
    if (!localSettings || !localSettings.cryptoRates) {
        return <div>Загружаем настройки...</div>;
    }
    return (
        <div className={`${s.settingsPage} ${isNavbarCollapsed ? s.navbarCollapsed : ''} ${isNova ? s.novaSkin : ''}`} data-ltr-force>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerContent}>
                    <h1 className={s.title}>Настройки сайта</h1>
                    <p className={s.subtitle}>Конфигурация глобальных параметров приложения</p>
                </div>
            </div>

            {/* Tab Navigation */}
            {/* Табы теперь встроены в каждую секцию */}

            {/* Content */}
            <div className={s.content} data-trans-untranslated={showUntranslatedOnly ? '1' : undefined}>
                {/* General Tab */}
                {activeTab === 'general' && (
                    <>
                        {/* Global Settings Section */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={s.firstSectionWithTabs}>
                            <h3 className={s.sectionTitle}>
                                <img src={siteSettingsIcon} alt="Global Settings" className={s.sectionIcon} />
                                {isNova && <SlidersHorizontal size={13} strokeWidth={2.2} aria-hidden="true" />}
                                Основные настройки
                            </h3>
                            <div className={s.sectionContent}>
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={siteSettingsIcon} alt="Site Name" className={s.sectionIcon} />
                                            {fieldGlyph(Type)}
                                            Название сайта
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Название сайта, которое будет отображаться в навигации, модальных окнах и заголовке страницы
                                        </div>
                                    </div>
                                    <div className={s.fieldInput} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <input
                                                type="text"
                                                className={s.input}
                                                placeholder="暗号交換"
                                                value={localSettings.siteName || '暗号交換'}
                                                onChange={(e) => handleInputChange('', 'siteName', e.target.value)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <ThemedSelect nova={isNova}
                                                className={s.input}
                                                    value={localSettings.siteTitleFontUrl ? '__CUSTOM_FONT__' : (localSettings.siteTitleFont || "'Inter', sans-serif")}
                                                onChange={(e) => {
                                                        const selectedValue = e.target.value;
                                                        if (selectedValue === '__CUSTOM_FONT__') {
                                                            // Если выбран "Свой шрифт", ничего не делаем (URL уже есть)
                                                            return;
                                                        } else {
                                                            // Если выбран шрифт из списка, очищаем URL
                                                            handleInputChange('', 'siteTitleFont', selectedValue);
                                                            handleInputChange('', 'siteTitleFontUrl', '');
                                                    // Загружаем шрифт при выборе
                                                            loadFontByValue(selectedValue);
                                                        }
                                                }}
                                                style={{ width: '100%' }}
                                            >
                                                {googleFonts.map((font) => (
                                                    <option key={font.name} value={font.value}>
                                                        {font.name}
                                                    </option>
                                                ))}
                                                    <option value="__CUSTOM_FONT__">Свой шрифт</option>
                                            </ThemedSelect>
                                        </div>
                                    </div>
                                        <div>
                                        <input
                                            type="text"
                                            className={s.input}
                                            placeholder="https://fonts.google.com/specimen/Titillium+Web"
                                            value={localSettings.siteTitleFontUrl || ''}
                                                onChange={(e) => {
                                                    const url = e.target.value;
                                                    handleInputChange('', 'siteTitleFontUrl', url);
                                                    // Если URL вставлен, автоматически переключаемся на "Свой шрифт"
                                                    if (url.trim()) {
                                                        // Парсим ссылку Google Fonts и загружаем шрифт
                                                        const fontName = extractFontNameFromUrl(url.trim());
                                                        if (fontName) {
                                                            const fontValue = `'${fontName}', sans-serif`;
                                                            handleInputChange('', 'siteTitleFont', fontValue);
                                                            loadGoogleFont(fontName);
                                                        }
                                                    }
                                                }}
                                            onBlur={(e) => {
                                                const url = e.target.value.trim();
                                                if (url) {
                                                    // Парсим ссылку Google Fonts и загружаем шрифт
                                                    const fontName = extractFontNameFromUrl(url);
                                                    if (fontName) {
                                                        const fontValue = `'${fontName}', sans-serif`;
                                                        handleInputChange('', 'siteTitleFont', fontValue);
                                                        loadGoogleFont(fontName);
                                                    }
                                                }
                                            }}
                                        />
                                        <div className={s.fieldDescription} style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                            Вставьте ссылку на шрифт из Google Fonts (например: https://fonts.google.com/specimen/Titillium+Web)
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Толщина шрифта</label>
                                                <ThemedSelect nova={isNova}
                                                    className={s.input}
                                                    value={localSettings.siteTitleFontWeight || '400'}
                                                    onChange={(e) => handleInputChange('', 'siteTitleFontWeight', e.target.value)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="100">100 - Thin</option>
                                                    <option value="200">200 - Extra Light</option>
                                                    <option value="300">300 - Light</option>
                                                    <option value="400">400 - Regular</option>
                                                    <option value="500">500 - Medium</option>
                                                    <option value="600">600 - Semi Bold</option>
                                                    <option value="700">700 - Bold</option>
                                                    <option value="800">800 - Extra Bold</option>
                                                    <option value="900">900 - Black</option>
                                                </ThemedSelect>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Наклон</label>
                                                <ThemedSelect nova={isNova}
                                                    className={s.input}
                                                    value={localSettings.siteTitleFontStyle || 'normal'}
                                                    onChange={(e) => handleInputChange('', 'siteTitleFontStyle', e.target.value)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="normal">Обычный</option>
                                                    <option value="italic">Курсив</option>
                                                </ThemedSelect>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Расстояние</label>
                                                <input
                                                    type="text"
                                                    className={s.input}
                                                    placeholder="0px"
                                                    value={localSettings.siteTitleLetterSpacing || ''}
                                                    onChange={(e) => handleInputChange('', 'siteTitleLetterSpacing', e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Размер</label>
                                                <input
                                                    type="text"
                                                    className={s.input}
                                                    placeholder="20px"
                                                    value={localSettings.siteTitleFontSize || ''}
                                                    onChange={(e) => handleInputChange('', 'siteTitleFontSize', e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Отступ лого</label>
                                                <input
                                                    type="text"
                                                    className={s.input}
                                                    placeholder="10px"
                                                    value={localSettings.siteTitleLogoGap || ''}
                                                    onChange={(e) => handleInputChange('', 'siteTitleLogoGap', e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img 
                                                src={localSettings.siteLogo 
                                                    ? (localSettings.siteLogo.startsWith('/') || localSettings.siteLogo.startsWith('http') 
                                                        ? localSettings.siteLogo 
                                                        : `/logo/${encodeURIComponent(localSettings.siteLogo)}`)
                                                    : siteSettingsIcon} 
                                                alt="Logo" 
                                                className={s.sectionIcon}
                                                style={{ objectFit: 'contain' }}
                                                onError={(e) => {
                                                    console.error('[SiteSettings] Ошибка загрузки логотипа:', e.target.src);
                                                    // Пробуем альтернативный путь через /data/logo/
                                                    if (!e.target.src.includes('/data/logo/')) {
                                                        const filename = localSettings.siteLogo;
                                                        e.target.src = `/data/logo/${encodeURIComponent(filename)}`;
                                                    } else {
                                                        e.target.src = siteSettingsIcon;
                                                    }
                                                }}
                                            />
                                            {fieldGlyph(ImageGlyph)}
                                            Логотип сайта
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Загрузите логотип сайта, который будет отображаться в навигации и модальных окнах
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            {localSettings.siteLogo && (
                                                <div
                                                    className={isNova ? s.assetChip : undefined}
                                                    style={isNova ? undefined : { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                >
                                                    <AdaptiveLogo
                                                        src={localSettings.siteLogo.startsWith('/') || localSettings.siteLogo.startsWith('http') 
                                                            ? localSettings.siteLogo 
                                                            : `/logo/${encodeURIComponent(localSettings.siteLogo)}`} 
                                                        alt="Logo" 
                                                        className={isNova ? s.assetChipImg : undefined}
                                                        imgStyle={{ maxWidth: '60px', maxHeight: '30px', objectFit: 'contain' }}
                                                        onError={(e) => {
                                                            console.error('[SiteSettings] Ошибка загрузки логотипа для предпросмотра:', e.target.src);
                                                            // Пробуем альтернативный путь через /data/logo/
                                                            if (!e.target.src.includes('/data/logo/')) {
                                                                const filename = localSettings.siteLogo;
                                                                e.target.src = `/data/logo/${encodeURIComponent(filename)}`;
                                                            } else {
                                                                e.target.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleInputChange('', 'siteLogo', '')}
                                                        className={isNova ? s.assetRemove : undefined}
                                                        style={isNova ? undefined : {
                                                            padding: '4px 8px',
                                                            fontSize: '12px',
                                                            color: '#ef4444',
                                                            background: '#fee2e2',
                                                            border: '1px solid #fecaca',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={isNova ? undefined : (e) => {
                                                            e.target.style.background = '#fecaca';
                                                        }}
                                                        onMouseLeave={isNova ? undefined : (e) => {
                                                            e.target.style.background = '#fee2e2';
                                                        }}
                                                    >
                                                        {isNova && <Trash2 size={12} strokeWidth={2.3} aria-hidden="true" />}
                                                        Удалить
                                                    </button>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept=".png,.svg,.jpg,.jpeg,.webp"
                                                onChange={async (ev) => {
                                                    try {
                                                        const file = ev.target.files && ev.target.files[0];
                                                        if (!file) return;
                                                        const asDataUrl = await new Promise((res, rej) => {
                                                            const fr = new FileReader();
                                                            fr.onload = () => res(fr.result);
                                                            fr.onerror = rej;
                                                            fr.readAsDataURL(file);
                                                        });
                                                        const resp = await fetch(`/api/files/logo/${encodeURIComponent(file.name)}`, { 
                                                            method: 'POST', 
                                                            headers: { 'Content-Type': 'application/json' }, 
                                                            body: JSON.stringify({ data: asDataUrl }) 
                                                        });
                                                        
                                                        // Проверяем тип ответа
                                                        const contentType = resp.headers.get('content-type');
                                                        if (!contentType || !contentType.includes('application/json')) {
                                                            const text = await resp.text();
                                                            console.error('[SiteSettings] Сервер вернул не JSON:', text.substring(0, 200));
                                                            notifyError('Ошибка: сервер вернул неверный формат ответа. Убедитесь, что сервер запущен и перезапущен после изменений.');
                                                            return;
                                                        }
                                                        
                                                        const j = await resp.json();
                                                        if (j && j.success) {
                                                            // Используем имя файла из ответа сервера или из file.name
                                                            const savedFilename = j.filename || file.name;
                                                            handleInputChange('', 'siteLogo', savedFilename);
                                                            console.log('[SiteSettings] Логотип успешно загружен:', savedFilename);
                                                        } else {
                                                            notifyError('Не удалось загрузить логотип: ' + (j?.error || 'Неизвестная ошибка'));
                                                        }
                                                        ev.target.value = '';
                                                    } catch (e) {
                                                        console.error('[SiteSettings] Ошибка загрузки логотипа:', e);
                                                        notifyError('Ошибка загрузки логотипа: ' + e.message + '\n\nУбедитесь, что сервер запущен на порту 3002.');
                                                    }
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    width: '1px',
                                                    height: '1px',
                                                    padding: 0,
                                                    margin: '-1px',
                                                    overflow: 'hidden',
                                                    clip: 'rect(0 0 0 0)',
                                                    whiteSpace: 'nowrap',
                                                    border: 0
                                                }}
                                                id="siteLogoUpload"
                                            />
                                            {/* Нативный label вместо JS .click() — срабатывает во всех
                                                браузерах и встроенных вебвью */}
                                            <label
                                                htmlFor="siteLogoUpload"
                                                className={s.importBtn}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {isNova
                                                    ? <Upload size={14} strokeWidth={2.2} aria-hidden="true" />
                                                    : <img src={importButtonIcon} alt="Upload" className={s.buttonIcon} />}
                                                {localSettings.siteLogo ? 'Заменить логотип' : 'Загрузить логотип'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={siteSettingsIcon} alt="Theme" className={s.sectionIcon} />
                                            {fieldGlyph(Palette)}
                                            Тема оформления
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите тему оформления для всего сайта (светлая или темная)
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.theme || 'light'}
                                            onChange={(e) => {
                                                handleInputChange('', 'theme', e.target.value);
                                                // Применяем тему сразу
                                                document.documentElement.setAttribute('data-theme', e.target.value);
                                            }}
                                        >
                                            <option value="light">Светлая</option>
                                            <option value="dark">Темная</option>
                                            <option value="liquid-glass">Liquid Glass</option>
                                            <option value="solana">Solana</option>
                                            <option value="binance">Binance</option>
                                            <option value="nova">Nova (новый дизайн)</option>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}><LayoutDashboard size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                <div className={s.switchRowText}>
                                                    <span className={s.switchRowName}>Показывать Welcome блок в Dashboard</span>
                                                    <span className={s.switchRowHint}>Приветственный блок с обзором рынка на главной странице Dashboard</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.showWelcomeBlock !== false}
                                                    onChange={(e) => handleInputChange('', 'showWelcomeBlock', e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={siteSettingsIcon} alt="Show Welcome" className={s.sectionIcon} />
                                                {fieldGlyph(LayoutDashboard)}
                                                Показывать Welcome блок в Dashboard
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Включить или отключить отображение приветственного блока с обзором рынка на главной странице Dashboard
                                            </div>
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label className={s.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={s.checkbox}
                                                    checked={localSettings.showWelcomeBlock !== false}
                                                    onChange={(e) => handleInputChange('', 'showWelcomeBlock', e.target.checked)}
                                                />
                                                <span className={s.checkboxCustom}></span>
                                                Показывать Welcome блок
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon(localSettings.defaultCrypto || 'ETH')} alt="Default Crypto" className={s.sectionIcon} />
                                            {fieldGlyph(Bitcoin)}
                                            Криптовалюта по умолчанию
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите криптовалюту, которая будет использоваться по умолчанию во всем приложении
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.defaultCrypto || 'ETH'}
                                            onChange={(e) => handleInputChange('', 'defaultCrypto', e.target.value)}
                                        >
                                            <option value="BTC">Bitcoin (BTC)</option>
                                            <option value="ETH">Ethereum (ETH)</option>
                                            <option value="BNB">Binance Coin (BNB)</option>
                                            <option value="USDC">USD Coin (USDC)</option>
                                            <option value="SOL">Solana (SOL)</option>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={userInfoIcon} alt="User" className={s.sectionIcon} />
                                            {fieldGlyph(UserRound)}
                                            Имя пользователя по умолчанию
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Имя пользователя, которое будет отображаться в модальных окнах и транзакциях
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="text"
                                            className={s.input}
                                            placeholder="Lobruno Kevin Antonio"
                                            value={localSettings.defaultUserName || 'Lobruno Kevin Antonio'}
                                            onChange={(e) => handleInputChange('', 'defaultUserName', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={userInfoIcon} alt="Avatar" className={s.sectionIcon} />
                                            {fieldGlyph(CircleUserRound)}
                                            Аватарка пользователя
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Загрузите изображение аватарки, которое будет отображаться в хедере вместо иконки
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            {isNova && (
                                                <div className={s.headerPreview}>
                                                    <span className={s.headerPreviewAvatar}>
                                                        {localSettings.defaultUserAvatar ? (
                                                            <img
                                                                src={localSettings.defaultUserAvatar.startsWith('/') || localSettings.defaultUserAvatar.startsWith('http')
                                                                    ? localSettings.defaultUserAvatar
                                                                    : `/avatar/${encodeURIComponent(localSettings.defaultUserAvatar)}`}
                                                                alt=""
                                                                onError={(e) => {
                                                                    if (!e.target.src.includes('/data/avatar/')) {
                                                                        e.target.src = `/data/avatar/${encodeURIComponent(localSettings.defaultUserAvatar)}`;
                                                                    } else {
                                                                        e.target.style.display = 'none';
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            (localSettings.defaultUserName || 'U').trim().charAt(0).toUpperCase()
                                                        )}
                                                    </span>
                                                    <span className={s.headerPreviewMeta}>
                                                        <span className={s.headerPreviewName}>{localSettings.defaultUserName || '—'}</span>
                                                        <span className={s.headerPreviewStatus}>
                                                            {(localSettings.isPremium || localSettings.premiumStatus) ? 'Premium' : 'Не верифицирован'}
                                                        </span>
                                                    </span>
                                                </div>
                                        )}
                                        {localSettings.defaultUserAvatar && (
                                                <div
                                                    className={isNova ? s.assetChip : undefined}
                                                    style={isNova ? undefined : { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                >
                                                    {/* В Nova круглое фото уже есть в мини-превью хедера — не дублируем */}
                                                    {!isNova && (
                                                    <img 
                                                        src={localSettings.defaultUserAvatar.startsWith('/') || localSettings.defaultUserAvatar.startsWith('http') 
                                                            ? localSettings.defaultUserAvatar 
                                                            : `/avatar/${encodeURIComponent(localSettings.defaultUserAvatar)}`}
                                                        alt="Avatar" 
                                                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            console.error('[SiteSettings] Ошибка загрузки аватарки:', e.target.src);
                                                            if (!e.target.src.includes('/data/avatar/')) {
                                                                const filename = localSettings.defaultUserAvatar;
                                                                e.target.src = `/data/avatar/${encodeURIComponent(filename)}`;
                                                            } else {
                                                                e.target.src = userInfoIcon;
                                                            }
                                                        }}
                                                    />
                                                    )}
                                                    <span className={isNova ? s.assetChipName : undefined} style={isNova ? undefined : { fontSize: '14px', color: '#64748b' }}>{localSettings.defaultUserAvatar}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleInputChange('', 'defaultUserAvatar', '');
                                                        }}
                                                        className={isNova ? s.assetRemove : undefined}
                                                        style={isNova ? undefined : { 
                                                            background: 'none', 
                                                            border: 'none', 
                                                            color: '#ef4444', 
                                                            cursor: 'pointer',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px'
                                                        }}
                                                        onMouseEnter={isNova ? undefined : (e) => e.target.style.background = '#fee2e2'}
                                                        onMouseLeave={isNova ? undefined : (e) => e.target.style.background = 'none'}
                                                    >
                                                        {isNova && <Trash2 size={12} strokeWidth={2.3} aria-hidden="true" />}
                                                        Удалить
                                                    </button>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept=".png,.svg,.jpg,.jpeg,.webp"
                                                onChange={async (ev) => {
                                                    try {
                                                        const file = ev.target.files && ev.target.files[0];
                                                        if (!file) return;
                                                        const asDataUrl = await new Promise((res, rej) => {
                                                            const fr = new FileReader();
                                                            fr.onload = () => res(fr.result);
                                                            fr.onerror = rej;
                                                            fr.readAsDataURL(file);
                                                        });
                                                        const resp = await fetch(`/api/files/avatar/${encodeURIComponent(file.name)}`, { 
                                                            method: 'POST', 
                                                            headers: { 'Content-Type': 'application/json' }, 
                                                            body: JSON.stringify({ data: asDataUrl }) 
                                                        });
                                                        
                                                        const contentType = resp.headers.get('content-type');
                                                        if (!contentType || !contentType.includes('application/json')) {
                                                            const text = await resp.text();
                                                            console.error('[SiteSettings] Сервер вернул не JSON:', text.substring(0, 200));
                                                            notifyError('Ошибка: сервер вернул неверный формат ответа. Убедитесь, что сервер запущен и перезапущен после изменений.');
                                                            return;
                                                        }
                                                        
                                                        const j = await resp.json();
                                                        if (j && j.success) {
                                                            const savedFilename = j.filename || file.name;
                                                            handleInputChange('', 'defaultUserAvatar', savedFilename);
                                                            console.log('[SiteSettings] Аватарка успешно загружена:', savedFilename);
                                                        } else {
                                                            notifyError('Не удалось загрузить аватарку: ' + (j?.error || 'Неизвестная ошибка'));
                                                        }
                                                        ev.target.value = '';
                                                    } catch (e) {
                                                        console.error('[SiteSettings] Ошибка загрузки аватарки:', e);
                                                        notifyError('Ошибка загрузки аватарки: ' + e.message + '\n\nУбедитесь, что сервер запущен на порту 3002.');
                                                    }
                                                }}
                                                style={{ display: 'none' }}
                                                id="userAvatarUpload"
                                            />
                                            <button
                                                type="button"
                                                className={s.importBtn}
                                                onClick={() => document.getElementById('userAvatarUpload')?.click()}
                                            >
                                                {isNova
                                                    ? <Upload size={14} strokeWidth={2.2} aria-hidden="true" />
                                                    : <img src={importButtonIcon} alt="Upload" className={s.buttonIcon} />}
                                                {localSettings.defaultUserAvatar ? 'Заменить аватарку' : 'Загрузить аватарку'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={userInfoIcon} alt="License" className={s.sectionIcon} />
                                            {fieldGlyph(ShieldCheck)}
                                            Номер лицензии брокера
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Номер лицензии брокера, который будет отображаться в хедере, чате и транзакциях
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="text"
                                            className={s.input}
                                            placeholder="918552"
                                            value={localSettings.brokerLicenseNumber || '918552'}
                                            onChange={(e) => handleInputChange('', 'brokerLicenseNumber', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}><Star size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                <div className={s.switchRowText}>
                                                    <span className={s.switchRowName}>OTZOVIK MODE</span>
                                                    <span className={s.switchRowHint}>Режим отзовика с упрощённым интерфейсом и специальными настройками</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.otzovikMode || false}
                                                    onChange={(e) => handleInputChange('', 'otzovikMode', e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={siteSettingsIcon} alt="OTZOVIK MODE" className={s.sectionIcon} />
                                                {fieldGlyph(Star)}
                                                OTZOVIK MODE
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Включить режим отзовика с упрощенным интерфейсом и специальными настройками
                                            </div>
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label className={s.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={s.checkbox}
                                                    checked={localSettings.otzovikMode || false}
                                                    onChange={(e) => handleInputChange('', 'otzovikMode', e.target.checked)}
                                                />
                                                <span className={s.checkboxCustom}></span>
                                                Включить OTZOVIK MODE
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}><Globe size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                <div className={s.switchRowText}>
                                                    <span className={s.switchRowName}>ARABIC MODE</span>
                                                    <span className={s.switchRowHint}>Режим RTL (справа налево) для арабского интерфейса</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.arabicMode || false}
                                                    onChange={(e) => handleInputChange('', 'arabicMode', e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={siteSettingsIcon} alt="ARABIC MODE" className={s.sectionIcon} />
                                                {fieldGlyph(Globe)}
                                                ARABIC MODE
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Включить режим RTL (справа налево) для арабского интерфейса
                                            </div>
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label className={s.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={s.checkbox}
                                                    checked={localSettings.arabicMode || false}
                                                    onChange={(e) => handleInputChange('', 'arabicMode', e.target.checked)}
                                                />
                                                <span className={s.checkboxCustom}></span>
                                                Включить ARABIC MODE
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={nameUserChatIcon} alt="Names File" className={s.sectionIcon} />
                                            {fieldGlyph(FileText)}
                                            Файл имен для бинпала
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Файл с именами пользователей для генерации таблицы в бинпале (файлы сохраняются в папку data/names/).
                                            <br />
                                            Формат: обычный .txt, по одному человеку на строку — полное имя и фамилия в одну строку, например «Lukas Müller». Каждая следующая строка — новый человек.
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={(() => {
                                                // Показываем выбранным ТОЛЬКО реально существующий файл.
                                                // Если в конфиге прописан отсутствующий файл — значение
                                                // пустое, чтобы селект показал плейсхолдер, а не фантом.
                                                const raw = (localSettings.namesFile || '').split('/').pop();
                                                const current = !raw ? '' : (raw.endsWith('.txt') ? raw : raw + '.txt');
                                                const exists = availableNameFiles.some(
                                                    (f) => f.filename.split('/').pop() === current
                                                );
                                                return exists ? current : '';
                                            })()}
                                            onChange={(e) => {
                                                // Извлекаем только имя файла без пути для сохранения в конфиг
                                                const selectedValue = e.target.value;
                                                let fileOnly = selectedValue.split('/').pop();
                                                // Убеждаемся, что есть расширение .txt
                                                if (fileOnly && !fileOnly.endsWith('.txt')) {
                                                    fileOnly = fileOnly + '.txt';
                                                }
                                                console.log('[SiteSettings] Выбран файл:', selectedValue, 'сохраняем как:', fileOnly);
                                                handleInputChange('', 'namesFile', fileOnly);
                                            }}
                                                style={{ flex: 1 }}
                                        >
                                            {(() => {
                                                // В списке — ТОЛЬКО реально загруженные файлы (из data/names/).
                                                // Никаких фантомных опций из конфига: если в конфиге указан
                                                // отсутствующий файл, показываем дизейбл-плейсхолдер, чтобы
                                                // было видно, что нужно выбрать/загрузить настоящий файл.
                                                const options = availableNameFiles.map((file) => {
                                                    const fileOnly = file.filename.split('/').pop();
                                                    return { value: fileOnly, label: file.name };
                                                });
                                                // Плейсхолдер (пустое значение) — когда файл не выбран
                                                // или в конфиге указан отсутствующий файл. Имя кривого
                                                // файла из конфига НЕ показываем — оно только путает.
                                                const placeholderLabel = options.length === 0
                                                    ? 'Файл не найден — загрузите новый'
                                                    : 'Файл не найден, не выбран — выберите из списка или загрузите новый';
                                                const rendered = options.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ));
                                                return [
                                                    <option key="__placeholder__" value="" disabled>{placeholderLabel}</option>,
                                                    ...rendered,
                                                ];
                                            })()}
                                        </ThemedSelect>
                                            <input
                                                type="file"
                                                accept=".txt"
                                                onChange={async (ev) => {
                                                    try {
                                                        const file = ev.target.files && ev.target.files[0];
                                                        if (!file) return;
                                                        
                                                        const content = await new Promise((res, rej) => {
                                                            const fr = new FileReader();
                                                            fr.onload = () => res(fr.result);
                                                            fr.onerror = rej;
                                                            fr.readAsText(file);
                                                        });
                                                        
                                                        const filename = file.name;
                                                        const apiUrl = getApiUrl(`api/files/names/${encodeURIComponent(filename)}`);
                                                        console.log('[SiteSettings] Загружаем файл имен:', filename, 'URL:', apiUrl);
                                                        
                                                        const resp = await fetch(apiUrl, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ content })
                                                        });
                                                        
                                                        console.log('[SiteSettings] Ответ сервера:', resp.status, resp.statusText);
                                                        
                                                        if (!resp.ok) {
                                                            const errorText = await resp.text();
                                                            console.error('[SiteSettings] Ошибка ответа сервера:', errorText);
                                                            throw new Error(`HTTP ${resp.status}: ${errorText.substring(0, 100)}`);
                                                        }
                                                        
                                                        const j = await resp.json();
                                                        console.log('[SiteSettings] Ответ JSON:', j);
                                                        
                                                        if (j && j.success) {
                                                            // Обновляем список файлов имен
                                                            await refreshNameFilesList();
                                                            // Устанавливаем загруженный файл как текущий
                                                            handleInputChange('', 'namesFile', filename);
                                                            window.dispatchEvent(new CustomEvent('app:toast', {
                                                                detail: { type: 'success', message: `Файл имен ${filename} успешно загружен` }
                                                            }));
                                                        } else {
                                                            window.dispatchEvent(new CustomEvent('app:toast', {
                                                                detail: { type: 'error', message: 'Не удалось загрузить файл имен' }
                                                            }));
                                                        }
                                                    } catch (e) {
                                                        console.error('Ошибка загрузки файла имен:', e);
                                                        window.dispatchEvent(new CustomEvent('app:toast', {
                                                            detail: { type: 'error', message: `Ошибка загрузки файла имен: ${e.message}` }
                                                        }));
                                                    }
                                                    // Сбрасываем input чтобы можно было загрузить тот же файл снова
                                                    ev.target.value = '';
                                                }}
                                                style={{ display: 'none' }}
                                                id="namesFileUpload"
                                            />
                                            <button
                                                type="button"
                                                className={s.importBtn}
                                                onClick={() => document.getElementById('namesFileUpload')?.click()}
                                            >
                                                {isNova
                                                    ? <Upload size={14} strokeWidth={2.2} aria-hidden="true" />
                                                    : <img src={importButtonIcon} alt="Upload" className={s.buttonIcon} />}
                                                Загрузить
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={timezoneIcon} alt="Timezone" className={s.sectionIcon} />
                                            {fieldGlyph(Earth)}
                                            Часовой пояс
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Часовой пояс для отображения времени транзакций и других временных меток
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.timezone || 'Asia/Kuala_Lumpur'}
                                            onChange={(e) => handleInputChange('', 'timezone', e.target.value)}
                                            optionIcon={(tzValue) => {
                                                const tz = timezones.find((t) => t.value === tzValue);
                                                const code = emojiFlagToCode(tz?.label);
                                                return <FlagIcon code={code || 'unknown'} className={s.novaSelectFlag} alt="" />;
                                            }}
                                        >
                                            {timezones.map(tz => (
                                                <option key={tz.value} value={tz.value}>
                                                    {isNova ? stripEmojiFlag(tz.label) : tz.label}
                                                </option>
                                            ))}
                                        </ThemedSelect>
                                    </div>
                                </div>
                                {/* Настройки отображения даты и времени */}
                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}><CalendarClock size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                <div className={s.switchRowText}>
                                                    <span className={s.switchRowName}>Отображение даты и времени</span>
                                                    <span className={s.switchRowHint}>Показывать дату с временем или только дату в модалах (в чатах всегда только дата)</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.dateTimeSettings?.showDateTime !== false}
                                                    onChange={(e) => handleInputChange('', 'dateTimeSettings.showDateTime', e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={timezoneIcon} alt="DateTime Display" className={s.sectionIcon} />
                                                {fieldGlyph(CalendarClock)}
                                                Отображение даты и времени
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Показывать дату с временем или только дату в модалах (в чатах всегда только дата)
                                            </div>
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label className={s.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={s.checkbox}
                                                    checked={localSettings.dateTimeSettings?.showDateTime !== false}
                                                    onChange={(e) => handleInputChange('', 'dateTimeSettings.showDateTime', e.target.checked)}
                                                />
                                                <span className={s.checkboxCustom}></span>
                                                Показывать время вместе с датой
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={timezoneIcon} alt="Date Format" className={s.sectionIcon} />
                                            {fieldGlyph(CalendarDays)}
                                            Формат даты
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите формат отображения даты
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.dateTimeSettings?.dateFormat || 'dd/MM/yyyy'}
                                            onChange={(e) => handleInputChange('', 'dateTimeSettings.dateFormat', e.target.value)}
                                        >
                                            <option value="dd/MM/yyyy">31/12/2024 (DD/MM/YYYY)</option>
                                            <option value="MM/dd/yyyy">12/31/2024 (MM/DD/YYYY)</option>
                                            <option value="yyyy-MM-dd">2024-12-31 (YYYY-MM-DD)</option>
                                            <option value="dd.MM.yyyy">31.12.2024 (DD.MM.YYYY)</option>
                                            <option value="dd-MM-yyyy">31-12-2024 (DD-MM-YYYY)</option>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={timezoneIcon} alt="Time Format" className={s.sectionIcon} />
                                            {fieldGlyph(Clock)}
                                            Формат времени
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите формат отображения времени (24-часовой или 12-часовой)
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.dateTimeSettings?.timeFormat || '24h'}
                                            onChange={(e) => handleInputChange('', 'dateTimeSettings.timeFormat', e.target.value)}
                                        >
                                            <option value="24h">23:45 (24-часовой формат)</option>
                                            <option value="12h">11:45 PM (12-часовой формат)</option>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="#3b82f6"
                                                className={s.sectionIcon}
                                            >
                                                <path d="M21.6,9.84A4.57,4.57,0,0,1,21.18,9,4,4,0,0,1,21,8.07a4.21,4.21,0,0,0-.64-2.16,4.25,4.25,0,0,0-1.87-1.28,4.77,4.77,0,0,1-.85-.43A5.11,5.11,0,0,1,17,3.54a4.2,4.2,0,0,0-1.8-1.4A4.22,4.22,0,0,0,13,2.21a4.24,4.24,0,0,1-1.94,0,4.22,4.22,0,0,0-2.24-.07A4.2,4.2,0,0,0,7,3.54a5.11,5.11,0,0,1-.66.66,4.77,4.77,0,0,1-.85.43,4.25,4.25,0,0,0-1.87,1.28A4.19,4.19,0,0,0,3,8.07,4,4,0,0,1,2.82,9a4.57,4.57,0,0,1-.42.82A4.3,4.3,0,0,0,1.63,12a4.3,4.3,0,0,0,.77,2.16,4,4,0,0,1,.42.82,4.11,4.11,0,0,1,.15.95,4.19,4.19,0,0,0,.64,2.16,4.25,4.25,0,0,0,1.87,1.28,4.77,4.77,0,0,1,.85.43,5.11,5.11,0,0,1,.66.66,4.12,4.12,0,0,0,1.8,1.4,3,3,0,0,0,.87.13A6.66,6.66,0,0,0,11,21.81a4,4,0,0,1,1.94,0,4.33,4.33,0,0,0,2.24.06,4.12,4.12,0,0,0,1.8-1.4,5.11,5.11,0,0,1,.66-.66,4.77,4.77,0,0,1,.85-.43,4.25,4.25,0,0,0,1.87-1.28A4.19,4.19,0,0,0,21,15.94a4.11,4.11,0,0,1,.15-.95,4.57,4.57,0,0,1,.42-.82A4.3,4.3,0,0,0,22.37,12,4.3,4.3,0,0,0,21.6,9.84ZM16.71,10.71l-5,5a1,1,0,0,1-1.42,0l-3-3a1,1,0,0,1,1.42-1.42L11,13.59l4.29-4.3a1,1,0,0,1,1.42,1.42Z"/>
                                            </svg>
                                            {fieldGlyph(BadgeCheck)}
                                            {isNova ? 'Галочка верификации' : 'Настройка синей галочки верификации'}
                                        </div>
                                        <div className={s.fieldDescription}>
                                            {isNova
                                                ? 'Сколько секунд галочка на аватаре горит после нажатия'
                                                : 'Управление отображением синей галочки верификации в профиле'}
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        {isNova ? (
                                            <div className={s.verifyCtl}>
                                                <div className={s.verifyCtlRow}>
                                                    <span className={s.verifyCtlText}>
                                                        <span className={s.verifyCtlTitle}>Время свечения</span>
                                                        <span className={s.verifyCtlHint}>после нажатия на аватар</span>
                                                    </span>
                                                    <div className={`${s.verifyDuration} ${localSettings.verificationAlwaysOn ? s.verifyDurationOff : ''}`}>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="300"
                                                            value={localSettings.verificationIconDuration || 15}
                                                            onChange={(e) => handleInputChange('', 'verificationIconDuration', parseInt(e.target.value))}
                                                            disabled={localSettings.verificationAlwaysOn}
                                                        />
                                                        <span className={s.verifyDurationUnit}>сек</span>
                                                    </div>
                                                </div>
                                                <label className={s.verifyCtlRow} style={{ cursor: 'pointer' }}>
                                                    <span className={s.verifyCtlText}>
                                                        <span className={s.verifyCtlTitle}>Гореть всегда</span>
                                                        <span className={s.verifyCtlHint}>галочка не гаснет по таймеру</span>
                                                    </span>
                                                    <input
                                                        type="checkbox"
                                                        className={s.novaSwitch}
                                                        checked={localSettings.verificationAlwaysOn || false}
                                                        onChange={(e) => handleInputChange('', 'verificationAlwaysOn', e.target.checked)}
                                                    />
                                                </label>
                                            </div>
                                        ) : (
                                        <div className={s.verificationSettings}>
                                            <div className={s.verificationRow}>
                                                <label className={s.durationLabel}>Время показа (сек):</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="300"
                                                    className={s.durationInput}
                                                    value={localSettings.verificationIconDuration || 15}
                                                    onChange={(e) => handleInputChange('', 'verificationIconDuration', parseInt(e.target.value))}
                                                    disabled={localSettings.verificationAlwaysOn}
                                                />
                                            </div>
                                            <div className={s.verificationRow}>
                                                <label className={s.customCheckbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={localSettings.verificationAlwaysOn || false}
                                                        onChange={(e) => handleInputChange('', 'verificationAlwaysOn', e.target.checked)}
                                                    />
                                                    <span className={s.checkmark}></span>
                                                    <span className={s.checkboxLabel}>Всегда показывать синюю галочку</span>
                                                </label>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="#3b82f6"
                                                className={s.sectionIcon}
                                            >
                                                <path d="M12 5v14m-7-7l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {fieldGlyph(SendHorizontal)}
                                            Настройки поведения кнопки отправки
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите, как будет вести себя кнопка отправки при нажатии: анимация, модалы, состояния
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.sendButtonBehavior || 'default'}
                                            onChange={(e) => { handleInputChange('', 'sendButtonBehavior', e.target.value); setSendPreviewState('default'); sendPreviewTimers.current.forEach(clearTimeout); }}
                                        >
                                            <option value="default">{isNova ? 'По умолчанию (обработка → успех → исходная)' : 'По умолчанию (анимация → успех → синяя)'}</option>
                                            <option value="showCommission">Показать модал Комиссии → красная кнопка</option>
                                            <option value="showVerification">Показать модал Верификации → красная кнопка</option>
                                            <option value="showSuccess">Показать модал Успеха → зеленая кнопка</option>
                                            <option value="showTax">Показать модал Налога → красная кнопка</option>
                                            <option value="showCryptoConversion">Показать модал Крипто конвертации → красная кнопка</option>
                                            <option value="showBankConversion">Показать модал Банк конвертации → красная кнопка</option>
                                            <option value="showPremium">Показать модал Премиум доступа → красная кнопка</option>
                                        </ThemedSelect>
                                        {isNova && (
                                            <div className={s.sendPreview}>
                                                <span className={s.sendPreviewLabel}>Предпросмотр — можно нажать</span>
                                                <button
                                                    type="button"
                                                    className={`${s.sendPreviewBtn} ${s['spState_' + sendPreviewState] || ''}`}
                                                    onClick={runSendPreview}
                                                    disabled={sendPreviewState !== 'default'}
                                                >
                                                    {sendPreviewState === 'loading' && (<><span className={s.sendPreviewSpinner} aria-hidden="true" />Обработка…</>)}
                                                    {sendPreviewState === 'success' && (<><Check size={15} strokeWidth={2.2} aria-hidden="true" />Успешно</>)}
                                                    {sendPreviewState === 'error' && (<><XGlyph size={15} strokeWidth={2.2} aria-hidden="true" />Ошибка</>)}
                                                    {sendPreviewState === 'default' && (<><ArrowUp size={15} strokeWidth={2.2} aria-hidden="true" />Конвертировать</>)}
                                                </button>
                                                <span className={s.sendPreviewHint}>{sendPreviewHint()}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={znakovPosleZapyatoiIcon} alt="Decimal Places" className={s.sectionIcon} />
                                            {fieldGlyph(Hash)}
                                            Знаков после запятой для крипты
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Количество знаков после запятой при отображении криптовалют (от 0 до 8)
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        {isNova ? (
                                            <NovaSegmented
                                                ariaLabel="Знаков после запятой"
                                                value={localSettings.cryptoDecimalPlaces ?? 5}
                                                onChange={(n) => handleInputChange('', 'cryptoDecimalPlaces', n)}
                                                options={[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: String(n), color: 'var(--accent)' }))}
                                            />
                                        ) : (
                                        <input
                                            type="number"
                                            min="0"
                                            max="8"
                                            className={s.numberInput}
                                            value={localSettings.cryptoDecimalPlaces || 5}
                                            onChange={(e) => handleInputChange('', 'cryptoDecimalPlaces', parseInt(e.target.value))}
                                        />
                                        )}
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={currencySymbolIcon} alt="Currency Symbol" className={s.sectionIcon} />
                                            {fieldGlyph(CircleDollarSign)}
                                            Символ валюты
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Символ или аббревиатура валюты для отображения в приложении (например: {localSettings.currencyFormat || 'RM'}, $, €, ₽)
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="text"
                                            className={s.input}
                                            placeholder="RM"
                                            maxLength="5"
                                            value={localSettings.currencyFormat || 'RM'}
                                            onChange={(e) => handleInputChange('', 'currencyFormat', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <FlagIcon code={localSettings.selectedFlag || 'unknown'} className={s.sectionIcon} alt="Displayed region" />
                                            {isNova && (
                                                <span className={s.fieldGlyph}>
                                                    <FlagIcon code={localSettings.selectedFlag || 'unknown'} className={s.fieldGlyphFlag} alt="" />
                                                </span>
                                            )}
                                            Отображаемый флаг
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Флаг страны, который будет отображаться в NavBar, MailBox и Wallet
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.selectedFlag || 'unknown'}
                                            onChange={(e) => handleInputChange('', 'selectedFlag', e.target.value)}
                                            optionIcon={(code) => <FlagIcon code={code || 'unknown'} className={s.novaSelectFlag} alt="" />}
                                        >
                                            <optgroup label="🌐 Без региона">
                                                <option value="unknown">Unknown</option>
                                            </optgroup>
                                            <optgroup label="🌟 Популярные страны">
                                                {popularCountries.map(code => {
                                                    const flagInfo = getFlagInfo(code);
                                                    return (
                                                        <option key={code} value={code}>
                                                            {flagInfo.name}
                                                        </option>
                                                    );
                                                })}
                                            </optgroup>
                                            <optgroup label="🌍 Все страны">
                                                {getFlatFlagsList()
                                                    .filter(flag => flag.code !== 'unknown' && !popularCountries.includes(flag.code))
                                                    .map(flag => (
                                                        <option key={flag.code} value={flag.code}>
                                                            {flag.name} ({flag.region})
                                                        </option>
                                                    ))}
                                            </optgroup>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={siteSettingsIcon} alt="IRS Background" className={s.sectionIcon} />
                                            {fieldGlyph(Wallpaper)}
                                            Фоновое изображение окна налога
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Фоновое изображение для модального окна налога IRS
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.irsBackground || (availableIrsBackgrounds.length > 0 ? availableIrsBackgrounds[0].fullPath : 'img/irs.png')}
                                            onChange={(e) => handleInputChange('', 'irsBackground', e.target.value)}
                                                style={{ flex: 1 }}
                                        >
                                            {availableIrsBackgrounds.length > 0 ? (
                                                availableIrsBackgrounds.map((bg, index) => (
                                                    <option key={index} value={bg.fullPath}>
                                                        {bg.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="img/irs.png">Нет доступных фоновых изображений</option>
                                            )}
                                        </ThemedSelect>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={async (ev) => {
                                                    const file = ev.target.files?.[0];
                                                    if (!file) return;
                                                    
                                                    try {
                                                        // Читаем файл как base64
                                                        const base64 = await new Promise((res, rej) => {
                                                            const fr = new FileReader();
                                                            fr.onload = () => res(fr.result);
                                                            fr.onerror = rej;
                                                            fr.readAsDataURL(file);
                                                        });
                                                        
                                                        const filename = file.name;
                                                        const apiUrl = getApiUrl(`api/files/irsbackgrounds/${encodeURIComponent(filename)}`);
                                                        console.log('[SiteSettings] Загружаем фоновое изображение IRS:', filename, 'URL:', apiUrl);
                                                        
                                                        const resp = await fetch(apiUrl, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ data: base64 })
                                                        });
                                                        
                                                        console.log('[SiteSettings] Ответ сервера:', resp.status, resp.statusText);
                                                        
                                                        if (!resp.ok) {
                                                            const errorText = await resp.text();
                                                            console.error('[SiteSettings] Ошибка ответа сервера:', errorText);
                                                            throw new Error(`HTTP ${resp.status}: ${errorText.substring(0, 100)}`);
                                                        }
                                                        
                                                        const j = await resp.json();
                                                        console.log('[SiteSettings] Ответ JSON:', j);
                                                        
                                                        if (j && j.success) {
                                                            // Обновляем список фоновых изображений
                                                            await refreshIrsBackgroundsList();
                                                            // Устанавливаем загруженное изображение как текущее
                                                            handleInputChange('', 'irsBackground', `irsbackgrounds/${filename}`);
                                                            window.dispatchEvent(new CustomEvent('app:toast', {
                                                                detail: { type: 'success', message: `Фоновое изображение ${filename} успешно загружено` }
                                                            }));
                                                        } else {
                                                            window.dispatchEvent(new CustomEvent('app:toast', {
                                                                detail: { type: 'error', message: 'Не удалось загрузить фоновое изображение' }
                                                            }));
                                                        }
                                                    } catch (e) {
                                                        console.error('Ошибка загрузки фонового изображения IRS:', e);
                                                        window.dispatchEvent(new CustomEvent('app:toast', {
                                                            detail: { type: 'error', message: `Ошибка загрузки фонового изображения: ${e.message}` }
                                                        }));
                                                    }
                                                    // Сбрасываем input чтобы можно было загрузить тот же файл снова
                                                    ev.target.value = '';
                                                }}
                                                style={{ display: 'none' }}
                                                id="irsBackgroundUpload"
                                            />
                                            <button
                                                type="button"
                                                className={s.importBtn}
                                                onClick={() => document.getElementById('irsBackgroundUpload')?.click()}
                                            >
                                                {isNova
                                                    ? <Upload size={14} strokeWidth={2.2} aria-hidden="true" />
                                                    : <img src={importButtonIcon} alt="Upload" className={s.buttonIcon} />}
                                                Загрузить
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={znakovPosleZapyatoiIcon} alt="Number Format" className={s.sectionIcon} />
                                            {fieldGlyph(Calculator)}
                                            Формат чисел
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Как отображать суммы: с точкой или запятой в качестве разделителя
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <ThemedSelect nova={isNova}
                                            className={s.select}
                                            value={localSettings.numberFormat || 'comma'}
                                            onChange={(e) => handleInputChange('', 'numberFormat', e.target.value)}
                                        >
                                            <option value="comma">10,294.98 (запятая для тысяч, точка для десятых)</option>
                                            <option value="dot">10.294,98 (точка для тысяч, запятая для десятых)</option>
                                        </ThemedSelect>
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={userInfoIcon} alt="Bank Account Display" className={s.sectionIcon} />
                                            {fieldGlyph(CreditCard)}
                                            Отображение банковского счета
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Название поля для банковского счета, которое будет отображаться во всем приложении вместо "IBAN/BIC"
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="text"
                                            className={s.input}
                                            placeholder="IBAN/BIC"
                                            maxLength="20"
                                            value={localSettings.bankAccountDisplayName || 'IBAN/BIC'}
                                            onChange={(e) => handleInputChange('', 'bankAccountDisplayName', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* ── Режим оплаты: крипта ↔ банковский счёт.
                                    При включении модалки оплаты и раздел кошельков
                                    переключаются с крипто-адресов на банковские счета. */}
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={userInfoIcon} alt="Bank Payment Mode" className={s.sectionIcon} />
                                            {fieldGlyph(Landmark)}
                                            Оплата банковским счётом (вместо крипты)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            В модальных окнах оплаты (комиссия, верификация, налог, конвертация, премиум) вместо крипто-адреса и QR показывается банковский счёт с логотипом банка. Счета настраиваются ниже, в разделе «Кошельки».
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <label className={s.qaToggle} title="Оплата банком вместо крипты">
                                            <input
                                                type="checkbox"
                                                className={s.novaSwitch}
                                                checked={!!localSettings.bankPaymentMode}
                                                onChange={(e) => handleInputChange('', 'bankPaymentMode', e.target.checked)}
                                            />
                                            {localSettings.bankPaymentMode ? 'Включено' : 'Выключено'}
                                        </label>
                                    </div>
                                </div>

                                {/* ── Nova: Быстрые действия — один блок, порядок перетаскиванием */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(Zap)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Быстрые действия в админке</div>
                                            <div className={s.stackDesc}>Порядок кнопок меняется перетаскиванием за ручку — как они будут стоять в админ-панели</div>
                                        </div>
                                    </div>
                                    <div className={s.qaList} ref={qaListRef}>
                                        {qaOrder.map((actionKey, index) => {
                                            const action = (localSettings.quickActions || {})[actionKey];
                                            if (!action) return null;
                                            return (
                                                <div
                                                    key={actionKey}
                                                    data-qa-key={actionKey}
                                                    className={`${s.qaRow} ${qaDragKey === actionKey ? s.qaRowDrag : ''}`}
                                                >
                                                    <span
                                                        className={s.qaHandle}
                                                        onPointerDown={(e) => startQaDrag(e, actionKey)}
                                                        title="Перетащить"
                                                    >
                                                        <GripVertical size={14} strokeWidth={2} aria-hidden="true" />
                                                    </span>
                                                    <span className={s.qaIndex}>{index + 1}</span>
                                                    <div className={s.qaInfo}>
                                                        <span className={s.qaName}>{getDefaultActionName(actionKey)}</span>
                                                        <span className={s.qaKey}>{actionKey}</span>
                                                    </div>
                                                    <label className={s.qaToggle}>
                                                        <input
                                                            type="checkbox"
                                                            className={s.novaSwitch}
                                                            checked={action.enabled !== false}
                                                            onChange={(e) => handleInputChange('', `quickActions.${actionKey}.enabled`, e.target.checked)}
                                                        />
                                                        Включено
                                                    </label>
                                                    <label className={s.qaToggle}>
                                                        <input
                                                            type="checkbox"
                                                            className={s.novaSwitch}
                                                            checked={action.emailEnabled !== false}
                                                            onChange={(e) => handleInputChange('', `quickActions.${actionKey}.emailEnabled`, e.target.checked)}
                                                        />
                                                        Письма
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                )}

                                {/* ── Nova: Генерация сумм — один блок с диаграммой весов */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(Coins)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Генерация сумм в бинпале</div>
                                            <div className={s.stackDesc}>Диапазоны сумм для таблицы: чем больше вес, тем чаще выпадают суммы из диапазона</div>
                                        </div>
                                    </div>
                                    {(() => {
                                        const ranges = localSettings.brokerAmountGeneration?.ranges || [];
                                        const totalWeight = ranges.reduce((sum, r) => sum + (parseFloat(r.weight) || 0), 0) || 1;
                                        const setRanges = (next) => handleInputChange('brokerAmountGeneration', 'ranges', next);
                                        return (
                                            <>
                                                {/* Диаграмма распределения весов */}
                                                {ranges.length > 0 && (
                                                    <div className={s.rangeMeter} aria-hidden="true">
                                                        {ranges.map((range, i) => (
                                                            <span
                                                                key={range.id}
                                                                className={s.rangeMeterSeg}
                                                                style={{
                                                                    width: `${((parseFloat(range.weight) || 0) / totalWeight) * 100}%`,
                                                                    background: RANGE_COLORS[i % RANGE_COLORS.length],
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                                <div className={s.rangeList}>
                                                    {ranges.map((range, index) => (
                                                        <div key={range.id} className={s.rangeRow}>
                                                            <span className={s.rangeDot} style={{ background: RANGE_COLORS[index % RANGE_COLORS.length] }} aria-hidden="true" />
                                                            <div className={s.rangeFields}>
                                                                <label className={s.rangeField}>
                                                                    <span>Название</span>
                                                                    <input
                                                                        type="text"
                                                                        value={range.name}
                                                                        onChange={(e) => {
                                                                            const next = [...ranges];
                                                                            next[index] = { ...range, name: e.target.value };
                                                                            setRanges(next);
                                                                        }}
                                                                    />
                                                                </label>
                                                                <label className={s.rangeField}>
                                                                    <span>Мин. сумма</span>
                                                                    <input
                                                                        type="number"
                                                                        value={range.min}
                                                                        onChange={(e) => {
                                                                            const next = [...ranges];
                                                                            next[index] = { ...range, min: parseNumber(e.target.value) };
                                                                            setRanges(next);
                                                                        }}
                                                                    />
                                                                </label>
                                                                <label className={s.rangeField}>
                                                                    <span>Макс. сумма</span>
                                                                    <input
                                                                        type="number"
                                                                        value={range.max}
                                                                        onChange={(e) => {
                                                                            const next = [...ranges];
                                                                            next[index] = { ...range, max: parseNumber(e.target.value) };
                                                                            setRanges(next);
                                                                        }}
                                                                    />
                                                                </label>
                                                                <label className={`${s.rangeField} ${s.rangeFieldWeight}`}>
                                                                    <span>Вес</span>
                                                                    <div className={s.rangeWeightBox}>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            max="100"
                                                                            value={range.weight}
                                                                            onChange={(e) => {
                                                                                const next = [...ranges];
                                                                                next[index] = { ...range, weight: parseNumber(e.target.value, true) };
                                                                                setRanges(next);
                                                                            }}
                                                                        />
                                                                        <span className={s.rangeWeightUnit}>%</span>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className={s.deleteRangeButton}
                                                                title="Удалить диапазон"
                                                                onClick={() => setRanges(ranges.filter((_, i) => i !== index))}
                                                            >
                                                                <Trash2 size={13} strokeWidth={2.2} aria-hidden="true" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    className={s.addRangeButton}
                                                    onClick={() => setRanges([...ranges, { id: `range_${Date.now()}`, name: 'Новый диапазон', min: 100000, max: 1000000, weight: 10 }])}
                                                >
                                                    + Добавить диапазон сумм
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                                )}

                                {!isNova && (<>
                                {/* Quick Actions Configuration */}
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={quickActionsIcon} alt="Quick Actions" className={s.sectionIcon} />
                                            {fieldGlyph(Zap)}
                                            Быстрые действия в админке
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Управление порядком и доступностью кнопок быстрых действий в админ-панели
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions List */}
                                {Object.entries(localSettings.quickActions || {})
                                    .sort(([,a], [,b]) => (a.priority || 999) - (b.priority || 999))
                                    .map(([actionKey, action], index) => (
                                    <div key={actionKey} className={s.quickActionCard}>
                                        <div className={s.quickActionHeader}>
                                            <div className={s.quickActionInfo}>
                                                <div className={s.quickActionName}>
                                                    {getDefaultActionName(actionKey)}
                                                </div>
                                                <div className={s.quickActionKey}>
                                                    Ключ: {actionKey}
                                                </div>
                                            </div>
                                            <div className={s.quickActionControls}>
                                                <div className={s.quickActionEnabled}>
                                                    <label className={s.checkboxLabel}>
                                                        <input
                                                            type="checkbox"
                                                            checked={action.enabled !== false}
                                                            onChange={(e) => handleInputChange('', `quickActions.${actionKey}.enabled`, e.target.checked)}
                                                            className={s.checkbox}
                                                        />
                                                        <span className={s.checkboxText}>Включено</span>
                                                    </label>
                                                    <label className={s.checkboxLabel} style={{ marginLeft: 16 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={action.emailEnabled !== false}
                                                            onChange={(e) => handleInputChange('', `quickActions.${actionKey}.emailEnabled`, e.target.checked)}
                                                            className={s.checkbox}
                                                        />
                                                        <span className={s.checkboxText}>Отправка писем</span>
                                                    </label>
                                                </div>
                                                <div className={s.quickActionPriority}>
                                                    <label className={s.priorityLabel}>
                                                        Приоритет:
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="20"
                                                            value={action.priority || 1}
                                                            onChange={(e) => handleInputChange('', `quickActions.${actionKey}.priority`, parseInt(e.target.value))}
                                                            className={s.priorityInput}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className={s.amountGenerationSection}>
                                    <div className={s.amountGenerationTitle}>
                                        <img src={siteSettingsIcon} alt="Amount Generation" className={s.sectionIcon} />
                                        {fieldGlyph(Coins)}
                                        Генерация сумм в бинпале
                                    </div>
                                    <div className={s.amountGenerationDescription}>
                                        Настройка диапазона сумм и их частоты появления в таблице
                                    </div>
                                </div>
                                {/* Диапазоны сумм */}
                                {(localSettings.brokerAmountGeneration?.ranges || []).map((range, index) => (
                                    <div key={range.id} className={s.amountRangeCard}>
                                        <div className={s.amountRangeHeader}>
                                            <div className={s.amountRangeInfo}>
                                                <div className={s.amountRangeName}>
                                                    {range.name}
                                                </div>
                                                <div className={s.amountRangeDetails}>
                                                    Диапазон от {range.min?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} до {range.max?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}, вес: {range.weight}%
                                                </div>
                                            </div>
                                            <button 
                                                className={s.deleteRangeButton}
                                                onClick={() => {
                                                    const newRanges = localSettings.brokerAmountGeneration.ranges.filter((_, i) => i !== index);
                                                    handleInputChange('brokerAmountGeneration', 'ranges', newRanges);
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        
                                        <div className={s.amountRangeFields}>
                                            <div className={s.amountFieldGroup}>
                                                <label className={s.amountFieldLabel}>Название</label>
                                                <input
                                                    type="text"
                                                    className={s.amountInput}
                                                    value={range.name}
                                                    onChange={(e) => {
                                                        const newRanges = [...localSettings.brokerAmountGeneration.ranges];
                                                        newRanges[index] = { ...range, name: e.target.value };
                                                        handleInputChange('brokerAmountGeneration', 'ranges', newRanges);
                                                    }}
                                                />
                                            </div>
                                            <div className={s.amountFieldGroup}>
                                                <label className={s.amountFieldLabel}>Мин. сумма</label>
                                                <input
                                                    type="number"
                                                    className={s.amountInput}
                                                    value={range.min}
                                                    onChange={(e) => {
                                                        const newRanges = [...localSettings.brokerAmountGeneration.ranges];
                                                        newRanges[index] = { ...range, min: parseNumber(e.target.value) };
                                                        handleInputChange('brokerAmountGeneration', 'ranges', newRanges);
                                                    }}
                                                />
                                            </div>
                                            <div className={s.amountFieldGroup}>
                                                <label className={s.amountFieldLabel}>Макс. сумма</label>
                                                <input
                                                    type="number"
                                                    className={s.amountInput}
                                                    value={range.max}
                                                    onChange={(e) => {
                                                        const newRanges = [...localSettings.brokerAmountGeneration.ranges];
                                                        newRanges[index] = { ...range, max: parseNumber(e.target.value) };
                                                        handleInputChange('brokerAmountGeneration', 'ranges', newRanges);
                                                    }}
                                                />
                                            </div>
                                            <div className={s.amountFieldGroup}>
                                                <label className={s.amountFieldLabel}>Вес (%)</label>
                                                <input
                                                    type="number"
                                                    className={s.amountInput}
                                                    value={range.weight}
                                                    min="1"
                                                    max="100"
                                                    onChange={(e) => {
                                                        const newRanges = [...localSettings.brokerAmountGeneration.ranges];
                                                        newRanges[index] = { ...range, weight: parseNumber(e.target.value, true) };
                                                        handleInputChange('brokerAmountGeneration', 'ranges', newRanges);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Кнопка добавления нового диапазона */}
                                <button 
                                    className={s.addRangeButton}
                                    onClick={() => {
                                        const newRange = {
                                            id: `range_${Date.now()}`,
                                            name: 'Новый диапазон',
                                            min: 100000,
                                            max: 1000000,
                                            weight: 10
                                        };
                                        const currentRanges = localSettings.brokerAmountGeneration?.ranges || [];
                                        handleInputChange('brokerAmountGeneration', 'ranges', [...currentRanges, newRange]);
                                    }}
                                >
                                    + Добавить диапазон сумм
                                </button>
                                </>)}

                                {/* Тумблер: разрешить одноразовое редактирование имён в таблице
                                    «Последние выводы» брокера (двойной клик по имени → ввод своего).
                                    Значение временное, нигде не сохраняется. */}
                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}><PencilLine size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                <div className={s.switchRowText}>
                                                    <span className={s.switchRowName}>Редактирование строк в бинпале</span>
                                                    <span className={s.switchRowHint}>Двойной клик по значению в таблице «Последние выводы» — правка одноразовая, нигде не сохраняется</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.brokerEditableNames || false}
                                                    onChange={(e) => handleInputChange('', 'brokerEditableNames', e.target.checked)}
                                                />
                                            </label>
                                            {localSettings.brokerEditableNames && (
                                                <div className={s.switchNestedGroup}>
                                                    {BROKER_EDIT_FIELDS.map(({ key, label, hint, Icon }) => (
                                                        <label key={key} className={`${s.switchRow} ${s.switchRowNested}`}>
                                                            <span className={s.switchRowGlyph}><Icon size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                            <div className={s.switchRowText}>
                                                                <span className={s.switchRowName}>{label}</span>
                                                                <span className={s.switchRowHint}>{hint}</span>
                                                            </div>
                                                            <input
                                                                type="checkbox"
                                                                className={s.novaSwitch}
                                                                checked={localSettings.brokerEditableFields?.[key] !== false}
                                                                onChange={(e) => handleInputChange('', `brokerEditableFields.${key}`, e.target.checked)}
                                                            />
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={siteSettingsIcon} alt="Edit rows" className={s.sectionIcon} />
                                                {fieldGlyph(PencilLine)}
                                                Редактирование строк в бинпале
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Двойной клик по значению в таблице «Последние выводы» — правка одноразовая, нигде не сохраняется
                                            </div>
                                            {localSettings.brokerEditableNames && (
                                                <div className={s.brokerEditNest}>
                                                    {BROKER_EDIT_FIELDS.map(({ key, label, hint, Icon }) => (
                                                        <label key={key} className={s.brokerEditNestRow}>
                                                            <input
                                                                type="checkbox"
                                                                className={s.checkbox}
                                                                checked={localSettings.brokerEditableFields?.[key] !== false}
                                                                onChange={(e) => handleInputChange('', `brokerEditableFields.${key}`, e.target.checked)}
                                                            />
                                                            <span className={s.checkboxCustom}></span>
                                                            <span className={s.brokerEditNestGlyph}><Icon size={13} strokeWidth={1.9} aria-hidden="true" /></span>
                                                            <span className={s.brokerEditNestText}>
                                                                <span className={s.brokerEditNestName}>{label}</span>
                                                                <span className={s.brokerEditNestHint}>{hint}</span>
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label className={s.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={s.checkbox}
                                                    checked={localSettings.brokerEditableNames || false}
                                                    onChange={(e) => handleInputChange('', 'brokerEditableNames', e.target.checked)}
                                                />
                                                <span className={s.checkboxCustom}></span>
                                                Разрешить редактирование
                                            </label>
                                        </div>
                                    </div>
                                )}


                            </div>
                            <div className={s.helpText}>
                                Как работает вес: Чем больше вес диапазона, тем чаще будут генерироваться суммы из этого диапазона. Например: диапазон с весом 50% будет использоваться в 2 раза чаще диапазона с весом 25%.
                            </div>
                        </div>
                        </div>
                      </>
                  )}
                {activeTab === 'finance' && (
                    <>
                        {/* Crypto Rates Section */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={`${s.firstSectionWithTabs} ${s.cryptoField}`}>
                            <h3 className={s.sectionTitle}>
                                <img src={icon1} alt="Currency Rates" className={s.sectionIcon} />
                                {isNova && <Bitcoin size={13} strokeWidth={2.2} aria-hidden="true" />}
                                Курсы криптовалют
                            </h3>
                            <div className={s.sectionContent}>
                                {/* ── Nova: компактный список курсов */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(Bitcoin)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Курсы к местной валюте</div>
                                            <div className={s.stackDesc}>Сколько {localSettings.currencyFormat || 'RM'} стоит одна единица криптовалюты — по этим курсам считаются суммы в модалах</div>
                                        </div>
                                    </div>
                                    <div className={s.rateList}>
                                        {[
                                            ['BTC', 'Bitcoin'],
                                            ['ETH', 'Ethereum'],
                                            ['BNB', 'Binance Coin'],
                                            ['USDC', 'USD Coin'],
                                            ['SOL', 'Solana'],
                                        ].map(([sym, name]) => (
                                            <div key={sym} className={s.rateRow}>
                                                <img src={getCryptoIcon(sym)} alt={name} className={s.rateIcon} />
                                                <div className={s.rateInfo}>
                                                    <span className={s.rateName}>{name}</span>
                                                    <span className={s.rateTicker}>{sym}</span>
                                                </div>
                                                <span className={s.rateEq}>1 {sym} =</span>
                                                <label className={s.rateInputBox}>
                                                    <input
                                                        type="number"
                                                        step={sym === 'USDC' ? '0.01' : undefined}
                                                        value={localSettings.cryptoRates?.[sym] ?? ''}
                                                        onChange={(e) => handleInputChange('cryptoRates', sym, parseNumber(e.target.value))}
                                                    />
                                                    <span className={s.rateCur}>{localSettings.currencyFormat || 'RM'}</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                )}

                                {!isNova && (<>
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon('BTC')} alt="Bitcoin" className={s.cryptoIcon} />
                                            Bitcoin (BTC)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Курс Bitcoin к вашей местной валюте (1 BTC = X {localSettings.currencyFormat || 'RM'})
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="number"
                                            className={s.numberInput}
                                            value={localSettings.cryptoRates?.BTC ?? ''}
                                            onChange={(e) => handleInputChange('cryptoRates', 'BTC', parseNumber(e.target.value))}
                                        />
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon('ETH')} alt="Ethereum" className={s.cryptoIcon} />
                                            Ethereum (ETH)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Курс Ethereum к вашей местной валюте (1 ETH = X {localSettings.currencyFormat || 'RM'})
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="number"
                                            className={s.numberInput}
                                            value={localSettings.cryptoRates?.ETH ?? ''}
                                            onChange={(e) => handleInputChange('cryptoRates', 'ETH', parseNumber(e.target.value))}
                                        />
                                    </div>
                                </div>

                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon('BNB')} alt="Binance Coin" className={s.cryptoIcon} />
                                            Binance Coin (BNB)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Курс Binance Coin к вашей местной валюте (1 BNB = X {localSettings.currencyFormat || 'RM'})
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="number"
                                            className={s.numberInput}
                                            value={localSettings.cryptoRates?.BNB ?? ''}
                                            onChange={(e) => handleInputChange('cryptoRates', 'BNB', parseNumber(e.target.value))}
                                        />
                                    </div>
                                </div>
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon('USDC')} alt="USD Coin" className={s.cryptoIcon} />
                                            USD Coin (USDC)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Курс USD Coin к вашей местной валюте (1 USDC = X {localSettings.currencyFormat || 'RM'})
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={s.numberInput}
                                            value={localSettings.cryptoRates?.USDC ?? ''}
                                            onChange={(e) => handleInputChange('cryptoRates', 'USDC', parseNumber(e.target.value))}
                                        />
                                    </div>
                                </div>
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={getCryptoIcon('SOL')} alt="Solana" className={s.cryptoIcon} />
                                            Solana (SOL)
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Курс Solana к вашей местной валюте (1 SOL = X {localSettings.currencyFormat || 'RM'})
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <input
                                            type="number"
                                            className={s.numberInput}
                                            value={localSettings.cryptoRates?.SOL ?? ''}
                                            onChange={(e) => handleInputChange('cryptoRates', 'SOL', parseNumber(e.target.value))}
                                        />
                                    </div>
                                </div>
                                </>)}
                            </div>
                            <div className={s.helpText}>
                                Установите актуальные курсы криптовалют к вашей местной валюте
                            </div>
                        </div>

                        {/* Tariff Settings Section */}
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>
                                <img src={tarifsSettingsIcon} alt="Tariffs Settings" className={s.sectionIcon} />
                                {isNova && <Gem size={13} strokeWidth={2.2} aria-hidden="true" />}
                                Настройки тарифов
                            </h3>
                            <div className={s.sectionContent}>
                                {/* ── Nova: тарифы одним блоком — селектор тарифа, режим значений, список параметров */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(SlidersHorizontal)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Параметры тарифов</div>
                                            <div className={s.stackDesc}>Суммы и проценты для модалов — отдельно для каждого тарифа. Выберите тариф и заполните значения</div>
                                        </div>
                                    </div>

                                    <div className={s.tariffTabsRow}>
                                        {tariffTabs.map((tab, idx) => {
                                            const tone = [s.tPick1, s.tPick2, s.tPick3, s.tPick4, s.tPickVip][idx] || s.tPick1;
                                            const Glyph = [Coins, Banknote, Gem, Flame, Crown][idx] || Coins;
                                            return (
                                                <button
                                                    key={tab.id}
                                                    type="button"
                                                    className={`${s.tariffPick} ${tone} ${activeTariffTab === tab.id ? s.tariffPickActive : ''}`}
                                                    onClick={() => setActiveTariffTab(tab.id)}
                                                >
                                                    {/* Пунктирный контур незанятого таба (низ клипается — язычок) */}
                                                    <svg className={s.tPickDash} aria-hidden="true"><rect x="0.6" y="0.6" rx="9" /></svg>
                                                    <Glyph size={13} strokeWidth={2} aria-hidden="true" />
                                                    {tab.label.replace(' ТАРИФ', '')}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className={s.tariffPanel}>
                                    <div className={s.tariffMode}>
                                        <span className={s.tariffModeGlyph}><Shuffle size={14} strokeWidth={2} aria-hidden="true" /></span>
                                        <div className={s.tariffModeText}>
                                            <span className={s.tariffModeTitle}>Рандомить диапазоны</span>
                                            <span className={s.tariffModeHint}>
                                                {localSettings.randomizeRanges
                                                    ? 'Суммы берутся случайно из диапазона «от…до» для каждого пользователя'
                                                    : 'Используются фиксированные значения — одинаковые для всех пользователей'}
                                            </span>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className={s.novaSwitch}
                                            checked={!!localSettings.randomizeRanges}
                                            onChange={(e) => handleInputChange('', 'randomizeRanges', e.target.checked)}
                                        />
                                    </div>

                                    <div className={s.tariffList}>
                                        {renderNovaTariffRow(Trophy, 'Выигрыш пользователя', 'Итоговая сумма выигрыша для данного тарифа', 'tariffBaseAmounts', 'tariffBaseAmountsRanges')}
                                        {renderNovaTariffRow(PiggyBank, 'Депозитная сумма', 'Первоначальный взнос пользователя (для расчёта графика)', 'tariffDepositAmounts', 'tariffDepositAmountsRanges', { step: '1', isInt: true })}
                                        {renderNovaTariffRow(CircleDollarSign, 'Размер комиссии', 'Комиссия за вывод средств', 'tariffCommissions', 'tariffCommissionsRanges')}
                                        {renderNovaTariffRow(ShieldCheck, 'Сумма верификации', 'Сумма для модала верификации аккаунта', 'tariffVerificationAmounts', 'tariffVerificationAmountsRanges')}
                                        {renderNovaTariffRow(PlusCircle, 'Дополнительная сумма', 'Добавляется на вериф-платеже: софт наторговал ещё', 'tariffAdditionalAmounts', 'tariffAdditionalAmountsRanges')}
                                        {renderNovaTariffRow(Percent, 'Процент налога', 'Налог на доходы от торговли', 'tariffTaxPercents', 'tariffTaxPercentsRanges', { step: '0.1', suffix: '%' })}
                                        {renderNovaTariffRow(ArrowLeftRight, 'Комиссия за конвертацию', 'Комиссия за обмен криптовалюты на фиатную валюту', 'tariffConversionCommissions', 'tariffConversionCommissionsRanges')}
                                        {renderNovaTariffRow(Landmark, 'Банковская комиссия', 'Комиссия за перевод средств в банк', 'tariffBankFees', 'tariffBankFeesRanges')}
                                        {renderNovaTariffRow(Briefcase, 'Оплата работы брокера', 'Сколько юзер отправляет брокеру после получения прибыли', 'tariffBrokerFees', 'tariffBrokerFeesRanges')}
                                        {renderNovaTariffRow(PieChart, 'Процент от выигрыша', 'Какой процент от выигранной суммы отправляет юзер', 'tariffWinningPercentages', 'tariffWinningPercentagesRanges')}
                                        {renderNovaTariffRow(Crown, 'Взнос за премиум-доступ', 'Единоразовый взнос для перехода на уровень PREMIUM', 'tariffPremiumFees', 'tariffPremiumFeesRanges')}

                                        {/* Лимит премиума — всегда одно текстовое значение */}
                                        <div className={s.tariffRow}>
                                            <span className={s.tariffRowGlyph}><Gauge size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                            <div className={s.tariffRowInfo}>
                                                <span className={s.tariffRowName}>Лимит (премиум-доступ)</span>
                                                <span className={s.tariffRowDesc}>Лимит суммы для отображения в модале премиум-доступа</span>
                                            </div>
                                            <label className={s.tariffInputBox}>
                                                <input
                                                    type="text"
                                                    placeholder="10,000.00"
                                                    value={localSettings.tariffPremiumLimits?.[activeTariffTab] ?? ''}
                                                    onChange={(e) => handleInputChange('tariffPremiumLimits', activeTariffTab, e.target.value)}
                                                />
                                            </label>
                                        </div>

                                        {/* Кастомные поля из modalTemplates */}
                                        {localSettings.modalTemplates?.filter((template) => template.financeFieldName && template.enabled !== false).map((template) => {
                                            const actionKey = template.actionKey;
                                            const fieldKey = `tariff${actionKey.charAt(0).toUpperCase() + actionKey.slice(1)}Amounts`;
                                            const icon = template.icon || '';
                                            const isEmojiIcon = icon && !icon.startsWith('/') && !icon.startsWith('http');
                                            return renderNovaTariffRow(
                                                Puzzle,
                                                template.financeFieldName || template.name || `Кастомное поле ${actionKey}`,
                                                `Поле для модала «${template.name}»`,
                                                fieldKey,
                                                `${fieldKey}Ranges`,
                                                { emoji: isEmojiIcon ? icon : null }
                                            );
                                        })}
                                    </div>
                                    </div>
                                </div>
                                )}

                                {!isNova && (<>
                                {/* Tariff Tab Navigation */}
                                <div className={s.tariffTabNavigation}>
                                    {tariffTabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            className={`${s.tariffTabButton} ${activeTariffTab === tab.id ? s.active : ''}`}
                                            onClick={() => setActiveTariffTab(tab.id)}
                                        >
                                            <img src={tab.icon} alt={tab.label} className={s.tariffTabIcon} />
                                            <span className={s.tariffTabLabel}>{tab.label}</span>
                                        </button>
                                    ))}
                                </div>

                                {/* Tariff Tab Content */}
                                <div className={s.tariffTabContent}>
                                    {/* Галочка "Рандомить диапазоны" */}
                                    <div className={s.field} style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                Рандомить диапазоны
                                            </div>
                                            <div className={s.fieldDescription}>
                                                При включении суммы будут генерироваться случайным образом в указанных диапазонах для каждого пользователя
                                            </div>
                                        </div>
                                        <div className={s.fieldInput}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!localSettings.randomizeRanges}
                                                    onChange={(e) => {
                                                        handleInputChange('', 'randomizeRanges', e.target.checked);
                                                    }}
                                                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                />
                                                <span>Включить рандомизацию</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Выигрыш пользователя */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={usersWinningsIcon} alt="User Winnings" className={s.sectionIcon} />
                                                Выигрыш пользователя
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Итоговая сумма выигрыша для данного тарифа
                                            </div>
                                        </div>
                                        <div className={s.fieldInput} style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Фиксированная сумма</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={s.numberInput}
                                                value={localSettings.tariffBaseAmounts?.[activeTariffTab] ?? ''}
                                                onChange={(e) => handleInputChange('tariffBaseAmounts', activeTariffTab, parseNumber(e.target.value))}
                                                    disabled={localSettings.randomizeRanges}
                                                    style={{ opacity: localSettings.randomizeRanges ? 0.5 : 1 }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>От</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className={s.numberInput}
                                                    value={localSettings.tariffBaseAmountsRanges?.[activeTariffTab]?.min ?? ''}
                                                    onChange={(e) => {
                                                        const ranges = localSettings.tariffBaseAmountsRanges || {};
                                                        const tariffRanges = ranges[activeTariffTab] || {};
                                                        handleInputChange('tariffBaseAmountsRanges', activeTariffTab, { ...tariffRanges, min: parseNumber(e.target.value) });
                                                    }}
                                                    disabled={!localSettings.randomizeRanges}
                                                    style={{ opacity: !localSettings.randomizeRanges ? 0.5 : 1 }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>До</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className={s.numberInput}
                                                    value={localSettings.tariffBaseAmountsRanges?.[activeTariffTab]?.max ?? ''}
                                                    onChange={(e) => {
                                                        const ranges = localSettings.tariffBaseAmountsRanges || {};
                                                        const tariffRanges = ranges[activeTariffTab] || {};
                                                        handleInputChange('tariffBaseAmountsRanges', activeTariffTab, { ...tariffRanges, max: parseNumber(e.target.value) });
                                                    }}
                                                    disabled={!localSettings.randomizeRanges}
                                                    style={{ opacity: !localSettings.randomizeRanges ? 0.5 : 1 }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Депозитная сумма */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={deposutSumIcon} alt="Deposit Sum" className={s.sectionIcon} />
                                                Депозитная сумма
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Первоначальный взнос пользователя (для расчета графика)
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffDepositAmounts', 'tariffDepositAmountsRanges', deposutSumIcon, 'Депозитная сумма', 'Первоначальный взнос пользователя (для расчета графика)', '1', true)}
                                    </div>

                                    {/* Размер комиссии */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={commissionSizeIcon} alt="Commission Size" className={s.sectionIcon} />
                                                Размер комиссии
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Комиссия за вывод средств
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffCommissions', 'tariffCommissionsRanges', commissionSizeIcon, 'Размер комиссии', 'Комиссия за вывод средств')}
                                    </div>

                                    {/* Сумма верификации */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={verificationModalIcon} alt="Verification Amount" className={s.sectionIcon} />
                                                Сумма верификации
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Сумма для модала верификации аккаунта
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffVerificationAmounts', 'tariffVerificationAmountsRanges', verificationModalIcon, 'Сумма верификации', 'Сумма для модала верификации аккаунта')}
                                    </div>

                                    {/* Дополнительная сумма */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={additionalSumIcon} alt="Additional Sum" className={s.sectionIcon} />
                                                Дополнительная сумма
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Добавляется на вериф платеже, потому что софт наторговал еще
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffAdditionalAmounts', 'tariffAdditionalAmountsRanges', additionalSumIcon, 'Дополнительная сумма', 'Добавляется на вериф платеже, потому что софт наторговал еще')}
                                    </div>
                                    {/* Процент налога */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={taxPercentIcon} alt="Tax Percent" className={s.sectionIcon} />
                                                Процент налога
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Налог на доходы от торговли
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffTaxPercents', 'tariffTaxPercentsRanges', taxPercentIcon, 'Процент налога', 'Налог на доходы от торговли', '0.1', false, '%')}
                                    </div>

                                    {/* Комиссия за конвертацию */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={cryptoConversionModalIcon} alt="Crypto Conversion" className={s.sectionIcon} />
                                                Комиссия за конвертацию
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Комиссия за обмен криптовалюты на фиатную валюту
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffConversionCommissions', 'tariffConversionCommissionsRanges', cryptoConversionModalIcon, 'Комиссия за конвертацию', 'Комиссия за обмен криптовалюты на фиатную валюту')}
                                    </div>

                                    {/* Банковская комиссия */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={bankConversionModalIcon} alt="Bank Conversion" className={s.sectionIcon} />
                                                Банковская комиссия
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Комиссия за перевод средств в банк
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffBankFees', 'tariffBankFeesRanges', bankConversionModalIcon, 'Банковская комиссия', 'Комиссия за перевод средств в банк')}
                                    </div>

                                    {/* Оплата работы брокера */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={transactionsIcon} alt="Broker Payment" className={s.sectionIcon} />
                                                Оплата работы брокера
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Сколько юзер должен отправить брокеру после получения прибыли
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffBrokerFees', 'tariffBrokerFeesRanges', transactionsIcon, 'Оплата работы брокера', 'Сколько юзер должен отправить брокеру после получения прибыли')}
                                    </div>

                                    {/* Процент от выигрыша юзера */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={customValuesIcon} alt="Winning Percentage" className={s.sectionIcon} />
                                                Процент от выигрыша юзера
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Какой процент от выигранной суммы должен отправить юзер после получения прибыли
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffWinningPercentages', 'tariffWinningPercentagesRanges', customValuesIcon, 'Процент от выигрыша юзера', 'Какой процент от выигранной суммы должен отправить юзер после получения прибыли')}
                                        </div>

                                    {/* Единоразовый взнос для премиум доступа */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={verificationModalIcon} alt="Premium Fee" className={s.sectionIcon} />
                                                Единоразовый взнос (Премиум доступ)
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Единоразовый взнос для перехода на уровень PREMIUM
                                            </div>
                                        </div>
                                        {renderFieldWithRanges('tariffPremiumFees', 'tariffPremiumFeesRanges', verificationModalIcon, 'Единоразовый взнос (Премиум доступ)', 'Единоразовый взнос для перехода на уровень PREMIUM')}
                                    </div>

                                    {/* Лимит для премиум доступа */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                <img src={verificationModalIcon} alt="Premium Limit" className={s.sectionIcon} />
                                                Лимит (Премиум доступ)
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Лимит суммы для отображения в модале премиум доступа
                                            </div>
                                        </div>
                                        <div className={s.fieldInput} style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Фиксированная сумма</label>
                                                <input
                                                    type="text"
                                                    className={s.numberInput}
                                                    value={localSettings.tariffPremiumLimits?.[activeTariffTab] ?? ''}
                                                    onChange={(e) => handleInputChange('tariffPremiumLimits', activeTariffTab, e.target.value)}
                                                    placeholder="10,000.00"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Кастомные поля из modalTemplates */}
                                    {localSettings.modalTemplates?.filter(template => template.financeFieldName && template.enabled !== false).map(template => {
                                        const actionKey = template.actionKey;
                                        const fieldKey = `tariff${actionKey.charAt(0).toUpperCase() + actionKey.slice(1)}Amounts`;
                                        const rangesKey = `${fieldKey}Ranges`;
                                        const icon = template.icon || '📝';
                                        const label = template.financeFieldName || template.name || `Кастомное поле ${actionKey}`;
                                        const description = `Поле для модала "${template.name}"`;
                                        
                                        return (
                                            <div key={fieldKey} className={s.field}>
                                                <div className={s.fieldInfo}>
                                                    <div className={s.fieldLabel}>
                                                        {icon.startsWith('/') || icon.startsWith('http') ? (
                                                            <img src={icon} alt={label} className={s.sectionIcon} />
                                                        ) : (
                                                            <span style={{ fontSize: '20px', marginRight: '8px' }}>{icon}</span>
                                                        )}
                                                        {label}
                                    </div>
                                                    <div className={s.fieldDescription}>
                                                        {description}
                                                    </div>
                                                </div>
                                                {renderFieldWithRanges(fieldKey, rangesKey, icon, label, description)}
                                            </div>
                                        );
                                    })}
                                </div>
                                </>)}
                            </div>
                            <div className={s.helpText}>
                                Настройки для каждого тарифа применяются к соответствующим модальным окнам
                            </div>
                        </div>
                        </div>
                    </>
                )}

                {/* Wallets Tab */}
                {activeTab === 'wallets' && (
                    <>
                        {/* Wallets by Currency Section */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={`${s.firstSectionWithTabs} ${s.walletField}`}>
                            <h3 className={s.sectionTitle}>
                                <img src={walletsIcon} alt="Wallets by crypto" className={s.sectionIcon} />
                                {isNova && <WalletGlyph size={13} strokeWidth={2.2} aria-hidden="true" />}
                                {localSettings.bankPaymentMode ? 'Банковские счета по модулям' : 'Кошельки по криптовалютам'}
                            </h3>
                            <div className={s.helpText}>
                                {localSettings.bankPaymentMode
                                    ? 'Включён банковский режим оплаты. Для каждого модуля укажите банк и номер счёта — они покажутся в модалках оплаты вместо крипты.'
                                    : 'Показываются только добавленные криптовалюты. Если в конфиге одна крипта, она отображается сразу; остальные можно добавить вручную.'}
                            </div>
                            <div className={s.sectionContent}>
                                {localSettings.bankPaymentMode && (() => {
                                    const banksList = Object.keys(localSettings.banks || {});
                                    const accountLabel = localSettings.bankAccountDisplayName || 'IBAN/BIC';
                                    const selectedBank = getSelectedPaymentBank();
                                    const selectedBankSettings = localSettings.banks?.[selectedBank] || {};
                                    const bankData = getLocalBankAccounts();
                                    const customWalletTypes = (localSettings.modalTemplates || [])
                                        .filter(template => template.enabled !== false && template.actionKey)
                                        .map(template => ({
                                            key: `custom_${template.actionKey}`,
                                            label: template.name || `Custom Modal ${template.actionKey}`,
                                            description: `Счёт для кастомного модала "${template.name || template.actionKey}"`
                                        }));
                                    const walletTypes = [
                                        ...WALLET_TYPES.map(type => ({ ...type, description: walletTypeDescriptions[type.key] || '' })),
                                        ...customWalletTypes
                                    ];
                                    const WALLET_TYPE_GLYPHS = {
                                        main: WalletGlyph, commission: CircleDollarSign, verification: ShieldCheck,
                                        tax: Percent, cryptoConversion: ArrowLeftRight, bankConversion: Landmark, premium: Crown,
                                    };
                                    const bankPicker = (
                                        <div className={s.bankPickerWrap}>
                                            <button
                                                type="button"
                                                className={s.bankPickerTrigger}
                                                onClick={() => setBankPickerOpen(o => !o)}
                                            >
                                                <Landmark size={14} strokeWidth={2.1} aria-hidden="true" />
                                                {selectedBank ? 'Сменить банк' : 'Выбрать банк'}
                                                <ChevronDown size={14} strokeWidth={2.2} aria-hidden="true" className={bankPickerOpen ? s.bankPickerChevOpen : undefined} />
                                            </button>
                                            {bankPickerOpen && (
                                                <>
                                                    <div className={s.bankPickerBackdrop} onClick={() => setBankPickerOpen(false)} />
                                                    <div className={s.bankPickerMenu} role="listbox">
                                                        {banksList.map(b => (
                                                            <button
                                                                key={b}
                                                                type="button"
                                                                className={`${s.bankPickerItem} ${b === selectedBank ? s.bankPickerItemActive : ''}`}
                                                                onClick={() => handleSelectPaymentBank(b)}
                                                            >
                                                                <img
                                                                    src={getBankIconUrl(localSettings.banks?.[b]?.iconFileName)}
                                                                    alt=""
                                                                    className={s.bankPickerItemLogo}
                                                                    onError={(e) => { e.target.src = getBankIconUrl('default-bank.png'); }}
                                                                />
                                                                <span>{b}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                    return (
                                        <>
                                            <div className={`${s.field} ${s.fieldStack}`}>
                                                <div className={s.stackHead}>
                                                    {fieldGlyph(Landmark)}
                                                    <div className={s.stackHeadText}>
                                                        <div className={s.stackTitle}>Банковский счёт</div>
                                                        <div className={s.stackDesc}>Банк всегда один — выберите его в шапке карточки (сменятся логотип и название). Ниже укажите номер счёта для каждого модуля; дополнительные поля добавляются кнопкой.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {banksList.length === 0 ? (
                                                <div className={s.helpText}>
                                                    Сначала добавьте банки во вкладке «Банки», затем выберите один здесь.
                                                </div>
                                            ) : (
                                                <div className={`${s.field} ${s.fieldStack}`}>
                                                    <div className={s.walletCardHead}>
                                                        <img
                                                            src={getBankIconUrl(selectedBankSettings.iconFileName)}
                                                            alt={selectedBank || ''}
                                                            className={s.walletCoin}
                                                            onError={(e) => { e.target.src = getBankIconUrl('default-bank.png'); }}
                                                        />
                                                        <div className={s.walletCoinInfo}>
                                                            <span className={s.walletCoinName}>{selectedBank || 'Банк не выбран'}</span>
                                                            <span className={s.walletCoinChain}>Банковский счёт · {accountLabel}</span>
                                                        </div>
                                                        {bankPicker}
                                                    </div>
                                                    <div className={s.walletTypeList}>
                                                        {walletTypes.map(type => {
                                                            const TypeGlyph = WALLET_TYPE_GLYPHS[type.key] || Puzzle;
                                                            const entry = bankData[type.key] || {};
                                                            const extraFields = Array.isArray(entry.fields) ? entry.fields : [];
                                                            return (
                                                                <div key={`nova-bank-${type.key}`} className={s.walletTypeRow}>
                                                                    <span className={s.walletTypeGlyph}>
                                                                        <TypeGlyph size={13} strokeWidth={1.9} aria-hidden="true" />
                                                                    </span>
                                                                    <div className={s.walletTypeInfo}>
                                                                        <span className={s.walletTypeName}>{type.label}</span>
                                                                        {type.description && <span className={s.walletTypeDesc}>{type.description}</span>}
                                                                    </div>
                                                                    <div className={s.bankWalletFields}>
                                                                        <div className={s.bankWalletNumberRow}>
                                                                            <input
                                                                                type="text"
                                                                                className={s.walletTypeInput}
                                                                                placeholder={accountLabel}
                                                                                maxLength="60"
                                                                                spellCheck="false"
                                                                                value={entry.number || ''}
                                                                                onChange={(e) => handleBankAccountChange(type.key, 'number', e.target.value)}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                className={s.walletAddBtn}
                                                                                onClick={() => handleAddBankField(type.key)}
                                                                                title="Добавить своё поле (название + значение)"
                                                                            >
                                                                                <PlusCircle size={13} strokeWidth={2.1} aria-hidden="true" />
                                                                                Добавить поле
                                                                            </button>
                                                                        </div>
                                                                        {extraFields.map((fld, idx) => (
                                                                            <div key={`nova-bankfield-${type.key}-${idx}`} className={s.bankWalletFieldRow}>
                                                                                <input
                                                                                    type="text"
                                                                                    className={`${s.walletTypeInput} ${s.bankFieldLabelInput}`}
                                                                                    placeholder="Название поля (напр. Titular)"
                                                                                    maxLength="40"
                                                                                    value={fld?.label || ''}
                                                                                    onChange={(e) => handleBankFieldChange(type.key, idx, 'label', e.target.value)}
                                                                                />
                                                                                <input
                                                                                    type="text"
                                                                                    className={s.walletTypeInput}
                                                                                    placeholder="Значение"
                                                                                    maxLength="120"
                                                                                    value={fld?.value || ''}
                                                                                    onChange={(e) => handleBankFieldChange(type.key, idx, 'value', e.target.value)}
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    className={s.bankHolderRemoveBtn}
                                                                                    onClick={() => handleRemoveBankField(type.key, idx)}
                                                                                    title="Удалить поле"
                                                                                >
                                                                                    <XGlyph size={13} strokeWidth={2.2} aria-hidden="true" />
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                                {!localSettings.bankPaymentMode && (() => {
                                    const walletsByCurrency = getNormalizedWalletsByCurrency();
                                    const configuredSymbols = getConfiguredWalletCurrencies();
                                    const availableToAdd = CRYPTO_OPTIONS.filter(option => !configuredSymbols.includes(option.symbol));
                                    const customWalletTypes = (localSettings.modalTemplates || [])
                                        .filter(template => template.enabled !== false && template.actionKey)
                                        .map(template => ({
                                            key: `custom_${template.actionKey}`,
                                            label: template.name || `Custom Modal ${template.actionKey}`,
                                            description: `Кошелек для кастомного модала "${template.name || template.actionKey}"`
                                        }));
                                    const walletTypes = [
                                        ...WALLET_TYPES.map(type => ({
                                            ...type,
                                            description: walletTypeDescriptions[type.key] || ''
                                        })),
                                        ...customWalletTypes
                                    ];

                                    if (isNova) {
                                        // Глиф типа кошелька — по соответствующему модалу
                                        const WALLET_TYPE_GLYPHS = {
                                            main: WalletGlyph,
                                            commission: CircleDollarSign,
                                            verification: ShieldCheck,
                                            tax: Percent,
                                            cryptoConversion: ArrowLeftRight,
                                            bankConversion: Landmark,
                                            premium: Crown,
                                        };
                                        return (
                                            <>
                                                <div className={`${s.field} ${s.fieldStack}`}>
                                                    <div className={s.stackHead}>
                                                        {fieldGlyph(WalletGlyph)}
                                                        <div className={s.stackHeadText}>
                                                            <div className={s.stackTitle}>Адреса по криптовалютам</div>
                                                            <div className={s.stackDesc}>Для каждой крипты из конфига — свой набор кошельков: главный адрес и адреса для каждого модала</div>
                                                        </div>
                                                        {availableToAdd.length > 0 && (
                                                            <div className={s.walletAddRow}>
                                                                <ThemedSelect
                                                                    nova
                                                                    className={s.select}
                                                                    value={walletCryptoToAdd}
                                                                    onChange={(e) => setWalletCryptoToAdd(e.target.value)}
                                                                >
                                                                    <option value="">Добавить криптовалюту…</option>
                                                                    {availableToAdd.map(option => (
                                                                        <option key={option.symbol} value={option.symbol}>
                                                                            {option.name} ({option.symbol})
                                                                        </option>
                                                                    ))}
                                                                </ThemedSelect>
                                                                <button
                                                                    type="button"
                                                                    className={s.walletAddBtn}
                                                                    onClick={handleAddWalletCurrency}
                                                                    disabled={!walletCryptoToAdd}
                                                                >
                                                                    <PlusCircle size={14} strokeWidth={2.1} aria-hidden="true" />
                                                                    Добавить
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {configuredSymbols.map(symbol => {
                                                    const crypto = CRYPTO_OPTIONS.find(option => option.symbol === symbol) || { symbol, name: symbol, chain: '' };
                                                    const canRemove = configuredSymbols.length > 1;
                                                    return (
                                                        <div key={`nova-wallets-${symbol}`} className={`${s.field} ${s.fieldStack}`}>
                                                            <div className={s.walletCardHead}>
                                                                <img src={getCryptoIcon(symbol)} alt={crypto.name} className={s.walletCoin} />
                                                                <div className={s.walletCoinInfo}>
                                                                    <span className={s.walletCoinName}>
                                                                        {crypto.name}
                                                                        <span className={s.rateTicker}>{symbol}</span>
                                                                    </span>
                                                                    <span className={s.walletCoinChain}>{crypto.chain || 'Криптовалюта из конфига'}</span>
                                                                </div>
                                                                {canRemove && (
                                                                    <button
                                                                        type="button"
                                                                        className={s.walletRemoveBtn}
                                                                        onClick={() => handleRemoveWalletCurrency(symbol)}
                                                                    >
                                                                        <Trash2 size={12} strokeWidth={2.2} aria-hidden="true" />
                                                                        Удалить
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className={s.walletTypeList}>
                                                                {walletTypes.map(type => {
                                                                    const TypeGlyph = WALLET_TYPE_GLYPHS[type.key] || Puzzle;
                                                                    return (
                                                                        <div key={`nova-${symbol}-${type.key}`} className={s.walletTypeRow}>
                                                                            <span className={s.walletTypeGlyph}>
                                                                                <TypeGlyph size={13} strokeWidth={1.9} aria-hidden="true" />
                                                                            </span>
                                                                            <div className={s.walletTypeInfo}>
                                                                                <span className={s.walletTypeName}>{type.label}</span>
                                                                                {type.description && (
                                                                                    <span className={s.walletTypeDesc}>{type.description}</span>
                                                                                )}
                                                                            </div>
                                                                            <input
                                                                                type="text"
                                                                                className={s.walletTypeInput}
                                                                                placeholder={`${symbol} address`}
                                                                                maxLength="140"
                                                                                spellCheck="false"
                                                                                value={walletsByCurrency?.[symbol]?.[type.key]?.address || ''}
                                                                                onChange={(e) => handleCurrencyWalletChange(symbol, type.key, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    }

                                    return (
                                        <>
                                            {availableToAdd.length > 0 && (
                                                <div className={s.field}>
                                                    <div className={s.fieldInfo}>
                                                        <div className={s.fieldLabel}>
                                                            <img src={walletsIcon} alt="Add crypto wallet" className={s.sectionIcon} />
                                                            Добавить криптовалюту
                                                        </div>
                                                        <div className={s.fieldDescription}>
                                                            Выберите только те криптовалюты, для которых нужны отдельные кошельки в этом конфиге
                                                        </div>
                                                    </div>
                                                    <div className={`${s.fieldInput} ${s.walletCryptoAddRow}`}>
                                                        <select
                                                            className={s.select}
                                                            value={walletCryptoToAdd}
                                                            onChange={(e) => setWalletCryptoToAdd(e.target.value)}
                                                        >
                                                            <option value="">Выберите криптовалюту</option>
                                                            {availableToAdd.map(option => (
                                                                <option key={option.symbol} value={option.symbol}>
                                                                    {option.name} ({option.symbol})
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            className={s.walletCryptoAddButton}
                                                            onClick={handleAddWalletCurrency}
                                                            disabled={!walletCryptoToAdd}
                                                        >
                                                            Добавить
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {configuredSymbols.map(symbol => {
                                                const crypto = CRYPTO_OPTIONS.find(option => option.symbol === symbol) || { symbol, name: symbol, chain: '' };
                                                const canRemove = configuredSymbols.length > 1;

                                                return (
                                                    <div key={`wallets-${symbol}`} className={s.field} style={{ alignItems: 'flex-start' }}>
                                                        <div className={s.fieldInfo}>
                                                            <div className={s.fieldLabel}>
                                                                <img src={getCryptoIcon(symbol)} alt={crypto.name} className={s.sectionIcon} />
                                                                {crypto.name} ({symbol})
                                                            </div>
                                                            <div className={s.fieldDescription}>
                                                                {crypto.chain ? `Сеть: ${crypto.chain}` : 'Криптовалюта из конфига'}
                                                            </div>
                                                            {canRemove && (
                                                                <button
                                                                    type="button"
                                                                    className={s.walletCryptoRemoveButton}
                                                                    onClick={() => handleRemoveWalletCurrency(symbol)}
                                                                >
                                                                    Удалить из конфига
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className={s.fieldInput} style={{ display: 'grid', gap: '10px' }}>
                                                            {walletTypes.map(type => {
                                                                const walletValue = walletsByCurrency?.[symbol]?.[type.key]?.address || '';
                                                                return (
                                                                    <label key={`${symbol}-${type.key}`} style={{ display: 'grid', gap: '4px' }}>
                                                                        <span className={s.fieldDescription} style={{ fontWeight: 600 }}>
                                                                            {type.label}
                                                                        </span>
                                                                        <input
                                                                            type="text"
                                                                            className={s.addressInput}
                                                                            placeholder={`${symbol} address`}
                                                                            maxLength="140"
                                                                            value={walletValue}
                                                                            onChange={(e) => handleCurrencyWalletChange(symbol, type.key, e.target.value)}
                                                                        />
                                                                        {type.description && (
                                                                            <span className={s.fieldDescription}>{type.description}</span>
                                                                        )}
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        {/* Modal Address Visibility Section */}
                        <div className={`${s.section} ${s.walletField}`}>
                            <h3 className={s.sectionTitle}>
                                <img src={siteSettingsIcon} alt="Hide Address" className={s.sectionIcon} />
                                {isNova && <EyeOff size={13} strokeWidth={2.2} aria-hidden="true" />}
                                Скрытие адресов в модалах
                            </h3>
                            <div className={s.sectionContent}>
                                {/* ── Nova: скрытие адресов — список строк-переключателей */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(EyeOff)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Скрытие адресов в модалах</div>
                                            <div className={s.stackDesc}>Кнопка копирования заменяется на иконку глаза — адрес маскируется до клика по ней</div>
                                        </div>
                                    </div>
                                    <div className={s.switchList}>
                                        <label className={s.switchRow}>
                                            <span className={s.switchRowGlyph}><EyeOff size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                            <div className={s.switchRowText}>
                                                <span className={s.switchRowName}>Скрывать адреса</span>
                                                <span className={s.switchRowHint}>Включает маскировку адресов кошельков во всех модальных окнах</span>
                                            </div>
                                            <input
                                                type="checkbox"
                                                className={s.novaSwitch}
                                                checked={localSettings.hideModalAddresses || false}
                                                onChange={(e) => handleInputChange('', 'hideModalAddresses', e.target.checked)}
                                            />
                                        </label>
                                        {localSettings.hideModalAddresses && (
                                            <>
                                                <label className={`${s.switchRow} ${s.switchRowNested}`}>
                                                    <span className={s.switchRowGlyph}><Eye size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                    <div className={s.switchRowText}>
                                                        <span className={s.switchRowName}>Показывать последние 4 символа</span>
                                                        <span className={s.switchRowHint}>Если выключено — адрес скрыт полностью до нажатия на глаз</span>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        className={s.novaSwitch}
                                                        checked={localSettings.showLast4AddressChars !== false}
                                                        onChange={(e) => handleInputChange('', 'showLast4AddressChars', e.target.checked)}
                                                    />
                                                </label>
                                                <label className={`${s.switchRow} ${s.switchRowNested}`}>
                                                    <span className={s.switchRowGlyph}><ShieldCheck size={14} strokeWidth={1.9} aria-hidden="true" /></span>
                                                    <div className={s.switchRowText}>
                                                        <span className={s.switchRowName}>Адрес скрыт по умолчанию</span>
                                                        <span className={s.switchRowHint}>При открытии модала адрес уже замаскирован</span>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        className={s.novaSwitch}
                                                        checked={localSettings.modalAddressHiddenByDefault || false}
                                                        onChange={(e) => handleInputChange('', 'modalAddressHiddenByDefault', e.target.checked)}
                                                    />
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </div>
                                )}

                                {!isNova && (
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={siteSettingsIcon} alt="Hide Address" className={s.sectionIcon} />
                                            Скрытие адресов в модалах
                            </div>
                                        <div className={s.fieldDescription}>
                                            Включить функцию скрытия адресов кошельков в модальных окнах (кнопка копирования будет заменена на иконку глаза)
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <label className={s.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                className={s.checkbox}
                                                checked={localSettings.hideModalAddresses || false}
                                                onChange={(e) => handleInputChange('', 'hideModalAddresses', e.target.checked)}
                                            />
                                            <span className={s.checkboxCustom}></span>
                                            Включить скрытие адресов в модалах
                                        </label>
                                        
                                        {localSettings.hideModalAddresses && (
                                            <div className={s.checkboxNestedGroup}>
                                                <label className={s.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        className={s.checkbox}
                                                        checked={localSettings.showLast4AddressChars !== false}
                                                        onChange={(e) => handleInputChange('', 'showLast4AddressChars', e.target.checked)}
                                                    />
                                                    <span className={s.checkboxCustom}></span>
                                                    Показывать последние 4 символа адреса
                                                </label>
                                                
                                                <label className={s.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        className={s.checkbox}
                                                        checked={localSettings.modalAddressHiddenByDefault || false}
                                                        onChange={(e) => handleInputChange('', 'modalAddressHiddenByDefault', e.target.checked)}
                                                    />
                                                    <span className={s.checkboxCustom}></span>
                                                    Адрес скрыт по умолчанию
                                                </label>
                                                
                                                <div className={s.fieldDescription}>
                                                    Если "Показывать последние 4 символа" выключено - адрес скрывается полностью до нажатия на иконку глаза
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                )}
                                
                            <div className={s.helpText}>
                                Настройка влияет только на отображение уже выбранных кошельков в модальных окнах.
                                </div>
                            </div>
                        </div>
                        </div>
                    </>
                )}
                {/* Banks Tab */}
                {activeTab === 'banks' && (
                    <>
                        {/* Banks Management Section */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={`${s.firstSectionWithTabs} ${s.banksField}`}>
                            <h3 className={s.sectionTitle}>
                                <div className={s.sectionTitleLeft}>
                                    <img src={bankConfigurationIcon} alt="Banks" className={s.sectionIcon} />
                                    {isNova && <Landmark size={13} strokeWidth={2.2} aria-hidden="true" />}
                                    Управление банками
                                </div>
                                <button 
                                    className={s.addBankButton}
                                    onClick={handleAddBank}
                                    title="Добавить новый банк"
                                >
                                    {isNova
                                        ? <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                                        : <img src={require('../../img/iconsas/add_button.png')} alt="Add" className={s.buttonIcon} />}
                                    Добавить банк
                                </button>
                            </h3>
                            <div className={s.sectionContent}>
                                {/* ── Nova: банки одним блоком — порядок перетаскиванием,
                                    иконка выбирается темизированным дропдауном с превью */}
                                {isNova && (
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        {fieldGlyph(Landmark)}
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Банки для выбора в админке</div>
                                            <div className={s.stackDesc}>Порядок строк — перетаскиванием за ручку: так банки будут идти в списке «Selected Network». Выключенные банки скрыты из выбора</div>
                                        </div>
                                    </div>
                                    <div className={s.bankList} ref={bankListRef}>
                                        <div className={s.bankGhost} ref={bankGhostRef} aria-hidden="true" />
                                        {bankOrder.map((bankName, index) => {
                                            const bankSettings = (localSettings.banks || {})[bankName];
                                            if (!bankSettings) return null;
                                            return (
                                                <div
                                                    key={`nova-bank-${bankName}`}
                                                    data-bank-name={bankName}
                                                    className={`${s.bankRow} ${bankDragName === bankName ? s.bankRowDrag : ''} ${newlyAddedBank === bankName ? s.bankRowNew : ''} ${bankSettings.enabled ? '' : s.bankRowOff}`}
                                                >
                                                    <span
                                                        className={s.qaHandle}
                                                        onPointerDown={(e) => startBankDrag(e, bankName)}
                                                        title="Перетащить"
                                                    >
                                                        <GripVertical size={14} strokeWidth={2} aria-hidden="true" />
                                                    </span>
                                                    <span className={s.qaIndex}>{index + 1}</span>
                                                    <img
                                                        src={getBankIconUrl(bankSettings.iconFileName)}
                                                        alt={bankName}
                                                        className={s.bankRowIcon}
                                                        onError={(e) => { e.target.src = getBankIconUrl('default-bank.png'); }}
                                                    />
                                                    <input
                                                        type="text"
                                                        className={`${s.bankNameInput} ${s.bankRowName}`}
                                                        value={editingBankName[bankName] !== undefined ? editingBankName[bankName] : bankName}
                                                        onFocus={() => handleStartEditingBankName(bankName)}
                                                        onChange={(e) => handleUpdateEditingBankName(bankName, e.target.value)}
                                                        onBlur={() => handleFinishEditingBankName(bankName)}
                                                        onKeyPress={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                        placeholder="Название банка"
                                                    />
                                                    <div className={s.bankRowIconSelect}>
                                                        <ThemedSelect
                                                            nova
                                                            value={bankSettings.iconFileName}
                                                            onChange={(e) => handleInputChange('banks', `${bankName}.iconFileName`, e.target.value)}
                                                            optionIcon={(val) => (
                                                                <img
                                                                    src={getBankIconUrl(val)}
                                                                    alt=""
                                                                    className={s.bankOptIcon}
                                                                    onError={(e) => { e.target.src = getBankIconUrl('default-bank.png'); }}
                                                                />
                                                            )}
                                                        >
                                                            {mergedBankIcons.map(iconFile => (
                                                                <option key={iconFile} value={iconFile}>
                                                                    {iconFile.replace(/\.(png|svg|jpe?g|webp)$/i, '').replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                                </option>
                                                            ))}
                                                        </ThemedSelect>
                                                    </div>
                                                    <label className={s.bankUploadBtn} title="Загрузить свою иконку">
                                                        <ImageUp size={14} strokeWidth={2} aria-hidden="true" />
                                                        <input
                                                            type="file"
                                                            accept=".png,.svg,.jpg,.jpeg,.webp"
                                                            style={{ display: 'none' }}
                                                            onChange={(ev) => {
                                                                uploadBankIcon(bankName, ev.target.files && ev.target.files[0]);
                                                                ev.target.value = '';
                                                            }}
                                                        />
                                                    </label>
                                                    <label className={s.qaToggle} title="Показывать банк в админке">
                                                        <input
                                                            type="checkbox"
                                                            className={s.novaSwitch}
                                                            checked={!!bankSettings.enabled}
                                                            onChange={(e) => handleInputChange('banks', `${bankName}.enabled`, e.target.checked)}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className={s.deleteRangeButton}
                                                        title="Удалить банк"
                                                        onClick={() => handleDeleteBank(bankName)}
                                                    >
                                                        <Trash2 size={13} strokeWidth={2.2} aria-hidden="true" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                )}

                                {!isNova && (
                                <div className={s.banksGrid}>
                                    {Object.keys(localSettings.banks || {})
                                        .map((bankName) => ({
                                            name: bankName,
                                            settings: localSettings.banks[bankName]
                                        }))
                                        .sort((a, b) => a.settings.priority - b.settings.priority)
                                        .map(({ name: bankName, settings: bankSettings }, index) => {
                                        
                                        return (
                                            <div 
                                                key={`bank-${index}-${bankName}-${forceUpdate}`} 
                                                className={`${s.bankCard} ${s.sortableCard} ${
                                                    reorderingBank === bankName ? s.reordering : ''
                                                } ${newlyAddedBank === bankName ? s.newlyAdded : ''}`}
                                                style={{ 
                                                    '--order': index,
                                                    animationDelay: `${index * 0.05}s` 
                                                }}
                                            >
                                                <div className={s.bankHeader}>
                                                    <div className={s.bankInfo}>
                                                                                                <img 
                                            src={getBankIconUrl(bankSettings.iconFileName)} 
                                            alt={bankName}
                                            className={s.bankIcon}
                                            onError={(e) => {
                                                e.target.src = getBankIconUrl('default-bank.png');
                                            }}
                                        />
                                                        <div className={s.bankDetails}>
                                                            <input
                                                                type="text"
                                                                className={s.bankNameInput}
                                                                value={editingBankName[bankName] !== undefined ? editingBankName[bankName] : bankName}
                                                                onFocus={() => handleStartEditingBankName(bankName)}
                                                                onChange={(e) => handleUpdateEditingBankName(bankName, e.target.value)}
                                                                onBlur={() => handleFinishEditingBankName(bankName)}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.target.blur();
                                                                    }
                                                                }}
                                                                placeholder="Название банка"
                                                            />
                                                            <div className={s.bankPriority}>
                                                                <span className={s.priorityLabel}>Приоритет:</span>
                                                                <span className={`${s.priorityBadge} ${s[`priority${bankSettings.priority}`]}`}>
                                                                    #{bankSettings.priority}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={s.bankControls}>
                                                        <label className={s.switch}>
                                                            <input
                                                                type="checkbox"
                                                                checked={bankSettings.enabled}
                                                                onChange={(e) => handleInputChange('banks', `${bankName}.enabled`, e.target.checked)}
                                                            />
                                                            <span className={s.slider}></span>
                                                        </label>
                                                        <button 
                                                            className={s.deleteBankButton}
                                                            onClick={() => handleDeleteBank(bankName)}
                                                            title="Удалить банк"
                                                        >
                                                            <img src={require('../../img/iconsas/reset_settings_button.png')} alt="Delete" className={s.buttonIcon} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className={s.bankSettings}>
                                                    <div className={s.fieldGroup}>
                                                        <label className={s.fieldLabel}>Приоритет:</label>
                                                        <input
                                                            type="number"
                                                            className={s.numberInput}
                                                            min="1"
                                                            defaultValue={bankSettings.priority || 1}
                                                            onBlur={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (!isNaN(value) && value >= 1) {
                                                                    handleInputChange('banks', `${bankName}.priority`, value);
                                                                } else {
                                                                    // Возвращаем исходное значение при некорректном вводе
                                                                    e.target.value = bankSettings.priority || 1;
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.target.blur();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={s.fieldGroup}>
                                                        <label className={s.fieldLabel}>Иконка:</label>
                                                        <select
                                                            className={s.select}
                                                            value={bankSettings.iconFileName}
                                                            onChange={(e) => handleInputChange('banks', `${bankName}.iconFileName`, e.target.value)}
                                                        >
                                                            {mergedBankIcons.map(iconFile => (
                                                                <option key={iconFile} value={iconFile}>
                                                                    {iconFile.replace('.png', '').replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div style={{ marginTop: 8 }}>
                                                        <input type="file" accept=".png,.svg,.jpg,.jpeg,.webp" onChange={async (ev) => {
                                                            try {
                                                                const file = ev.target.files && ev.target.files[0];
                                                                if (!file) return;
                                                                const asDataUrl = await new Promise((res, rej) => {
                                                                    const fr = new FileReader();
                                                                    fr.onload = () => res(fr.result);
                                                                    fr.onerror = rej;
                                                                    fr.readAsDataURL(file);
                                                                });
                                                                const resp = await fetch(`/api/files/bankicons/${file.name}`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ data: asDataUrl }) });
                                                                const j = await resp.json();
                                                                if (j && j.success) {
                                                                    try { const list = await (await fetch('/api/files/bankicons')).json(); if (Array.isArray(list.files)) setServerBankIcons(list.files); } catch(_) {}
                                                                    setIconVersions((prev) => ({ ...prev, [file.name]: Date.now() }));
                                                                    handleInputChange('banks', `${bankName}.iconFileName`, file.name);
                                                                } else {
                                                                    notifyError('Не удалось загрузить иконку');
                                                                }
                                                            } catch (e) { 
                                                                // Проверяем тип ошибки
                                                                if (e.message && e.message.includes('JSON')) {
                                                                    notifyError('Ошибка: сервер не поддерживает загрузку иконок. Используйте локальные иконки из папки src/img/bankicons/');
                                                                } else {
                                                                    notifyError('Ошибка загрузки иконки: ' + e.message); 
                                                                }
                                                            }
                                                        }} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                )}
                            </div>
                            {isNova ? (
                                <div className={s.helpText}>
                                    Позиция строки — это приоритет банка: верхний показывается первым в «Selected Network» админки. Свою иконку можно загрузить кнопкой рядом с выбором — она попадёт в общую папку bankicons.
                                </div>
                            ) : (
                                <div className={s.helpText}>
                                    <div className={s.helpTitle}>💡 Как это работает:</div>
                                    <ul className={s.helpList}>
                                        <li><strong>Включенные банки</strong> отображаются в AdminPanel в dropdown "Selected Network"</li>
                                        <li><strong>Приоритет</strong> определяет порядок отображения (1 = первый)</li>
                                        <li><strong>Автоматический обмен:</strong> При смене приоритета банки автоматически меняются местами</li>
                                        <li><strong>Выключенные банки</strong> скрыты из выбора в AdminPanel</li>
                                        <li><strong>Иконки</strong> можно выбрать из папки /img/bankicons/</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        </div>
                    </>
                )}
                {/* Translations Tab */}
                {activeTab === 'translations' && (
                    <>
                        {/* Language Selection & Translation Management */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={s.firstSectionWithTabs}>
                            <h3 className={s.sectionTitle}>
                                <img src={currencySymbolIcon} alt="Language" className={s.sectionIcon} />
                                {isNova && <Languages size={13} strokeWidth={2.2} aria-hidden="true" />}
                                Управление переводами
                            </h3>
                            <div className={s.sectionContent}>
                                {/* ── Nova: панель управления языками + фокус и поиск */}
                                {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.transToolbar}>
                                            <div className={s.transToolbarBlock}>
                                                <span className={s.transToolbarLabel}>Язык интерфейса</span>
                                                <div className={s.transLangSelectRow}>
                                                    <ThemedSelect
                                                        nova
                                                        value={localSettings.currentLanguage || 'ru'}
                                                        onChange={(e) => handleInputChange('', 'currentLanguage', e.target.value)}
                                                        optionIcon={(val) => langFlag(val, s.novaSelectFlag)}
                                                    >
                                                        {Object.keys(localSettings.translations || {}).map(langCode => {
                                                            const language = getLanguageByCode(langCode);
                                                            return (
                                                                <option key={langCode} value={langCode}>
                                                                    {language ? language.nativeName : langCode.toUpperCase()}
                                                                </option>
                                                            );
                                                        })}
                                                    </ThemedSelect>
                                                    <button
                                                        type="button"
                                                        className={s.transAddBtn}
                                                        onClick={() => setIsAddLanguageModalOpen(true)}
                                                        title="Добавить новый язык"
                                                    >
                                                        <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                                                        Добавить
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={s.transDeleteBtn}
                                                        onClick={() => handleDeleteLanguage(localSettings.currentLanguage)}
                                                        title="Удалить текущий язык"
                                                        disabled={localSettings.currentLanguage === 'ru' || Object.keys(localSettings.translations || {}).length <= 1}
                                                    >
                                                        <Minus size={14} strokeWidth={2.4} aria-hidden="true" />
                                                        Удалить
                                                    </button>
                                                </div>
                                            </div>
                                            <div className={s.transSearchBox}>
                                                <Search size={15} strokeWidth={2} aria-hidden="true" />
                                                <input
                                                    type="text"
                                                    value={transSearch}
                                                    onChange={(e) => setTransSearch(e.target.value)}
                                                    placeholder="Поиск по ключу или тексту…"
                                                    spellCheck="false"
                                                />
                                                {transSearch && (
                                                    <button type="button" className={s.transSearchClear} onClick={() => setTransSearch('')} title="Очистить">
                                                        <XGlyph size={13} strokeWidth={2.4} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className={s.transFocusRow}>
                                            <span className={s.transFocusLabel}>Показывать:</span>
                                            <button
                                                type="button"
                                                className={`${s.transFocusChip} ${transFocusLangs.length === 0 ? s.transFocusChipActive : ''}`}
                                                onClick={() => setTransFocusLangs([])}
                                            >
                                                Все языки
                                            </button>
                                            {Object.keys(localSettings.translations || {}).map((langCode) => (
                                                <button
                                                    key={langCode}
                                                    type="button"
                                                    className={`${s.transFocusChip} ${transFocusLangs.includes(langCode) ? s.transFocusChipActive : ''}`}
                                                    onClick={() => toggleTransFocusLang(langCode)}
                                                >
                                                    {langFlag(langCode, s.transFocusFlag)}
                                                    {langCode.toUpperCase()}
                                                </button>
                                            ))}
                                            {/* Показать только непереведённые ключи текущего языка:
                                                раскрывает все секции, прячет переведённое и пустые секции.
                                                Кнопка появляется только если непереведённые реально есть */}
                                            {untranslatedCount > 0 && (
                                                <button
                                                    type="button"
                                                    className={`${s.transUntransBtn} ${showUntranslatedOnly ? s.transUntransBtnActive : ''}`}
                                                    onClick={() => setShowUntranslatedOnly((v) => !v)}
                                                    title="Показать только ключи без перевода на текущий язык"
                                                >
                                                    <TriangleAlert size={13} strokeWidth={2.2} aria-hidden="true" />
                                                    Непереведённые
                                                    <span className={s.transUntransLang}>{untranslatedCount}</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <img src={currencySymbolIcon} alt="Language" className={s.sectionIcon} />
                                            Текущий язык интерфейса
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Выберите язык для отображения интерфейса сайта
                                        </div>
                                    </div>
                                    <div className={s.fieldInput}>
                                        <div className={s.languageSelector}>
                                            <select
                                                className={s.select}
                                                value={localSettings.currentLanguage || 'ru'}
                                                onChange={(e) => handleInputChange('', 'currentLanguage', e.target.value)}
                                            >
                                                {Object.keys(localSettings.translations || {}).map(langCode => {
                                                    const language = getLanguageByCode(langCode);
                                                    if (language) {
                                                        return (
                                                            <option key={langCode} value={langCode}>
                                                                {language.flag} {language.nativeName}
                                                            </option>
                                                        );
                                                    }
                                                    return (
                                                        <option key={langCode} value={langCode}>
                                                            🌍 {langCode.toUpperCase()}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <button
                                                type="button"
                                                className={s.addLanguageButton}
                                                onClick={() => setIsAddLanguageModalOpen(true)}
                                                title="Добавить новый язык"
                                            >
                                                <span className={s.addLanguageIcon}>+</span>
                                                <span className={s.addLanguageText}>Добавить перевод</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={s.deleteLanguageButton}
                                                onClick={() => handleDeleteLanguage(localSettings.currentLanguage)}
                                                title="Удалить текущий язык"
                                                disabled={localSettings.currentLanguage === 'ru' || Object.keys(localSettings.translations || {}).length <= 1}
                                            >
                                                <span className={s.deleteLanguageIcon}>−</span>
                                                <span className={s.deleteLanguageText}>Удалить перевод</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        </div>

                        {/* Header Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('header')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('header')}
                                    <img src={dashboardImg} alt="Header" className={s.sectionIcon} />
                                    Переводы блока Header
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.header ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.header}>
                            <div className={s.sectionContent}>

                                {/* Page Titles */}
                                {renderTranslationFields(
                                    'header.dashboard',
                                    'Заголовок страницы Dashboard',
                                    { ru: 'Главная', en: 'Dashboard', ms: 'Dashboard' }
                                )}

                                {renderTranslationFields(
                                    'header.trade',
                                    'Заголовок страницы торговли',
                                    { ru: 'Торговля', en: 'Trading', ms: 'Dagangan' }
                                )}

                                {renderTranslationFields(
                                    'header.markets',
                                    'Заголовок страницы рынков',
                                    { ru: 'Рынки', en: 'Markets', ms: 'Pasaran' }
                                )}

                                {renderTranslationFields(
                                    'header.wallet',
                                    'Заголовок страницы кошелька',
                                    { ru: 'Кошелек', en: 'Wallet', ms: 'Dompet' }
                                )}

                                {renderTranslationFields(
                                    'header.broker',
                                    'Заголовок страницы брокера',
                                    { ru: 'Для Брокера', en: 'For Broker', ms: 'Untuk Broker' }
                                )}

                                {renderTranslationFields(
                                    'header.mailbox',
                                    'Заголовок страницы сообщений',
                                    { ru: 'Сообщения', en: 'Messages', ms: 'Mesej' }
                                )}

                                {renderTranslationFields(
                                    'header.settings',
                                    'Заголовок страницы настроек',
                                    { ru: 'Настройки', en: 'Settings', ms: 'Tetapan' }
                                )}

                                {/* Breadcrumb */}
                                {renderTranslationFields(
                                    'header.brokerLicense',
                                    'Текст лицензии брокера в breadcrumb',
                                    { ru: 'Лицензия Брокера №', en: 'Broker License №', ms: 'Lesen Broker №' }
                                )}

                                {/* Search */}
                                {renderTranslationFields(
                                    'header.searchPlaceholder',
                                    'Placeholder для поля поиска',
                                    { ru: 'Найти активы, транзакции...', en: 'Search assets, transactions...', ms: 'Cari aset, transaksi...' }
                                )}

                                {/* Notifications */}
                                {renderTranslationFields(
                                    'header.notifications',
                                    'Заголовок панели уведомлений',
                                    { ru: 'Уведомления', en: 'Notifications', ms: 'Pemberitahuan' }
                                )}

                                {renderTranslationFields(
                                    'header.markAllRead',
                                    'Кнопка отметить все уведомления как прочитанные',
                                    { ru: 'Отметить все как прочитанные', en: 'Mark all as read', ms: 'Tandakan semua sebagai dibaca' }
                                )}

                                {/* User Status */}
                                {renderTranslationFields(
                                    'header.verified',
                                    'Статус подтвержденного пользователя',
                                    { ru: 'Подтверждён', en: 'Verified', ms: 'Disahkan' }
                                )}

                                {renderTranslationFields(
                                    'header.unverified',
                                    'Статус неподтвержденного пользователя',
                                    { ru: 'Не подтверждён', en: 'Unverified', ms: 'Belum Disahkan' }
                                )}

                                {/* Notification Messages */}
                                {renderTranslationFields(
                                    'header.notificationDeposit',
                                    'Уведомление о подтверждении депозита',
                                    { ru: 'Ваш депозит был подтверждён', en: 'Your deposit has been confirmed', ms: 'Deposit anda telah disahkan' }
                                )}

                                {renderTranslationFields(
                                    'header.notificationPair',
                                    'Уведомление о новой торговой паре',
                                    { ru: 'Новая торговая пара доступна', en: 'New trading pair available', ms: 'Pasangan dagangan baharu tersedia' }
                                )}

                                {renderTranslationFields(
                                    'header.notificationReport',
                                    'Уведомление о готовности отчета',
                                    { ru: 'Еженедельный отчёт рынка готов', en: 'Weekly market report is ready', ms: 'Laporan pasaran mingguan sudah siap' }
                                )}

                                {/* Time Labels */}
                                {renderTranslationFields(
                                    'header.timeMinutes',
                                    'Обозначение минут в времени уведомлений',
                                    { ru: 'мин. назад', en: 'min ago', ms: 'minit lalu' }
                                )}

                                {renderTranslationFields(
                                    'header.timeHour',
                                    'Обозначение часа в времени уведомлений',
                                    { ru: 'ч. назад', en: 'hour ago', ms: 'jam lalu' }
                                )}
                                {renderTranslationFields(
                                    'header.timeHours',
                                    'Обозначение часов в времени уведомлений',
                                    { ru: 'ч. назад', en: 'hours ago', ms: 'jam lalu' }
                                )}
                            </div>
                            </Collapsible>
                        </div>



                        {/* NavBar Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('navbar')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('navbar')}
                                    <img src={setting} alt="NavBar" className={s.sectionIcon} />
                                    Переводы блока навигации (NavBar)
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.navbar ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.navbar}>
                            <div className={s.sectionContent}>

                                {/* Dashboard */}
                                {renderTranslationFields(
                                    'navbar.dashboard',
                                    'Название главной страницы в навигационном меню',
                                    { ru: 'Главная', en: 'Dashboard', ms: 'Dashboard' }
                                )}

                                {/* Trading Section */}
                                {renderTranslationFields(
                                    'navbar.trading_section',
                                    'Название раздела торговли в навигационном меню',
                                    { ru: 'Торговля', en: 'Trading', ms: 'Seksyen Dagangan' }
                                )}

                                {/* Markets */}
                                {renderTranslationFields(
                                    'navbar.markets',
                                    'Название раздела рынков в навигационном меню',
                                    { ru: 'Рынки', en: 'Markets', ms: 'Pasaran' }
                                )}

                                {/* Analytics */}
                                {renderTranslationFields(
                                    'navbar.analytics',
                                    'Название раздела аналитики в навигационном меню',
                                    { ru: 'Аналитика', en: 'Analytics', ms: 'Analitik' }
                                )}

                                {/* Orders */}
                                {renderTranslationFields(
                                    'navbar.orders',
                                    'Название раздела ордеров в навигационном меню',
                                    { ru: 'Ордера', en: 'Orders', ms: 'Pesanan' }
                                )}

                                {/* History */}
                                {renderTranslationFields(
                                    'navbar.history',
                                    'Название раздела истории операций в навигационном меню',
                                    { ru: 'История', en: 'History', ms: 'Sejarah' }
                                )}

                                {/* Wallet */}
                                {renderTranslationFields(
                                    'navbar.wallet',
                                    'Название раздела кошелька в навигационном меню',
                                    { ru: 'Кошелек', en: 'Wallet', ms: 'Dompet' }
                                )}

                                {/* Earn / Staking */}
                                {renderTranslationFields(
                                    'navbar.earn',
                                    'Название раздела стейкинга в навигационном меню',
                                    { ru: 'Стейкинг', en: 'Staking', ms: 'Staking' }
                                )}

                                {/* Broker */}
                                {renderTranslationFields(
                                    'navbar.broker',
                                    'Название раздела брокера в навигационном меню',
                                    { ru: 'Брокер', en: 'Broker', ms: 'Broker' }
                                )}

                                {/* News */}
                                {renderTranslationFields(
                                    'navbar.news',
                                    'Название раздела новостей в навигационном меню',
                                    { ru: 'Новости', en: 'News', ms: 'Berita' }
                                )}

                                {/* Mailbox */}
                                {renderTranslationFields(
                                    'navbar.mailbox',
                                    'Название раздела почты в навигационном меню',
                                    { ru: 'Почта', en: 'Mailbox', ms: 'Peti Mel' }
                                )}

                                {/* Settings */}
                                {renderTranslationFields(
                                    'navbar.settings',
                                    'Название раздела настроек в навигационном меню',
                                    { ru: 'Настройки', en: 'Settings', ms: 'Tetapan' }
                                )}

                                {/* Region */}
                                {renderTranslationFields(
                                    'navbar.region',
                                    'Название элемента выбора региона в нижней части меню',
                                    { ru: 'Регион', en: 'Region', ms: 'Kawasan' }
                                )}

                                {/* Support */}
                                {renderTranslationFields(
                                    'navbar.support',
                                    'Название кнопки поддержки в нижней части меню',
                                    { ru: 'Поддержка', en: 'Support', ms: 'Sokongan' }
                                )}

                                {/* Logout */}
                                {renderTranslationFields(
                                    'navbar.logout',
                                    'Название кнопки выхода в нижней части меню',
                                    { ru: 'Выйти', en: 'Logout', ms: 'Log Keluar' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Dashboard Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('dashboard')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('dashboard')}
                                    <img src={dashboardImg} alt="Dashboard" className={s.sectionIcon} />
                                    Переводы блока Dashboard
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.dashboard ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.dashboard}>
                            <div className={s.sectionContent}>

                                {/* Welcome Back */}
                                {renderTranslationFields(
                                    'dashboard.welcomeBack',
                                    'Приветственный текст на главной странице',
                                    { ru: 'Добро пожаловать обратно', en: 'Welcome back', ms: 'Selamat kembali' }
                                )}

                                {/* Portfolio Today */}
                                {renderTranslationFields(
                                    'dashboard.portfolioToday',
                                    'Описание под приветствием на главной странице',
                                    { ru: 'Вот что происходит с вашим портфолио сегодня', en: 'Here\'s what\'s happening with your portfolio today', ms: 'Inilah yang berlaku dengan portfolio anda hari ini' }
                                )}

                                {/* Balance Amount */}
                                {renderTranslationFields(
                                    'dashboard.balanceAmount',
                                    'Название для карточки с балансом',
                                    { ru: 'Сумма баланса', en: 'Balance Amount', ms: 'Jumlah Baki' }
                                )}

                                {/* Today's P&L */}
                                {renderTranslationFields(
                                    'dashboard.todayPnL',
                                    'Название для карточки с прибылью/убытком за день',
                                    { ru: 'P&L сегодня', en: 'Today\'s P&L', ms: 'P&L Hari Ini' }
                                )}

                                {/* Active Positions */}
                                {renderTranslationFields(
                                    'dashboard.activePositions',
                                    'Название для карточки с активными позициями',
                                    { ru: 'Активные позиции', en: 'Active Positions', ms: 'Kedudukan Aktif' }
                                )}

                                {/* Portfolio Growth */}
                                {renderTranslationFields(
                                    'dashboard.portfolioGrowth',
                                    'Название для карточки с ростом портфолио',
                                    { ru: 'Рост портфолио', en: 'Portfolio Growth', ms: 'Pertumbuhan Portfolio' }
                                )}

                                {/* No Active Trading Sessions */}
                                {renderTranslationFields(
                                    'dashboard.noActiveTradingSessions',
                                    'Сообщение когда нет активных торговых сессий',
                                    { ru: 'Нет активных торговых сессий', en: 'No Active Trading Sessions', ms: 'Tiada Sesi Dagangan Aktif' }
                                )}

                                {/* This Month */}
                                {renderTranslationFields(
                                    'dashboard.thisMonth',
                                    'Период времени для роста портфолио',
                                    { ru: 'В этом месяце', en: 'This Month', ms: 'Bulan Ini' }
                                )}

                                {/* Portfolio Overview */}
                                {renderTranslationFields(
                                    'dashboard.portfolioOverview',
                                    'Заголовок секции с графиком портфолио',
                                    { ru: 'Обзор портфолио', en: 'Portfolio Overview', ms: 'Gambaran Portfolio' }
                                )}

                                {/* Your Assets */}
                                {renderTranslationFields(
                                    'dashboard.yourAssets',
                                    'Заголовок списка активов пользователя',
                                    { ru: 'Ваши активы', en: 'Your Assets', ms: 'Aset Anda' }
                                )}

                                {/* View All */}
                                {renderTranslationFields(
                                    'dashboard.viewAll',
                                    'Кнопка для просмотра всех активов',
                                    { ru: 'Посмотреть все', en: 'View All', ms: 'Lihat Semua' }
                                )}

                                {/* Deposit */}
                                {renderTranslationFields(
                                    'dashboard.deposit',
                                    'Кнопка депозита в торговой панели',
                                    { ru: 'Депозит', en: 'Deposit', ms: 'Deposit' }
                                )}
                                {/* Withdraw */}
                                {renderTranslationFields(
                                    'dashboard.withdraw',
                                    'Кнопка вывода в торговой панели',
                                    { ru: 'Вывод', en: 'Withdraw', ms: 'Keluarkan' }
                                )}

                                {/* Coming Soon */}
                                {renderTranslationFields(
                                    'dashboard.comingSoon',
                                    'Заголовок для функции депозита',
                                    { ru: 'Скоро появится', en: 'Coming Soon', ms: 'Deposit Akan Datang' }
                                )}

                                {/* Deposit Description */}
                                {renderTranslationFields(
                                    'dashboard.depositDescription',
                                    'Описание для функции депозита',
                                    { ru: 'Мы работаем над функцией депозита. Пожалуйста, проверьте позже.', en: 'We are working on the deposit feature. Please check back later.', ms: 'Kami sedang bekerja pada fungsi deposit. Sila semak kembali kemudian.' }
                                )}

                                {/* Withdrawal Amount */}
                                {renderTranslationFields(
                                    'dashboard.withdrawalAmount',
                                    'Заголовок секции с суммой вывода',
                                    { ru: 'Сумма вывода', en: 'Withdrawal Amount', ms: 'Jumlah Pengeluaran' }
                                )}

                                {/* Enter Amount */}
                                {renderTranslationFields(
                                    'dashboard.enterAmount',
                                    'Подсказка для ввода суммы вывода',
                                    { ru: 'Введите сумму, которую хотите вывести', en: 'Enter the amount you want to withdraw', ms: 'Masukkan jumlah yang ingin anda keluarkan' }
                                )}

                                {/* Available Balance */}
                                {renderTranslationFields(
                                    'dashboard.availableBalance',
                                    'Надпись для доступного баланса',
                                    { ru: 'Доступный баланс', en: 'Available Balance', ms: 'Baki Tersedia' }
                                )}

                                {/* Network Fee */}
                                {renderTranslationFields(
                                    'dashboard.networkFee',
                                    'Надпись для сетевой комиссии',
                                    { ru: 'Сетевая комиссия', en: 'Network Fee', ms: 'Yuran Rangkaian' }
                                )}

                                {/* Total Amount */}
                                {renderTranslationFields(
                                    'dashboard.totalAmount',
                                    'Надпись для общей суммы',
                                    { ru: 'Общая сумма', en: 'Total Amount', ms: 'Jumlah Keseluruhan' }
                                )}

                                {/* Withdrawal Details */}
                                {renderTranslationFields(
                                    'dashboard.withdrawalDetails',
                                    'Заголовок секции с деталями вывода',
                                    { ru: 'Детали вывода', en: 'Withdrawal Details', ms: 'Butiran Pengeluaran' }
                                )}

                                {/* Enter Bank Details */}
                                {renderTranslationFields(
                                    'dashboard.enterBankDetails',
                                    'Подсказка для ввода банковских данных',
                                    { ru: 'Введите данные вашего банковского счета', en: 'Enter your bank account information', ms: 'Masukkan maklumat akaun bank anda' }
                                )}

                                {/* Account Number */}
                                {renderTranslationFields(
                                    'dashboard.accountNumber',
                                    'Надпись для поля номера счета',
                                    { ru: 'Номер счета', en: 'Account Number', ms: 'Nombor Akaun' }
                                )}

                                {/* Enter Account Number */}
                                {renderTranslationFields(
                                    'dashboard.enterAccountNumber',
                                    'Подсказка для поля номера счета',
                                    { ru: 'Введите номер вашего счета', en: 'Enter your account number', ms: 'Masukkan nombor akaun anda' }
                                )}

                                {/* Bank Wallet */}
                                {renderTranslationFields(
                                    'dashboard.bankWallet',
                                    'Надпись для выбора банка/кошелька',
                                    { ru: 'Банк / Цифровой кошелек', en: 'Bank / Digital Wallet', ms: 'Bank / Dompet Digital' }
                                )}

                                {/* Recipient Name */}
                                {renderTranslationFields(
                                    'dashboard.recipientName',
                                    'Надпись для поля имени получателя',
                                    { ru: 'Имя получателя', en: 'Recipient Name', ms: 'Nama Penerima' }
                                )}

                                {/* Enter Recipient Name */}
                                {renderTranslationFields(
                                    'dashboard.enterRecipientName',
                                    'Подсказка для поля имени получателя',
                                    { ru: 'Введите имя получателя', en: 'Enter recipient name', ms: 'Masukkan nama penerima' }
                                )}

                                {/* Convert */}
                                {renderTranslationFields(
                                    'dashboard.convert',
                                    'Кнопка конвертации в торговой панели',
                                    { ru: 'Конвертировать', en: 'Convert', ms: 'Tukar' }
                                )}

                                {/* Processing */}
                                {renderTranslationFields(
                                    'dashboard.processing',
                                    'Состояние кнопки во время обработки',
                                    { ru: 'Обработка', en: 'Processing', ms: 'Memproses' }
                                )}

                                {/* Success */}
                                {renderTranslationFields(
                                    'dashboard.success',
                                    'Состояние кнопки при успешном выполнении',
                                    { ru: 'Успешно', en: 'Success', ms: 'Berjaya' }
                                )}

                                {/* Error */}
                                {renderTranslationFields(
                                    'dashboard.error',
                                    'Состояние кнопки при ошибке',
                                    { ru: 'Ошибка', en: 'Error', ms: 'Ralat' }
                                )}

                                {/* Nova: активный профиль клиента в hero-секции */}
                                {renderTranslationFields(
                                    'dashboard.workingWithClient',
                                    'Подпись активного профиля клиента (hero-секция)',
                                    { ru: 'Активный профиль клиента:', en: 'Active client profile:', ms: 'Profil klien aktif:' }
                                )}

                                {/* Nova: подсказка на графике */}
                                {renderTranslationFields(
                                    'dashboard.chartTooltip.balance',
                                    'Подсказка графика: баланс',
                                    { ru: 'Баланс', en: 'Balance', ms: 'Baki' }
                                )}

                                {renderTranslationFields(
                                    'dashboard.chartTooltip.change',
                                    'Подсказка графика: изменение',
                                    { ru: 'Изменение', en: 'Change', ms: 'Perubahan' }
                                )}

                                {renderTranslationFields(
                                    'dashboard.chartTooltip.initial',
                                    'Подсказка графика: стартовая сумма',
                                    { ru: 'Старт', en: 'Initial', ms: 'Permulaan' }
                                )}

                                {/* Nova: блок трендов */}
                                {renderTranslationFields(
                                    'dashboard.trendingTitle',
                                    'Заголовок блока трендовых монет',
                                    { ru: 'В тренде', en: 'Trending', ms: 'Sohor kini' }
                                )}

                                {renderTranslationFields(
                                    'dashboard.trendingRank',
                                    'Подсказка ранга монеты по капитализации',
                                    { ru: 'Ранг по капитализации', en: 'Market cap rank', ms: 'Kedudukan permodalan' }
                                )}

                                {/* Nova: блок новостей */}
                                {renderTranslationFields(
                                    'dashboard.newsTitle',
                                    'Заголовок блока новостей',
                                    { ru: 'Новости', en: 'News', ms: 'Berita' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* TradingDashboard Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('tradingDashboard')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('tradingDashboard')}
                                    <img src={trade} alt="TradingDashboard" className={s.sectionIcon} />
                                    Переводы блока TradingDashboard
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.tradingDashboard ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.tradingDashboard}>
                            <div className={s.sectionContent}>

                                {/* Order Types */}
                                {renderTranslationFields(
                                    'tradingDashboard.orderTypes.limit',
                                    'Тип ордера: лимитный',
                                    { ru: 'Лимит', en: 'Limit', ms: 'Had' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.orderTypes.market',
                                    'Тип ордера: рыночный',
                                    { ru: 'Рынок', en: 'Market', ms: 'Pasaran' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.orderTypes.stop',
                                    'Тип ордера: стоп',
                                    { ru: 'Стоп', en: 'Stop', ms: 'Berhenti' }
                                )}

                                {/* Trading Sides */}
                                {renderTranslationFields(
                                    'tradingDashboard.sides.buy',
                                    'Кнопка покупки',
                                    { ru: 'Купить', en: 'Buy', ms: 'Beli' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.sides.sell',
                                    'Кнопка продажи',
                                    { ru: 'Продать', en: 'Sell', ms: 'Jual' }
                                )}

                                {/* Form Labels */}
                                {renderTranslationFields(
                                    'tradingDashboard.form.priceLabel',
                                    'Метка поля цены',
                                    { ru: 'Цена (USD)', en: 'Price (USD)', ms: 'Harga (USD)' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.form.amountLabel',
                                    'Метка поля количества',
                                    { ru: 'Количество', en: 'Amount', ms: 'Jumlah' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.form.pricePlaceholder',
                                    'Плейсхолдер поля цены',
                                    { ru: '113,418.97', en: '113,418.97', ms: '113,418.97' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.form.amountPlaceholder',
                                    'Плейсхолдер поля количества',
                                    { ru: '0.00', en: '0.00', ms: '0.00' }
                                )}
                                {/* Statistics */}
                                {renderTranslationFields(
                                    'tradingDashboard.stats.high24h',
                                    'Максимум за 24 часа',
                                    { ru: 'Макс. 24ч', en: '24h High', ms: 'Tinggi 24j' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.stats.low24h',
                                    'Минимум за 24 часа',
                                    { ru: 'Мин. 24ч', en: '24h Low', ms: 'Rendah 24j' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.stats.volume24h',
                                    'Объём торгов за 24 часа',
                                    { ru: 'Объём 24ч', en: '24h Volume', ms: 'Volum 24j' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.stats.marketCap',
                                    'Рыночная капитализация',
                                    { ru: 'Рын. капитал', en: 'Market Cap', ms: 'Had Pasaran' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.stats.change24h',
                                    'Изменение за 24 часа',
                                    { ru: 'Изменение 24ч', en: '24h Change', ms: 'Perubahan 24j' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.stats.indexPrice',
                                    'Индексная цена',
                                    { ru: 'Индексная цена', en: 'Index Price', ms: 'Harga Indeks' }
                                )}

                                {/* Section Headers */}
                                {renderTranslationFields(
                                    'tradingDashboard.sections.orderBook',
                                    'Заголовок секции книги ордеров',
                                    { ru: 'Книга ордеров', en: 'Order Book', ms: 'Buku Pesanan' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.sections.recentTrades',
                                    'Заголовок секции последних сделок',
                                    { ru: 'Последние сделки', en: 'Recent Trades', ms: 'Dagangan Terkini' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.sections.openOrders',
                                    'Заголовок секции открытых ордеров',
                                    { ru: 'Открытые ордера', en: 'Open Orders', ms: 'Pesanan Terbuka' }
                                )}

                                {/* Table Headers */}
                                {renderTranslationFields(
                                    'tradingDashboard.table.time',
                                    'Заголовок колонки времени',
                                    { ru: 'Время', en: 'Time', ms: 'Masa' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.price',
                                    'Заголовок колонки цены',
                                    { ru: 'Цена', en: 'Price', ms: 'Harga' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.amount',
                                    'Заголовок колонки количества',
                                    { ru: 'Количество', en: 'Amount', ms: 'Jumlah' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.side',
                                    'Заголовок колонки стороны сделки',
                                    { ru: 'Сторона', en: 'Side', ms: 'Pihak' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.type',
                                    'Заголовок колонки типа ордера',
                                    { ru: 'Тип', en: 'Type', ms: 'Jenis' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.pair',
                                    'Заголовок колонки торговой пары',
                                    { ru: 'Пара', en: 'Pair', ms: 'Pasangan' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.filled',
                                    'Заголовок колонки исполненного объёма',
                                    { ru: 'Исполнено', en: 'Filled', ms: 'Diisi' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.table.action',
                                    'Заголовок колонки действий',
                                    { ru: 'Действие', en: 'Action', ms: 'Tindakan' }
                                )}

                                {/* Buttons */}
                                {renderTranslationFields(
                                    'tradingDashboard.buttons.cancel',
                                    'Кнопка отмены ордера',
                                    { ru: 'Отменить', en: 'Cancel', ms: 'Batal' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.buttons.placeOrder',
                                    'Кнопка размещения ордера',
                                    { ru: 'Разместить ордер', en: 'Place Order', ms: 'Tempah Pesanan' }
                                )}

                                {/* Summary */}
                                {renderTranslationFields(
                                    'tradingDashboard.summary.available',
                                    'Доступные средства',
                                    { ru: 'Доступно', en: 'Available', ms: 'Tersedia' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.summary.total',
                                    'Общая сумма ордера',
                                    { ru: 'Итого', en: 'Total', ms: 'Jumlah' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.summary.fee',
                                    'Комиссия за сделку',
                                    { ru: 'Комиссия (0.1%)', en: 'Fee (0.1%)', ms: 'Yuran (0.1%)' }
                                )}

                                {/* Empty State */}
                                {renderTranslationFields(
                                    'tradingDashboard.emptyState.noOpenOrders',
                                    'Сообщение об отсутствии открытых ордеров',
                                    { ru: 'Нет открытых ордеров', en: 'No open orders', ms: 'Tiada pesanan terbuka' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.emptyState.noData',
                                    'Сообщение об отсутствии данных (вкладки истории)',
                                    { ru: 'Нет данных', en: 'No data', ms: 'Tiada data' }
                                )}

                                {/* Nova: нижние вкладки терминала */}
                                {renderTranslationFields(
                                    'tradingDashboard.tabs.orderHistory',
                                    'Вкладка истории ордеров',
                                    { ru: 'История ордеров', en: 'Order History', ms: 'Sejarah pesanan' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.tabs.tradeHistory',
                                    'Вкладка истории сделок',
                                    { ru: 'История сделок', en: 'Trade History', ms: 'Sejarah dagangan' }
                                )}

                                {renderTranslationFields(
                                    'tradingDashboard.tabs.funds',
                                    'Вкладка средств',
                                    { ru: 'Средства', en: 'Funds', ms: 'Dana' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* Wallet Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('wallet')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('wallet')}
                                    <img src={wallet} alt="Wallet" className={s.sectionIcon} />
                                    Переводы блока Wallet
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.wallet ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.wallet}>
                            <div className={s.sectionContent}>

                                {/* Transaction Actions */}
                                {renderTranslationFields(
                                    'wallet.actions.none',
                                    'Действие "Нет"',
                                    { ru: 'Нет', en: 'None', ms: 'Tiada' }
                                )}

                                {renderTranslationFields(
                                    'wallet.actions.fixNow',
                                    'Действие "Исправить сейчас"',
                                    { ru: 'Исправить сейчас', en: 'Fix now', ms: 'Betulkan sekarang' }
                                )}

                                {/* Withdrawal Status */}
                                {renderTranslationFields(
                                    'wallet.withdrawalStatus.failed',
                                    'Статус неудачного вывода',
                                    { ru: 'вывод не удался - требуется действие', en: 'withdrawal failed - action required', ms: 'pengeluaran gagal - tindakan diperlukan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.withdrawalStatus.pending',
                                    'Статус ожидающего вывода',
                                    { ru: 'в процессе вывода в данный момент', en: 'currently in withdrawal process', ms: 'dalam pengeluaran pada masa ini' }
                                )}

                                {renderTranslationFields(
                                    'wallet.withdrawalStatus.inProcess',
                                    'Статус вывода в процессе',
                                    { ru: 'вывод в процессе', en: 'withdrawal in process', ms: 'pengeluaran dalam proses' }
                                )}

                                {renderTranslationFields(
                                    'wallet.withdrawalStatus.success',
                                    'Статус успешного вывода',
                                    { ru: 'вывод успешно завершён', en: 'withdrawal successfully completed', ms: 'pengeluaran berjaya selesai' }
                                )}

                                {/* Section Headers */}
                                {renderTranslationFields(
                                    'wallet.sections.totalBalance',
                                    'Заголовок секции общего баланса',
                                    { ru: 'Общий баланс', en: 'Total Balance', ms: 'Jumlah Baki' }
                                )}

                                {renderTranslationFields(
                                    'wallet.sections.yourPortfolio',
                                    'Заголовок секции портфеля',
                                    { ru: 'Ваш портфель', en: 'Your Portfolio', ms: 'Portfolio Anda' }
                                )}

                                {renderTranslationFields(
                                    'wallet.sections.transactionHistory',
                                    'Заголовок секции истории транзакций',
                                    { ru: 'История транзакций', en: 'Transaction History', ms: 'Sejarah Transaksi' }
                                )}

                                {/* Buttons */}
                                {renderTranslationFields(
                                    'wallet.buttons.deposit',
                                    'Кнопка депозита',
                                    { ru: 'Депозит', en: 'Deposit', ms: 'Deposit' }
                                )}

                                {renderTranslationFields(
                                    'wallet.buttons.withdraw',
                                    'Кнопка вывода',
                                    { ru: 'Вывести', en: 'Withdraw', ms: 'Keluarkan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.buttons.viewAll',
                                    'Кнопка "Посмотреть все"',
                                    { ru: 'Посмотреть все', en: 'View All', ms: 'Lihat Semua' }
                                )}

                                {/* Transaction Filters */}
                                {renderTranslationFields(
                                    'wallet.filters.allTransactions',
                                    'Фильтр "Все транзакции"',
                                    { ru: 'Все транзакции', en: 'All Transactions', ms: 'Semua Transaksi' }
                                )}

                                {renderTranslationFields(
                                    'wallet.filters.withdrawals',
                                    'Фильтр "Выводы"',
                                    { ru: 'Выводы', en: 'Withdrawals', ms: 'Pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'wallet.filters.deposits',
                                    'Фильтр "Депозиты"',
                                    { ru: 'Депозиты', en: 'Deposits', ms: 'Deposit' }
                                )}

                                {renderTranslationFields(
                                    'wallet.filters.conversions',
                                    'Фильтр "Конвертации"',
                                    { ru: 'Конвертации', en: 'Conversions', ms: 'Penukaran' }
                                )}

                                {/* Table Headers */}
                                {renderTranslationFields(
                                    'wallet.tableHeaders.description',
                                    'Заголовок колонки описания',
                                    { ru: 'Описание', en: 'Description', ms: 'Perihalan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.tableHeaders.currency',
                                    'Заголовок колонки валюты',
                                    { ru: 'Валюта', en: 'Currency', ms: 'Mata Wang' }
                                )}

                                {renderTranslationFields(
                                    'wallet.tableHeaders.amount',
                                    'Заголовок колонки суммы',
                                    { ru: 'Сумма', en: 'Amount', ms: 'Jumlah' }
                                )}

                                {renderTranslationFields(
                                    'wallet.tableHeaders.status',
                                    'Заголовок колонки статуса',
                                    { ru: 'Статус', en: 'Status', ms: 'Status' }
                                )}

                                {renderTranslationFields(
                                    'wallet.tableHeaders.action',
                                    'Заголовок колонки действий',
                                    { ru: 'Действие', en: 'Action', ms: 'Tindakan' }
                                )}

                                {/* Pagination */}
                                {renderTranslationFields(
                                    'wallet.pagination.transactions',
                                    'Слово "Транзакций" в пагинации',
                                    { ru: 'Транзакций', en: 'Transactions', ms: 'Transaksi' }
                                )}

                                {renderTranslationFields(
                                    'wallet.pagination.all',
                                    'Опция "Все" в селекте',
                                    { ru: 'Все', en: 'All', ms: 'Semua' }
                                )}

                                {renderTranslationFields(
                                    'wallet.pagination.transfer',
                                    'Опция "Перевод" в селекте',
                                    { ru: 'Перевод', en: 'Transfer', ms: 'Transfer' }
                                )}

                                {/* Transaction Types */}
                                {renderTranslationFields(
                                    'wallet.transactionTypes.transfer',
                                    'Слово "Перевод" (для любой валюты)',
                                    { ru: 'Перевод', en: 'Transfer', ms: 'Transfer' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionTypes.conversion',
                                    'Слово "Конвертация" (для любой криптовалюты)',
                                    { ru: 'Конвертация', en: 'Conversion', ms: 'Penukaran' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionTypes.tradingSession',
                                    'Тип транзакции "Торговая сессия"',
                                    { ru: 'Торговая сессия', en: 'Trading Session', ms: 'Sesi Dagangan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionTypes.deposit',
                                    'Слово "Депозит" (для любой валюты)',
                                    { ru: 'Депозит', en: 'Deposit', ms: 'Deposit' }
                                )}

                                {/* Statuses */}
                                {renderTranslationFields(
                                    'wallet.statuses.success',
                                    'Статус "Успешно"',
                                    { ru: 'Успешно', en: 'Success', ms: 'Success' }
                                )}

                                {renderTranslationFields(
                                    'wallet.statuses.pending',
                                    'Статус "Ожидание"',
                                    { ru: 'Ожидание', en: 'Pending', ms: 'Pending' }
                                )}

                                {renderTranslationFields(
                                    'wallet.statuses.inProcess',
                                    'Статус "В процессе"',
                                    { ru: 'В процессе', en: 'In Process', ms: 'In Process' }
                                )}
                                {renderTranslationFields(
                                    'wallet.statuses.error',
                                    'Статус "Ошибка"',
                                    { ru: 'Ошибка', en: 'Error', ms: 'Error' }
                                )}

                                {renderTranslationFields(
                                    'wallet.statuses.fixed',
                                    'Статус "Исправлено"',
                                    { ru: 'Исправлено', en: 'Fixed', ms: 'Fixed' }
                                )}

                                {/* Transaction Description */}
                                {renderTranslationFields(
                                    'wallet.transactionDescription.toLocalCurrency',
                                    'Предлог "к/в/to/ke/a" для описания конвертации в местную валюту',
                                    { ru: 'в', en: 'to', ms: 'ke' }
                                )}

                                {renderTranslationFields(
                                    'wallet.balanceOnHold',
                                    'Чип «На выводе» у заблокированного баланса',
                                    { ru: 'На выводе', en: 'Pending withdrawal', ms: 'Pengeluaran dijalankan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionDescription.toClientAccount',
                                    'Описание депозита «На счёт клиента: имя»',
                                    { ru: 'На счёт клиента', en: 'To client account', ms: 'Ke akaun pelanggan' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionDescription.broker',
                                    'Подпись брокера в описании торговой сессии',
                                    { ru: 'Брокер', en: 'Broker', ms: 'Broker' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionDescription.licenseNo',
                                    'Подпись номера лицензии в описании торговой сессии',
                                    { ru: 'Лицензия №', en: 'License No', ms: 'No. Lesen' }
                                )}

                                {renderTranslationFields(
                                    'wallet.transactionDescription.toAddress',
                                    'Предлог «на/to» перед адресом в описании перевода',
                                    { ru: 'на', en: 'to', ms: 'ke' }
                                )}

                                {/* Nova: подраздел трендовых монет в портфеле */}
                                {renderTranslationFields(
                                    'wallet.sections.mayInterest',
                                    'Заголовок подраздела «Вам может быть интересно»',
                                    { ru: 'Вам может быть интересно', en: 'You may be interested in', ms: 'Anda mungkin berminat' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* BrokerProgram Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('brokerProgram')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('brokerProgram')}
                                    <img src={broker} alt="BrokerProgram" className={s.sectionIcon} />
                                    Переводы блока BrokerProgram
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.brokerProgram ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.brokerProgram}>
                            <div className={s.sectionContent}>

                                {/* Header Buttons */}
                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.addClient',
                                    'Кнопка "Добавить клиента"',
                                    { ru: 'Добавить клиента', en: 'Add Client', ms: 'Tambah Klien' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.brokerCenter',
                                    'Кнопка "Центр брокера"',
                                    { ru: 'Центр брокера', en: 'Broker Center', ms: 'Pusat Broker' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.orderCenter',
                                    'Кнопка "Центр заказов"',
                                    { ru: 'Центр заказов', en: 'Order Center', ms: 'Pusat Pesanan' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.options',
                                    'Кнопка "Опции"',
                                    { ru: 'Опции', en: 'Options', ms: 'Pilihan' }
                                )}

                                {/* Nova: дополнительные кнопки брокера */}
                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.reports',
                                    'Кнопка "Отчёты P&L"',
                                    { ru: 'Отчёты P&L', en: 'P&L Reports', ms: 'Laporan P&L' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.assets',
                                    'Кнопка "Распределение активов"',
                                    { ru: 'Распределение активов', en: 'Asset Distribution', ms: 'Agihan aset' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.defi',
                                    'Кнопка "DeFi Стейкинг"',
                                    { ru: 'DeFi Стейкинг', en: 'DeFi Staking', ms: 'Staking DeFi' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.apiKeys',
                                    'Кнопка "API-ключи"',
                                    { ru: 'API-ключи', en: 'API Keys', ms: 'Kunci API' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.kyc',
                                    'Кнопка "Верификация KYC"',
                                    { ru: 'Верификация KYC', en: 'KYC Verification', ms: 'Pengesahan KYC' }
                                )}

                                {/* Nova: таймлайн статуса в модалке транзакции */}
                                {renderTranslationFields(
                                    'brokerProgram.timeline.sent',
                                    'Шаг таймлайна «Отправлено»',
                                    { ru: 'Отправлено', en: 'Sent', ms: 'Dihantar' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.timeline.processing',
                                    'Шаг таймлайна «В обработке»',
                                    { ru: 'В обработке', en: 'Processing', ms: 'Dalam proses' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.timeline.credited',
                                    'Шаг таймлайна «Зачислено»',
                                    { ru: 'Зачислено', en: 'Credited', ms: 'Dikreditkan' }
                                )}

                                {/* Nova: таблица последних выводов */}
                                {renderTranslationFields(
                                    'brokerProgram.table.client',
                                    'Колонка «Клиент»',
                                    { ru: 'Клиент', en: 'Client', ms: 'Pelanggan' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.bank',
                                    'Колонка «Банк-получатель»',
                                    { ru: 'Банк-получатель', en: 'Recipient bank', ms: 'Bank penerima' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.amount',
                                    'Колонка «Сумма»',
                                    { ru: 'Сумма', en: 'Amount', ms: 'Jumlah' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.live',
                                    'Бейдж «LIVE»',
                                    { ru: 'LIVE', en: 'LIVE', ms: 'LIVE' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.transactions',
                                    'Футер «Транзакции»',
                                    { ru: 'Транзакции', en: 'Transactions', ms: 'Transaksi' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.totalVolume',
                                    'Футер «Общий объём»',
                                    { ru: 'Общий объём', en: 'Total volume', ms: 'Jumlah volum' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.assetDistribution',
                                    'Кнопка "Распределение активов"',
                                    { ru: 'Распределение активов', en: 'Asset Distribution', ms: 'Pengedaran Aset' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.defi',
                                    'Кнопка "DeFi"',
                                    { ru: 'DeFi', en: 'DeFi', ms: 'DeFi' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.porReports',
                                    'Кнопка "Отчеты PoR"',
                                    { ru: 'Отчеты PoR', en: 'PoR Reports', ms: 'Laporan PoR' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.headerButtons.settings',
                                    'Кнопка "Настройки"',
                                    { ru: 'Настройки', en: 'Settings', ms: 'Tetapan' }
                                )}

                                {/* Statistics */}
                                {renderTranslationFields(
                                    'brokerProgram.statistics.brokerStatistics',
                                    'Заголовок "Статистика брокера"',
                                    { ru: 'Статистика брокера', en: 'Broker Statistics', ms: 'Statistik Broker' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.viewMore',
                                    'Кнопка "Посмотреть еще"',
                                    { ru: 'Посмотреть еще', en: 'View More', ms: 'Lihat Lagi' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.totalClients',
                                    'Метка "Общее количество клиентов"',
                                    { ru: 'Общее количество клиентов', en: 'Total Clients', ms: 'Jumlah Klien' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.averagePnL',
                                    'Метка "Средний P&L (%)"',
                                    { ru: 'Средний P&L (%)', en: 'Average P&L (%)', ms: 'Purata P&L (%)' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.mainCurrency',
                                    'Метка "Основная валюта"',
                                    { ru: 'Основная валюта', en: 'Main Currency', ms: 'Mata Wang Utama' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.sent24h',
                                    'Метка "Отправлено (24ч)"',
                                    { ru: 'Отправлено (24ч)', en: 'Sent (24h)', ms: 'Dihantar (24j)' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.tradableAssets',
                                    'Метка "Торгуемые активы"',
                                    { ru: 'Торгуемые активы', en: 'Tradable Assets', ms: 'Aset Boleh Didagangkan' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statistics.dailyVolume',
                                    'Метка "Дневной объем"',
                                    { ru: 'Дневной объем', en: 'Daily Volume', ms: 'Volum Harian' }
                                )}

                                {/* P&L Summary */}
                                {renderTranslationFields(
                                    'brokerProgram.pnlSummary.title',
                                    'Заголовок "Отчет сводки P&L"',
                                    { ru: 'Отчет сводки P&L', en: 'P&L Summary Report', ms: 'Laporan Ringkasan P&L' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.pnlSummary.viewMore',
                                    'Кнопка "Посмотреть еще" (P&L блок)',
                                    { ru: 'Посмотреть еще', en: 'View More', ms: 'Lihat Lagi' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.pnlSummary.dailyPnL',
                                    'Метка "P&L Дневной (%)"',
                                    { ru: 'P&L Дневной (%)', en: 'Daily P&L (%)', ms: 'P&L Harian (%)' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.pnlSummary.cumulativePnL',
                                    'Метка "P&L Накопительный (%)"',
                                    { ru: 'P&L Накопительный (%)', en: 'Cumulative P&L (%)', ms: 'P&L Terkumpul (%)' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.pnlSummary.spreads',
                                    'Метка "Спреды"',
                                    { ru: 'Спреды', en: 'Spreads', ms: 'Spread' }
                                )}

                                {/* Nova: панели распределения в обзоре брокера */}
                                {renderTranslationFields(
                                    'brokerProgram.overview.byAsset',
                                    'Заголовок панели «Доход по активам»',
                                    { ru: 'Доход по активам', en: 'Profit by asset', ms: 'Untung mengikut aset' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.overview.byBank',
                                    'Заголовок панели «Объём по банкам»',
                                    { ru: 'Объём по банкам', en: 'Volume by bank', ms: 'Volum mengikut bank' }
                                )}

                                {/* Table Headers */}
                                {renderTranslationFields(
                                    'brokerProgram.table.latestWithdrawals',
                                    'Заголовок "Последние выводы"',
                                    { ru: 'Последние выводы', en: 'Latest Withdrawals', ms: 'Pengeluaran Terkini' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.brokerTitle',
                                    'Заголовок таблицы с именем брокера',
                                    { ru: 'Последние выводы / Брокер: Lobruno Kevin Antonio (918552)', en: 'Latest Withdrawals / Broker: Lobruno Kevin Antonio (918552)', ms: 'Pengeluaran Terkini / Broker: Lobruno Kevin Antonio (918552)' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.information',
                                    'Заголовок колонки "Информация"',
                                    { ru: 'Информация', en: 'Information', ms: 'Information' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.cbucvu',
                                    'Заголовок колонки "CBU/CVU"',
                                    { ru: 'CBU/CVU', en: 'CBU/CVU', ms: 'CBU/CVU' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.amountBank',
                                    'Заголовок колонки "Сумма/Банк"',
                                    { ru: 'Сумма/Банк', en: 'Amount/Bank', ms: 'Amount/Bank' }
                                )}
                                {renderTranslationFields(
                                    'brokerProgram.table.status',
                                    'Заголовок колонки "Статус"',
                                    { ru: 'Статус', en: 'Status', ms: 'Status' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.table.action',
                                    'Заголовок колонки "Действие"',
                                    { ru: 'Действие', en: 'Action', ms: 'Action' }
                                )}

                                {/* Actions */}
                                {renderTranslationFields(
                                    'brokerProgram.actions.transfer',
                                    'Слово "Перевод" (для любой валюты)',
                                    { ru: 'Перевод', en: 'Transfer', ms: 'Transfer' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.actions.goToAccount',
                                    'Кнопка "Перейти в аккаунт"',
                                    { ru: 'Перейти в аккаунт', en: 'Go to Account', ms: 'Pergi ke Akaun' }
                                )}

                                {/* Statuses */}
                                {renderTranslationFields(
                                    'brokerProgram.statuses.success',
                                    'Статус "Успешно"',
                                    { ru: 'Успешно', en: 'Success', ms: 'Success' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statuses.pending',
                                    'Статус "Ожидание"',
                                    { ru: 'Ожидание', en: 'Pending', ms: 'Pending' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.statuses.inProcess',
                                    'Статус "В процессе"',
                                    { ru: 'В процессе', en: 'In Process', ms: 'In Process' }
                                )}

                                {/* Modal */}
                                {renderTranslationFields(
                                    'brokerProgram.modal.sent',
                                    'Заголовок модального окна "Отправлено!"',
                                    { ru: 'Отправлено!', en: 'Sent!', ms: 'Dihantar!' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.modal.name',
                                    'Поле "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.modal.bank',
                                    'Поле "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.modal.paymentDate',
                                    'Поле "Дата платежа:"',
                                    { ru: 'Дата платежа:', en: 'Payment Date:', ms: 'Tarikh pembayaran:' }
                                )}

                                {renderTranslationFields(
                                    'brokerProgram.modal.transactionHistory',
                                    'Кнопка "История транзакций"',
                                    { ru: 'История транзакций', en: 'Transaction History', ms: 'Sejarah Transaksi' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* Settings Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('settings')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('settings')}
                                    <img src={siteSettingsIcon} alt="Settings" className={s.sectionIcon} />
                                    Переводы блока Settings
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.settings ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.settings}>
                            <div className={s.sectionContent}>
                                {/* Tabs */}
                                {renderTranslationFields(
                                    'settings.tabs.personal',
                                    'Вкладка "Личные данные"',
                                    { ru: 'Личные данные', en: 'Personal Info', ms: 'Peribadi' }
                                )}

                                {renderTranslationFields(
                                    'settings.tabs.preferences',
                                    'Вкладка "Настройки"',
                                    { ru: 'Настройки', en: 'Preferences', ms: 'Pilihan' }
                                )}

                                {renderTranslationFields(
                                    'settings.tabs.payments',
                                    'Вкладка "Платежи"',
                                    { ru: 'Платежи', en: 'Payments', ms: 'Pembayaran' }
                                )}

                                {renderTranslationFields(
                                    'settings.tabs.security',
                                    'Вкладка "Безопасность"',
                                    { ru: 'Безопасность', en: 'Security', ms: 'Keselamatan' }
                                )}

                                {renderTranslationFields(
                                    'settings.tabs.address',
                                    'Вкладка "Адрес"',
                                    { ru: 'Адрес', en: 'Address', ms: 'Alamat' }
                                )}

                                {/* Personal Information */}
                                {renderTranslationFields(
                                    'settings.personal.title',
                                    'Заголовок "Личная информация"',
                                    { ru: 'Личная информация', en: 'Personal Information', ms: 'Maklumat Peribadi' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.changeImage',
                                    'Кнопка "Изменить изображение"',
                                    { ru: 'Изменить изображение', en: 'Change Image', ms: 'Tukar Imej' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.name',
                                    'Поле "Имя"',
                                    { ru: 'Имя', en: 'Name', ms: 'Nama' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.namePlaceholder',
                                    'Подсказка для поля имени',
                                    { ru: 'Введите ваше полное имя', en: 'Enter your full name', ms: 'Masukkan nama penuh anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.country',
                                    'Поле "Страна / Регион"',
                                    { ru: 'Страна / Регион', en: 'Country / Region', ms: 'Negara / Wilayah' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.bank',
                                    'Поле "Банк"',
                                    { ru: 'Банк', en: 'Bank', ms: 'Bank' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.account',
                                    'Поле "CBU/CVU"',
                                    { ru: 'CBU/CVU', en: 'CBU/CVU', ms: 'CBU/CVU' }
                                )}

                                {renderTranslationFields(
                                    'settings.personal.accountPlaceholder',
                                    'Подсказка для поля счета',
                                    { ru: 'Введите номер вашего счета', en: 'Enter your account number', ms: 'Masukkan nombor akaun anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.saveChanges',
                                    'Кнопка "Сохранить изменения"',
                                    { ru: 'Сохранить изменения', en: 'Save Changes', ms: 'Simpan Perubahan' }
                                )}

                                {/* Preferences Section */}
                                {renderTranslationFields(
                                    'settings.preferences.title',
                                    'Заголовок "Настройки"',
                                    { ru: 'Настройки', en: 'Preferences', ms: 'Pilihan' }
                                )}

                                {/* Order Confirmation */}
                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.title',
                                    'Заголовок "Подтверждение ордера"',
                                    { ru: 'Подтверждение ордера', en: 'Order Confirmation', ms: 'Pengesahan Pesanan' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.description',
                                    'Описание подтверждения ордера',
                                    { ru: 'Если функция предупреждения ордера включена, она должна быть подтверждена повторно каждый раз при отправке ордера', en: 'If the order warning function is enabled, it must be confirmed again each time an order is submitted', ms: 'Jika fungsi peringatan pesanan diaktifkan, ia mesti disahkan semula setiap kali pesanan dihantar' }
                                )}
                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.limitOrder',
                                    'Лимитный ордер',
                                    { ru: 'Лимитный ордер', en: 'Limit Order', ms: 'Pesanan Had' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.marketOrder',
                                    'Рыночный ордер',
                                    { ru: 'Рыночный ордер', en: 'Market Order', ms: 'Pesanan Pasaran' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.stopLimitOrder',
                                    'Стоп-лимитный ордер',
                                    { ru: 'Стоп-лимитный ордер', en: 'Stop Limit Order', ms: 'Pesanan Henti-Had' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.orderConfirmation.autoBorrow',
                                    'Авто займ / Повтор Маржи',
                                    { ru: 'Авто займ / Повтор Маржи', en: 'Auto Borrow / Margin Repeat', ms: 'Pinjam Auto / Ulang Margin' }
                                )}

                                {/* Notifications */}
                                {renderTranslationFields(
                                    'settings.preferences.notifications.title',
                                    'Заголовок "Уведомления"',
                                    { ru: 'Уведомления', en: 'Notifications', ms: 'Pemberitahuan' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.notifications.description',
                                    'Описание уведомлений',
                                    { ru: 'Это повлияет на настройки языка электронной почты и уведомлений приложения', en: 'This will affect email language and app notification settings', ms: 'Ini akan mempengaruhi tetapan bahasa e-mel dan pemberitahuan aplikasi' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.notifications.activities',
                                    'Активность',
                                    { ru: 'Активность', en: 'Activities', ms: 'Aktiviti' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.notifications.tradeNotifications',
                                    'Уведомления о торговле',
                                    { ru: 'Уведомления о торговле', en: 'Trade Notifications', ms: 'Pemberitahuan Dagangan' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.notifications.emailNotifications',
                                    'Уведомления по электронной почте',
                                    { ru: 'Уведомления по электронной почте', en: 'Email Notifications', ms: 'Pemberitahuan E-mel' }
                                )}

                                {renderTranslationFields(
                                    'settings.preferences.notifications.systemMessages',
                                    'Системные сообщения',
                                    { ru: 'Системные сообщения', en: 'System Messages', ms: 'Mesej Sistem' }
                                )}

                                {/* Payments Section */}
                                {renderTranslationFields(
                                    'settings.payments.title',
                                    'Заголовок "Настройки платежей"',
                                    { ru: 'Настройки платежей', en: 'Payment Settings', ms: 'Tetapan Pembayaran' }
                                )}

                                {renderTranslationFields(
                                    'settings.payments.addPaymentMethod',
                                    'Кнопка "Добавить новый способ оплаты"',
                                    { ru: 'Добавить новый способ оплаты', en: 'Add New Payment Method', ms: 'Tambah Kaedah Pembayaran Baharu' }
                                )}

                                {renderTranslationFields(
                                    'settings.payments.removeButton',
                                    'Кнопка "Удалить"',
                                    { ru: 'Удалить', en: 'Remove', ms: 'Buang' }
                                )}

                                {/* Security Section */}
                                {renderTranslationFields(
                                    'settings.security.title',
                                    'Заголовок "Двухфакторная аутентификация (2FA)"',
                                    { ru: 'Двухфакторная аутентификация (2FA)', en: 'Two-Factor Authentication (2FA)', ms: 'Pengesahan Dua Faktor (2FA)' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.advancedSecurity',
                                    'Заголовок "Расширенная безопасность"',
                                    { ru: 'Расширенная безопасность', en: 'Advanced Security', ms: 'Keselamatan Lanjutan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.securityKey.title',
                                    'Заголовок "Ключ безопасности"',
                                    { ru: 'Ключ безопасности', en: 'Security Key', ms: 'Kunci Keselamatan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.securityKey.description',
                                    'Описание ключа безопасности',
                                    { ru: 'Защитите свой аккаунт с помощью ключа безопасности', en: 'Protect your account with a security key', ms: 'Lindungi akaun anda dengan kunci keselamatan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.securityKey.status',
                                    'Статус "НЕ УСТАНОВЛЕН"',
                                    { ru: 'НЕ УСТАНОВЛЕН', en: 'NOT SET', ms: 'BELUM DITETAPKAN' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.securityKey.action',
                                    'Кнопка "Активировать"',
                                    { ru: 'Активировать', en: 'Enable', ms: 'Aktifkan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.phoneVerification.title',
                                    'Заголовок "Подтверждение номера телефона"',
                                    { ru: 'Подтверждение номера телефона', en: 'Phone Number Verification', ms: 'Pengesahan Nombor Telefon' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.phoneVerification.description',
                                    'Описание подтверждения телефона',
                                    { ru: 'Защитите свой аккаунт и транзакции', en: 'Protect your account and transactions', ms: 'Lindungi akaun dan transaksi anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.phoneVerification.action',
                                    'Кнопка "Изменить"',
                                    { ru: 'Изменить', en: 'Change', ms: 'Tukar' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.emailVerification.title',
                                    'Заголовок "Подтверждение электронной почты"',
                                    { ru: 'Подтверждение электронной почты', en: 'Email Verification', ms: 'Pengesahan E-mel' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.emailVerification.description',
                                    'Описание подтверждения email',
                                    { ru: 'Защитите свой аккаунт и транзакции', en: 'Protect your account and transactions', ms: 'Lindungi akaun dan transaksi anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.emailVerification.action',
                                    'Кнопка "Изменить"',
                                    { ru: 'Изменить', en: 'Change', ms: 'Tukar' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.loginPassword.title',
                                    'Заголовок "Пароль для входа"',
                                    { ru: 'Пароль для входа', en: 'Login Password', ms: 'Kata Laluan Log Masuk' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.loginPassword.description',
                                    'Описание пароля для входа',
                                    { ru: 'Защитите свой аккаунт с помощью пароля', en: 'Protect your account with a password', ms: 'Lindungi akaun anda dengan kata laluan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.loginPassword.status',
                                    'Статус "АКТИВЕН"',
                                    { ru: 'АКТИВЕН', en: 'ACTIVE', ms: 'AKTIF' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.loginPassword.action',
                                    'Кнопка "Изменить"',
                                    { ru: 'Изменить', en: 'Change', ms: 'Change' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.withdrawalWhitelist.title',
                                    'Заголовок "Белый список вывода"',
                                    { ru: 'Белый список вывода', en: 'Withdrawal Whitelist', ms: 'Senarai Putih Pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.withdrawalWhitelist.description',
                                    'Описание белого списка вывода',
                                    { ru: 'Ваш аккаунт может выводить только на предварительно авторизованные адреса вывода', en: 'Your account can only withdraw to pre-authorized withdrawal addresses', ms: 'Akaun anda hanya boleh mengeluarkan ke alamat pengeluaran yang telah dibenarkan sebelumnya' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.withdrawalWhitelist.status',
                                    'Статус "ВЫКЛЮЧЕН"',
                                    { ru: 'ВЫКЛЮЧЕН', en: 'OFF', ms: 'MATI' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.withdrawalWhitelist.action',
                                    'Кнопка "Активировать"',
                                    { ru: 'Активировать', en: 'Enable', ms: 'Aktifkan' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.antiPhishing.title',
                                    'Заголовок "Код против фишинга"',
                                    { ru: 'Код против фишинга', en: 'Anti-Phishing Code', ms: 'Kod Anti-Phishing' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.antiPhishing.description',
                                    'Описание кода против фишинга',
                                    { ru: 'Защитите свой аккаунт от попыток фишинга и убедитесь, что уведомления по электронной почте приходят только от Spectrum Alpha', en: 'Protect your account from phishing attempts and ensure notification emails only come from Spectrum Alpha', ms: 'Lindungi akaun anda daripada percubaan phishing dan pastikan e-mel pemberitahuan hanya datang dari Spectrum Alpha' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.antiPhishing.status',
                                    'Статус "ВЫКЛЮЧЕН"',
                                    { ru: 'ВЫКЛЮЧЕН', en: 'OFF', ms: 'MATI' }
                                )}

                                {renderTranslationFields(
                                    'settings.security.antiPhishing.action',
                                    'Кнопка "Изменить"',
                                    { ru: 'Изменить', en: 'Change', ms: 'Tukar' }
                                )}

                                {/* Devices Section */}
                                {renderTranslationFields(
                                    'settings.devices.title',
                                    'Заголовок "Устройства"',
                                    { ru: 'Устройства', en: 'Devices', ms: 'Peranti' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.currentSessions',
                                    'Заголовок "Текущие сессии"',
                                    { ru: 'Текущие сессии', en: 'Current Sessions', ms: 'Sesi Semasa' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.activeSessions',
                                    'Заголовок "Активные сессии"',
                                    { ru: 'Активные сессии', en: 'Active Sessions', ms: 'Sesi Aktif' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.terminateAllSessions',
                                    'Кнопка "Завершить все сессии"',
                                    { ru: 'Завершить все сессии', en: 'Terminate All Sessions', ms: 'Tamatkan Semua Sesi' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.online',
                                    'Статус "В сети"',
                                    { ru: 'В сети', en: 'Online', ms: 'Dalam Talian' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.hoursAgo',
                                    'Время "часов назад"',
                                    { ru: 'ч. назад', en: 'hours ago', ms: 'jam lalu' }
                                )}

                                {renderTranslationFields(
                                    'settings.devices.minutesAgo',
                                    'Время "минут назад"',
                                    { ru: 'мин. назад', en: 'minutes ago', ms: 'minit lalu' }
                                )}

                                {/* Address Section */}
                                {renderTranslationFields(
                                    'settings.address.title',
                                    'Заголовок "Настройки адреса"',
                                    { ru: 'Настройки вашего адреса', en: 'Your Address Settings', ms: 'Tetapan Alamat Anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.copyPersonalAddress',
                                    'Текст "Скопируйте личный адрес"',
                                    { ru: 'Скопируйте ваш личный адрес', en: 'Copy your personal address', ms: 'Salin alamat peribadi anda' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.generateAddress',
                                    'Кнопка "Создать адрес"',
                                    { ru: 'Создать адрес', en: 'Generate Address', ms: 'Jana Alamat' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.generating',
                                    'Текст "Генерируем..."',
                                    { ru: 'Генерируем...', en: 'Generating...', ms: 'Menjana...' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.deleteButton',
                                    'Кнопка "Удалить"',
                                    { ru: 'Удалить', en: 'Delete', ms: 'Padam' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.generateQR',
                                    'Заголовок QR генерации',
                                    { ru: 'Создайте адрес для получения QR', en: 'Generate address to get QR', ms: 'Jana alamat untuk mendapat QR' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.scanQR',
                                    'Текст "Сканируйте QR код"',
                                    { ru: 'Сканируйте QR код', en: 'Scan QR code', ms: 'Imbas kod QR' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.generatingAddress',
                                    'Текст "Генерируем адрес..."',
                                    { ru: 'Генерируем адрес...', en: 'Generating address...', ms: 'Menjana alamat...' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.uniqueAddressFor',
                                    'Текст "Уникальный адрес для"',
                                    { ru: 'Уникальный адрес для', en: 'Unique address for', ms: 'Alamat Unik untuk' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.clickGenerateAddress',
                                    'Инструкция для генерации адреса',
                                    { ru: 'Нажмите "Создать адрес" для генерации адреса', en: 'Click "Generate Address" to generate address', ms: 'Tekan "Jana Alamat" untuk menjana alamat' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.copy',
                                    'Кнопка "Копировать"',
                                    { ru: 'Копировать', en: 'Copy', ms: 'Salin' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.secondAddressLabel',
                                    'Подпись второго адреса (с плейсхолдером {crypto})',
                                    { ru: 'Второй {crypto} адрес:', en: 'Second {crypto} Address:', ms: 'Alamat {crypto} Kedua:' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.contactSupport',
                                    'Инструкция связи с поддержкой (с плейсхолдером {crypto})',
                                    { ru: 'Свяжитесь с поддержкой для создания резервного {crypto} адреса', en: 'Contact support to generate a backup {crypto} address', ms: 'Hubungi sokongan untuk menjana alamat {crypto} sandaran' }
                                )}
                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.title',
                                    'Заголовок "API Ограничения"',
                                    { ru: 'API Ограничения', en: 'API Restrictions', ms: 'Sekatan API' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.read',
                                    'API право "Чтение"',
                                    { ru: 'Чтение', en: 'Read', ms: 'Baca' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.spotMarginTrading',
                                    'API право "Спот и маржинальная торговля"',
                                    { ru: 'Спот и маржинальная торговля', en: 'Spot & Margin Trading', ms: 'Dagangan Spot & Margin' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.margin',
                                    'API право "Маржа"',
                                    { ru: 'Маржа', en: 'Margin', ms: 'Margin' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.futures',
                                    'API право "Фьючерсы"',
                                    { ru: 'Фьючерсы', en: 'Futures', ms: 'Niaga Hadapan' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.apiRestrictions.withdrawals',
                                    'API право "Выводы"',
                                    { ru: 'Выводы', en: 'Withdrawals', ms: 'Pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.ipAccessRestrictions.title',
                                    'Заголовок "IP Ограничения доступа"',
                                    { ru: 'IP Ограничения доступа', en: 'IP Access Restrictions', ms: 'Sekatan Akses IP' }
                                )}

                                {renderTranslationFields(
                                    'settings.address.ipAccessRestrictions.noRestrictions',
                                    'Текст "Без ограничений"',
                                    { ru: 'Без ограничений', en: 'No Restrictions', ms: 'Tiada Sekatan' }
                                )}

                                {/* FAQ Section */}
                                {renderTranslationFields(
                                    'settings.faq.title',
                                    'Заголовок FAQ "Часто задаваемые вопросы"',
                                    { ru: 'Часто задаваемые вопросы', en: 'Frequently Asked Questions', ms: 'Soalan Lazim' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.helpCenter',
                                    'Кнопка "Центр помощи"',
                                    { ru: 'Центр помощи', en: 'Help Center', ms: 'Pusat Bantuan' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.dataProtection.question',
                                    'FAQ вопрос: Защищена ли информация?',
                                    { ru: 'Защищена ли моя информация?', en: 'Is my information protected?', ms: 'Adakah maklumat saya dilindungi?' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.dataProtection.answer',
                                    'FAQ ответ: О защите информации',
                                    { ru: 'Да, ваша информация защищена PGP шифрованием и хранится максимум 3 месяца. По истечении этого периода необходимо обновить ваши личные данные.', en: 'Yes, your information is protected with PGP encryption and stored for a maximum of 3 months. At the end of that period, it is necessary to update your personal data.', ms: 'Ya, maklumat anda dilindungi dengan penyulitan PGP dan disimpan untuk maksimum 3 bulan. Pada akhir tempoh tersebut, adalah perlu untuk mengemaskini data peribadi anda.' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.addPaymentMethod.question',
                                    'FAQ вопрос: Как добавить способ оплаты?',
                                    { ru: 'Как добавить способ оплаты?', en: 'How to add a payment method?', ms: 'Bagaimana untuk menambah kaedah pembayaran?' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.addPaymentMethod.answer',
                                    'FAQ ответ: О добавлении способа оплаты',
                                    { ru: 'Вы можете добавить способ оплаты в разделе "Настройки" в левом боковом меню', en: 'You can add a payment method in the "Settings" section in the left sidebar menu', ms: 'Anda boleh menambah kaedah pembayaran di bahagian "Tetapan" dalam menu bar sisi kiri' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.enableNotifications.question',
                                    'FAQ вопрос: Как включить уведомления?',
                                    { ru: 'Как включить уведомления?', en: 'How to enable notifications?', ms: 'Bagaimana untuk mengaktifkan pemberitahuan?' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.enableNotifications.answer',
                                    'FAQ ответ: О включении уведомлений',
                                    { ru: 'Вы можете включить уведомления в разделе "Настройки" вашего профиля в левом боковом меню', en: 'You can enable notifications in the "Settings" section of your profile in the left sidebar menu', ms: 'Anda boleh mengaktifkan pemberitahuan di bahagian "Tetapan" profil anda dalam menu bar sisi kiri' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.withdrawBalance.question',
                                    'FAQ вопрос: Как вывести баланс?',
                                    { ru: 'Как я могу вывести свой баланс?', en: 'How can I withdraw my balance?', ms: 'Bagaimana saya boleh mengeluarkan baki saya?' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.withdrawBalance.answer',
                                    'FAQ ответ: О выводе баланса',
                                    { ru: 'Вы можете вывести свой баланс через вашего брокера. Свяжитесь с ними для получения помощи и подробной информации.', en: 'You can withdraw your balance through your broker. Contact them for assistance and detailed information.', ms: 'Anda boleh mengeluarkan baki anda melalui broker anda. Hubungi mereka untuk mendapat bantuan dan maklumat terperinci.' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.connectAPI.question',
                                    'FAQ вопрос: Как подключить API?',
                                    { ru: 'Как я могу подключить ваш API?', en: 'How can I connect your API?', ms: 'Bagaimana saya boleh menyambung API anda?' }
                                )}

                                {renderTranslationFields(
                                    'settings.faq.questions.connectAPI.answer',
                                    'FAQ ответ: О подключении API',
                                    { ru: 'Вы можете найти ваш личный API ключ в разделе Настройки в левом боковом меню.', en: 'You can find your personal API key in the Settings section in the left sidebar menu.', ms: 'Anda boleh menemui kunci API peribadi anda di bahagian Tetapan dalam menu bar sisi kiri.' }
                                )}
                            </div>
                            </Collapsible>
                        </div>


                        {/* Mailbox Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('mailbox')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('mailbox')}
                                    <img src={mailbox} alt="Mailbox" className={s.sectionIcon} />
                                    Переводы блока Mailbox
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.mailbox ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.mailbox}>
                            <div className={s.sectionContent}>
                                {/* Balance Section */}
                                {renderTranslationFields(
                                    'mailbox.balance.title',
                                    'Заголовок "Сумма баланса"',
                                    { ru: 'Сумма баланса', en: 'Balance Amount', ms: 'Jumlah Baki' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.balance.warning.title',
                                    'Предупреждение "Инвестиции недоступны"',
                                    { ru: 'Инвестиции недоступны:', en: 'Investments unavailable:', ms: 'Pelaburan tidak tersedia:' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.balance.warning.description',
                                    'Описание предупреждения',
                                    { ru: 'Вывод прибыли ожидает уплаты налогов IRS', en: 'Profit withdrawal awaiting IRS tax payment', ms: 'Pengeluaran keuntungan menunggu pembayaran cukai IRS' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.balance.actions.deposit',
                                    'Кнопка "Депозит"',
                                    { ru: 'Депозит', en: 'Deposit', ms: 'Deposit' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.balance.actions.withdraw',
                                    'Кнопка "Вывод"',
                                    { ru: 'Вывод', en: 'Withdraw', ms: 'Keluarkan' }
                                )}

                                {/* Mail Section */}
                                {renderTranslationFields(
                                    'mailbox.mail.title',
                                    'Заголовок "Полученные письма"',
                                    { ru: 'Полученные письма', en: 'Received Mail', ms: 'Mel Diterima' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.mail.count',
                                    'Счетчик сообщений',
                                    { ru: '2 сообщения', en: '2 messages', ms: '2 mesej' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.mail.markAllRead',
                                    'Кнопка "Отметить все как прочитанные"',
                                    { ru: 'Отметить все как прочитанные', en: 'Mark All as Read', ms: 'Tandakan Semua Sebagai Dibaca' }
                                )}

                                {/* IRS Email */}
                                {renderTranslationFields(
                                    'mailbox.emails.irs.sender',
                                    'Отправитель IRS письма',
                                    { ru: 'Служба внутренних доходов', en: 'Internal Revenue Service', ms: 'Perkhidmatan Hasil Dalam Negeri' }
                                )}
                                {renderTranslationFields(
                                            'mailbox.emails.irs.subject',
                                            'Тема IRS письма',
                                            { ru: 'Уведомление IRS для', en: 'IRS Notice for', ms: 'Notis IRS untuk' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.time',
                                            'Время IRS письма',
                                            { ru: '2 часа назад', en: '2 hours ago', ms: '2 jam lalu' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.status',
                                            'Статус IRS письма',
                                            { ru: 'Важное', en: 'Important', ms: 'Penting' }
                                )}

                                {/* IRS Email Content */}
                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.department',
                                            'Заголовок "Служба внутренних доходов"',
                                            { ru: 'СЛУЖБА ВНУТРЕННИХ ДОХОДОВ', en: 'INTERNAL REVENUE SERVICE', ms: 'PERKHIDMATAN HASIL DALAM NEGERI' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.noticeType',
                                            'Тип уведомления "Официальное налоговое уведомление"',
                                            { ru: 'ОФИЦИАЛЬНОЕ НАЛОГОВОЕ УВЕДОМЛЕНИЕ', en: 'OFFICIAL TAX NOTICE', ms: 'NOTIS CUKAI RASMI' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.to',
                                            'Метка "КОМУ"',
                                            { ru: 'КОМУ', en: 'TO', ms: 'KEPADA' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.date',
                                            'Метка "ДАТА"',
                                            { ru: 'ДАТА', en: 'DATE', ms: 'TARIKH' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.caseNumber',
                                            'Метка "ДЕЛО #"',
                                            { ru: 'ДЕЛО #', en: 'CASE #', ms: 'KES #' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.transactionDetection',
                                            'Заголовок "Обнаружение транзакций"',
                                            { ru: 'ОБНАРУЖЕНИЕ ТРАНЗАКЦИЙ', en: 'TRANSACTION DETECTION', ms: 'PENGESANAN TRANSAKSI' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.transactionDescription',
                                            'Описание обнаружения транзакций',
                                            { ru: 'Мы обнаружили транзакции цифровых активов на банковский счет Малайзии. Эти транзакции подлежат налоговому регулированию Малайзии в соответствии с текущими рекомендациями IRS.', en: 'We have detected digital asset transactions to a Malaysian bank account. These transactions are subject to Malaysian tax regulations under current IRS guidelines.', ms: 'Kami telah mengesan transaksi aset digital ke akaun bank Malaysia. Transaksi ini tertakluk kepada peraturan cukai Malaysia di bawah garis panduan IRS semasa.' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.actionRequired',
                                            'Заголовок "Требуются действия"',
                                            { ru: 'ТРЕБУЮТСЯ ДЕЙСТВИЯ', en: 'ACTION REQUIRED', ms: 'TINDAKAN DIPERLUKAN' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.actionList.filing',
                                            'Действие: Подача декларации',
                                            { ru: 'Заполните налоговую декларацию (Форма 1040, 1040-SR)', en: 'Complete tax filing (Form 1040, 1040-SR)', ms: 'Lengkapkan pemfailan cukai (Borang 1040, 1040-SR)' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.actionList.payment',
                                            'Действие: Отправка платежа',
                                            { ru: 'Отправьте платеж через сеть', en: 'Submit payment via network', ms: 'Hantar pembayaran melalui rangkaian' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.actionList.deadline',
                                            'Действие: Крайний срок',
                                            { ru: 'Крайний срок соблюдения: 30 дней с момента уведомления', en: 'Compliance deadline: 30 days from notice', ms: 'Tarikh akhir pematuhan: 30 hari dari notis' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.warning',
                                            'Предупреждение о штрафах',
                                            { ru: 'ВАЖНО: Избегайте штрафов, обеспечив своевременное соблюдение требований. Платеж должен быть произведен в той же сети {crypto}, что и ваша исходная транзакция.', en: 'IMPORTANT: Avoid penalties by ensuring timely compliance. Payment must be made on the same {crypto} network as your original transaction.', ms: 'PENTING: Elakkan penalti dengan memastikan pematuhan tepat pada masanya. Pembayaran mesti dibuat pada rangkaian {crypto} yang sama seperti transaksi asal anda.' }
                                )}

                                {/* Receipt Details */}
                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.title',
                                            'Заголовок "Детали транзакции"',
                                            { ru: 'ДЕТАЛИ ТРАНЗАКЦИИ', en: 'TRANSACTION DETAILS', ms: 'BUTIRAN TRANSAKSI' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.receiptNumber',
                                            'Номер чека',
                                            { ru: 'Чек №', en: 'Receipt #', ms: 'Resit #' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.account',
                                            'Поле "Аккаунт"',
                                            { ru: 'Аккаунт:', en: 'Account:', ms: 'Akaun:' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.bank',
                                            'Поле "Банк"',
                                            { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.recipient',
                                            'Поле "Получатель"',
                                            { ru: 'Получатель:', en: 'Recipient:', ms: 'Penerima:' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.country',
                                            'Поле "Страна"',
                                            { ru: 'Страна:', en: 'Country:', ms: 'Negara:' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.taxOwed',
                                            'Поле "Задолженность по налогу"',
                                            { ru: 'Задолженность по налогу:', en: 'Tax Owed:', ms: 'Cukai Terhutang:' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.verified',
                                            'Штамп "IRS подтвержден"',
                                            { ru: 'IRS ПОДТВЕРЖДЕН', en: 'IRS VERIFIED', ms: 'IRS DISAHKAN' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.receiptDetails.payButton',
                                            'Кнопка "Оплатить налог"',
                                            { ru: 'Оплатить налог сейчас', en: 'Pay Tax Now', ms: 'Bayar Cukai Sekarang' }
                                )}

                                {/* Signature */}
                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.signature.sincerely',
                                            'Подпись "С уважением"',
                                            { ru: 'С уважением,', en: 'Sincerely,', ms: 'Yang benar,' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.signature.department',
                                            'Название департамента в подписи',
                                            { ru: 'Служба внутренних доходов', en: 'Internal Revenue Service', ms: 'Perkhidmatan Hasil Dalam Negeri' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.signature.thanks',
                                            'Благодарность в подписи',
                                            { ru: 'Спасибо за ваше сотрудничество в соблюдении налогового законодательства.', en: 'Thank you for your cooperation in tax compliance.', ms: 'Terima kasih atas kerjasama anda dalam pematuhan cukai.' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.irs.content.contact',
                                            'Контактная информация',
                                            { ru: 'Нужна помощь? Посетите irs.gov или позвоните 1-800-TAX-FORM', en: 'Need help? Visit irs.gov or call 1-800-TAX-FORM', ms: 'Perlukan bantuan? Lawati irs.gov atau hubungi 1-800-TAX-FORM' }
                                )}

                                {/* Welcome Email */}
                                {renderTranslationFields(
                                            'mailbox.emails.welcome.sender',
                                            'Отправитель приветственного письма',
                                            { ru: 'Торговая платформа', en: 'Trading Platform', ms: 'Platform Pertukaran' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.welcome.subject',
                                            'Тема приветственного письма',
                                            { ru: 'Добро пожаловать', en: 'Welcome', ms: 'Selamat datang' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.welcome.time',
                                            'Время приветственного письма',
                                            { ru: '1 день назад', en: '1 day ago', ms: '1 hari lalu' }
                                )}

                                {renderTranslationFields(
                                            'mailbox.emails.welcome.status',
                                            'Статус приветственного письма',
                                            { ru: 'Добро пожаловать', en: 'Welcome', ms: 'Selamat Datang' }
                                )}

                                {renderTranslationFields(
                                    'mailbox.emails.welcome.content',
                                    'Содержимое приветственного письма',
                                    { ru: 'Добро пожаловать {name}! Мы рады видеть вас с нами. Желаем приятного дня!', en: 'Welcome {name}! We\'re glad to have you with us. Have a wonderful day!', ms: 'Selamat datang {name}! Kami gembira мelihat anda bersama kami. Semoga hari anda menyenangkan!' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* Commission Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('commissionsModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('commissionsModal')}
                                    <img src={commissionModalIcon} alt="Commission Modal" className={s.sectionIcon} />
                                    Перевод модала Комиссии
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.commissionsModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.commissionsModal}>
                            <div className={s.sectionContent}>
                                {/* Commission Modal */}
                                {renderTranslationFields(
                                    'modals.commission.title',
                                    'Заголовок модального окна Commission',
                                    { ru: 'Ошибка отправки!', en: 'Sending Error!', ms: 'Ralat Penghantaran!' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '✕', en: '✕', ms: '✕' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.userDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.userDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.userDetails.ibanBic',
                                    'Метка "IBAN / BIC:"',
                                    { ru: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, en: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, ms: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:` }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.userDetails.withdrawalAmount',
                                    'Метка "Сумма вывода:"',
                                    { ru: 'Сумма вывода:', en: 'Withdrawal amount:', ms: 'Jumlah pengeluaran:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.userDetails.dateTime',
                                    'Метка "Дата и время:"',
                                    { ru: 'Дата и время:', en: 'Date and time:', ms: 'Tarikh dan masa:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.instruction',
                                    'Инструкция по оплате комиссии (с плейсхолдером {amount} и strong тегами)',
                                    { ru: 'Для завершения транзакции и получения <strong>{amount}</strong>, необходимо оплатить комиссионный сбор для завершения операции между японской и американской биржами.', en: 'To complete the transaction and receive <strong>{amount}</strong>, it is necessary to pay a commission fee to complete the operation between Japanese and US exchanges.', ms: 'Untuk melengkapkan transaksi dan menerima <strong>{amount}</strong>, adalah perlu membayar yuran komisen untuk menyelesaikan operasi antara bursa Jepun dan AS.' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.paymentRequired',
                                    'Заголовок "Требуется оплата"',
                                    { ru: 'Требуется оплата для завершения транзакции:', en: 'Payment required to complete the transaction:', ms: 'Pembayaran diperlukan untuk melengkapkan transaksi:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.percentageNote',
                                    'Примечание о проценте (с плейсхолдером {percentage})',
                                    { ru: '({percentage}% от суммы вывода)', en: '({percentage}% of withdrawal amount)', ms: '({percentage}% daripada jumlah pengeluaran)' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.paymentTitle',
                                    'Заголовок "Оплатите комиссию"',
                                    { ru: 'Оплатите комиссию на этот адрес:', en: 'Pay commission to this address:', ms: 'Bayar komisen ke alamat ini:' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.networkInfo',
                                    'Информация о сети (с плейсхолдерами {network} и {chain} и strong тегами)',
                                    { ru: 'Платеж должен быть в <strong>{network}</strong> в сети <strong>{chain}</strong>', en: 'Payment must be in <strong>{network}</strong> on <strong>{chain}</strong> network', ms: 'Pembayaran mesti dalam <strong>{network}</strong> pada rangkaian <strong>{chain}</strong>' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.processingNote',
                                    'Заметка о сроке зачисления после оплаты',
                                    { ru: 'После оплаты деньги поступят на ваш счёт в течение 1–2 часов, в зависимости от скорости обработки вашим банком.', en: 'After payment, the funds will arrive in your account within 1–2 hours, depending on your bank\u2019s processing speed.', ms: 'Selepas pembayaran, dana akan masuk ke akaun anda dalam masa 1–2 jam, bergantung pada kelajuan pemprosesan bank anda.' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.footerNote',
                                    'Финальное примечание (с плейсхолдером {amount} и strong тегами)',
                                    { ru: 'После совершения платежа нажмите <strong>«Я заплатил»</strong>. После подтверждения платежа сумма <strong>{amount}</strong> будет отправлена на указанные выше банковские координаты.', en: 'After making payment, press <strong>«I Have Paid»</strong>. After payment is confirmed, the amount <strong>{amount}</strong> will be sent to the bank coordinates specified above.', ms: 'Setelah membuat pembayaran, tekan <strong>«Saya Telah Bayar»</strong>. Setelah pembayaran disahkan, jumlah <strong>{amount}</strong> akan dihantar ke koordinat bank yang dinyatakan di atas.' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.cancelButton',
                                    'Кнопка "Отмена"',
                                    { ru: 'Отмена', en: 'Cancel', ms: 'Batal' }
                                )}

                                {renderTranslationFields(
                                    'modals.commission.paidButton',
                                    'Кнопка "Я заплатил"',
                                    { ru: 'Я заплатил', en: 'I Have Paid', ms: 'Saya Telah Bayar' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* Verification Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('verificationModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('verificationModal')}
                                    <img src={verificationModalIcon} alt="Verification Modal" className={s.sectionIcon} />
                                    Перевод модала Верификации
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.verificationModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.verificationModal}>
                            <div className={s.sectionContent}>
                                {/* Verification Modal */}
                                {renderTranslationFields(
                                    'modals.verification.title',
                                    'Заголовок модального окна Verification',
                                    { ru: 'Требуется подтверждение аккаунта!', en: 'Account Verification Required!', ms: 'Pengesahan Akaun Diperlukan!' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.titleAccount',
                                    'Заголовок с именем аккаунта (тема Nova, плейсхолдер {name})',
                                    { ru: 'Верификация аккаунта: {name}', en: 'Account verification: {name}', ms: 'Pengesahan akaun: {name}' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.amountReady',
                                    'Статус суммы "Готово к отправке" (тема Nova)',
                                    { ru: 'Готово к отправке', en: 'Ready to send', ms: 'Sedia untuk dihantar' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.dataAsOf',
                                    'Приписка "данные актуальны на..." (тема Nova, плейсхолдер {dateTime})',
                                    { ru: 'данные актуальны на {dateTime}', en: 'details valid as of {dateTime}', ms: 'data sah pada {dateTime}' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '✕', en: '✕', ms: '✕' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.withdrawalDetails',
                                    'Заголовок "Детали вывода"',
                                    { ru: 'Детали вывода:', en: 'Withdrawal details:', ms: 'Butiran pengeluaran:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.userDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.userDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.userDetails.ibanBic',
                                    'Метка "IBAN / BIC:"',
                                    { ru: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, en: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, ms: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:` }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.userDetails.amount',
                                    'Метка "Сумма:"',
                                    { ru: 'Сумма:', en: 'Amount:', ms: 'Jumlah:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.userDetails.dateTime',
                                    'Метка "Дата и время:"',
                                    { ru: 'Дата и время:', en: 'Date and time:', ms: 'Tarikh dan masa:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.instruction',
                                    'Инструкция по верификации (с плейсхолдером {amount} и strong тегами)',
                                    { ru: 'Для защиты ваших доходов и отправки <strong>{amount}</strong> на правильный адрес, сделайте верификационный платеж с теми же данными, что и депозит.', en: 'To protect your earnings and send <strong>{amount}</strong> to the correct address, make a verification payment with the same data as the deposit.', ms: 'Untuk melindungi pendapatan anda dan menghantar <strong>{amount}</strong> ke alamat yang betul, buat pembayaran pengesahan dengan data yang sama seperti deposit.' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.verificationNote',
                                    'Примечание о верификации',
                                    { ru: 'Пожалуйста, сделайте верификационный платеж с теми же данными, откуда был сделан депозит. Если данные совпадают, это поможет нам убедиться, что деньги будут отправлены правильно.', en: 'Please make a verification payment with the same data from which the deposit was made. If the data matches, it will help us ensure that the money will be sent correctly.', ms: 'Sila buat pembayaran pengesahan dengan data yang sama dari mana deposit dibuat. Jika data sepadan, ia akan membantu kami memastikan wang akan dihantar dengan betul.' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.paymentRequired',
                                    'Заголовок "Требуется верификационный платеж"',
                                    { ru: 'Требуется верификационный платеж:', en: 'Verification payment required:', ms: 'Pembayaran pengesahan diperlukan:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.paymentTitle',
                                    'Заголовок "Отправьте платеж"',
                                    { ru: 'Отправьте платеж на этот адрес:', en: 'Send payment to this address:', ms: 'Hantar pembayaran ke alamat ini:' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.networkInfo',
                                    'Информация о сети (с плейсхолдерами {network} и {chain} и strong тегами)',
                                    { ru: 'Платеж должен быть в <strong>{network}</strong> в сети <strong>{chain}</strong>', en: 'Payment must be in <strong>{network}</strong> on <strong>{chain}</strong> network', ms: 'Pembayaran mesti dalam <strong>{network}</strong> pada rangkaian <strong>{chain}</strong>' }
                                )}
                                {renderTranslationFields(
                                    'modals.verification.cancelButton',
                                    'Кнопка "Отмена"',
                                    { ru: 'Отмена', en: 'Cancel', ms: 'Batal' }
                                )}

                                {renderTranslationFields(
                                    'modals.verification.paidButton',
                                    'Кнопка "Я заплатил"',
                                    { ru: 'Я заплатил', en: 'I Have Paid', ms: 'Saya Telah Bayar' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Premium Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('premiumModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('premiumModal')}
                                    <img src={verificationModalIcon} alt="Premium Modal" className={s.sectionIcon} />
                                    Перевод модала Премиум доступа
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.premiumModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.premiumModal}>
                            <div className={s.sectionContent}>
                                {/* Premium Modal */}
                                {renderTranslationFields(
                                    'modals.premium.title',
                                    'Заголовок модального окна Premium',
                                    { ru: 'АВТОРИЗАЦИЯ ОГРАНИЧЕНА', en: 'AUTHORIZATION LIMITED', es: 'AUTORIZACIÓN LIMITADA', ms: 'KELULUSAN TERHAD' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.subtitle',
                                    'Подзаголовок модального окна Premium',
                                    { ru: 'ПРОТОКОЛ БЕЗОПАСНОСТИ: AML-V4', en: 'SECURITY PROTOCOL: AML-V4', es: 'PROTOCOLO DE SEGURIDAD: AML-V4', ms: 'PROTOKOL KESELAMATAN: AML-V4' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.statusLabel',
                                    'Метка "Статус транзакции"',
                                    { ru: 'СТАТУС ТРАНЗАКЦИИ', en: 'TRANSACTION STATUS', es: 'ESTADO DE LA TRANSACCIÓN', ms: 'STATUS TRANSAKSI' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.statusTitle',
                                    'Заголовок статуса "Ожидание апгрейда"',
                                    { ru: 'ОЖИДАНИЕ АПГРЕЙДА', en: 'UPGRADE PENDING', es: 'ACTUALIZACIÓN PENDIENTE', ms: 'MENUNGGU NAIK TARAF' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.statusText',
                                    'Описание статуса (с strong тегами для BASIC и PREMIUM)',
                                    { ru: 'Лимит <strong>BASIC</strong> аккаунта превышен. Требуется переход на уровень <strong>PREMIUM</strong> для завершения выплаты.', en: 'The <strong>BASIC</strong> account limit has been exceeded. An upgrade to <strong>PREMIUM</strong> level is required to complete the payout.', es: 'Se ha excedido el límite de la cuenta <strong>BASIC</strong>. Se requiere una actualización al nivel <strong>PREMIUM</strong> para completar el pago.', ms: 'Had <strong>BASIC</strong> akaun telah melebihi. Peningkatan ke tahap <strong>PREMIUM</strong> diperlukan untuk melengkapkan pembayaran.' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.limitLabel',
                                    'Метка "Лимит"',
                                    { ru: 'лимит', en: 'limit', es: 'límite', ms: 'had' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.withdrawalLabel',
                                    'Метка "Сумма к выводу"',
                                    { ru: 'СУММА К ВЫВОДУ:', en: 'WITHDRAWAL AMOUNT:', es: 'MONTO A RETIRAR:', ms: 'JUMLAH PENGELUARAN:' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.allowedLabel',
                                    'Подпись под шкалой "В пределах лимита" (тема Nova)',
                                    { ru: 'В пределах лимита', en: 'Within limit', es: 'Dentro del límite', ms: 'Dalam had' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.excessLabel',
                                    'Подпись под шкалой "Превышение" (тема Nova)',
                                    { ru: 'Превышение', en: 'Excess', es: 'Excedente', ms: 'Lebihan' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.upgradeButton',
                                    'Кнопка "Повысить статус до PREMIUM"',
                                    { ru: 'ПОВЫСИТЬ СТАТУС ДО PREMIUM', en: 'UPGRADE TO PREMIUM', es: 'ACTUALIZAR A PREMIUM', ms: 'NAIK TARAF KE PREMIUM' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.feeLabel',
                                    'Метка "Единоразовый взнос"',
                                    { ru: 'ЕДИНОРАЗОВЫЙ ВЗНОС:', en: 'ONE-TIME FEE:', es: 'TARIFA ÚNICA:', ms: 'YURAN SEKALI:' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.disclaimer',
                                    'Текст дисклеймера',
                                    { ru: 'ДАННАЯ ПРОЦЕДУРА ЯВЛЯЕТСЯ ОБЯЗАТЕЛЬНОЙ СОГЛАСНО ДИРЕКТИВЕ AMLDS ПО БОРЬБЕ С ОТМЫВАНИЕМ СРЕДСТВ ДЛЯ ТРАНЗАКЦИЙ ВЫСОКОГО ОБЪЕМА.', en: 'THIS PROCEDURE IS MANDATORY ACCORDING TO AMLDS DIRECTIVE ON ANTI-MONEY LAUNDERING FOR HIGH-VOLUME TRANSACTIONS.', es: 'ESTE PROCEDIMIENTO ES OBLIGATORIO SEGÚN LA DIRECTIVA AMLDS SOBRE LA LUCHA CONTRA EL BLANQUEO DE CAPITALES PARA TRANSACCIONES DE ALTO VOLUMEN.', ms: 'PROSEDUR INI ADALAH WAJIB MENURUT ARAHAN AMLDS MENGENAI PENCEGAHAN PENCUCIAN WANG UNTUK TRANSAKSI VOLUM TINGGI.' }
                                )}

                                {renderTranslationFields(
                                    'modals.premium.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '✕', en: '✕', es: '✕', ms: '✕' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Success Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('successModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('successModal')}
                                    <img src={successModalIcon} alt="Success Modal" className={s.sectionIcon} />
                                    Перевод модала Успеха
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.successModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.successModal}>
                            <div className={s.sectionContent}>
                                {/* Success Modal */}
                                {renderTranslationFields(
                                    'modals.success.title',
                                    'Заголовок модального окна Success',
                                    { ru: 'Отправлено!', en: 'Sent!', ms: 'Dihantar!' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '✕', en: '✕', ms: '✕' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.amountSent',
                                    'Заголовок блока суммы',
                                    { ru: 'Сумма отправлена', en: 'Amount Sent', es: 'Monto enviado', ms: 'Jumlah dihantar' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.recipientDetails',
                                    'Заголовок блока данных получателя',
                                    { ru: 'Данные получателя', en: 'Recipient Details', es: 'Datos del destinatario', ms: 'Butiran penerima' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.userDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.userDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.userDetails.ibanBic',
                                    'Метка "IBAN / BIC:"',
                                    { ru: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, en: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:`, ms: `${localSettings.bankAccountDisplayName || 'IBAN / BIC'}:` }
                                )}

                                {renderTranslationFields(
                                    'modals.success.userDetails.paymentDate',
                                    'Метка "Дата платежа:"',
                                    { ru: 'Дата платежа:', en: 'Payment date:', ms: 'Tarikh pembayaran:' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.viewHistoryButton',
                                    'Кнопка "История транзакций"',
                                    { ru: 'История транзакций', en: 'Transaction History', ms: 'Sejarah Transaksi' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.eyeIcon.show',
                                    'Подсказка для показа адреса',
                                    { ru: 'Показать', en: 'Show', ms: 'Tunjuk' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.eyeIcon.hide',
                                    'Подсказка для скрытия адреса',
                                    { ru: 'Скрыть', en: 'Hide', ms: 'Sembunyikan' }
                                )}

                                {renderTranslationFields(
                                    'modals.success.close',
                                    'Кнопка "Закрыть" внизу модала',
                                    { ru: 'Закрыть', en: 'Close', es: 'Cerrar', ms: 'Tutup' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Tax Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('taxModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('taxModal')}
                                    <img src={taxModalIcon} alt="Tax Modal" className={s.sectionIcon} />
                                    Перевод модала Налогов
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.taxModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.taxModal}>
                            <div className={s.sectionContent}>
                                {/* Tax Modal */}
                                {renderTranslationFields(
                                    'modals.tax.userName',
                                    'Приветствие пользователя с плейсхолдером {name}',
                                    { ru: '{name}!', en: '{name}!', ms: '{name}!' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.mainText',
                                    'Основной текст с информацией о налогах',
                                    { ru: 'Обратите внимание, что согласно действующему законодательству США, переводы криптовалюты за границу облагаются налогом. Мы обязаны приостановить ваш перевод до уплаты налога. Информация приведена ниже:', en: 'Please note that under current US legislation, cryptocurrency transfers abroad are subject to tax. We are required to suspend your transfer until tax is paid. Information is provided below:', ms: 'Sila ambil perhatian bahawa mengikut perundangan AS semasa, pemindahan mata wang kripto ke luar negara tertakluk kepada cukai. Kami diwajibkan menahan pemindahan anda sehingga cukai dibayar. Maklumat diberikan di bawah:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.recipientAccount',
                                    'Метка "Счет получателя:"',
                                    { ru: 'Счет получателя:', en: 'Recipient account:', ms: 'Akaun penerima:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.transferAmount',
                                    'Метка "Сумма перевода:"',
                                    { ru: 'Сумма перевода:', en: 'Transfer amount:', ms: 'Jumlah pemindahan:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.taxPercent',
                                    'Метка "Налог %:"',
                                    { ru: 'Налог %:', en: 'Tax %:', ms: 'Cukai %:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.cryptoAddress',
                                    'Метка адреса криптовалюты с плейсхолдерами {crypto} и {chain}',
                                    { ru: 'Адрес {crypto} ({chain}):', en: '{crypto} address ({chain}):', ms: 'Alamat {crypto} ({chain}):' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.userDetails.totalTax',
                                    'Метка "Сумма налога:"',
                                    { ru: 'Сумма налога:', en: 'Tax amount:', ms: 'Jumlah cukai:' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.buttons.showAddress',
                                    'Кнопка "Показать адрес"',
                                    { ru: 'Показать адрес', en: 'Show address', ms: 'Tunjuk alamat' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.buttons.copy',
                                    'Кнопка "Копировать"',
                                    { ru: 'Копировать', en: 'Copy', ms: 'Salin' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.deadlineText',
                                    'Текст о сроке платежа с плейсхолдером {date} и strong тегами',
                                    { ru: '<strong>14 дней</strong> дается для уплаты налога с текущей даты: <strong>{date}</strong>. В противном случае перевод будет заблокирован и будут приняты соответствующие меры.', en: '<strong>14 days</strong> are given to pay tax from current date: <strong>{date}</strong>. Otherwise, the transfer will be blocked and appropriate measures will be taken.', ms: '<strong>14 hari</strong> diberikan untuk membayar cukai dari tarikh semasa: <strong>{date}</strong>. Jika tidak, pemindahan akan disekat dan langkah-langkah yang sesuai akan diambil.' }
                                )}

                                {renderTranslationFields(
                                    'modals.tax.returnButton',
                                    'Кнопка "Вернуться к обмену"',
                                    { ru: 'Вернуться к обмену и заплатить налог', en: 'Return to exchange and pay tax', ms: 'Kembali ke pertukaran dan bayar cukai' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        {/* Transaction Error Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('transactionErrorModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('transactionErrorModal')}
                                    <img src={cryptoConversionModalIcon} alt="Transaction Error Modal" className={s.sectionIcon} />
                                    Перевод модала Ошибки транзакции
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.transactionErrorModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.transactionErrorModal}>
                            <div className={s.sectionContent}>
                                {/* Transaction Error Modal */}
                                {renderTranslationFields(
                                    'modals.transactionError.title',
                                    'Заголовок модального окна ошибки транзакции',
                                    { ru: 'ТРЕБУЕТСЯ ДЕЙСТВИЕ - Ошибка обработки транзакции', en: 'ACTION REQUIRED - Transaction Processing Error', ms: 'TINDAKAN DIPERLUKAN - Ralat Pemprosesan Transaksi' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '×', en: '×', ms: '×' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.conversion.title',
                                    'Заголовок секции конвертации',
                                    { ru: 'Конвертация криптовалюты', en: 'Cryptocurrency Conversion', ms: 'Penukaran Mata Wang Kripto' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.conversion.status',
                                    'Статус "Требуется оплата"',
                                    { ru: 'Требуется оплата', en: 'Payment Required', ms: 'Pembayaran Diperlukan' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.conversion.note',
                                    'Примечание о комиссии за обработку',
                                    { ru: 'Комиссия за обработку требуется для завершения конвертации', en: 'Processing fee required to complete conversion', ms: 'Yuran pemprosesan diperlukan untuk melengkapkan penukaran' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.conversion.arrow',
                                    'Стрелка конвертации',
                                    { ru: '→', en: '→', ms: '→' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.payment.title',
                                    'Заголовок секции платежа',
                                    { ru: 'Отправить платеж на', en: 'Send Payment To', ms: 'Hantar Pembayaran Ke' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.payment.addressLabel',
                                    'Метка "Адрес кошелька"',
                                    { ru: 'Адрес кошелька', en: 'Wallet Address', ms: 'Alamat Dompet' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.fee.title',
                                    'Заголовок секции комиссии',
                                    { ru: 'Комиссия за обработку', en: 'Processing Fee', ms: 'Yuran Pemprosesan' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.fee.description',
                                    'Описание комиссии',
                                    { ru: 'Требуется для обработки конвертации', en: 'Required for conversion processing', ms: 'Diperlukan untuk pemprosesan penukaran' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.bankDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.bankDetails.amountToReceive',
                                    'Метка "Сумма к получению:"',
                                    { ru: 'Сумма к получению:', en: 'Amount to receive:', ms: 'Jumlah untuk diterima:' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.bankDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.bankDetails.account',
                                    'Метка "Счет:"',
                                    { ru: 'Счет:', en: 'Account:', ms: 'Akaun:' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.buttons.copy',
                                    'Кнопка "Копировать"',
                                    { ru: 'Копировать', en: 'Copy', ms: 'Salin' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.buttons.copied',
                                    'Кнопка "Скопировано"',
                                    { ru: 'Скопировано', en: 'Copied', ms: 'Disalin' }
                                )}

                                {renderTranslationFields(
                                    'modals.transactionError.buttons.payNow',
                                    'Кнопка "Оплатить сейчас"',
                                    { ru: 'ОПЛАТИТЬ СЕЙЧАС', en: 'PAY NOW', ms: 'BAYAR SEKARANG' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Bank Transfer Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('bankTransferModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('bankTransferModal')}
                                    <img src={bankConversionModalIcon} alt="Bank Transfer Modal" className={s.sectionIcon} />
                                    Перевод модала Банковского перевода
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.bankTransferModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.bankTransferModal}>
                            <div className={s.sectionContent}>
                                {/* Bank Transfer Modal */}
                                {renderTranslationFields(
                                    'modals.bankTransfer.title',
                                    'Заголовок модального окна банковского перевода',
                                    { ru: 'ТРЕБУЕТСЯ ДЕЙСТВИЕ - Обработка банковского перевода', en: 'ACTION REQUIRED - Bank Transfer Processing', ms: 'TINDAKAN DIPERLUKAN - Pemprosesan Pemindahan Bank' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '×', en: '×', ms: '×' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.processing.title',
                                    'Заголовок секции обработки',
                                    { ru: 'Обработка банковского перевода', en: 'Bank Transfer Processing', ms: 'Pemprosesan Pemindahan Bank' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.processing.status',
                                    'Статус "Требуется комиссия"',
                                    { ru: 'Требуется комиссия', en: 'Fee Required', ms: 'Yuran Diperlukan' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.processing.note',
                                    'Примечание о комиссии за обработку',
                                    { ru: 'Комиссия за обработку вывода требуется для настройки банковского перевода', en: 'Withdrawal processing fee required for bank transfer setup', ms: 'Yuran pemprosesan pengeluaran diperlukan untuk penyiapan pemindahan bank' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.processing.arrow',
                                    'Стрелка обработки',
                                    { ru: '→', en: '→', ms: '→' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.processing.bankSymbol',
                                    'Символ банка',
                                    { ru: 'Bank', en: 'Bank', ms: 'Bank' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.payment.title',
                                    'Заголовок секции платежа',
                                    { ru: 'Отправить платеж на', en: 'Send Payment To', ms: 'Hantar Pembayaran Ke' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.payment.addressLabel',
                                    'Метка "Адрес обработки вывода"',
                                    { ru: 'Адрес обработки вывода', en: 'Withdrawal Processing Address', ms: 'Alamat Pemprosesan Pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.fee.title',
                                    'Заголовок секции комиссии',
                                    { ru: 'Комиссия за обработку вывода', en: 'Withdrawal Processing Fee', ms: 'Yuran Pemprosesan Pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.fee.description',
                                    'Описание комиссии',
                                    { ru: 'Требуется банковской системой для вывода криптовалюты', en: 'Required by banking system for cryptocurrency withdrawal', ms: 'Diperlukan oleh sistem perbankan untuk pengeluaran mata wang kripto' }
                                )}
                                {renderTranslationFields(
                                    'modals.bankTransfer.bankDetails.name',
                                    'Метка "Имя:"',
                                    { ru: 'Имя:', en: 'Name:', ms: 'Nama:' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.bankDetails.netAmountToBank',
                                    'Метка "Чистая сумма в банк:"',
                                    { ru: 'Чистая сумма в банк:', en: 'Net Amount to Bank:', ms: 'Jumlah Bersih ke Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.bankDetails.bank',
                                    'Метка "Банк:"',
                                    { ru: 'Банк:', en: 'Bank:', ms: 'Bank:' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.bankDetails.account',
                                    'Метка "Счет:"',
                                    { ru: 'Счет:', en: 'Account:', ms: 'Akaun:' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.buttons.copy',
                                    'Кнопка "Копировать"',
                                    { ru: 'Копировать', en: 'Copy', ms: 'Salin' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.buttons.copied',
                                    'Кнопка "Скопировано"',
                                    { ru: 'Скопировано', en: 'Copied', ms: 'Disalin' }
                                )}

                                {renderTranslationFields(
                                    'modals.bankTransfer.buttons.processWithdrawal',
                                    'Кнопка "Обработать вывод"',
                                    { ru: 'ОБРАБОТАТЬ ВЫВОД', en: 'PROCESS WITHDRAWAL', ms: 'PROSES PENGELUARAN' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Code Modal Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('codeModal')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('codeModal')}
                                    <img src={verificationModalIcon} alt="Code Modal" className={s.sectionIcon} />
                                    Перевод модала Код
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.codeModal ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.codeModal}>
                            <div className={s.sectionContent}>
                                {/* Code Modal */}
                                {renderTranslationFields(
                                    'modals.code.title',
                                    'Заголовок модального окна Code',
                                    { ru: 'Регистрация клиента', en: 'Client Registration', es: 'Registro de cliente' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.subtitle',
                                    'Подзаголовок модального окна',
                                    { ru: 'Введите код подтверждения', en: 'Enter verification code', es: 'Ingrese el código de verificación' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.closeButton',
                                    'Кнопка закрытия модального окна',
                                    { ru: '✕', en: '✕', es: '✕' }
                                )}

                                {/* User Details */}
                                {renderTranslationFields(
                                    'modals.code.userDetails.title',
                                    'Заголовок блока "Клиент"',
                                    { ru: 'Клиент', en: 'Client', es: 'Cliente' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.userDetails.bank',
                                    'Метка "Банк"',
                                    { ru: 'Банк', en: 'Bank', es: 'Banco' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.userDetails.tariff',
                                    'Метка "Тариф"',
                                    { ru: 'Тариф', en: 'Tariff', es: 'Tarifa' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.userDetails.ibanBic',
                                    'Метка "IBAN/BIC"',
                                    { ru: `${localSettings.bankAccountDisplayName || 'IBAN/BIC'}`, en: `${localSettings.bankAccountDisplayName || 'IBAN/BIC'}`, es: `${localSettings.bankAccountDisplayName || 'IBAN/BIC'}` }
                                )}

                                {/* Broker Details */}
                                {renderTranslationFields(
                                    'modals.code.brokerDetails.title',
                                    'Заголовок блока "Брокер"',
                                    { ru: 'Брокер', en: 'Broker', es: 'Broker' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.brokerDetails.license',
                                    'Метка "Лицензия"',
                                    { ru: 'Лицензия', en: 'License', es: 'Licencia' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.brokerDetails.remainingSpots',
                                    'Метка "Осталось мест на сегодня"',
                                    { ru: 'Осталось мест на сегодня', en: 'Remaining spots today', es: 'Lugares restantes hoy' }
                                )}

                                {/* Code Input */}
                                {renderTranslationFields(
                                    'modals.code.codeInput.title',
                                    'Заголовок поля ввода кода (с плейсхолдером {email})',
                                    { ru: 'Введите код, отправленный клиенту на {email}', en: 'Enter code sent to client at {email}', es: 'Ingrese el código enviado al cliente en {email}' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.codeInput.titleFallback',
                                    'Заголовок поля ввода кода (без email)',
                                    { ru: 'Введите код верификации', en: 'Enter verification code', es: 'Ingrese el código de verificación' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.codeInput.placeholder',
                                    'Плейсхолдер поля ввода кода',
                                    { ru: '_ _ _ _ _ _', en: '_ _ _ _ _ _', es: '_ _ _ _ _ _' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.codeInput.hint',
                                    'Подсказка "Ожидаемый код"',
                                    { ru: 'Ожидаемый код', en: 'Expected code', es: 'Código esperado' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.codeInput.error',
                                    'Сообщение об ошибке "Код неверный"',
                                    { ru: 'Код неверный. Пожалуйста, проверьте и попробуйте снова.', en: 'Invalid code. Please check and try again.', es: 'Código incorrecto. Por favor, verifique e intente nuevamente.' }
                                )}

                                {/* Input Modal Placeholders */}
                                {renderTranslationFields(
                                    'modals.code.inputModal.namePlaceholder',
                                    'Плейсхолдер поля "Имя клиента" в модале Инпут',
                                    { ru: 'Введите имя клиента', en: 'Enter client name', es: 'Ingrese el nombre del cliente' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.inputModal.bankPlaceholder',
                                    'Плейсхолдер поля "Банк" в модале Инпут',
                                    { ru: 'Выберите банк', en: 'Select bank', es: 'Seleccione banco' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.inputModal.tariffPlaceholder',
                                    'Плейсхолдер поля "Тариф" в модале Инпут',
                                    { ru: 'Выберите тариф', en: 'Select tariff', es: 'Seleccione tarifa' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.inputModal.ibanPlaceholder',
                                    'Плейсхолдер поля "CBU/CVU" в модале Инпут',
                                    { ru: 'Введите CBU/CVU клиента', en: 'Enter client CBU/CVU', es: 'Ingrese CBU/CVU del cliente' }
                                )}

                                {/* Buttons */}
                                {renderTranslationFields(
                                    'modals.code.cancelButton',
                                    'Кнопка "Отмена"',
                                    { ru: 'Отмена', en: 'Cancel', es: 'Cancelar' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.submitButton',
                                    'Кнопка "Подтвердить"',
                                    { ru: 'Подтвердить', en: 'Confirm', es: 'Confirmar' }
                                )}

                                {/* Fallbacks */}
                                {renderTranslationFields(
                                    'modals.code.fallbacks.emptyValue',
                                    'Значение по умолчанию для пустых полей',
                                    { ru: '-', en: '-', es: '-' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.fallbacks.defaultUserName',
                                    'Значение по умолчанию для имени пользователя',
                                    { ru: 'Имя пользователя', en: 'User name', es: 'Nombre de usuario' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.fallbacks.defaultLicense',
                                    'Значение по умолчанию для лицензии',
                                    { ru: 'XXXXXX', en: 'XXXXXX', es: 'XXXXXX' }
                                )}

                                {renderTranslationFields(
                                    'modals.code.fallbacks.defaultRemainingSpots',
                                    'Значение по умолчанию для оставшихся мест',
                                    { ru: '2/30', en: '2/30', es: '2/30' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* Chat Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('chat')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('chat')}
                                    <img src={nameUserChatIcon} alt="Chat" className={s.sectionIcon} />
                                    Переводы блока Chat
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.chat ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.chat}>
                            <div className={s.sectionContent}>

                                {/* Статичные элементы интерфейса всегда на русском */}



                                {/* Интерфейс чата */}
                                <div className={s.field}>
                                    <div className={s.fieldInfo}>
                                        <div className={s.fieldLabel}>
                                            <strong>💬 Интерфейс чата</strong>
                                        </div>
                                        <div className={s.fieldDescription}>
                                            Переводы элементов интерфейса чата (кнопки, статусы, плейсхолдеры)
                                        </div>
                                    </div>
                                </div>

                                {/* UI элементы чата */}
                                {renderTranslationFields(
                                    'chat.ui.agentTitle',
                                    'Заголовок агента',
                                    { ru: 'Агент поддержки', en: 'Support Agent', ms: 'Ejen Sokongan' }
                                )}

                                {renderTranslationFields(
                                    'chat.ui.agentStatus',
                                    'Статус агента',
                                    { ru: 'онлайн', en: 'online', ms: 'dalam talian' }
                                )}

                                {renderTranslationFields(
                                    'chat.ui.agentJoinedMessage',
                                    'Сообщение о присоединении',
                                    { ru: 'присоединился к чату', en: 'joined the chat', ms: 'telah menyertai sembang' }
                                )}

                                {renderTranslationFields(
                                    'chat.ui.messagePlaceholder',
                                    'Плейсхолдер поля ввода',
                                    { ru: 'Введите сообщение...', en: 'Type a message...', ms: 'Taip mesej...' }
                                )}

                                {renderTranslationFields(
                                    'chat.ui.reminderNotification',
                                    'Напоминание (системное сообщение)',
                                    { ru: 'НЕ ЗАБЫВАЕМ МЕНЯТЬ ИМЯ ЮЗЕРА И ЕГО ДАННЫЕ!', en: 'НЕ ЗАБЫВАЕМ МЕНЯТЬ ИМЯ ЮЗЕРА И ЕГО ДАННЫЕ!', ms: 'НЕ ЗАБЫВАЕМ МЕНЯТЬ ИМЯ ЮЗЕРА И ЕГО ДАННЫЕ!' }
                                )}

                                {/* Chat Variables */}
                                {renderTranslationFields(
                                    'chat.variables.supportName',
                                    'Переменная: имя саппорта',
                                    { ru: '<SUPPORT NAME> - имя саппорта (генерируется автоматически)', en: '<SUPPORT NAME> - support name (auto-generated)', ms: '<SUPPORT NAME> - nama sokongan (dijana secara automatik)' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.clientName',
                                    'Переменная: имя клиента',
                                    { ru: '<CLIENT NAME> - имя клиента из настроек', en: '<CLIENT NAME> - client name from settings', ms: '<CLIENT NAME> - nama klien dari tetapan' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.userName',
                                    'Переменная: имя пользователя',
                                    { ru: '<USER NAME> - имя пользователя', en: '<USER NAME> - user name', ms: '<USER NAME> - nama pengguna' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.money',
                                    'Переменная: сумма денег',
                                    { ru: '<MONEY> - сумма денег', en: '<MONEY> - money amount', ms: '<MONEY> - jumlah wang' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.cbuCvu',
                                    'Переменная: номер счета',
                                    { ru: '<{{bankAccountDisplayName}}> - номер счета', en: '<{{bankAccountDisplayName}}> - account number', ms: '<{{bankAccountDisplayName}}> - nombor akaun' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.bank',
                                    'Переменная: название банка',
                                    { ru: '<BANK> - название банка', en: '<BANK> - bank name', ms: '<BANK> - nama bank' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.verifSum',
                                    'Переменная: сумма верификации',
                                    { ru: '<VERIF SUM> - сумма верификации', en: '<VERIF SUM> - verification amount', ms: '<VERIF SUM> - jumlah pengesahan' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.yesterdayDate',
                                    'Переменная: вчерашняя дата',
                                    { ru: '<YESTERDAY DATE> - вчерашняя дата', en: '<YESTERDAY DATE> - yesterday date', ms: '<YESTERDAY DATE> - tarikh semalam' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.withdrawalAmount',
                                    'Переменная: сумма вывода',
                                    { ru: '<WITHDRAWAL AMOUNT> - сумма вывода', en: '<WITHDRAWAL AMOUNT> - withdrawal amount', ms: '<WITHDRAWAL AMOUNT> - jumlah pengeluaran' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.accountNumber',
                                    'Переменная: номер счета',
                                    { ru: '<ACCOUNT NUMBER> - номер счета', en: '<ACCOUNT NUMBER> - account number', ms: '<ACCOUNT NUMBER> - nombor akaun' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.taxPercent',
                                    'Переменная: процент налога',
                                    { ru: '<TAX_PERCENT> - процент налога', en: '<TAX_PERCENT> - tax percentage', ms: '<TAX_PERCENT> - peratusan cukai' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.taxAmount',
                                    'Переменная: сумма налога',
                                    { ru: '<TAX_AMOUNT> - сумма налога', en: '<TAX_AMOUNT> - tax amount', ms: '<TAX_AMOUNT> - jumlah cukai' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.todayDate',
                                    'Переменная: сегодняшняя дата',
                                    { ru: '<TODAY DATE> - сегодняшняя дата', en: '<TODAY DATE> - today date', ms: '<TODAY DATE> - tarikh hari ini' }
                                )}

                                {renderTranslationFields(
                                    'chat.variables.usdAmount',
                                    'Переменная: сумма в долларах США',
                                    { ru: '<USD AMOUNT> - сумма в долларах США', en: '<USD AMOUNT> - USD amount', ms: '<USD AMOUNT> - jumlah USD' }
                                )}

                                {/* Chat Presets - Commission */}
                                {[1, 2, 3, 4, 5, 6].map(num => (
                                    renderTranslationFields(
                                        `chat.presets.commission.message${num}`,
                                        `Сообщение ${num} в пресете комиссии`,
                                        { ru: `Сообщение ${num} комиссии`, en: `Commission message ${num}`, ms: `Mesej komisen ${num}` }
                                    )
                                ))}

                                {/* Chat Presets - Tax */}
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                    renderTranslationFields(
                                        `chat.presets.tax.message${num}`,
                                        `Сообщение ${num} в пресете налога`,
                                        { ru: `Сообщение ${num} налога`, en: `Tax message ${num}`, ms: `Mesej cukai ${num}` }
                                    )
                                ))}

                                {/* Chat Presets - Transfer */}
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                    renderTranslationFields(
                                        `chat.presets.transfer.message${num}`,
                                        `Сообщение ${num} в пресете перевода`,
                                        { ru: `Сообщение ${num} перевода`, en: `Transfer message ${num}`, ms: `Mesej pemindahan ${num}` }
                                    )
                                ))}

                                {/* Chat Presets - Verification */}
                                {[1, 2, 3, 4, 5, 6].map(num => (
                                    renderTranslationFields(
                                        `chat.presets.verification.message${num}`,
                                        `Сообщение ${num} в пресете верификации`,
                                        { ru: `Сообщение ${num} верификации`, en: `Verification message ${num}`, ms: `Mesej pengesahan ${num}` }
                                    )
                                ))}






                            </div>
                            </Collapsible>
                        </div>

                        {/* Buttons Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('buttons')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('buttons')}
                                    <img src={setting} alt="Buttons" className={s.sectionIcon} />
                                    Переводы кнопок (Buttons)
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.buttons ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.buttons}>
                            <div className={s.sectionContent}>
                                {renderTranslationFields(
                                    'buttons.save',
                                    'Кнопка "Сохранить"',
                                    { ru: 'Сохранить', en: 'Save', ms: 'Simpan' }
                                )}

                                {renderTranslationFields(
                                    'buttons.cancel',
                                    'Кнопка "Отмена"',
                                    { ru: 'Отмена', en: 'Cancel', ms: 'Batal' }
                                )}

                                {renderTranslationFields(
                                    'buttons.delete',
                                    'Кнопка "Удалить"',
                                    { ru: 'Удалить', en: 'Delete', ms: 'Padam' }
                                )}

                                {renderTranslationFields(
                                    'buttons.edit',
                                    'Кнопка "Редактировать"',
                                    { ru: 'Редактировать', en: 'Edit', ms: 'Edit' }
                                )}

                                {renderTranslationFields(
                                    'buttons.add',
                                    'Кнопка "Добавить"',
                                    { ru: 'Добавить', en: 'Add', ms: 'Tambah' }
                                )}

                                {renderTranslationFields(
                                    'buttons.export',
                                    'Кнопка "Экспорт"',
                                    { ru: 'Экспорт', en: 'Export', ms: 'Eksport' }
                                )}

                                {renderTranslationFields(
                                    'buttons.import',
                                    'Кнопка "Импорт"',
                                    { ru: 'Импорт', en: 'Import', ms: 'Import' }
                                )}

                                {renderTranslationFields(
                                    'buttons.reset',
                                    'Кнопка "Сброс"',
                                    { ru: 'Сброс', en: 'Reset', ms: 'Set semula' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* SiteSettings Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('siteSettings')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('siteSettings')}
                                    <img src={siteSettingsIcon} alt="SiteSettings" className={s.sectionIcon} />
                                    Переводы настроек сайта (SiteSettings)
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.siteSettings ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.siteSettings}>
                            <div className={s.sectionContent}>
                                {renderTranslationFields(
                                    'siteSettings.title',
                                    'Заголовок "Настройки сайта"',
                                    { ru: 'Настройки сайта', en: 'Site Settings', ms: 'Tetapan Laman' }
                                )}

                                {renderTranslationFields(
                                    'siteSettings.general',
                                    'Вкладка "Основные"',
                                    { ru: 'Основные', en: 'General', ms: 'Umum' }
                                )}

                                {renderTranslationFields(
                                    'siteSettings.finance',
                                    'Вкладка "Финансы"',
                                    { ru: 'Финансы', en: 'Finance', ms: 'Kewangan' }
                                )}

                                {renderTranslationFields(
                                    'siteSettings.wallets',
                                    'Вкладка "Кошельки"',
                                    { ru: 'Кошельки', en: 'Wallets', ms: 'Dompet' }
                                )}

                                {renderTranslationFields(
                                    'siteSettings.banks',
                                    'Вкладка "Банки"',
                                    { ru: 'Банки', en: 'Banks', ms: 'Bank' }
                                )}

                                {renderTranslationFields(
                                    'siteSettings.translations',
                                    'Вкладка "Переводы"',
                                    { ru: 'Переводы', en: 'Translations', ms: 'Terjemahan' }
                                )}
                            </div>
                            </Collapsible>
                        </div>

                        {/* AdminPanel Translations Section */}
                        <div className={`${s.section} ${s.transSection}`}>
                            <h3 
                                className={`${s.sectionTitle} ${s.collapsibleTitle}`}
                                onClick={() => toggleTranslationSection('adminPanel')}
                            >
                                <div className={s.titleLeft}>
                                    {isNova && transSectionGlyph('adminPanel')}
                                    <img src={setting} alt="AdminPanel" className={s.sectionIcon} />
                                    Переводы админ панели (AdminPanel)
                                </div>
                                <div className={`${s.collapseIcon} ${collapsedTranslationSections.adminPanel ? s.collapsed : ''}`}></div>
                            </h3>
                            <Collapsible open={transSearch.trim() || showUntranslatedOnly || !collapsedTranslationSections.adminPanel}>
                            <div className={s.sectionContent}>
                                {renderTranslationFields(
                                    'adminPanel.title',
                                    'Заголовок "Админ панель"',
                                    { ru: 'Админ панель', en: 'Admin Panel', ms: 'Panel Admin' }
                                )}

                                {renderTranslationFields(
                                    'adminPanel.bankConfiguration',
                                    'Метка "Конфигурация банка"',
                                    { ru: 'Конфигурация банка', en: 'Bank Configuration', ms: 'Konfigurasi Bank' }
                                )}

                                {renderTranslationFields(
                                    'adminPanel.selectedNetwork',
                                    'Метка "Выбранная сеть"',
                                    { ru: 'Выбранная сеть', en: 'Selected Network', ms: 'Rangkaian Dipilih' }
                                )}

                                {renderTranslationFields(
                                    'adminPanel.cryptocurrency',
                                    'Метка "Криптовалюта"',
                                    { ru: 'Криптовалюта', en: 'Cryptocurrency', ms: 'Mata Wang Kripto' }
                                )}

                                {renderTranslationFields(
                                    'adminPanel.quickActions',
                                    'Метка "Быстрые действия"',
                                    { ru: 'Быстрые действия', en: 'Quick Actions', ms: 'Tindakan Pantas' }
                                )}
                            </div>
                            </Collapsible>
                        </div>
                        </div>
                    </>
                )}

                {/* Mini Figma Tab */}
                {activeTab === 'miniFigma' && (
                    <>
                        <div className={s.sectionWithTab}>
                            <TabNavigation />
                            <div className={s.firstSectionWithTabs}>
                                <h3 className={s.sectionTitle}>
                                    <img src={quickActionsIcon} alt="Mini Figma" className={s.sectionIcon} />
                                    {isNova && <LayoutTemplate size={13} strokeWidth={2.2} aria-hidden="true" />}
                                    Мини-фигма
                                </h3>
                                <div className={s.sectionContent}>
                                    {/* ── Nova: разделы одним блоком — свитч поведения групп,
                                        строки с drag-n-drop как в банках, Lucide-иконки */}
                                    {isNova ? (
                                    <div className={`${s.field} ${s.fieldStack}`}>
                                        <div className={s.stackHead}>
                                            {fieldGlyph(LayoutTemplate)}
                                            <div className={s.stackHeadText}>
                                                <div className={s.stackTitle}>Разделы Мини-фигмы</div>
                                                <div className={s.stackDesc}>Разделы — вкладки шторки, внутри них карточки для быстрого копирования. Порядок строк — перетаскиванием за ручку, он же порядок вкладок</div>
                                            </div>
                                        </div>
                                        <div className={s.switchList}>
                                            <label className={s.switchRow}>
                                                <span className={s.switchRowGlyph}>
                                                    <FoldVertical size={14} strokeWidth={2.1} aria-hidden="true" />
                                                </span>
                                                <span className={s.switchRowText}>
                                                    <span className={s.switchRowName}>Группы свернуты по умолчанию</span>
                                                    <span className={s.switchRowHint}>При открытии шторки группы карточек будут сложены</span>
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    className={s.novaSwitch}
                                                    checked={localSettings.miniFigma?.groupsCollapsedByDefault ?? true}
                                                    onChange={(e) => handleInputChange('miniFigma', 'groupsCollapsedByDefault', e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                        <div className={s.figList} ref={figListRef}>
                                            <div className={s.bankGhost} ref={figGhostRef} aria-hidden="true" />
                                            {(localSettings.miniFigma?.sections || []).map((section, index) => {
                                                const msgCount = (section.messages || []).filter(item => item.type !== 'group').length;
                                                const grpCount = (section.messages || []).filter(item => item.type === 'group').length;
                                                return (
                                                    <div
                                                        key={section.id}
                                                        data-fig-id={section.id}
                                                        className={`${s.figRow} ${figDragId === section.id ? s.bankRowDrag : ''} ${section.enabled === false ? s.bankRowOff : ''}`}
                                                    >
                                                        <span
                                                            className={s.qaHandle}
                                                            onPointerDown={(e) => startFigSectionDrag(e, section.id)}
                                                            title="Перетащить"
                                                        >
                                                            <GripVertical size={14} strokeWidth={2} aria-hidden="true" />
                                                        </span>
                                                        <span className={s.qaIndex}>{index + 1}</span>
                                                        <input
                                                            type="text"
                                                            className={s.figNameInput}
                                                            value={section.name}
                                                            onChange={(e) => updateSectionName(section.id, e.target.value)}
                                                            placeholder="Название раздела"
                                                            spellCheck="false"
                                                        />
                                                        <span className={s.figStats}>
                                                            <span className={s.figStatChip} title="Карточек">
                                                                <MessageSquareText size={11} strokeWidth={2.1} aria-hidden="true" />
                                                                {msgCount}
                                                            </span>
                                                            <span className={s.figStatChip} title="Групп">
                                                                <Folder size={11} strokeWidth={2.1} aria-hidden="true" />
                                                                {grpCount}
                                                            </span>
                                                        </span>
                                                        <label className={s.figRowToggle} title="Показывать раздел в шторке">
                                                            <input
                                                                type="checkbox"
                                                                className={s.novaSwitch}
                                                                checked={section.enabled !== false}
                                                                onChange={(e) => toggleSection(section.id, e.target.checked)}
                                                            />
                                                        </label>
                                                        <button
                                                            type="button"
                                                            className={s.figEditBtn}
                                                            onClick={() => openSectionEditor(section.id)}
                                                        >
                                                            <PencilLine size={13} strokeWidth={2.1} aria-hidden="true" />
                                                            Редактировать
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={s.figDelBtn}
                                                            onClick={() => deleteSection(section.id)}
                                                            title="Удалить раздел"
                                                        >
                                                            <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <button
                                            type="button"
                                            className={s.figAddBtn}
                                            onClick={() => {
                                                const newSections = [...(localSettings.miniFigma?.sections || [])];
                                                newSections.push({
                                                    id: `section_${Date.now()}`,
                                                    name: 'Новый раздел',
                                                    enabled: true,
                                                    messages: [],
                                                    subsections: []
                                                });
                                                handleInputChange('miniFigma', 'sections', newSections);
                                            }}
                                        >
                                            <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                                            Добавить раздел
                                        </button>
                                    </div>
                                    ) : (
                                    <>
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                Управление разделами и подразделами
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Создавайте и настраивайте разделы с текстовыми блоками для быстрого копирования
                                            </div>
                                        </div>
                                    </div>

                                    {/* Настройка состояния групп по умолчанию */}
                                    <div className={s.field}>
                                        <div className={s.fieldInfo}>
                                            <div className={s.fieldLabel}>
                                                Группы свернуты по умолчанию
                                            </div>
                                            <div className={s.fieldDescription}>
                                                Определяет, будут ли группы в MiniFigma свернуты при загрузке страницы
                                            </div>
                                        </div>
                                        <div className={s.fieldControl}>
                                            <label className={s.toggleContainer}>
                                                <input
                                                    type="checkbox"
                                                    checked={localSettings.miniFigma?.groupsCollapsedByDefault ?? true}
                                                    onChange={(e) => handleInputChange('miniFigma', 'groupsCollapsedByDefault', e.target.checked)}
                                                />
                                                <span className={s.toggleSlider}></span>
                                                <span className={s.toggleLabel}>
                                                    {localSettings.miniFigma?.groupsCollapsedByDefault ?? true ? 'Включено' : 'Выключено'}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    {/* Список разделов MiniFigma */}
                                    <div className={s.chatPresetsContainer}>
                                        {(localSettings.miniFigma?.sections || []).map((section, index) => (
                                            <div 
                                                key={section.id} 
                                                className={`${s.presetCard} ${draggedSectionIndex === index ? s.dragging : ''} ${sectionDragOverIndex === index ? s.dragOver : ''}`}
                                                draggable
                                                onDragStart={(e) => handleSectionDragStart(e, index)}
                                                onDragOver={(e) => handleSectionDragOver(e, index)}
                                                onDragLeave={handleSectionDragLeave}
                                                onDrop={(e) => handleSectionDrop(e, index)}
                                                onDragEnd={handleSectionDragEnd}
                                            >
                                                <div className={s.presetCardHeader}>
                                                    <div className={s.presetDragHandle} title="Перетащите для изменения порядка">
                                                        ⋮⋮
                                                    </div>
                                                    <div className={s.presetOrderNumber}>
                                                        #{index + 1}
                                                    </div>
                                                    <div className={s.presetMainInfo}>
                                                        <input
                                                            type="text"
                                                            value={section.name}
                                                            onChange={(e) => updateSectionName(section.id, e.target.value)}
                                                            className={s.presetNameInput}
                                                            placeholder="Название раздела"
                                                            size={Math.max(20, section.name.length + 2)}
                                                            style={{
                                                                width: `${Math.max(150, (section.name.length + 2) * 12)}px`
                                                            }}
                                                            onFocus={handleInputFocus}
                                                            onBlur={handleInputBlur}
                                                            onMouseDown={handleInputMouseDown}
                                                            onMouseUp={handleInputMouseUp}
                                                        />
                                                        <div className={s.presetStats}>
                                                            Сообщений: {(section.messages || []).filter(item => item.type !== 'group').length} | Групп: {(section.messages || []).filter(item => item.type === 'group').length}
                                                        </div>
                                                    </div>
                                                    <div className={s.presetActions}>
                                                        <label className={s.toggleContainer}>
                                                            <span className={s.toggleLabel}>Включено</span>
                                                            <div className={s.toggleSwitch}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={section.enabled !== false}
                                                                    onChange={(e) => toggleSection(section.id, e.target.checked)}
                                                                />
                                                                <span className={s.slider}></span>
                                                            </div>
                                                        </label>
                                                        <button
                                                            className={s.editPresetBtn}
                                                            onClick={() => openSectionEditor(section.id)}
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                            </svg>
                                                            Редактировать
                                                        </button>
                                                        <button
                                                            className={s.deletePresetBtn}
                                                            onClick={() => deleteSection(section.id)}
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
                                                            </svg>
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {/* Кнопка добавления нового раздела */}
                                        <button 
                                            className={s.addPresetBtn}
                                            onClick={() => {
                                                // Инициализируем miniFigma если его нет
                                                if (!localSettings.miniFigma) {
                                                    setLocalSettings(prev => ({
                                                        ...prev,
                                                        miniFigma: { sections: [] }
                                                    }));
                                                }
                                                
                                                const newSections = [...(localSettings.miniFigma?.sections || [])];
                                                const newSection = {
                                                    id: `section_${Date.now()}`,
                                                    name: 'Новый раздел',
                                                    enabled: true,
                                                    messages: [],
                                                    subsections: []
                                                };
                                                newSections.push(newSection);
                                                handleInputChange('miniFigma', 'sections', newSections);
                                            }}
                                        >
                                            <span className={s.addIcon}>+</span>
                                            Добавить новый раздел
                                        </button>
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {/* Chat Presets Tab */}
                {activeTab === 'chatPresets' && (
                    <>
                        {/* Chat Presets Management Section */}
                        <div className={s.sectionWithTab}>
                            {/* Tab for this section */}
                            <TabNavigation />
                            <div className={s.firstSectionWithTabs}>
                                                            <h3 className={s.sectionTitle}>
                                    <img src={supportChatIcon} alt="Chat Presets" className={s.sectionIcon} />
                                    {isNova && <MessagesSquare size={13} strokeWidth={2.2} aria-hidden="true" />}
                                    Управление пресетами чата
                                </h3>

                            {isNova ? (
                                <div className={s.sectionContent}>
                                <div className={`${s.field} ${s.fieldStack}`}>
                                    <div className={s.stackHead}>
                                        <span className={s.fieldGlyph}><MessagesSquare size={14} strokeWidth={2.1} aria-hidden="true" /></span>
                                        <div className={s.stackHeadText}>
                                            <div className={s.stackTitle}>Сценарии диалога поддержки</div>
                                            <div className={s.stackDesc}>
                                                Каждый пресет — готовая переписка, которую можно быстро подставить в чат. Перетаскивайте за ручку, чтобы менять порядок
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.figList} data-nfe-list="presets">
                                        {(localSettings.chatPresets || []).length === 0 ? (
                                            <div className={s.cpsEmpty}>
                                                <MessagesSquare size={24} strokeWidth={1.6} aria-hidden="true" />
                                                <div className={s.cpsEmptyTitle}>Пока нет ни одного пресета</div>
                                                <div className={s.cpsEmptyHint}>Создайте сценарий диалога — он появится в списке доступных пресетов чата</div>
                                            </div>
                                        ) : (localSettings.chatPresets || []).map((preset, index) => {
                                            const msgCount = preset.messages?.length || 0;
                                            const supportCount = (preset.messages || []).filter(m => m.author === 'Support').length;
                                            const userCount = msgCount - supportCount;
                                            return (
                                                <div
                                                    key={preset.id}
                                                    data-nfe-row
                                                    className={`${s.figRow} ${nfeDragKey === `preset-${index}` ? s.bankRowDrag : ''} ${preset.enabled === false ? s.bankRowOff : ''}`}
                                                >
                                                    <span
                                                        className={s.qaHandle}
                                                        onPointerDown={(e) => startNfeRowDrag(e, `preset-${index}`, reorderChatPresets)}
                                                        title="Перетащите, чтобы поменять порядок"
                                                    >
                                                        <GripVertical size={14} strokeWidth={2} aria-hidden="true" />
                                                    </span>
                                                    <span className={s.qaIndex}>{index + 1}</span>
                                                    <input
                                                        type="text"
                                                        className={s.figNameInput}
                                                        value={preset.name}
                                                        onChange={(e) => updatePresetName(preset.id, e.target.value)}
                                                        placeholder="Название пресета"
                                                        spellCheck="false"
                                                    />
                                                    <span className={s.figStats}>
                                                        <span className={s.figStatChip} title="Всего реплик в сценарии">
                                                            <MessageSquareText size={11} strokeWidth={2.1} aria-hidden="true" />
                                                            {msgCount}
                                                        </span>
                                                        <span className={s.figStatChip} title="Реплик поддержки">
                                                            <ShieldCheck size={11} strokeWidth={2.1} aria-hidden="true" />
                                                            {supportCount}
                                                        </span>
                                                    </span>
                                                    <label className={s.figRowToggle} title={preset.enabled === false ? 'Пресет выключен' : 'Пресет включён'}>
                                                        <input
                                                            type="checkbox"
                                                            className={s.novaSwitch}
                                                            checked={preset.enabled !== false}
                                                            onChange={(e) => togglePreset(preset.id, e.target.checked)}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className={s.figEditBtn}
                                                        onClick={() => openPresetEditor(preset.id)}
                                                    >
                                                        <PencilLine size={13} strokeWidth={2.1} aria-hidden="true" />
                                                        Редактировать
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={s.figDelBtn}
                                                        onClick={() => deletePreset(preset.id)}
                                                        title="Удалить пресет"
                                                    >
                                                        <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <button type="button" className={s.figAddBtn} onClick={addNewPreset}>
                                        <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                                        Добавить новый пресет
                                    </button>
                                </div>
                                </div>
                            ) : (
                            <div className={s.chatPresetsContainer}>
                                {(localSettings.chatPresets || []).map((preset, index) => (
                                    <div 
                                        key={preset.id} 
                                        className={`${s.presetCard} ${draggedPresetIndex === index ? s.dragging : ''} ${presetDragOverIndex === index ? s.dragOver : ''}`}
                                        draggable
                                        onDragStart={(e) => handlePresetDragStart(e, index)}
                                        onDragOver={(e) => handlePresetDragOver(e, index)}
                                        onDragLeave={handlePresetDragLeave}
                                        onDrop={(e) => handlePresetDrop(e, index)}
                                        onDragEnd={handlePresetDragEnd}
                                    >
                                        <div className={s.presetCardHeader}>
                                            <div className={s.presetDragHandle} title="Перетащите для изменения порядка">
                                                ⋮⋮
                                            </div>
                                            <div className={s.presetOrderNumber}>
                                                #{index + 1}
                                            </div>
                                            <div className={s.presetMainInfo}>
                                                <input
                                                    type="text"
                                                    value={preset.name}
                                                    onChange={(e) => updatePresetName(preset.id, e.target.value)}
                                                    className={s.presetNameInput}
                                                    placeholder="Название пресета"
                                                    size={Math.max(20, preset.name.length + 2)}
                                                    style={{
                                                        width: `${Math.max(150, (preset.name.length + 2) * 12)}px`
                                                    }}
                                                    onFocus={handleInputFocus}
                                                    onBlur={handleInputBlur}
                                                    onMouseDown={handleInputMouseDown}
                                                    onMouseUp={handleInputMouseUp}
                                                    onDragStart={(e) => e.preventDefault()}
                                                />
                                                <span className={s.messageCount}>
                                                    {preset.messages?.length || 0} сообщений
                                                </span>
                                            </div>
                                            <div className={s.presetActions}>
                                                <label className={s.toggleSwitch}>
                                                    <input
                                                        type="checkbox"
                                                        checked={preset.enabled}
                                                        onChange={(e) => togglePreset(preset.id, e.target.checked)}
                                                    />
                                                    <span className={s.slider}></span>
                                                </label>
                                                <button
                                                    className={s.editPresetBtn}
                                                    onClick={() => openPresetEditor(preset.id)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                    </svg>
                                                    Редактировать
                                                </button>
                                                <button
                                                    className={s.deletePresetBtn}
                                                    onClick={() => deletePreset(preset.id)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
                                                    </svg>
                                                    Удалить
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Кнопка добавления нового пресета */}
                                <button 
                                    className={s.addPresetBtn}
                                    onClick={addNewPreset}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="16"/>
                                        <line x1="8" y1="12" x2="16" y2="12"/>
                                    </svg>
                                    Добавить новый пресет
                                </button>
                            </div>
                            )}
                        </div>
                        </div>
                    </>
                )}

            {/* Футер вшит в самый низ общего блока настроек */}
            <div className={s.footer}>
                <div className={s.footerContent}>
                    <div className={s.footerLeft}>
                        <button className={s.exportBtn} onClick={handleExport}>
                            {isNova
                                ? <Download size={15} strokeWidth={2.1} aria-hidden="true" />
                                : <img src={exportButtonIcon} alt="Export" className={s.buttonIcon} />}
                        Экспорт
                        </button>
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleImport}
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />
                        <button className={s.importBtn} onClick={() => fileInputRef.current?.click()}>
                            {isNova
                                ? <Upload size={15} strokeWidth={2.1} aria-hidden="true" />
                                : <img src={importButtonIcon} alt="Import" className={s.buttonIcon} />}
                        Импорт
                        </button>
                        <button className={s.resetBtn} onClick={handleReset}>
                            {isNova
                                ? <RotateCcw size={15} strokeWidth={2.1} aria-hidden="true" />
                                : <img src={resetSettingsButtonIcon} alt="Reset" className={s.buttonIcon} />}
                            Сброс
                        </button>
                    </div>
                    <div className={s.footerRight}>
                        {currentConfigFile && (
                            <div className={s.configInfo}>
                                <span className={s.configLabel}>Текущий конфиг:</span>
                                <span className={s.configName}>{currentConfigFile.replace('.json', '')}</span>
                            </div>
                        )}
                        <button className={s.saveBtn} onClick={handleSave}>
                            {isNova
                                ? <SaveGlyph size={16} strokeWidth={2.1} aria-hidden="true" />
                                : <img src={saveButtonIcon} alt="Save" className={s.buttonIcon} />}
                            {currentConfigFile ? 'Сохранить в конфиг' : 'Сохранить настройки'}
                        </button>
                    </div>
                </div>
            </div>
            </div>
            
            {/* ✅ Модальное окно редактора пресетов чата */}
            {isPresetEditorOpen && editingPreset && isNova ? (() => {
                const closePresetEditor = () => setIsPresetEditorOpen(false);
                const presetVars = [
                    '<SUPPORT NAME>', '<USER NAME>', '<MONEY>',
                    '<MONEY AND ADDITIONAL AMOUNT>', '<MONEY+ADD AMOUNT+VERIF MONEYBACK>',
                    '<BANK>', '<VERIF SUM>', '<YESTERDAY DATE>', '<ACCOUNT NUMBER>',
                    '<TAX_PERCENT>', '<TAX_AMOUNT>', '<TODAY DATE>', '<USD AMOUNT>',
                    '<CRYPTO_CONVERT_AMOUNT>', '<BANK_CONVERT_AMOUNT>',
                ];
                const msgs = editingPreset.messages || [];
                const plural = (n, one, few, many) => {
                    const m10 = n % 10, m100 = n % 100;
                    if (m10 === 1 && m100 !== 11) return one;
                    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
                    return many;
                };
                return (
                <div className={s.cpeOverlay} onClick={closePresetEditor}>
                    <div className={s.cpe} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        {/* Шапка */}
                        <div className={s.cpeHead}>
                            <div className={s.cpeHeadTop}>
                                <span className={s.cpeHeadGlyph}>
                                    <MessagesSquare size={16} strokeWidth={2.1} aria-hidden="true" />
                                </span>
                                <input
                                    className={s.cpeName}
                                    type="text"
                                    value={editingPreset.name || ''}
                                    onChange={(e) => setEditingPreset(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Название пресета"
                                    spellCheck="false"
                                />
                                <label className={s.cpeOnOff} title="Показывать пресет в списке доступных">
                                    <span>{editingPreset.enabled !== false ? 'Включён' : 'Выключен'}</span>
                                    <input
                                        type="checkbox"
                                        className={s.novaSwitch}
                                        checked={editingPreset.enabled !== false}
                                        onChange={(e) => setEditingPreset(prev => ({ ...prev, enabled: e.target.checked }))}
                                    />
                                </label>
                                <button type="button" className={s.cpeClose} onClick={closePresetEditor} aria-label="Закрыть">
                                    <XGlyph size={17} strokeWidth={2.2} aria-hidden="true" />
                                </button>
                            </div>
                            <div className={s.cpeHint}>
                                Сценарий переписки: реплики проигрываются в чате сверху вниз. «Поддержка» — сообщения агента, «Вы» — от лица пользователя
                            </div>
                        </div>

                        {/* Тулбар */}
                        <div className={s.cpeBar}>
                            <button type="button" className={s.cpeAddMsg} onClick={addMessageToPreset}>
                                <MessageSquarePlus size={14} strokeWidth={2.2} aria-hidden="true" />
                                Добавить реплику
                            </button>
                            <div className={s.cpeBarSpacer} />
                            <button
                                type="button"
                                className={`${s.cpeVarsToggle} ${cpeVarsOpen ? s.cpeVarsToggleOn : ''}`}
                                onClick={() => setCpeVarsOpen(v => !v)}
                            >
                                <Braces size={13} strokeWidth={2.2} aria-hidden="true" />
                                Переменные
                                <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" className={s.cpeVarsChevron} />
                            </button>
                        </div>
                        <Collapsible open={cpeVarsOpen}>
                            <div className={s.cpeVars}>
                                <div className={s.cpeVarsHint}>
                                    Поставьте курсор в текст реплики и кликните по переменной — она вставится. В чате переменная заменится реальным значением
                                </div>
                                <div className={s.cpeVarsChips}>
                                    {presetVars.map((variable, idx) => (
                                        <button
                                            type="button"
                                            key={idx}
                                            className={s.cpeVarChip}
                                            onClick={() => insertVariableAtCursor(variable)}
                                            title={getVariableDescription(variable)}
                                        >
                                            {variable}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </Collapsible>

                        {/* Лента реплик */}
                        <div className={s.cpeList} data-nfe-list="msgs">
                            {msgs.length === 0 ? (
                                <div className={s.cpeEmpty}>
                                    <MessageSquareText size={24} strokeWidth={1.6} aria-hidden="true" />
                                    <div className={s.cpeEmptyTitle}>В сценарии пока нет реплик</div>
                                    <div className={s.cpeEmptyHint}>Добавьте первую реплику — с неё начнётся диалог в чате</div>
                                    <button type="button" className={s.cpeAddMsg} onClick={addMessageToPreset}>
                                        <MessageSquarePlus size={14} strokeWidth={2.2} aria-hidden="true" />
                                        Добавить реплику
                                    </button>
                                </div>
                            ) : msgs.map((message, index) => {
                                const isSupport = message.author === 'Support';
                                return (
                                    <div
                                        key={index}
                                        data-nfe-row
                                        className={`${s.cpeMsg} ${isSupport ? s.cpeMsgSupport : s.cpeMsgUser} ${nfeDragKey === `msg-${index}` ? s.nfeDragging : ''}`}
                                    >
                                        <div className={s.cpeMsgHead}>
                                            <span
                                                className={s.qaHandle}
                                                onPointerDown={(e) => startNfeRowDrag(e, `msg-${index}`, reorderPresetMessages)}
                                                title="Перетащите, чтобы поменять порядок"
                                            >
                                                <GripVertical size={13} strokeWidth={2} aria-hidden="true" />
                                            </span>
                                            <span className={s.cpeMsgNum}>{index + 1}</span>
                                            <NovaSegmented
                                                ariaLabel="Автор реплики"
                                                value={isSupport ? 'Support' : 'You'}
                                                onChange={(v) => updatePresetMessage(index, 'author', v)}
                                                options={[
                                                    { value: 'Support', label: 'Поддержка', color: 'var(--accent)' },
                                                    { value: 'You', label: 'Вы', color: 'var(--good)' },
                                                ]}
                                            />
                                            <button
                                                type="button"
                                                className={`${s.cpeIconBtn} ${s.cpeIconBtnDanger}`}
                                                onClick={() => removeMessageFromPreset(index)}
                                                title="Удалить реплику"
                                            >
                                                <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                                            </button>
                                        </div>
                                        <textarea
                                            className={s.cpeTextarea}
                                            value={message.text}
                                            ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
                                            onChange={(e) => { updatePresetMessage(index, 'text', e.target.value); e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }}
                                            placeholder="Текст реплики… Можно вставлять переменные кнопкой «Переменные» сверху"
                                            rows={2}
                                            spellCheck="false"
                                            data-message-index={index}
                                            onFocus={(e) => setActiveTextarea(e.target)}
                                            onClick={(e) => setActiveTextarea(e.target)}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Футер */}
                        <div className={s.cpeFoot}>
                            <span className={s.cpeFootInfo}>
                                {msgs.length} {plural(msgs.length, 'реплика', 'реплики', 'реплик')}
                            </span>
                            <button type="button" className={s.cpeCancel} onClick={closePresetEditor}>
                                Отмена
                            </button>
                            <button type="button" className={s.cpeSave} onClick={savePresetChanges}>
                                <SaveGlyph size={15} strokeWidth={2.1} aria-hidden="true" />
                                Сохранить изменения
                            </button>
                        </div>
                    </div>
                </div>
                );
            })() : isPresetEditorOpen && editingPreset && (
                <div 
                    className={s.modalOverlay} 
                    onMouseDown={handleModalMouseDown}
                    onClick={handleModalClick}
                >
                    <div className={s.presetEditorModal} onClick={(e) => e.stopPropagation()}>
                        <div className={s.modalHeader}>
                            <h3>Редактирование пресета: {editingPreset.name}</h3>
                            <button 
                                className={s.modalCloseBtn}
                                onClick={() => setIsPresetEditorOpen(false)}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className={s.modalContent}>
                            {/* Фиксированная верхняя часть */}
                            <div className={s.modalFixedHeader}>
                                {/* Настройки пресета */}
                                <div className={s.presetSettingsCard}>
                                    <div className={s.presetNameSection}>
                                        <div className={s.presetNameLabel}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                            </svg>
                                            Название пресета
                                        </div>
                                        <input
                                            type="text"
                                            value={editingPreset.name}
                                            onChange={(e) => setEditingPreset(prev => ({ ...prev, name: e.target.value }))}
                                            className={s.presetNameInput}
                                            placeholder="Введите название пресета..."
                                            onFocus={handleInputFocus}
                                            onBlur={handleInputBlur}
                                            onMouseDown={handleInputMouseDown}
                                            onMouseUp={handleInputMouseUp}
                                            onDragStart={(e) => e.preventDefault()}
                                        />
                                    </div>
                                </div>

                                {/* Заголовок сообщений и кнопка добавления */}
                                <div className={s.messagesHeaderCard}>
                                    <div className={s.messagesInfo}>
                                        <div className={s.messagesTitle}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                            </svg>
                                            Сообщения диалога
                                        </div>
                                        <div className={s.messagesCount}>
                                            {(editingPreset.messages || []).length} сообщений
                                        </div>
                                    </div>
                                    <button 
                                        className={s.addMessageBtn}
                                        onClick={addMessageToPreset}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <line x1="12" y1="8" x2="12" y2="16"/>
                                            <line x1="8" y1="12" x2="16" y2="12"/>
                                        </svg>
                                        Добавить сообщение
                                    </button>
                                </div>

                                {/* Компактная панель переменных */}
                                <div className={s.variablesPanel}>
                                    <div className={s.variablesPanelHeader}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="16 18 22 12 16 6"/>
                                            <polyline points="8 6 2 12 8 18"/>
                                        </svg>
                                        <span>Переменные (клик = вставить)</span>
                                    </div>
                                    <div className={s.variablesChips}>
                                        {[
                                            '<SUPPORT NAME>',
                                            '<USER NAME>',
                                            '<MONEY>',
                                            '<MONEY AND ADDITIONAL AMOUNT>',
                                            '<MONEY+ADD AMOUNT+VERIF MONEYBACK>',
                                            '<BANK>',
                                            '<VERIF SUM>',
                                            '<YESTERDAY DATE>',
                                            '<ACCOUNT NUMBER>',
                                            '<TAX_PERCENT>',
                                            '<TAX_AMOUNT>',
                                            '<TODAY DATE>',
                                            '<USD AMOUNT>',
                                            '<CRYPTO_CONVERT_AMOUNT>',
                                            '<BANK_CONVERT_AMOUNT>'
                                        ].map((variable, idx) => (
                                            <button
                                                key={idx}
                                                className={s.variableChip}
                                                onClick={() => insertVariableAtCursor(variable)}
                                                onMouseEnter={(e) => showPortalTooltip(e, getVariableDescription(variable))}
                                                onMouseLeave={hidePortalTooltip}
                                            >
                                                {variable}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {/* Скроллируемый список сообщений */}
                            <div className={s.messagesScrollContainer}>
                                <div className={s.messagesList}>
                                    {(editingPreset.messages || []).map((message, index) => (
                                        <div 
                                            key={index} 
                                            className={`${s.messageEditor} ${draggedMessageIndex === index ? s.dragging : ''} ${dragOverIndex === index ? s.dragOver : ''}`}
                                            draggable
                                            onDragStart={(e) => handleMessageDragStart(e, index)}
                                            onDragOver={(e) => handleMessageDragOver(e, index)}
                                            onDragLeave={handleMessageDragLeave}
                                            onDrop={(e) => handleMessageDrop(e, index)}
                                            onDragEnd={handleMessageDragEnd}
                                        >
                                            <div className={s.messageHeader}>
                                                <div className={s.dragHandle} title="Перетащите для изменения порядка">
                                                    ⋮⋮
                                                </div>
                                                <div className={s.messageNumber}>
                                                    #{index + 1}
                                                </div>
                                                <div className={s.authorSelector}>
                                                    <label>Автор:</label>
                                                    <select
                                                        value={message.author}
                                                        onChange={(e) => updatePresetMessage(index, 'author', e.target.value)}
                                                        className={s.authorSelect}
                                                    >
                                                        <option value="Support">Поддержка</option>
                                                        <option value="You">Вы</option>
                                                    </select>
                                                </div>
                                                <button
                                                    className={s.deleteMessageBtn}
                                                    onClick={() => removeMessageFromPreset(index)}
                                                    title="Удалить сообщение"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
                                                    </svg>
                                                </button>
                                            </div>
                                            <textarea
                                                value={message.text}
                                                onChange={(e) => updatePresetMessage(index, 'text', e.target.value)}
                                                className={s.messageTextarea}
                                                placeholder="Текст сообщения..."
                                                rows={4}
                                                data-message-index={index}
                                                onFocus={(e) => {
                                                    console.log('Поле получило фокус:', e.target);
                                                    setActiveTextarea(e.target);
                                                    handleInputFocus(e);
                                                }}
                                                onBlur={(e) => {
                                                    handleInputBlur(e);
                                                }}
                                                onClick={(e) => {
                                                    console.log('Клик по полю:', e.target);
                                                    setActiveTextarea(e.target);
                                                }}
                                                onMouseDown={handleInputMouseDown}
                                                onMouseUp={handleInputMouseUp}
                                                onDragStart={(e) => e.preventDefault()}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className={s.modalFooter}>
                            <button 
                                className={s.cancelBtn}
                                onClick={() => setIsPresetEditorOpen(false)}
                            >
                                Отмена
                            </button>
                            <button 
                                className={s.saveBtn}
                                onClick={savePresetChanges}
                            >
                                Сохранить изменения
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ Модальное окно добавления языка */}
            <AddLanguageModal
                isOpen={isAddLanguageModalOpen}
                onClose={() => setIsAddLanguageModalOpen(false)}
                onAddLanguage={handleAddLanguage}
                existingLanguages={Object.keys(localSettings.translations || {})}
                nova={isNova}
            />

            {/* Portal тултип - рендерится вне модального окна */}
            {portalTooltip && createPortal(
                <div
                    className={s.portalTooltip}
                    style={{
                        position: 'fixed',
                        left: `${portalTooltip.x}px`,
                        top: `${portalTooltip.y}px`,
                        transform: 'translateX(-50%) translateY(-100%)',
                        zIndex: 999999,
                        pointerEvents: 'none'
                    }}
                >
                    <div className={s.portalTooltipContent}>
                        {portalTooltip.content}
                    </div>
                    <div className={s.portalTooltipArrow}></div>
                </div>,
                document.body
            )}
            
            {/* Portal тултип для переменных MiniFigma */}
            {figmaPortalTooltip && createPortal(
                <div 
                    className={s.portalTooltip}
                    style={{
                        position: 'fixed',
                        left: figmaPortalTooltip.x,
                        top: figmaPortalTooltip.y,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 999999,
                        pointerEvents: 'none'
                    }}
                >
                    <div className={s.portalTooltipContent}>
                        {figmaPortalTooltip.description}
                    </div>
                    <div className={s.portalTooltipArrow}></div>
                </div>,
                document.body
            )}
            
            {/* Модальное окно редактора раздела MiniFigma */}
            {(() => {
                console.log('🔍 Modal render check - isSectionEditorOpen:', isSectionEditorOpen, 'editingSection:', editingSection);
                return null;
            })()}
            {isSectionEditorOpen && editingSection && (
                <div className={s.modalOverlay} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {/* ── Nova: полностью своя разметка редактора (nfe*), не перекраска */}
                    {isNova ? (() => {
                        const closeEditor = () => {
                            setIsSectionEditorOpen(false);
                            setCollapsedMessages({});
                            setCollapsingMessages({});
                            setCollapsedGroups({});
                        };
                        // Nova: прямой флип без таймаутов старой анимации —
                        // плавность даёт Collapsible
                        const novaToggleMsg = (id) =>
                            setCollapsedMessages(prev => ({ ...prev, [id]: !prev[id] }));
                        // Вложенные в группу: по умолчанию свёрнуты (undefined = collapsed)
                        const novaToggleNested = (id) =>
                            setCollapsedMessages(prev => ({ ...prev, [id]: prev[id] === false }));
                        const figmaVars = [
                            '<USER_WIN>', '<DEPOSIT_AMOUNT>', '<COMMISSION_SIZE>',
                            '<VERIFICATION_AMOUNT>', '<ADDITIONAL_AMOUNT>', '<TAX_PERCENT>',
                            '<TAX_AMOUNT>', '<CRYPTO_COMMISSION>', '<BANK_COMMISSION>',
                            '<COMMISSION_PERCENT>', '<BROKER_FEE>', '<WINNING_PERCENT>',
                            '<WINNING_AMOUNT>', '<DEFAULT_CRYPTO>',
                            ...(localSettings.modalTemplates || [])
                                .filter(t => t.financeFieldName && t.actionKey && t.enabled !== false)
                                .map(t => `<${t.actionKey.toUpperCase()}_AMOUNT>`)
                        ];
                        const allItems = editingSection.messages || [];
                        const totalCards = allItems.filter(i => i.type !== 'group').length
                            + allItems.filter(i => i.type === 'group').reduce((n, g) => n + (g.messages || []).length, 0);
                        const totalGroups = allItems.filter(i => i.type === 'group').length;
                        const plural = (n, one, few, many) => {
                            const m10 = n % 10, m100 = n % 100;
                            if (m10 === 1 && m100 !== 11) return one;
                            if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
                            return many;
                        };

                        // Поля карточки — общие для отдельных и вложенных в группу
                        const renderCardFields = (msg, idxData, onField) => (
                            <div className={s.nfeCardBody}>
                                <div className={s.nfeField}>
                                    <label>
                                        Заголовок
                                        <span className={s.nfeFieldHint}>крупный текст на карточке в шторке</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={msg.title || ''}
                                        placeholder="Например: 👋 Приветствие"
                                        spellCheck="false"
                                        data-message-index={idxData}
                                        data-field="title"
                                        onFocus={(e) => setActiveFigmaTextarea(e.target)}
                                        onChange={(e) => onField('title', e.target.value)}
                                    />
                                </div>
                                <div className={s.nfeField}>
                                    <label>
                                        Подзаголовок
                                        <span className={s.nfeFieldHint}>маленькая подпись под заголовком, можно оставить пустым</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={msg.subtitle || ''}
                                        placeholder="Короткое пояснение"
                                        spellCheck="false"
                                        data-message-index={idxData}
                                        data-field="subtitle"
                                        onFocus={(e) => setActiveFigmaTextarea(e.target)}
                                        onChange={(e) => onField('subtitle', e.target.value)}
                                    />
                                </div>
                                <div className={s.nfeField}>
                                    <label>
                                        Текст для копирования
                                        <span className={s.nfeFieldHint}>именно он попадёт в буфер обмена по клику на карточку</span>
                                    </label>
                                    <textarea
                                        value={msg.text || ''}
                                        placeholder="Текст сообщения… Можно вставлять переменные — кнопка «Переменные» сверху"
                                        rows={3}
                                        spellCheck="false"
                                        data-message-index={idxData}
                                        data-field="text"
                                        onFocus={(e) => setActiveFigmaTextarea(e.target)}
                                        onChange={(e) => onField('text', e.target.value)}
                                    />
                                </div>
                            </div>
                        );

                        return (
                        <div className={s.nfe} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                            {/* Шапка: имя раздела редактируется прямо здесь */}
                            <div className={s.nfeHead}>
                                <div className={s.nfeHeadTop}>
                                    <span className={s.nfeHeadGlyph}>
                                        <LayoutTemplate size={16} strokeWidth={2.1} aria-hidden="true" />
                                    </span>
                                    <input
                                        className={s.nfeName}
                                        type="text"
                                        value={editingSection.name || ''}
                                        onChange={(e) => setEditingSection(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Название раздела"
                                        spellCheck="false"
                                    />
                                    <label className={s.nfeOnOff} title="Показывать раздел в шторке">
                                        <span>{editingSection.enabled !== false ? 'Виден' : 'Скрыт'}</span>
                                        <input
                                            type="checkbox"
                                            className={s.novaSwitch}
                                            checked={editingSection.enabled !== false}
                                            onChange={(e) => setEditingSection(prev => ({ ...prev, enabled: e.target.checked }))}
                                        />
                                    </label>
                                    <button type="button" className={s.nfeClose} onClick={closeEditor} aria-label="Закрыть">
                                        <XGlyph size={17} strokeWidth={2.2} aria-hidden="true" />
                                    </button>
                                </div>
                                <div className={s.nfeHint}>
                                    Раздел — вкладка в шторке Мини-фигмы. Карточки внутри копируют свой текст по клику
                                </div>
                            </div>

                            {/* Тулбар: действия + переменные */}
                            <div className={s.nfeBar}>
                                <button type="button" className={s.nfeAddCard} onClick={addMessageToSection}>
                                    <MessageSquarePlus size={14} strokeWidth={2.2} aria-hidden="true" />
                                    Добавить карточку
                                </button>
                                <button type="button" className={s.nfeAddGroup} onClick={addGroupToSection}>
                                    <FolderPlus size={14} strokeWidth={2.2} aria-hidden="true" />
                                    Создать группу
                                </button>
                                <div className={s.nfeBarSpacer} />
                                <button
                                    type="button"
                                    className={`${s.nfeVarsToggle} ${nfeVarsOpen ? s.nfeVarsToggleOn : ''}`}
                                    onClick={() => setNfeVarsOpen(v => !v)}
                                >
                                    <Braces size={13} strokeWidth={2.2} aria-hidden="true" />
                                    Переменные
                                    <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" className={s.nfeVarsChevron} />
                                </button>
                            </div>
                            <Collapsible open={nfeVarsOpen}>
                                <div className={s.nfeVars}>
                                    <div className={s.nfeVarsHint}>
                                        Поставьте курсор в поле карточки и кликните по переменной — она вставится в текст.
                                        При показе в шторке переменная заменится реальным значением
                                    </div>
                                    <div className={s.nfeVarsChips}>
                                        {figmaVars.map((variable, idx) => (
                                            <button
                                                type="button"
                                                key={idx}
                                                className={s.nfeVarChip}
                                                onClick={() => insertFigmaVariableAtCursor(variable)}
                                                title={getMiniFigmaVariableDescription(variable)}
                                            >
                                                {variable}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </Collapsible>

                            {/* Список карточек и групп */}
                            <div className={s.nfeList} ref={nfeListRef} data-nfe-list="top">
                                {allItems.length === 0 ? (
                                    <div className={s.nfeEmpty}>
                                        <LayoutTemplate size={26} strokeWidth={1.6} aria-hidden="true" />
                                        <div className={s.nfeEmptyTitle}>Раздел пока пуст</div>
                                        <div className={s.nfeEmptyHint}>
                                            Добавьте карточку — она появится в шторке и будет копировать свой текст по клику.
                                            Несколько карточек можно объединить в цветную группу
                                        </div>
                                        <button type="button" className={s.nfeAddCard} onClick={addMessageToSection}>
                                            <MessageSquarePlus size={14} strokeWidth={2.2} aria-hidden="true" />
                                            Добавить первую карточку
                                        </button>
                                    </div>
                                ) : allItems.map((item, index) => {
                                    // ── Группа
                                    if (item.type === 'group') {
                                        const grpCollapsed = collapsedGroups[item.id];
                                        return (
                                            <div
                                                key={item.id}
                                                data-nfe-row
                                                className={`${s.nfeGroup} ${nfeDragKey === `top-${index}` ? s.nfeDragging : ''}`}
                                                style={{ '--grp': item.color || '#6366f1' }}
                                            >
                                                <div className={s.nfeGroupHead}>
                                                    <span
                                                        className={s.nfeGrip}
                                                        onPointerDown={(e) => startNfeRowDrag(e, `top-${index}`, reorderTopMessages)}
                                                        title="Перетащите, чтобы поменять порядок"
                                                    >
                                                        <GripVertical size={13} strokeWidth={2} aria-hidden="true" />
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={`${s.nfeChevron} ${grpCollapsed ? s.nfeChevronClosed : ''}`}
                                                        onClick={() => toggleGroupCollapse(item.id)}
                                                        title={grpCollapsed ? 'Развернуть' : 'Свернуть'}
                                                    >
                                                        <ChevronDown size={14} strokeWidth={2.4} aria-hidden="true" />
                                                    </button>
                                                    <label className={s.nfeGrpColor} title="Цвет группы — нажмите, чтобы поменять">
                                                        <Folder size={15} strokeWidth={2} aria-hidden="true" fill="currentColor" fillOpacity={0.3} />
                                                        <input
                                                            type="color"
                                                            value={item.color || '#6366f1'}
                                                            onChange={(e) => updateSectionMessage(index, 'color', e.target.value)}
                                                        />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className={s.nfeGrpName}
                                                        value={item.title || ''}
                                                        onChange={(e) => updateSectionMessage(index, 'title', e.target.value)}
                                                        placeholder="Название группы"
                                                        spellCheck="false"
                                                    />
                                                    <span className={s.nfeCountChip}>{(item.messages || []).length}</span>
                                                    <label
                                                        className={s.nfeGrpPin}
                                                        title="Всегда раскрыта: группа в шторке не сворачивается"
                                                    >
                                                        <Pin size={12} strokeWidth={2.2} aria-hidden="true" />
                                                        <input
                                                            type="checkbox"
                                                            className={s.novaSwitch}
                                                            checked={item.alwaysOpen || false}
                                                            onChange={() => toggleGroupAlwaysOpen(index)}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className={s.nfeIconBtn}
                                                        onClick={() => editExistingGroup(index)}
                                                        title="Состав группы: выбрать, какие карточки входят"
                                                    >
                                                        <PencilLine size={13} strokeWidth={2.1} aria-hidden="true" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${s.nfeIconBtn} ${s.nfeIconBtnDanger}`}
                                                        onClick={() => removeMessageFromSection(index)}
                                                        title="Удалить группу"
                                                    >
                                                        <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                                                    </button>
                                                </div>
                                                <Collapsible open={!grpCollapsed}>
                                                    <div className={s.nfeGroupBody} data-nfe-list={`g${index}`}>
                                                        {(item.messages || []).map((gm, mi) => {
                                                            const mid = `${item.id}_${gm.id}`;
                                                            const mCollapsed = collapsedMessages[mid] !== false;
                                                            return (
                                                                <div
                                                                    key={gm.id}
                                                                    data-nfe-row
                                                                    className={`${s.nfeCard} ${nfeDragKey === `g${index}-${mi}` ? s.nfeDragging : ''}`}
                                                                >
                                                                    <div className={s.nfeCardHead} onClick={() => novaToggleNested(mid)}>
                                                                        <span
                                                                            className={s.nfeGrip}
                                                                            onPointerDown={(e) => startNfeRowDrag(e, `g${index}-${mi}`, reorderGroupMessages(index))}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title="Перетащите, чтобы поменять порядок"
                                                                        >
                                                                            <GripVertical size={13} strokeWidth={2} aria-hidden="true" />
                                                                        </span>
                                                                        <span className={`${s.nfeChevron} ${mCollapsed ? s.nfeChevronClosed : ''}`}>
                                                                            <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" />
                                                                        </span>
                                                                        <div className={s.nfeCardPreview}>
                                                                            <span className={s.nfeCardTitle}>{gm.title || 'Новая карточка'}</span>
                                                                            {gm.subtitle && <span className={s.nfeCardSub}>{gm.subtitle}</span>}
                                                                        </div>
                                                                        <label className={s.nfeWide} title="Карточка занимает весь ряд в шторке" onClick={(e) => e.stopPropagation()}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={gm.fullWidth || false}
                                                                                onChange={(e) => updateGroupMessage(index, mi, 'fullWidth', e.target.checked)}
                                                                            />
                                                                            <MoveHorizontal size={12} strokeWidth={2.3} aria-hidden="true" />
                                                                        </label>
                                                                        <button
                                                                            type="button"
                                                                            className={`${s.nfeIconBtn} ${s.nfeIconBtnDanger}`}
                                                                            onClick={(e) => { e.stopPropagation(); removeMessageFromGroup(index, mi); }}
                                                                            title="Удалить карточку"
                                                                        >
                                                                            <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                                                                        </button>
                                                                    </div>
                                                                    <Collapsible open={!mCollapsed}>
                                                                        {renderCardFields(gm, `${index}_${mi}`, (f, v) => updateGroupMessage(index, mi, f, v))}
                                                                    </Collapsible>
                                                                </div>
                                                            );
                                                        })}
                                                        <button type="button" className={s.nfeGroupAdd} onClick={() => addMessageToGroup(index)}>
                                                            <Plus size={13} strokeWidth={2.4} aria-hidden="true" />
                                                            Добавить карточку в группу
                                                        </button>
                                                    </div>
                                                </Collapsible>
                                            </div>
                                        );
                                    }
                                    // ── Отдельная карточка
                                    const cardCollapsed = collapsedMessages[item.id];
                                    return (
                                        <div
                                            key={item.id}
                                            data-message-id={item.id}
                                            data-nfe-row
                                            className={`${s.nfeCard} ${nfeDragKey === `top-${index}` ? s.nfeDragging : ''}`}
                                        >
                                            <div className={s.nfeCardHead} onClick={() => novaToggleMsg(item.id)}>
                                                <span
                                                    className={s.nfeGrip}
                                                    onPointerDown={(e) => startNfeRowDrag(e, `top-${index}`, reorderTopMessages)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Перетащите, чтобы поменять порядок"
                                                >
                                                    <GripVertical size={13} strokeWidth={2} aria-hidden="true" />
                                                </span>
                                                <span className={`${s.nfeChevron} ${cardCollapsed ? s.nfeChevronClosed : ''}`}>
                                                    <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" />
                                                </span>
                                                <div className={s.nfeCardPreview}>
                                                    <span className={s.nfeCardTitle}>{item.title || 'Новая карточка'}</span>
                                                    {item.subtitle && <span className={s.nfeCardSub}>{item.subtitle}</span>}
                                                </div>
                                                <label className={s.nfeWide} title="Карточка занимает весь ряд в шторке" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={item.fullWidth || false}
                                                        onChange={(e) => updateSectionMessage(index, 'fullWidth', e.target.checked)}
                                                    />
                                                    <MoveHorizontal size={12} strokeWidth={2.3} aria-hidden="true" />
                                                </label>
                                                <button
                                                    type="button"
                                                    className={`${s.nfeIconBtn} ${s.nfeIconBtnDanger}`}
                                                    onClick={(e) => { e.stopPropagation(); removeMessageFromSection(index); }}
                                                    title="Удалить карточку"
                                                >
                                                    <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                                                </button>
                                            </div>
                                            <Collapsible open={!cardCollapsed}>
                                                {renderCardFields(item, `${index}`, (f, v) => updateSectionMessage(index, f, v))}
                                            </Collapsible>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Футер */}
                            <div className={s.nfeFoot}>
                                <span className={s.nfeFootInfo}>
                                    {totalCards} {plural(totalCards, 'карточка', 'карточки', 'карточек')} · {totalGroups} {plural(totalGroups, 'группа', 'группы', 'групп')}
                                </span>
                                <button type="button" className={s.nfeCancel} onClick={closeEditor}>
                                    Отмена
                                </button>
                                <button type="button" className={s.nfeSave} onClick={saveSectionChanges}>
                                    <SaveGlyph size={15} strokeWidth={2.1} aria-hidden="true" />
                                    Сохранить изменения
                                </button>
                            </div>
                        </div>
                        );
                    })() : (
                    <div className={`${s.presetEditorModal} ${s.miniFigmaModal}`} style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        zIndex: 10001
                    }}>
                        <div className={s.modalHeader}>
                            <h3 className={s.modalTitle}>
                                {isNova && (
                                    <span className={s.figModalGlyph}>
                                        <LayoutTemplate size={15} strokeWidth={2.2} aria-hidden="true" />
                                    </span>
                                )}
                                Редактирование раздела: {editingSection.name}
                            </h3>
                                            <button 
                                                className={s.modalCloseBtn}
                                                onClick={() => {
                                                    setIsSectionEditorOpen(false);
                                                    setCollapsedMessages({});
                                                    setCollapsingMessages({});
                                                    setCollapsedGroups({});
                                                }}
                                            >
                                {isNova ? <XGlyph size={17} strokeWidth={2.2} aria-hidden="true" /> : '×'}
                            </button>
                        </div>
                        
                        <div className={s.modalBody}>
                            <div className={s.fieldGroup}>
                                <div className={s.sectionHeaderRow}>
                                    <div className={s.sectionNameField}>
                                        <label className={s.fieldLabel}>Название раздела:</label>
                                        <input
                                            type="text"
                                            value={editingSection.name || ''}
                                            onChange={(e) => setEditingSection(prev => ({ ...prev, name: e.target.value }))}
                                            className={s.fieldInput}
                                            placeholder="Введите название раздела"
                                        />
                                    </div>
                                    <div className={s.sectionToggleField}>
                                        <div className={s.toggleContainer}>
                                            <span className={s.toggleLabel}>Включено</span>
                                            <label className={s.toggleSwitch}>
                                                <input
                                                    type="checkbox"
                                                    checked={editingSection.enabled !== false}
                                                    onChange={(e) => setEditingSection(prev => ({ ...prev, enabled: e.target.checked }))}
                                                />
                                                <span className={s.slider}></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Панель переменных для MiniFigma */}
                            <div className={s.variablesPanel}>
                                <div className={s.variablesPanelHeader}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="16 18 22 12 16 6"/>
                                        <polyline points="8 6 2 12 8 18"/>
                                    </svg>
                                    <span>Финансовые переменные (клик = вставить)</span>
                                </div>
                                <div className={s.variablesChips}>
                                    {[
                                        '<USER_WIN>',
                                        '<DEPOSIT_AMOUNT>',
                                        '<COMMISSION_SIZE>',
                                        '<VERIFICATION_AMOUNT>',
                                        '<ADDITIONAL_AMOUNT>',
                                        '<TAX_PERCENT>',
                                        '<TAX_AMOUNT>',
                                        '<CRYPTO_COMMISSION>',
                                        '<BANK_COMMISSION>',
                                        '<COMMISSION_PERCENT>',
                                        '<BROKER_FEE>',
                                        '<WINNING_PERCENT>',
                                        '<WINNING_AMOUNT>',
                                        '<DEFAULT_CRYPTO>',
                                        // Добавляем переменные для кастомных модалов
                                        ...(localSettings.modalTemplates || [])
                                            .filter(template => template.financeFieldName && template.actionKey && template.enabled !== false)
                                            .map(template => {
                                                const actionKeyUpper = template.actionKey.toUpperCase();
                                                return `<${actionKeyUpper}_AMOUNT>`;
                                            })
                                    ].map((variable, idx) => (
                                        <button
                                            key={idx}
                                            className={s.variableChip}
                                            onClick={() => insertFigmaVariableAtCursor(variable)}
                                            onMouseEnter={(e) => showFigmaPortalTooltip(e, getMiniFigmaVariableDescription(variable))}
                                            onMouseLeave={hideFigmaPortalTooltip}
                                        >
                                            {variable}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className={s.sectionHeader}>
                                <h4>Сообщения раздела</h4>
                                <div className={s.addButtonsGroup}>
                                    <button 
                                        className={s.addMessageBtn}
                                        onClick={addMessageToSection}
                                    >
                                        {isNova ? <MessageSquarePlus size={14} strokeWidth={2.2} aria-hidden="true" /> : '+'} Добавить сообщение
                                    </button>
                                    <button 
                                        className={s.addGroupBtn}
                                        onClick={addGroupToSection}
                                    >
                                        {isNova ? <FolderPlus size={14} strokeWidth={2.2} aria-hidden="true" /> : '+'} Добавить группу
                                    </button>
                                </div>
                            </div>
                            
                            <div className={s.modalScrollableContent}>
                                <div className={s.sectionContentEditor}>
                                    <div className={s.messagesSection}>
                                        
                                        {(editingSection.messages || []).length === 0 ? (
                                            <div className={s.emptyState}>
                                                <p>В разделе пока нет сообщений</p>
                                                <p>Нажмите "Добавить сообщение" чтобы создать первое</p>
                                            </div>
                                        ) : (
                                            <div className={s.messagesList}>
                                                {(editingSection.messages || []).map((item, index) => {
                                                    const isCollapsed = collapsedMessages[item.id];
                                                    const isCollapsing = collapsingMessages[item.id];
                                                    const isDragging = draggedMessageIndex === index;
                                                    const isDragOver = messageDragOverIndex === index;
                                                    
                                                    // Рендерим группу
                                                    if (item.type === 'group') {
                                                        return (
                                                            <div 
                                                                key={item.id} 
                                                                className={`${s.groupCard} ${isDragging ? s.dragging : ''} ${isDragOver ? s.dragOver : ''}`}
                                                                style={isNova ? {
                                                                    // Цвет группы уходит в CSS-переменную — тона считает
                                                                    // color-mix, яркие сохранённые цвета не режут глаз
                                                                    '--grp': item.color || '#6366f1'
                                                                } : {
                                                                    borderColor: item.color || '#1e40af',
                                                                    backgroundColor: `${item.color || '#1e40af'}08`
                                                                }}
                                                                draggable={false}
                                                                onDragOver={(e) => handleMessageDragOver(e, index)}
                                                                onDragLeave={handleMessageDragLeave}
                                                                onDrop={(e) => handleMessageDrop(e, index)}
                                                            >
                                                                <div 
                                                                    className={s.groupHeader}
                                                                    style={isNova ? undefined : {
                                                                        borderBottomColor: item.color || '#1e40af'
                                                                    }}
                                                                >
                                                                    <span 
                                                                        className={s.dragHandle}
                                                                        draggable={true}
                                                                        onDragStart={(e) => {
                                                                            e.stopPropagation();
                                                                            handleMessageDragStart(e, index);
                                                                        }}
                                                                        onDragEnd={handleMessageDragEnd}
                                                                        title="Перетащите для изменения порядка"
                                                                    >
                                                                        {isNova ? <GripVertical size={14} strokeWidth={2} aria-hidden="true" /> : '⋮⋮'}
                                                                    </span>
                                                                    <button
                                                                        className={s.collapseBtn}
                                                                        onClick={() => toggleGroupCollapse(item.id)}
                                                                        title={collapsedGroups[item.id] ? "Развернуть группу" : "Свернуть группу"}
                                                                    >
                                                                        {isNova
                                                                            ? <ChevronDown size={14} strokeWidth={2.4} aria-hidden="true" style={{ transform: collapsedGroups[item.id] ? 'rotate(-90deg)' : 'none', transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)' }} />
                                                                            : (collapsedGroups[item.id] ? '▶' : '▼')}
                                                                    </button>
                                                                    <span className={s.groupIcon}>
                                                                        {isNova ? (
                                                                            <label
                                                                                className={s.grpColorPick}
                                                                                title="Цвет группы — нажмите, чтобы поменять"
                                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                            >
                                                                                <Folder
                                                                                    size={15}
                                                                                    strokeWidth={2}
                                                                                    aria-hidden="true"
                                                                                    style={{ color: item.color || 'var(--accent)' }}
                                                                                    fill={item.color || 'transparent'}
                                                                                    fillOpacity={0.35}
                                                                                />
                                                                                <input
                                                                                    type="color"
                                                                                    value={item.color || '#6366f1'}
                                                                                    onChange={(e) => updateSectionMessage(index, 'color', e.target.value)}
                                                                                />
                                                                            </label>
                                                                        ) : '📁'}
                                                                    </span>
                                                                    <div className={s.groupHeaderContent}>
                                                                        <input
                                                                            type="text"
                                                                            className={s.groupTitleInputFlexible}
                                                                            value={item.title || ''}
                                                                            onChange={(e) => updateSectionMessage(index, 'title', e.target.value)}
                                                                            placeholder="Название группы"
                                                                            onFocus={(e) => e.target.draggable = false}
                                                                            onBlur={(e) => e.target.draggable = true}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onDragStart={(e) => e.preventDefault()}
                                                                            onSelectStart={(e) => e.stopPropagation()}
                                                                        />
                                                                        
                                                                        <div className={s.groupControlsContainer}>
                                                                            {/* «Всегда раскрыта»: в Nova — свитч с пином */}
                                                                            {isNova ? (
                                                                                <label
                                                                                    className={s.grpPinToggle}
                                                                                    title="Всегда раскрыта — группа в шторке не сворачивается"
                                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <Pin size={12} strokeWidth={2.2} aria-hidden="true" />
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className={s.novaSwitch}
                                                                                        checked={item.alwaysOpen || false}
                                                                                        onChange={(e) => {
                                                                                            e.stopPropagation();
                                                                                            toggleGroupAlwaysOpen(index);
                                                                                        }}
                                                                                    />
                                                                                </label>
                                                                            ) : (
                                                                            <label className={s.checkboxLabelInline}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={item.alwaysOpen || false}
                                                                                    onChange={(e) => {
                                                                                        e.stopPropagation();
                                                                                        toggleGroupAlwaysOpen(index);
                                                                                    }}
                                                                                    className={s.checkboxSmall}
                                                                                />
                                                                                <span className={s.checkboxTextSmall}>Всегда раскрыта</span>
                                                                            </label>
                                                                            )}
                                                                            
                                                                            <div className={s.groupButtonsContainer}>
                                                                                <button
                                                                                    className={s.addMessageToGroupBtn}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        addMessageToGroup(index);
                                                                                    }}
                                                                                    title="Добавить сообщение в группу"
                                                                                >
                                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: '13px', height: '13px', minWidth: '13px', minHeight: '13px'}}>
                                                                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                                                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                                                                    </svg>
                                                                                </button>
                                                                                <button
                                                                                    className={s.editGroupBtn}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        editExistingGroup(index);
                                                                                    }}
                                                                                    title="Редактировать группу"
                                                                                >
                                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '13px', height: '13px', minWidth: '13px', minHeight: '13px'}}>
                                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                                                    </svg>
                                                                                </button>
                                                                                <button
                                                                                    className={s.deleteMessageBtn}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        removeMessageFromSection(index);
                                                                                    }}
                                                                                    title="Удалить группу"
                                                                                >
                                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '13px', height: '13px', minWidth: '13px', minHeight: '13px'}}>
                                                                                        <polyline points="3,6 5,6 21,6"></polyline>
                                                                                        <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                                                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                                                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                                                                    </svg>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Сообщения внутри группы */}
                                                                <Collapsible open={!collapsedGroups[item.id]}>
                                                                <div className={s.groupMessages}>
                                                                    {(item.messages || []).map((groupMessage, messageIndex) => {
                                                                        const messageId = `${item.id}_${groupMessage.id}`;
                                                                        const isCollapsed = collapsedMessages[messageId] !== false; // По умолчанию свернуто
                                                                        const isCollapsing = collapsingMessages[messageId];
                                                                        const isGroupMessageDragging = draggedGroupMessageIndex === messageIndex && draggedGroupIndex === index;
                                                                        const isGroupMessageDragOver = groupMessageDragOverIndex === messageIndex && draggedGroupIndex === index;
                                                                        
                                                                        return (
                                                                            <div 
                                                                                key={groupMessage.id} 
                                                                                className={`${s.groupMessageCard} ${isGroupMessageDragging ? s.dragging : ''} ${isGroupMessageDragOver ? s.dragOver : ''}`}
                                                                                data-message-id={messageId}
                                                                                draggable={true}
                                                                                onDragStart={(e) => handleGroupMessageDragStart(e, index, messageIndex)}
                                                                                onDragOver={(e) => handleGroupMessageDragOver(e, index, messageIndex)}
                                                                                onDragLeave={handleGroupMessageDragLeave}
                                                                                onDrop={(e) => handleGroupMessageDrop(e, index, messageIndex)}
                                                                                onDragEnd={handleGroupMessageDragEnd}
                                                                            >
                                                                                <div 
                                                                                    className={s.groupMessageHeader}
                                                                                    onClick={(e) => {
                                                                                        // Не сворачиваем если клик по drag handle
                                                                                        const clickedElement = e.target;
                                                                                        const dragHandle = clickedElement.closest('[title="Перетащите для изменения порядка"]');
                                                                                        if (!dragHandle) {
                                                                                            toggleMessageCollapse(messageId);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <span 
                                                                                        className={s.dragHandle}
                                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        title="Перетащите для изменения порядка"
                                                                                    >
                                                                                        {isNova ? <GripVertical size={13} strokeWidth={2} aria-hidden="true" /> : '⋮⋮'}
                                                                                    </span>
                                                                                    <span className={s.messageNumber}>#{messageIndex + 1}</span>
                                                                                    <span className={`${s.toggleIcon} ${!isCollapsed ? s.expanded : ''}`}>
                                                                                        {isNova ? <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" /> : '▶'}
                                                                                    </span>
                                                                                    <div className={s.messagePreview}>
                                                                                        <div className={s.messageTitle}>
                                                                                            {groupMessage.title || 'Без названия'}
                                                                                        </div>
                                                                                        {groupMessage.subtitle && (
                                                                                            <div className={s.messageSubtitle}>
                                                                                                {groupMessage.subtitle}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className={s.messageActions}>
                                                                                        <label 
                                                                                            className={s.fullWidthCheckbox}
                                                                                            title="На всю ширину"
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={groupMessage.fullWidth || false}
                                                                                                onChange={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    updateGroupMessage(index, messageIndex, 'fullWidth', e.target.checked);
                                                                                                }}
                                                                                            />
                                                                                            <span className={s.checkmark}>{isNova ? <MoveHorizontal size={12} strokeWidth={2.3} aria-hidden="true" /> : '✓'}</span>
                                                                                        </label>
                                                                                        <button
                                                                                            className={s.deleteMessageBtn}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                removeMessageFromGroup(index, messageIndex);
                                                                                            }}
                                                                                            title="Удалить сообщение"
                                                                                        >
                                                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: '11px', height: '11px', minWidth: '11px', minHeight: '11px'}}>
                                                                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                                                                            </svg>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                <Collapsible open={!isCollapsed}>
                                                                                    <div className={`${s.groupMessageFields} ${isCollapsing ? s.collapsing : ''}`}>
                                                                                <div className={s.fieldGroup}>
                                                                                    <label>Заголовок:</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={groupMessage.title || ''}
                                                                                        onChange={(e) => updateGroupMessage(index, messageIndex, 'title', e.target.value)}
                                                                                        placeholder="Название сообщения"
                                                                                        className={s.messageInput}
                                                                                        data-message-index={`${index}_${messageIndex}`}
                                                                                        data-field="title"
                                                                                        onFocus={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="true"]');
                                                                                            if (messageCard) messageCard.draggable = false;
                                                                                            setActiveFigmaTextarea(e.target);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="false"]');
                                                                                            if (messageCard) messageCard.draggable = true;
                                                                                        }}
                                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                                        onMouseUp={(e) => e.stopPropagation()}
                                                                                        onDragStart={(e) => e.preventDefault()}
                                                                                        onSelectStart={(e) => e.stopPropagation()}
                                                                                    />
                                                                                </div>
                                                                                
                                                                                <div className={s.fieldGroup}>
                                                                                    <label>Подзаголовок:</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={groupMessage.subtitle || ''}
                                                                                        onChange={(e) => updateGroupMessage(index, messageIndex, 'subtitle', e.target.value)}
                                                                                        placeholder="Краткое описание"
                                                                                        className={s.messageInput}
                                                                                        data-message-index={`${index}_${messageIndex}`}
                                                                                        data-field="subtitle"
                                                                                        onFocus={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="true"]');
                                                                                            if (messageCard) messageCard.draggable = false;
                                                                                            setActiveFigmaTextarea(e.target);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="false"]');
                                                                                            if (messageCard) messageCard.draggable = true;
                                                                                        }}
                                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                                        onMouseUp={(e) => e.stopPropagation()}
                                                                                        onDragStart={(e) => e.preventDefault()}
                                                                                        onSelectStart={(e) => e.stopPropagation()}
                                                                                    />
                                                                                </div>
                                                                                
                                                                                <div className={s.fieldGroup}>
                                                                                    <label>Текст для копирования:</label>
                                                                                    <textarea
                                                                                        value={groupMessage.text || ''}
                                                                                        onChange={(e) => updateGroupMessage(index, messageIndex, 'text', e.target.value)}
                                                                                        placeholder="Текст который будет скопирован при нажатии"
                                                                                        className={s.messageTextarea}
                                                                                        rows="3"
                                                                                        data-message-index={`${index}_${messageIndex}`}
                                                                                        data-field="text"
                                                                                        onFocus={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="true"]');
                                                                                            if (messageCard) messageCard.draggable = false;
                                                                                            setActiveFigmaTextarea(e.target);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const messageCard = e.target.closest('[draggable="false"]');
                                                                                            if (messageCard) messageCard.draggable = true;
                                                                                        }}
                                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                                        onMouseUp={(e) => e.stopPropagation()}
                                                                                        onDragStart={(e) => e.preventDefault()}
                                                                                        onSelectStart={(e) => e.stopPropagation()}
                                                                                    />
                                                                                </div>
                                                                                    </div>
                                                                                </Collapsible>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    
                                                                    {(item.messages || []).length === 0 && (
                                                                        <div className={s.emptyGroupState}>
                                                                            <p>В группе пока нет сообщений</p>
                                                                            <p>Нажмите "+" чтобы добавить первое</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                </Collapsible>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    // Рендерим обычное сообщение
                                                    return (
                                                        <div 
                                                            key={item.id} 
                                                            data-message-id={item.id}
                                                            className={`${s.messageCard} ${isDragging ? s.dragging : ''} ${isDragOver ? s.dragOver : ''}`}
                                                            draggable={true}
                                                            onDragStart={(e) => handleMessageDragStart(e, index)}
                                                            onDragOver={(e) => handleMessageDragOver(e, index)}
                                                            onDragLeave={handleMessageDragLeave}
                                                            onDrop={(e) => handleMessageDrop(e, index)}
                                                            onDragEnd={handleMessageDragEnd}
                                                        >
                                                            <div 
                                                                className={s.messageCardHeader}
                                                                onClick={() => toggleMessageCollapse(item.id)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className={s.messageHeaderContent}>
                                                                    <span 
                                                                        className={s.dragHandle}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        title="Перетащите для изменения порядка"
                                                                    >
                                                                        {isNova ? <GripVertical size={13} strokeWidth={2} aria-hidden="true" /> : '⋮⋮'}
                                                                    </span>
                                                                    <span className={s.messageNumber}>#{index + 1}</span>
                                                                    <span className={`${s.toggleIcon} ${!isCollapsed ? s.expanded : ''}`}>
                                                                        {isNova ? <ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" /> : '▶'}
                                                                    </span>
                                                                    <div className={s.messagePreview}>
                                                                        <div className={s.messageTitle}>
                                                                            {item.title || 'Без названия'}
                                                                        </div>
                                                                        {item.subtitle && (
                                                                            <div className={s.messageSubtitle}>
                                                                                {item.subtitle}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className={s.messageActions}>
                                                                    <label 
                                                                        className={s.fullWidthCheckbox}
                                                                        title="На всю ширину"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={item.fullWidth || false}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                updateSectionMessage(index, 'fullWidth', e.target.checked);
                                                                            }}
                                                                        />
                                                                        <span className={s.checkmark}>{isNova ? <MoveHorizontal size={12} strokeWidth={2.3} aria-hidden="true" /> : '✓'}</span>
                                                                    </label>
                                                                    <button
                                                                        className={s.deleteMessageBtn}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeMessageFromSection(index);
                                                                        }}
                                                                        title="Удалить сообщение"
                                                                    >
                                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: '11px', height: '11px', minWidth: '11px', minHeight: '11px'}}>
                                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <Collapsible open={!isCollapsed}>
                                                                <div className={`${s.messageFields} ${isCollapsing ? s.messageFieldsCollapsing : ''}`}>
                                                                    <div className={s.fieldGroup}>
                                                                        <label>Заголовок (отображается в MiniFigma):</label>
                                                                        <input
                                                                            type="text"
                                                                            value={item.title || ''}
                                                                            onChange={(e) => updateSectionMessage(index, 'title', e.target.value)}
                                                                            placeholder="Название сообщения"
                                                                            className={s.messageInput}
                                                                            onFocus={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="true"]');
                                                                                if (messageCard) messageCard.draggable = false;
                                                                                setActiveFigmaTextarea(e.target);
                                                                                e.target.dataset.messageIndex = index;
                                                                                e.target.dataset.field = 'title';
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="false"]');
                                                                                if (messageCard) messageCard.draggable = true;
                                                                            }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onMouseUp={(e) => e.stopPropagation()}
                                                                            onDragStart={(e) => e.preventDefault()}
                                                                            onSelectStart={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    
                                                                    <div className={s.fieldGroup}>
                                                                        <label>Подзаголовок (описание):</label>
                                                                        <input
                                                                            type="text"
                                                                            value={item.subtitle || ''}
                                                                            onChange={(e) => updateSectionMessage(index, 'subtitle', e.target.value)}
                                                                            placeholder="Краткое описание"
                                                                            className={s.messageInput}
                                                                            onFocus={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="true"]');
                                                                                if (messageCard) messageCard.draggable = false;
                                                                                setActiveFigmaTextarea(e.target);
                                                                                e.target.dataset.messageIndex = index;
                                                                                e.target.dataset.field = 'subtitle';
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="false"]');
                                                                                if (messageCard) messageCard.draggable = true;
                                                                            }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onMouseUp={(e) => e.stopPropagation()}
                                                                            onDragStart={(e) => e.preventDefault()}
                                                                            onSelectStart={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    
                                                                    <div className={s.fieldGroup}>
                                                                        <label>Текст для копирования:</label>
                                                                        <textarea
                                                                            value={item.text || ''}
                                                                            onChange={(e) => updateSectionMessage(index, 'text', e.target.value)}
                                                                            placeholder="Текст который будет скопирован при нажатии"
                                                                            className={s.messageTextarea}
                                                                            rows="4"
                                                                            onFocus={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="true"]');
                                                                                if (messageCard) messageCard.draggable = false;
                                                                                setActiveFigmaTextarea(e.target);
                                                                                e.target.dataset.messageIndex = index;
                                                                                e.target.dataset.field = 'text';
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const messageCard = e.target.closest('[draggable="false"]');
                                                                                if (messageCard) messageCard.draggable = true;
                                                                            }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onMouseUp={(e) => e.stopPropagation()}
                                                                            onDragStart={(e) => e.preventDefault()}
                                                                            onSelectStart={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Collapsible>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className={s.modalFooter}>
                            <button 
                                className={s.cancelBtn}
                                onClick={() => {
                                    setIsSectionEditorOpen(false);
                                    setCollapsedMessages({});
                                    setCollapsingMessages({});
                                }}
                            >
                                Отмена
                            </button>
                            <button 
                                className={s.saveBtn}
                                onClick={saveSectionChanges}
                            >
                                Сохранить изменения
                            </button>
                        </div>
                    </div>
                    )}
                </div>
            )}

            {/* Модальное окно для выбора сообщений в группу */}
            {isGroupSelectionModalOpen && (
                <div className={`${s.modalOverlay} ${s.groupSelectionOverlay}`}>
                    <div className={`${s.miniFigmaModal} ${s.groupSelectionModal}`}>
                        <div className={s.modalHeader}>
                            <h3>
                                {isNova && (
                                    <span className={s.figModalGlyph}>
                                        <FolderPlus size={15} strokeWidth={2.2} aria-hidden="true" />
                                    </span>
                                )}
                                Создать группу сообщений
                            </h3>
                            <button 
                                className={s.closeBtn}
                                onClick={closeGroupSelectionModal}
                            >
                                {isNova ? <XGlyph size={17} strokeWidth={2.2} aria-hidden="true" /> : '×'}
                            </button>
                        </div>
                        
                        <div className={s.modalBody}>
                            <div className={s.groupTitleSection}>
                                <div className={s.groupTitleRow}>
                                    <div className={s.groupTitleField}>
                                        <label>Название группы:</label>
                                        <input
                                            type="text"
                                            value={newGroupTitle}
                                            onChange={(e) => setNewGroupTitle(e.target.value)}
                                            placeholder="Название группы"
                                            className={s.groupTitleInput}
                                        />
                                    </div>
                                    <div className={s.groupColorField}>
                                        <label>Цвет группы:</label>
                                        <input
                                            type="color"
                                            value={newGroupColor}
                                            onChange={(e) => setNewGroupColor(e.target.value)}
                                            className={s.groupColorInput}
                                        />
                                    </div>
                                </div>
                                
                                <div className={s.groupOptionsRow}>
                                    <label className={s.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={newGroupAlwaysOpen}
                                            onChange={(e) => setNewGroupAlwaysOpen(e.target.checked)}
                                            className={s.checkbox}
                                        />
                                        <span className={s.checkboxText}>Всегда раскрыта (не сворачивается)</span>
                                    </label>
                                </div>
                            </div>
                            <div className={s.messageSelectionSection}>
                                <h4>Выберите сообщения для группы:</h4>
                                <div className={s.messageSelectionList}>
                                    {(() => {
                                        const editingGroupIndex = window.editingGroupIndex;
                                        const isEditingExistingGroup = editingGroupIndex !== undefined;
                                        
                                        // Получаем все доступные сообщения
                                        let availableMessages = (editingSection?.messages || [])
                                            .filter(msg => msg.type !== 'group');
                                        
                                        // Если редактируем существующую группу, добавляем её сообщения
                                        if (isEditingExistingGroup) {
                                            const existingGroup = editingSection?.messages[editingGroupIndex];
                                            if (existingGroup && existingGroup.type === 'group') {
                                                availableMessages = [...availableMessages, ...(existingGroup.messages || [])];
                                            }
                                        }
                                        
                                        return availableMessages.map((message) => (
                                            <div 
                                                key={message.id} 
                                                className={`${s.messageSelectionCard} ${selectedMessagesForGroup.includes(message.id) ? s.selected : ''}`}
                                            >
                                                <div className={s.messageSelectionInfo}>
                                                    <div className={s.messageTitle}>
                                                        {message.title || 'Без названия'}
                                                    </div>
                                                    {message.subtitle && (
                                                        <div className={s.messageSubtitle}>
                                                            {message.subtitle}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    className={`${s.selectMessageBtn} ${selectedMessagesForGroup.includes(message.id) ? s.selected : ''}`}
                                                    onClick={() => toggleMessageSelection(message.id)}
                                                >
                                                    {isNova
                                                        ? (selectedMessagesForGroup.includes(message.id)
                                                            ? <Check size={14} strokeWidth={2.6} aria-hidden="true" />
                                                            : <Plus size={14} strokeWidth={2.4} aria-hidden="true" />)
                                                        : (selectedMessagesForGroup.includes(message.id) ? '✓' : '+')}
                                                </button>
                                            </div>
                                        ));
                                    })()}
                                    
                                    {(() => {
                                        const editingGroupIndex = window.editingGroupIndex;
                                        const isEditingExistingGroup = editingGroupIndex !== undefined;
                                        
                                        let availableMessages = (editingSection?.messages || [])
                                            .filter(msg => msg.type !== 'group');
                                        
                                        if (isEditingExistingGroup) {
                                            const existingGroup = editingSection?.messages[editingGroupIndex];
                                            if (existingGroup && existingGroup.type === 'group') {
                                                availableMessages = [...availableMessages, ...(existingGroup.messages || [])];
                                            }
                                        }
                                        
                                        return availableMessages.length === 0 && (
                                            <div className={s.emptySelectionState}>
                                                <p>В разделе нет сообщений для группировки</p>
                                                <p>Сначала создайте несколько сообщений</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        
                        <div className={s.modalFooter}>
                            <button 
                                className={s.cancelBtn}
                                onClick={closeGroupSelectionModal}
                            >
                                Отмена
                            </button>
                            <button 
                                className={s.saveBtn}
                                onClick={createGroupFromSelectedMessages}
                                disabled={selectedMessagesForGroup.length === 0}
                            >
                                {(() => {
                                    const isEditingExistingGroup = window.editingGroupIndex !== undefined;
                                    return isEditingExistingGroup 
                                        ? `Сохранить изменения (${selectedMessagesForGroup.length})` 
                                        : `Создать группу (${selectedMessagesForGroup.length})`;
                                })()}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Модальное окно для выбора конфигов при сохранении */}
            {isSaveModalOpen && isNova ? (() => {
                const configsData = availableConfigs.map((config) => {
                    const filename = config.filename || config;
                    const displayName = config.name || filename.replace('.json', '');
                    // Флаг: сначала из кэша конфига (selectedFlag), потом — эмодзи в имени
                    const code = configFlags[filename] || emojiFlagToCode(displayName) || null;
                    return { filename, displayName, code, isCurrent: filename === currentConfigFile };
                });
                // Группы стран для дропдауна-фильтра (алфавит, «Без страны» в конце)
                const countryGroups = (() => {
                    const m = new Map();
                    configsData.forEach((c) => {
                        const key = c.code || 'unknown';
                        m.set(key, (m.get(key) || 0) + 1);
                    });
                    return Array.from(m.entries())
                        .map(([code, count]) => ({
                            code,
                            count,
                            name: code === 'unknown' ? 'Без страны' : getCountryNameByFlag(code),
                        }))
                        .sort((a, b) => {
                            if (a.code === 'unknown') return 1;
                            if (b.code === 'unknown') return -1;
                            return a.name.localeCompare(b.name);
                        });
                })();
                const countryName = (c) => (c === 'unknown' ? '\uffff' : getCountryNameByFlag(c));
                const q = saveSearch.trim().toLowerCase();
                let shown = q
                    ? configsData.filter((c) =>
                        c.displayName.toLowerCase().includes(q) || c.filename.toLowerCase().includes(q))
                    : configsData.slice();
                if (saveCountryFilter.length) {
                    shown = shown.filter((c) => saveCountryFilter.includes(c.code || 'unknown'));
                }
                // Сортировка группами по стране, внутри — по имени
                shown = shown.sort((a, b) => {
                    const ca = a.code || 'unknown', cb = b.code || 'unknown';
                    if (ca !== cb) return countryName(ca).localeCompare(countryName(cb));
                    return a.displayName.localeCompare(b.displayName);
                });
                const plural = (n, one, few, many) => {
                    const m10 = n % 10, m100 = n % 100;
                    if (m10 === 1 && m100 !== 11) return one;
                    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
                    return many;
                };
                return (
                <div className={s.scfOverlay} onClick={handleCloseSaveModal}>
                    <div className={s.scf} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <div className={s.scfHead}>
                            <span className={s.scfHeadGlyph}>
                                <SaveGlyph size={16} strokeWidth={2.1} aria-hidden="true" />
                            </span>
                            <div className={s.scfHeadText}>
                                <div className={s.scfTitle}>Сохранить настройки в конфиги</div>
                                <div className={s.scfSubtitle}>
                                    Выберите один или несколько конфигов — текущие настройки запишутся в каждый из них
                                </div>
                            </div>
                            <button type="button" className={s.scfClose} onClick={handleCloseSaveModal} aria-label="Закрыть">
                                <XGlyph size={17} strokeWidth={2.2} aria-hidden="true" />
                            </button>
                        </div>

                        {/* Закреплённая зона: выбранные конфиги «улетают» сюда */}
                        {!isLoadingConfigs && availableConfigs.length > 0 && (
                            <div className={s.scfTargets}>
                                <div className={s.scfTargetsHead}>
                                    <BadgeCheck size={13} strokeWidth={2.2} aria-hidden="true" />
                                    <span>Сохранить в</span>
                                    <span className={s.scfTargetsCount}>{selectedConfigs.length}</span>
                                </div>
                                {selectedConfigs.length === 0 ? (
                                    <div className={s.scfTargetsEmpty}>
                                        Отметьте конфиги ниже — они закрепятся здесь
                                    </div>
                                ) : (
                                    <div className={s.scfChips}>
                                        {selectedConfigs.map((filename) => {
                                            const meta = configsData.find((c) => c.filename === filename);
                                            const label = meta ? meta.displayName : filename;
                                            const code = meta ? meta.code : emojiFlagToCode(label);
                                            const cleanName = stripEmojiFlag(label) || label;
                                            return (
                                                <span key={filename} className={s.scfChip}>
                                                    <span className={s.scfChipFlag}>
                                                        {code
                                                            ? <FlagIcon code={code} className={s.scfChipFlagImg} alt="" />
                                                            : <FileText size={12} strokeWidth={2.2} aria-hidden="true" />}
                                                    </span>
                                                    <span className={s.scfChipName}>{cleanName}</span>
                                                    <button
                                                        type="button"
                                                        className={s.scfChipRemove}
                                                        onClick={() => handleConfigToggle(filename)}
                                                        aria-label={`Убрать ${cleanName}`}
                                                    >
                                                        <XGlyph size={12} strokeWidth={2.6} aria-hidden="true" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {!isLoadingConfigs && availableConfigs.length > 0 && (
                            <div className={s.scfTools}>
                                <div className={s.scfSearch}>
                                    <Search size={15} strokeWidth={2.1} aria-hidden="true" />
                                    <input
                                        type="text"
                                        value={saveSearch}
                                        onChange={(e) => setSaveSearch(e.target.value)}
                                        placeholder="Поиск конфига…"
                                        spellCheck="false"
                                    />
                                    {saveSearch && (
                                        <button type="button" className={s.scfSearchClear} onClick={() => setSaveSearch('')} aria-label="Очистить">
                                            <XGlyph size={13} strokeWidth={2.4} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                <div className={s.scfCountry}>
                                    <button
                                        type="button"
                                        className={`${s.scfCountryBtn} ${saveCountryFilter.length ? s.scfCountryBtnOn : ''}`}
                                        onClick={() => setSaveCountryOpen((v) => !v)}
                                    >
                                        <Globe size={14} strokeWidth={2.1} aria-hidden="true" />
                                        {saveCountryFilter.length
                                            ? `Страны: ${saveCountryFilter.length}`
                                            : 'Все страны'}
                                        <ChevronDown
                                            size={14}
                                            strokeWidth={2.2}
                                            aria-hidden="true"
                                            className={`${s.scfCountryChev} ${saveCountryOpen ? s.scfCountryChevOpen : ''}`}
                                        />
                                    </button>
                                    {saveCountryOpen && (
                                        <>
                                            <div className={s.scfCountryBackdrop} onClick={() => setSaveCountryOpen(false)} />
                                            <div className={s.scfCountryPop}>
                                                <button
                                                    type="button"
                                                    className={`${s.scfCountryOpt} ${saveCountryFilter.length === 0 ? s.scfCountryOptOn : ''}`}
                                                    onClick={() => setSaveCountryFilter([])}
                                                >
                                                    <span className={s.scfCountryCheck}>
                                                        {saveCountryFilter.length === 0 && <Check size={12} strokeWidth={3} aria-hidden="true" />}
                                                    </span>
                                                    <span className={s.scfCountryGlobe}>
                                                        <Globe size={14} strokeWidth={2} aria-hidden="true" />
                                                    </span>
                                                    <span className={s.scfCountryName}>Все страны</span>
                                                    <span className={s.scfCountryCount}>{configsData.length}</span>
                                                </button>
                                                <div className={s.scfCountryDivider} />
                                                {countryGroups.map((g) => {
                                                    const on = saveCountryFilter.includes(g.code);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={g.code}
                                                            className={`${s.scfCountryOpt} ${on ? s.scfCountryOptOn : ''}`}
                                                            onClick={() => setSaveCountryFilter((prev) =>
                                                                on ? prev.filter((x) => x !== g.code) : [...prev, g.code])}
                                                        >
                                                            <span className={s.scfCountryCheck}>
                                                                {on && <Check size={12} strokeWidth={3} aria-hidden="true" />}
                                                            </span>
                                                            {g.code === 'unknown'
                                                                ? <span className={s.scfCountryGlobe}><FileText size={14} strokeWidth={2} aria-hidden="true" /></span>
                                                                : <FlagIcon code={g.code} className={s.scfCountryFlag} alt="" />}
                                                            <span className={s.scfCountryName}>{g.name}</span>
                                                            <span className={s.scfCountryCount}>{g.count}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className={s.scfBody}>
                            {isLoadingConfigs ? (
                                <div className={s.scfLoading}>
                                    <div className={s.scfSpinner} />
                                    <p>Загружаем список конфигов…</p>
                                </div>
                            ) : availableConfigs.length === 0 ? (
                                <div className={s.scfEmpty}>
                                    <FileText size={26} strokeWidth={1.6} aria-hidden="true" />
                                    <p>Нет доступных конфигов для сохранения</p>
                                </div>
                            ) : shown.length === 0 ? (
                                <div className={s.scfEmpty}>
                                    <Search size={26} strokeWidth={1.6} aria-hidden="true" />
                                    <p>{saveSearch
                                        ? <>Ничего не найдено по запросу «{saveSearch}»</>
                                        : 'Нет конфигов для выбранных стран'}</p>
                                </div>
                            ) : (
                                <div className={s.scfList}>
                                    {shown.map((c) => {
                                        const isSelected = selectedConfigs.includes(c.filename);
                                        const code = c.code;
                                        const cleanName = stripEmojiFlag(c.displayName) || c.displayName;
                                        return (
                                            <button
                                                type="button"
                                                key={c.filename}
                                                className={`${s.scfRow} ${isSelected ? s.scfRowOn : ''}`}
                                                onClick={() => handleConfigToggle(c.filename)}
                                            >
                                                <span className={`${s.scfCheck} ${isSelected ? s.scfCheckOn : ''}`}>
                                                    {isSelected && <Check size={13} strokeWidth={3} aria-hidden="true" />}
                                                </span>
                                                <span className={s.scfFlag}>
                                                    {code
                                                        ? <FlagIcon code={code} className={s.scfFlagImg} alt="" />
                                                        : <FileText size={16} strokeWidth={2} aria-hidden="true" />}
                                                </span>
                                                <span className={s.scfInfo}>
                                                    <span className={s.scfName}>
                                                        {cleanName}
                                                        {c.isCurrent && <span className={s.scfCurrentBadge}>текущий</span>}
                                                    </span>
                                                    <span className={s.scfFile}>{c.filename}</span>
                                                </span>
                                                {isSelected && (
                                                    <span className={s.scfPinnedTag}>
                                                        <ArrowUp size={11} strokeWidth={2.6} aria-hidden="true" />
                                                        закреплён
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className={s.scfFoot}>
                            <span className={s.scfFootInfo}>
                                {selectedConfigs.length > 0
                                    ? <>Выбрано: <strong>{selectedConfigs.length}</strong> {plural(selectedConfigs.length, 'конфиг', 'конфига', 'конфигов')}</>
                                    : 'Ничего не выбрано'}
                            </span>
                            <button type="button" className={s.scfCancel} onClick={handleCloseSaveModal}>
                                Отмена
                            </button>
                            <button
                                type="button"
                                className={s.scfSave}
                                onClick={handleSaveToConfigs}
                                disabled={selectedConfigs.length === 0 || isLoadingConfigs}
                            >
                                <SaveGlyph size={15} strokeWidth={2.1} aria-hidden="true" />
                                Сохранить{selectedConfigs.length > 0 ? ` (${selectedConfigs.length})` : ''}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })() : isSaveModalOpen && (
                <div className={`${s.modalOverlay} ${s.saveConfigOverlay}`}>
                    <div className={`${s.miniFigmaModal} ${s.saveConfigModal}`}>
                        <div className={s.modalHeader}>
                            <h3>Выберите конфиги для сохранения</h3>
                            <button 
                                className={s.closeBtn}
                                onClick={handleCloseSaveModal}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className={s.modalBody}>
                            {isLoadingConfigs ? (
                                <div className={s.loadingContainer}>
                                    <div className={s.loadingSpinner}></div>
                                    <p>Загружаем список конфигов...</p>
                                </div>
                            ) : (
                                <div className={s.configsList}>
                                    <div className={s.configsHeader}>
                                        <p>Выберите конфиги, в которые хотите сохранить текущие настройки:</p>
                                    </div>
                                    
                                    {availableConfigs.length === 0 ? (
                                        <div className={s.noConfigs}>
                                            <p>Нет доступных конфигов для сохранения</p>
                                        </div>
                                    ) : (
                                        <div className={s.configsGrid}>
                                            {availableConfigs.map((config, index) => {
                                                const filename = config.filename || config;
                                                const displayName = config.name || filename.replace('.json', '');
                                                const isSelected = selectedConfigs.includes(filename);
                                                const isCurrent = filename === currentConfigFile;
                                                
                                                return (
                                                    <div 
                                                        key={index}
                                                        className={`${s.configItem} ${isSelected ? s.selected : ''} ${isCurrent ? s.current : ''}`}
                                                        onClick={() => handleConfigToggle(filename)}
                                                    >
                                                        <div className={s.configCheckbox}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleConfigToggle(filename)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className={s.configInfo}>
                                                            <div className={s.configName}>
                                                                {displayName}
                                                                {isCurrent && <span className={s.currentBadge}>текущий</span>}
                                                            </div>
                                                            <div className={s.configFilename}>{filename}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    <div className={s.selectionInfo}>
                                        <p>Выбрано конфигов: <strong>{selectedConfigs.length}</strong></p>
                                        {selectedConfigs.length === 0 && (
                                            <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                                                Выберите конфиги для сохранения
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className={s.modalFooter}>
                            <button 
                                className={s.cancelBtn}
                                onClick={handleCloseSaveModal}
                            >
                                Отмена
                            </button>
                            <button 
                                className={s.saveBtn}
                                onClick={handleSaveToConfigs}
                                disabled={selectedConfigs.length === 0 || isLoadingConfigs}
                            >
                                Сохранить в {pluralConfigs(selectedConfigs.length)}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiteSettingsPage;